/**
 * GameServer Connection Manager for REAVERS (Stateless-Aware)
 *
 * Handles direct WebSocket connections to GameServers with fallback
 * to proxied connections through Cloud Functions when direct fails.
 *
 * Features:
 * - Direct WebSocket connection with retry logic
 * - Automatic fallback to proxied connection
 * - Game session state management
 * - Message routing (game vs world commands)
 * - Reconnection handling with exponential backoff
 * - Connection health monitoring
 * - Stateless GameServer awareness (session restoration)
 * - Connection status UI updates
 */

/**
 * [channel-delivery-ledger Step 3] Active ordering kill-switch.
 *
 * When true, `_ledgerAdmit` actively dedups + reorders seq-bearing frames on
 * content channels 0-3 (buffer out-of-order, drop dups, release in seq order,
 * epoch-aware baseline, bounded-grace force-release). When false, admission
 * short-circuits to pure pass-through (`{admit:true, release:[]}`) — today's
 * behavior — so the active path can be disabled WITHOUT a code revert if the
 * VP gate surfaces trouble. The shadow recorder (`_ledgerShadowRecord`) keeps
 * running regardless of this flag.
 *
 * Spec: .claude_specs/engine/channel-delivery-ledger/step-3-reorder-dedup.md
 */
const LEDGER_ACTIVE_ORDERING = true;

/**
 * THE PUMP'S COMPLETION AUTHORITY — one record, one classifier, one terminal
 * writer, one backstop. [2026-07-28-004-ENGINE]
 *
 * Exactly ONE thing answers "does this frame belong to the beat I am
 * completing?": the live `CycleCompletion` record on `TurnPump._state`.
 *
 *   1. IDENTITY. A cycle starts UNBOUND. It BINDS to the cycle_id of the first
 *      server-stamped CURRENT content frame. Identity is never minted at input —
 *      the resolving phase's clock does not exist yet.
 *   2. CLASSIFICATION. `_attributeFrame` classifies every beat frame once, at
 *      ingress: current / prior / future / unstamped. Only `current` (and the
 *      documented temporal exceptions) mutate the record. `prior` and `future`
 *      may still RENDER, but are counted and logged LOUDLY — silent absorption
 *      is the bug class this replaced.
 *   3. TERMINAL. `_terminalizeCh0(sense)` is the sole writer of
 *      `ch0.rendered` / `ch0.voided`. First terminal wins; a second attempt is
 *      refused and counted. The two can never both be true.
 *   4. RENDER ORDER is a read-only VIEW of the same record (`_ch0Terminal`,
 *      `_ch1Discharged`, `_cycleBarrierReached`) — one state, two readers that
 *      cannot disagree.
 *   5. BACKSTOP. One rolling 90s watchdog (`_armStallWatchdog`), armed at cycle
 *      start and re-armed on every attributed-current frame. The pump can never
 *      hang with no escape.
 *
 * The two-signal unlock is unchanged: ch0 terminal AND the turn's
 * `turn_complete`. `phase_clock` supplies the reborn clock, never the unlock.
 *
 * Spec: .claude_specs/engine/pump-completion-consolidation/DESIGN.md
 */

/**
 * [channel-delivery-ledger Step 3] Bounded grace before a buffered gap is
 * force-released (no-deadlock invariant). Step 3 has NO NACK/resend (step 4),
 * so a truly-lost frame's gap never fills — we force-release after this window,
 * advancing past the gap and recording it `unfilled`. ~300ms ≈ one render tick;
 * generous enough that a merely-late reorder fills first, short enough that the
 * pump never visibly stalls. Timer-based (not frame-count): only a timer fires
 * when NO successor frame arrives — the dominant single-WS loss case.
 */
const LEDGER_GAP_GRACE_MS = 300;

/**
 * [channel-delivery-ledger Step 3/5] Content channels (FULL ordering treatment).
 * 0 = ch0 scene, 1 = ch1 commentary, 2 = ch2 mechanics, 3 = ch3 lifecycle,
 * 4 = ch4 dialogue.
 * Channel 9 is the CONTROL stream — it is no longer pass-through as of step 5;
 * it is classified by `_ledgerFrameClass` into `lifecycle-control` (admitted,
 * ordered, recoverable — with a SHORT force-release grace) or `bootstrap`
 * (connection-setup, pass-through). See LEDGER_CONTROL_CHANNEL below.
 *
 * [2026-08-03-002-PROMPT] CH4 IS ENROLLED HERE, AND THIS IS THE ENROLLMENT THAT
 * MATTERS. Ledger participation is opt-in PER CHANNEL — the ledger is
 * channel-*parameterized*, not channel-*generic*. A channel absent from this set
 * is classed `null` by `_ledgerFrameClass` and passes straight through with NO
 * ordering, NO dedup, NO gap detection, and NO NACK/resend: a lost dialogue line
 * would be indistinguishable from a line never spoken, which is the exact
 * failure mode ch4 was built to end.
 *
 * ⚠️ DO NOT also add 4 to the `participates` test in `_cycleNoteAbort` — that is
 * a DIFFERENT gate (whether a gap ABORTS the ch0 gameplay unlock) and enrolling
 * ch4 there would violate D1 by letting a lost dialogue line block the ch0
 * cycle. See the note at that call site.
 */
/**
 * Gate 1, ENROLMENT -- read off THE FRAME REGISTRY (`frame.md` P10), never
 * re-spelled here. One home: `services/plug_core/frame.py`, rendered to
 * `frame-registry.js`, which index.html loads BEFORE this file.
 *
 * ⚠️ NO FALLBACK, DELIBERATELY. If the registry is absent this throws on the
 * spot, and that is the only honest answer: a wrong enrolment set is SILENT --
 * a channel absent from it passes through unaccounted, and a lost frame becomes
 * indistinguishable from one that was never sent (CHANNEL REGISTRY LAW, gate
 * 3). A `|| [0,1,2,3,4]` here would reinstate the very duplication the registry
 * retires, and would do it invisibly.
 *
 * `globalThis`, not `window`: it is the one name the browser, the node:test
 * import-style suites, and the `vm` sandbox harnesses all agree on.
 */
const LEDGER_CONTENT_CHANNELS = globalThis.FrameRegistry.CONTENT_CHANNELS;

/**
 * [channel-delivery-ledger Step 5] The control stream channel id (server stamps
 * turn_complete/phase_clock/phase_settled/room_state/spawn_complete/
 * action_result on channel 9 — see stateless_gameserver._stamp_frame callsites
 * in networking.py). Frame CLASS, not channel number alone, decides treatment:
 * `lifecycle-control` (clock/turn lifecycle) is admitted+ordered+recoverable;
 * `bootstrap` (connection setup) passes straight through. See `_ledgerFrameClass`.
 */
const LEDGER_CONTROL_CHANNEL = globalThis.FrameRegistry.CONTROL_CHANNEL;

/**
 * Gate 2, BLOCKING -- whether a gap on this channel ABORTS the ch0 unlock.
 * Off the registry's `aborts_unlock` field (`frame.md` P10). This was
 * `(channel === 0 || channel === LEDGER_CONTROL_CHANNEL)` spelled inline at
 * the abort site, where the next channel's arrival would not have found it.
 *
 * ⚠️ NOT the same gate as LEDGER_CONTENT_CHANNELS, and the two are routinely
 * confused. ch4 is enrolled there and absent here ON PURPOSE: D1 of the ch4
 * contract is that ch0 never waits on ch4, so a lost dialogue line must not
 * poison the beat the player is waiting on. Changing that is a registry row,
 * not an edit at the call site.
 */
const LEDGER_ABORT_PARTICIPATING_CHANNELS =
    globalThis.FrameRegistry.ABORT_PARTICIPATING_CHANNELS;

/**
 * [channel-delivery-ledger Step 5] Wire `type`s on the control channel that are
 * `lifecycle-control` — they drive the visible clock + turn lifecycle, so they
 * are admitted under ordering with dedup + gap→NACK→resend, BUT with the SHORT
 * force-release grace (LEDGER_CONTROL_GAP_GRACE_MS) so the clock never stalls.
 */
const LEDGER_LIFECYCLE_CONTROL_TYPES = globalThis.FrameRegistry.LIFECYCLE_CONTROL_TYPES;

/**
 * [channel-delivery-ledger Step 5] Wire `type`s on the control channel that get
 * the `bootstrap` treatment: PASS-THROUGH (dispatched immediately, never
 * buffered) BUT seq-ACCOUNTED on ch9 so they never punch a phantom gap in the
 * shared control seq stream.
 *
 * Two sub-cases share this treatment:
 *   - true bootstrap (`room_state`, `spawn_complete`): connection-setup frames
 *     whose recovery is the step-4 handshake/resync, NOT in-cycle ordering.
 *     Gating them would risk stalling spawn/room sync, and they are idempotent
 *     room/spawn syncs the FSM already guards.
 *   - `action_result`: the per-phase mechanical result. It is dispatched
 *     immediately (its switch case applies the pump's beat-cursor rule itself —
 *     it must NOT wait behind a control hole), but the server stamps it
 *     a ch9 seq (networking._async_deliver_phase_result stamps action_result
 *     THEN turn_complete on the same ch9 stream within one phase delivery). If it
 *     were `null`-class (pure ignore), the NEXT lifecycle-control frame
 *     (action_result_seq + 1, i.e. turn_complete) would look like a phantom gap
 *     and fire a needless NACK -> server resend EVERY phase even on a clean run.
 *     Seq-accounting it (advance contiguity / mark seen) keeps the ch9 stream
 *     contiguous. See `_ledgerAdmit`'s bootstrap branch.
 *   - `spawn`: the third frame of the void -> vessel ARRIVAL completion bracket
 *     (`void_entry._send_control_frame`, whose own docstring already calls it a
 *     bootstrap frame). It was MISSING from this set until [2026-07-28-012-ENGINE]
 *     and, being seq-stamped but unclassed, advanced no watermark — so the
 *     ARRIVAL `phase_clock` behind it read as a gap over three frames that had
 *     all arrived, the force-release resolved them `aborted`, and every new
 *     life's first beat deadlocked to the 90s stall watchdog.
 */
const LEDGER_BOOTSTRAP_CONTROL_TYPES = globalThis.FrameRegistry.BOOTSTRAP_CONTROL_TYPES;

/**
 * [2026-07-28-012-ENGINE] Types this session has already screamed about, so the
 * unclassed-control-frame alarm fires ONCE per type instead of once per frame.
 * Exposed on `window.__LEDGER_UNCLASSED_CONTROL_TYPES` for the VP + the pins:
 * a non-empty set is a STANDING DEFECT, not a diagnostic curiosity.
 */
const LEDGER_UNCLASSED_CONTROL_TYPES = new Set();

/**
 * [channel-delivery-ledger Step 5] CLOCK-NEVER-STALLS carve-out (load-bearing).
 *
 * Lifecycle-control frames are latency-critical: the visible clock + the
 * two-signal prompt unlock depend on them. A control gap MUST never hold the
 * clock longer than ONE short grace. So control uses a much shorter force-release
 * grace than content (LEDGER_GAP_GRACE_MS=300). The step-4 NACK/resend still
 * fires first (LEDGER_NACK_COALESCE_MS=80) and usually wins the race on a real
 * loss; this is purely the aggressive backstop so the lifecycle advances within
 * about one render tick (~2 frames @ 60fps ≈ 33ms; 130ms gives the 80ms NACK a
 * chance to be answered yet still releases inside ~8 frames) even mid-repair.
 * Preserves REG-4 (clock greys in place), REG-2 (two-signal unlock), M2
 * (per-player countdown), M11 (viewport stability) — the clock advances rather
 * than freezing on a missing control frame.
 */
const LEDGER_CONTROL_GAP_GRACE_MS = 130;

/**
 * [channel-delivery-ledger Step 4] Coalescing tick before a buffered gap emits
 * a NACK. A merely-late reorder on a single WS usually fills within a frame, so
 * we wait this brief window before asking the server to resend — avoiding a
 * needless NACK for a frame that was only momentarily out of order. Must be
 * shorter than LEDGER_GAP_GRACE_MS so the NACK gets a chance to be answered by
 * a resend BEFORE the step-3 grace force-releases the gap (resend is the
 * upgrade; grace remains the ultimate no-deadlock backstop).
 */
const LEDGER_NACK_COALESCE_MS = 80;

/**
 * [channel-delivery-ledger Step 4] ACK is PRUNE-ONLY (DESIGN §0.2) — it frees
 * server ring memory, never drives retransmit. Frame volume is low (a handful
 * per phase), so we ACK whenever a channel's contiguous watermark advances,
 * debounced by this window to coalesce a burst of in-order frames into one ACK.
 * The happy path therefore adds at most one tiny ACK per channel per phase and
 * ZERO resend/NACK latency.
 */
const LEDGER_ACK_DEBOUNCE_MS = 250;

/**
 * [2026-06-24-002-ENGINE — ch1 divine commentary text-only] Tunable typewriter
 * cadence (ms per character) for ch1 (divine commentary, channel:1, italic).
 *
 * ch1 no longer carries voice (the engine half sends include_voice:false to
 * halve voice-GPU load — see ticket + F&F epic 2026-06-25-001-ENGINE). With no
 * audio to pace against, ch1 spools out as a FAST italic typewriter: a quick
 * story-nudge that fills ~8s of screen-time for typical commentary length,
 * NOT a 30s voice wait. Tune this SINGLE constant to make ch1 feel faster /
 * slower. Lower = faster spool. (ch0 stays voiced and audio-paced — unaffected.)
 *
 * Sizing: a typical ch1 line is ~320 chars; 25ms/char ≈ 8s screen-time.
 * This is deliberately faster than the legacy 30ms no-audio fallback (still
 * used by ch0 when its audio is missing) and far faster than full voice pacing.
 */
const CH1_TYPEWRITER_MS_PER_CHAR = 25;

/**
 * How long the ch0 typewriter will wait for the voice clip's `loadedmetadata`
 * before starting the text anyway. [2026-08-24-045-ENGINE]
 *
 * This is the whole cost of the voice/text alignment, and it is bounded on
 * purpose: the wait buys a synchronised start and the REAL audio duration, but
 * a slow probe must never become a slow BEAT. Past this the text starts on the
 * assumed pace and the voice lands where it lands -- LOUDLY, because a silent
 * text channel is a far worse defect than a late voice.
 *
 * Sized against the observed failure modes rather than a round number: the
 * drops this path already classifies are a 404 upload-race and a 403
 * signed-URL expiry, both of which resolve or fail well inside a second.
 */
const VOICE_READY_MAX_WAIT_MS = 1500;

/**
 * [FB-HUD-08] WHAT AN UNANSWERABLE BUILTIN READ LOOKS LIKE ON SCREEN.
 *
 * `BuiltinHandler` marks a listing it could not compute with an `error` field
 * and an EMPTY list (`builtin_commands.py::_handle_skills` / `_handle_inventory`).
 * `_displayBuiltinResult` renders one of these instead of the empty list, so an
 * unreadable kit never renders as "You have no skills." — the plausible zero
 * [2026-08-12-032-ENGINE] exists to prevent, and the sentence the character
 * screen is explicitly forbidden from saying (hud-screens.js:397-402).
 *
 * ⚠️ THESE ARE CLIENT-OWNED SENTENCES, NOT A SECOND AUTHORITY FOR THE REASON.
 * The server's `error` string is a UNION: `_handle_skills` writes a player-ready
 * sentence, but `BuiltinHandler.handle`'s except arm writes
 * `f"Failed to handle {type}: {e}"` and `_handle_stats` writes `{e!r}` — a Python
 * exception repr. Forwarding the field would ship the leak with the good case.
 * The reason still travels, intact, to the console. Same split the screens use.
 *
 * ⚠️ EACH ONE NAMES WHAT IS UNREADABLE. A single generic line would make an
 * unreadable kit and an unreadable inventory indistinguishable on screen, which
 * is the same conflation in a shorter costume.
 */
const BUILTIN_UNREADABLE = {
    skills: 'Your skills could not be read just now.',
    inventory: 'Your inventory could not be read just now.',
    stats: 'Your sheet could not be read just now.',
    help: 'The command list could not be read just now.',
    look: 'This room could not be read just now.',
    // Last resort — a builtin type this client has no sentence for. It still
    // must not render as an empty anything.
    _: 'That could not be read just now.',
};

/**
 * VP Instrumentation Hooks — live state introspection for the Virtual Player.
 *
 * Spec: .claude_specs/engine/vp-hardening-2026-05-09/spec.md (D2/D3/D4/D8/D9).
 *
 * Production safety contract:
 *   - All hooks are NO-OPs unless `window.__VP_INSTRUMENTATION_ENABLED === true`.
 *   - The flag defaults FALSE in prod builds; the VP browser harness sets it
 *     true via Playwright add_init_script BEFORE page load.
 *   - When enabled, hook overhead is bounded (constant-time DOM attr writes,
 *     synchronous state object writes, FIFO ring-buffer push capped at 200).
 *
 * Exposed globals (only when enabled):
 *   - window.__VP_PUMP_STATE          — synchronous snapshot of TurnPump state.
 *   - window.__VP_PUMP_STATE_HISTORY  — last 200 snapshots, FIFO ring buffer.
 *   - window.__VP_PROMPT_STATE        — synchronous snapshot of prompt + caret.
 *   - window.__VP_PROMPT_STATE_HISTORY — last 200 snapshots, FIFO ring buffer.
 *   - window.__vpAnnotate(node, channel, cycleId)
 *                                     — DOM annotation helper. Channels:
 *                                       'ch0', 'ch1', 'ch2_mechanics',
 *                                       'ch3_lifecycle', 'narration', 'system',
 *                                       'room_state', 'user_command'.
 */
(function vpInstrumentation() {
    if (typeof window === 'undefined') return;

    // Default OFF. VP harness flips this true via add_init_script before
    // page navigation. Production builds never expose any of the below.
    if (window.__VP_INSTRUMENTATION_ENABLED === undefined) {
        window.__VP_INSTRUMENTATION_ENABLED = false;
    }

    // Bounded ring-buffer constants (spec D8).
    const HISTORY_CAP = 200;

    // Shared in-memory ring buffers; accessed only by helpers below. Kept on
    // window so VP page.evaluate() can pull them; not deleted on flag flip
    // (no production state to leak — flag is set BEFORE first event).
    if (!Array.isArray(window.__VP_PUMP_STATE_HISTORY)) {
        window.__VP_PUMP_STATE_HISTORY = [];
    }
    if (!Array.isArray(window.__VP_PROMPT_STATE_HISTORY)) {
        window.__VP_PROMPT_STATE_HISTORY = [];
    }
    if (!window.__VP_PUMP_STATE) window.__VP_PUMP_STATE = null;
    if (!window.__VP_PROMPT_STATE) window.__VP_PROMPT_STATE = null;

    /**
     * Annotate a DOM node with channel-render attributes. No-op when
     * instrumentation is disabled OR node is missing.
     *
     * data-vp-kind    RENDER KIND taxonomy: ch0|ch1|ch2_mechanics|ch3_lifecycle|
     *                 narration|system|room_state|user_command|poi_arrival_image
     * data-render-ts  client perf.now() at render time (float ms)
     *
     * ⚠️ [2026-08-08-007-ENGINE] THIS NO LONGER WRITES `data-channel` OR
     * `data-cycle-id`. THE FUNNEL OWNS BOTH, AND AN ATTRIBUTE MAY HAVE ONE
     * WRITER.
     *
     * Those two stamps used to be written HERE, with a different vocabulary:
     * `data-channel="ch2_mechanics"` from this function versus
     * `data-channel="2"` from `TerminalFunnel`. Two writers, two vocabularies,
     * ONE attribute that an instrument dispatches on — and which value survived
     * depended on which ran last at each individual call site. That is "two
     * things wearing one name" on a dispatch key, the defect class this whole
     * ticket exists to close; shipping it inside the fix would have been absurd.
     *
     * The taxonomy this function carries was never a channel anyway:
     * `user_command`, `room_state` and `poi_arrival_image` are RENDER KINDS. So
     * it keeps the taxonomy under its own name, and the channel is read from the
     * funnel's stamp.
     *
     * The harness gains from this: the funnel stamps UNCONDITIONALLY, including
     * production, so `data-channel` / `data-cycle-id` no longer depend on an
     * instrumentation flag being flipped before navigation.
     */
    window.__vpAnnotate = function vpAnnotate(node, kind, _cycleId) {
        // [W1 TTL] The Terminal Truth Log (ttl-emitter.js, ?ttl=1) reads render
        // kind from this stamp, so the annotation gate opens for EITHER
        // instrument. Both default OFF in production.
        if (!window.__VP_INSTRUMENTATION_ENABLED && !window.__TTL_ENABLE) return node;
        if (!node || !node.setAttribute) return node;
        try {
            if (kind) node.setAttribute('data-vp-kind', String(kind));
            const ts = (typeof performance !== 'undefined' && performance.now)
                ? performance.now() : Date.now();
            node.setAttribute('data-render-ts', String(ts));
        } catch (_err) {
            // Defensive: never let annotation throw into render path.
        }
        return node;
    };

    /**
     * Push a pump-state snapshot. Synchronous; called from inside _advance()
     * and event() so VP page.evaluate(() => window.__VP_PUMP_STATE) sees
     * post-event state without races.
     */
    window.__vpPushPumpSnapshot = function vpPushPumpSnapshot(snapshot) {
        if (!window.__VP_INSTRUMENTATION_ENABLED) return;
        try {
            const entry = Object.assign({
                ts: (typeof performance !== 'undefined' && performance.now)
                    ? performance.now() : Date.now(),
            }, snapshot || {});
            window.__VP_PUMP_STATE = entry;
            const hist = window.__VP_PUMP_STATE_HISTORY;
            hist.push(entry);
            if (hist.length > HISTORY_CAP) {
                hist.splice(0, hist.length - HISTORY_CAP);
            }
        } catch (_err) {
            // Defensive: instrumentation never throws into reducer.
        }
    };

    /**
     * Push a prompt-state snapshot. Synchronous from the triggering event.
     */
    window.__vpPushPromptSnapshot = function vpPushPromptSnapshot(snapshot) {
        if (!window.__VP_INSTRUMENTATION_ENABLED) return;
        try {
            const entry = Object.assign({
                ts: (typeof performance !== 'undefined' && performance.now)
                    ? performance.now() : Date.now(),
            }, snapshot || {});
            window.__VP_PROMPT_STATE = entry;
            const hist = window.__VP_PROMPT_STATE_HISTORY;
            hist.push(entry);
            if (hist.length > HISTORY_CAP) {
                hist.splice(0, hist.length - HISTORY_CAP);
            }
        } catch (_err) {
            // Defensive.
        }
    };

    /**
     * Build a prompt-state snapshot from the current DOM. Idempotent.
     */
    window.__vpBuildPromptSnapshot = function vpBuildPromptSnapshot() {
        if (!window.__VP_INSTRUMENTATION_ENABLED) return null;
        try {
            const history = document.getElementById('history');
            // [2026-08-03-014-ENGINE] Reads the ONE authority rather than
            // restating it. This snapshot was the "canonical" definition that
            // nothing could call (it is gated behind the instrumentation flag),
            // which is why hud-shell.js had to keep its own copy. Both now ask
            // the same module, so the drift they were pinned against is not
            // possible rather than merely detected.
            const CL = window.CommandLine;
            const activeInput = CL ? CL.element(document) : null;
            const inputField = CL ? CL.inputField(document) : null;
            const cursor = activeInput
                ? activeInput.querySelector('.cursor')
                : null;
            const promptVisible = !!activeInput;
            const inputDisabled = !!(inputField && inputField.disabled);
            const promptLocked = CL ? CL.isLocked(document) : true;
            const inputValue = inputField ? (inputField.value || '') : '';
            const caret = inputField && typeof inputField.selectionStart === 'number'
                ? inputField.selectionStart : 0;
            const promptDomIndex = activeInput && history
                ? Array.prototype.indexOf.call(history.children, activeInput)
                : -1;
            const cursorVisible = !!(cursor && cursor.style && cursor.style.display !== 'none');
            return {
                prompt_visible: promptVisible,
                prompt_locked: promptLocked,
                prompt_dom_index: promptDomIndex,
                input_value: inputValue,
                caret_position: caret,
                viewport_scroll_top: (typeof window.scrollY === 'number')
                    ? window.scrollY : 0,
                cursor_visible: cursorVisible,
            };
        } catch (_err) {
            return null;
        }
    };
})();

/**
 * WaitingIndicator - Cycling typewriter phrases shown while waiting for LLM/GANDALF responses.
 *
 * Types one character at a time, pausing between phrases. start() begins the
 * animation; stop() removes it from the DOM.
 *
 * ── THE PHRASES ARE CONTENT, NOT CODE  [2026-08-03-026-ENGINE] ──────────────
 *
 * They are AUTHORED (`waiting-phrases`, a core:string-array on the `ux`
 * grammar; `reavers-waiting-ux-strings` carries the Reavers set) and arrive on
 * the `connected` frame as `ux_strings.waiting_phrases`. This is the same
 * discipline the project applies to every LLM prompt — "prompts are entities in
 * taxonomy, not strings in Python" — applied to the one line the player stares
 * at during every single wait in the game.
 *
 * ⚠️ FOR A LONG TIME NOTHING READ THEM. This object shipped a hardcoded copy
 * that was BYTE-IDENTICAL to the authored strings, and its own docstring called
 * them "hardcoded fallback phrases". A universe author editing the entity
 * changed nothing, and a second universe could not have its own voice here.
 * Nothing failed — it was simply inert, which is the dormant-authored-surface
 * class in its purest form.
 *
 * ⚠️ THE BUILT-IN LIST SURVIVES ONLY AS A **DETECTABLE** LAST RESORT. That is
 * the whole point: a silent fallback to an identical twin is exactly why this
 * was invisible for as long as it was — nobody could tell which one they were
 * looking at. `_usingFallback` and a console warning make the two states
 * distinguishable from the outside, and `__VP_WAITING_SOURCE` exposes it to the
 * harness. A fallback nobody can detect is not a fallback, it is a duplicate.
 */
const WaitingIndicator = {
    _phrases: ["Thinking...", "Determining...", "Considering...", "Pondering...", "Deliberating...", "Processing..."],
    _usingFallback: true,

    /**
     * Adopt the authored phrases. Called from the `connected` handler.
     *
     * REFUSES anything that is not a non-empty list of non-empty strings, and
     * keeps the built-in list instead. An empty authored list would render as a
     * permanently blank indicator, which reads to a player as a hung client —
     * so "authored badly" degrades to the same visible state as "not authored",
     * and both say so.
     */
    adoptPhrases(phrases) {
        const clean = Array.isArray(phrases)
            ? phrases.filter((p) => typeof p === 'string' && p.trim()).map((p) => p.trim())
            : [];
        if (!clean.length) {
            console.warn(
                '[waiting-indicator] no authored phrases on the connected frame — '
                + 'using the client\'s BUILT-IN list. Authoring edits to '
                + '`waiting-phrases` (tag `ux`) will have NO effect until the '
                + 'server can read them ([2026-08-03-026-ENGINE]).'
            );
            this._usingFallback = true;
        } else {
            this._phrases = clean;
            this._usingFallback = false;
            console.log(
                `[waiting-indicator] adopted ${clean.length} AUTHORED phrases`
            );
        }
        this._phraseIndex = 0;
        this._charIndex = 0;
        // Harness-visible, same idiom as window.__VP_PUMP_STATE. The point is
        // that "am I on the fallback?" is answerable from outside the client.
        if (typeof window !== 'undefined') {
            window.__VP_WAITING_SOURCE = this._usingFallback ? 'fallback' : 'authored';
        }
        return !this._usingFallback;
    },

    _element: null,
    _timer: null,
    _running: false,
    _phraseIndex: 0,
    _charIndex: 0,

    start(mode) {
        this.stop(); // clean up any previous run

        const history = document.getElementById('history');
        if (!history) return;

        // [2026-08-03-014-ENGINE stage 2] Two facts, split. `CL.isOpen()` is
        // WHETHER the prompt is live (state); `CL.anchor()` is WHERE content
        // goes (position). They share a source today and stop sharing one in
        // stage 3, when the command block leaves `#history` and the anchor
        // becomes null while the prompt stays open.
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        const activeInput = CL ? CL.anchor(history) : history.querySelector('.active-input-line');
        const promptOpen = CL ? CL.isOpen() : !!activeInput;

        // [2026-08-08-007-ENGINE] ORIGIN: server-authority. The thinking bridge
        // asserts a control-plane fact — THE BEAT IS IN FLIGHT — in the same
        // family as the clock and the action line. It carries no prose and is
        // addressed to no channel, so it must not claim one. Its absence is the
        // silent wedge this axis exists to prevent: a locked prompt with nothing
        // below it is indistinguishable from a hung client.
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!F) return;

        if (mode === 'passive') {
            // Passive mode: insert thinking bridge ABOVE the active input line.
            // Used for idle/other-player narration where prompt stays live.
            if (!promptOpen || !activeInput) return; // no prompt to insert above
            this._element = F.write('', {
                origin: F.ORIGIN.SERVER_AUTHORITY,
                className: 'message waiting-indicator',
                parent: history,
                before: activeInput
            });
            if (!this._element) return;
            console.log('[TEXT] actor=THINKING placement=above prompt=live channel=system len=12');
        } else {
            // Active mode: prompt is locked, append to history (after frozen input).
            // Invariant: Thinking requires a locked prompt.
            if (activeInput) {
                return;
            }
            this._element = F.write('', {
                origin: F.ORIGIN.SERVER_AUTHORITY,
                className: 'message waiting-indicator',
                parent: history
            });
            if (!this._element) return;
            console.log('[TEXT] actor=THINKING placement=below prompt=locked channel=system len=12');
        }

        this._running = true;
        this._phraseIndex = 0;
        this._charIndex = 0;
        this._tick();
    },

    _tick() {
        if (!this._running || !this._element) return;

        const phrase = this._phrases[this._phraseIndex];

        if (this._charIndex < phrase.length) {
            // Type next character
            this._element.textContent = phrase.substring(0, this._charIndex + 1);
            this._charIndex++;
            this._timer = setTimeout(() => this._tick(), 50);
        } else {
            // Phrase fully typed - pause, then clear and move to next
            this._timer = setTimeout(() => {
                if (!this._running || !this._element) return;
                this._element.textContent = '';
                this._charIndex = 0;
                this._phraseIndex = (this._phraseIndex + 1) % this._phrases.length;
                this._timer = setTimeout(() => this._tick(), 150);
            }, 800);
        }

        // Keep scrolled to bottom
        const history = document.getElementById('history');
        if (history) history.scrollTop = history.scrollHeight;
    },

    stop() {
        this._running = false;
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        if (this._element && this._element.parentNode) {
            this._element.remove();
        }
        this._element = null;
        this._charIndex = 0;
        this._phraseIndex = 0;
    },

    /**
     * [Phase 6c] Re-pin the indicator to the bottom of #history so it stays
     * below any new content streamed in (ch3 lifecycle tags, ch2 dice rows).
     * Without this the indicator becomes scroll-history when new content lands
     * via appendChild, leaving its partial-typewriter text stuck above the
     * new rows. Called from the ch3/ch2 drain loops in TurnPump._advance().
     *
     * Per M11 (viewport behavior for prompt stability) the indicator is the
     * thinking-bridge actor that visually owns the bottom of the viewport
     * while the prompt is locked.
     */
    repin() {
        if (!this._element) return;
        const history = document.getElementById('history');
        if (!history) return;
        // Appending an existing child MOVES it to the last position.
        // [2026-08-03-014-ENGINE stage 3] Unconditional: the command line left
        // #history, so the bottom of the scroll box IS the bottom of the
        // viewport and there is no prompt below to stay above.
        // [2026-08-08-007-ENGINE] `reposition`, not a fresh write — it refuses
        // any node that did not come through the funnel to begin with.
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (F) F.reposition(this._element, { parent: history });
    }
};

// Make WaitingIndicator available globally
if (typeof window !== 'undefined') {
    window.WaitingIndicator = WaitingIndicator;
}

/**
 * [2026-08-08-007-ENGINE] `insertContentInHistory` IS DELETED. Its replacement
 * is `window.TerminalFunnel` (src/scripts/terminal-funnel.js), and the
 * difference is not the transport — it is that the funnel REFUSES.
 *
 * WHAT THIS HELPER ACTUALLY WAS, by the 2026-08-08 audit: `appendChild` +
 * `scrollTop`. Nothing else. It was called "the unified insertion helper" and
 * it unified nothing — 5 of 18 write sites used it, 13 bypassed it,
 * ch2-renderer.js carried a byte-equivalent duplicate under a different name,
 * and EVERY caller wrote `if (typeof insertContentInHistory === 'function')
 * ... else history.appendChild(...)`. So it was never load-bearing, and the
 * boundary it appeared to hold did not exist. That is how a room's authored
 * prose came to render off a ch9 control frame for months, wearing the
 * narration class, accounted by nothing.
 *
 * ⚠️ DO NOT ADD A CONVENIENCE WRAPPER BACK. A helper callers MAY use is a
 * suggestion; the only thing that makes a funnel a funnel is that going around
 * it fails — the runtime refusal here, and the static scan in
 * __tests__/history-write-funnel-scan.test.js.
 */

/**
 * Sync Telemetry Logger - machine-comparable log lines for multiplayer clock sync debugging.
 * Both gameserver-connection.js and clock-line.js write to window._sync* and call logSync().
 */
if (typeof window !== 'undefined') {
    window._syncTurn = 0;
    window._syncPhase = 0;
    window._syncPumpState = 'idle';
    window._syncClockState = 'stopped';
    window._syncClockRemaining = 0;
    window._syncClockMode = 'unknown';
    window._syncLastHighPhase = 0;  // track highest phase for turn rollover detection
}

function logSync(source) {
    const turn = window._syncTurn || 0;
    const phase = window._syncPhase || 0;
    const pumpState = window._syncPumpState || 'unknown';
    const clockState = window._syncClockState || 'stopped';
    const remaining = window._syncClockRemaining || 0;
    const mode = window._syncClockMode || 'unknown';
    console.log(`[SYNC] t=${turn} p=${phase} pump=${pumpState} clock=${clockState} remaining=${remaining} mode=${mode} src=${source}`);
}

if (typeof window !== 'undefined') {
    window.logSync = logSync;
}

/**
 * PhaseBanner - Character-by-character typewriter for the phase-mode banner.
 *
 * Mirrors the style-guide preview contract (frontend/web/preview/style-guide/index.html
 * §"Phase Banner"). Fires on clock ticks with `is_boundary: true` when
 * `beat_mode === 'phase'` — i.e., turn rollover into phase-beat OR beat-mode
 * change (turn-beat -> phase-beat). The banner IS the active input line's
 * prompt bracket prefix: closing `]` is RED on the active banner, WHITE once
 * it scrolls to history. SFX `phase.banner.enter` fires when the `]` lands.
 *
 * Non-active (historical) lines render via play(..., { active: false }). The
 * default path fires an active banner because the clock tick is arming the
 * upcoming phase's prompt — see _STYLE-GUIDE.md for the contract.
 *
 * [ch2 T-4.4] Carryover mode (DECISIONS.md L7):
 *   When the incoming clock tick has `phase_number === 0 && is_boundary === true`,
 *   the caller invokes play(0, { mode: 'carryover' }). The banner then renders
 *   `[PHASE:00 CARRYOVER` with an AMBER closing `]` (via .carryover-close-bracket)
 *   to signal "turn-boundary resolving a prior-turn phased action". All other
 *   contract elements (typewriter cadence, SFX, cursor blink) are preserved.
 */
const PhaseBanner = {
    _typing: false,
    _element: null,
    _timer: null,

    /**
     * Fire the phase-banner typewriter.
     *
     * @param {number} phaseNumber - Zero-padded in display (e.g., 3 -> "PHASE:03").
     * @param {object} [opts]
     * @param {boolean} [opts.active=true] - If true, closing ] is red + blinking cursor retained.
     * @param {string}  [opts.mode='normal'] - 'normal' | 'carryover'. Carryover renders
     *                                        `[PHASE:00 CARRYOVER` with amber close bracket.
     * @param {HTMLElement|null} [opts.container=null] - Inject into this element; defaults to #history.
     * @returns {HTMLElement|null} The banner DOM element (for test introspection).
     */
    play(phaseNumber, opts = {}) {
        const active = opts.active !== false;
        const mode = opts.mode === 'carryover' ? 'carryover' : 'normal';
        const container = opts.container || document.getElementById('history');
        if (!container) return null;

        // Any in-flight typewriter is cut — back-to-back boundaries legitimately
        // trigger two animations, but only the most recent one finishes typing.
        this._cancel();

        const pn = String(Math.max(0, Math.min(99, phaseNumber | 0))).padStart(2, '0');
        // [ch2 T-4.4] Carryover prefix retains `PHASE:NN` scaffolding + adds mode marker.
        const text = mode === 'carryover'
            ? `[PHASE:${pn} CARRYOVER`
            : `[PHASE:${pn}`;
        const closeChar = ']';
        const perChar = 50;                     // ms per character — mechanical teletype

        const el = document.createElement('div');
        let className = active ? 'phase-banner active' : 'phase-banner';
        if (mode === 'carryover') className += ' carryover';
        el.className = className;
        // [2026-08-03-014-ENGINE stage 3] Append: no prompt in the container.
        container.appendChild(el);

        const cursor = document.createElement('span');
        cursor.className = 'phase-cursor';
        cursor.textContent = '\u2593';
        el.appendChild(cursor);

        this._element = el;
        this._typing = true;

        // [Phase 6e] Console marker for the VP `phase banner typewriter fired`
        // assertion. Substring "phase banner" / "phasebanner" is the grep
        // target. Per BASELINE-FIXES Issue 2b this call site is no longer
        // exercised from clock_received in the live game — but the style
        // guide preview and the PhaseBanner test harness still call it, and
        // any future reintroduction (e.g. dedicated phase-mode boundary
        // banner) will continue to fire this marker.
        console.log(`[phase banner] play phase=${pn} mode=${mode}`);

        // [ch2 T-4.4] Carryover soft herald: no dedicated cue in the audio bank
        // (AudioFX/manifest has: die.d20.roll, die.d20.settle, phase.banner.enter,
        // ui.pulse). Per spec, reuse `phase.banner.enter` at reduced volume as a
        // soft opening marker and log the absence of a dedicated herald.
        if (mode === 'carryover' && typeof window !== 'undefined' && window.audioBank) {
            try { window.audioBank.play('phase.banner.enter', { volume: 0.35 }); } catch (_) { /* audio optional */ }
        }

        let i = 0;
        const self = this;
        function step() {
            if (self._element !== el) return;  // cancelled
            if (i <= text.length) {
                // Collapse any previous head span before the cursor into a single text-so-far.
                while (el.firstChild !== cursor && el.firstChild.nextSibling !== cursor) {
                    el.removeChild(el.firstChild);
                }
                const head = document.createElement('span');
                head.textContent = text.slice(0, i);
                el.insertBefore(head, cursor);
                // SFX: keystroke-quiet pulse on every other character (matches style-guide cadence).
                if (i > 0 && i % 2 === 0 && typeof window !== 'undefined' && window.audioBank) {
                    try { window.audioBank.play('ui.pulse', { volume: 0.25 }); } catch (_) { /* audio optional */ }
                }
                i += 1;
                self._timer = setTimeout(step, perChar);
            } else {
                // Closing bracket — this is where the red (or amber, for carryover) lives on active lines.
                const close = document.createElement('span');
                const closeClass = mode === 'carryover'
                    ? 'close-bracket carryover-close-bracket'
                    : 'close-bracket';
                close.className = closeClass;
                close.textContent = closeChar;
                el.insertBefore(close, cursor);
                // SFX: the `]` landing is the confirmation chime.
                if (typeof window !== 'undefined' && window.audioBank) {
                    try { window.audioBank.play('phase.banner.enter', { volume: 1.0 }); } catch (_) { /* audio optional */ }
                }
                if (active) {
                    cursor.classList.add('blink');
                } else if (cursor.parentNode) {
                    cursor.parentNode.removeChild(cursor);
                }
                self._typing = false;
                self._timer = null;
            }
        }
        step();

        return el;
    },

    /** True while a typewriter run is in progress. */
    isTyping() { return this._typing === true; },

    /** Stop the current run in place (does NOT remove the DOM element). */
    _cancel() {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        this._typing = false;
        this._element = null;
    },

    /**
     * [Wave2 T2.4] Demote all active banners to history (remove `.active`
     * class, which swaps the red/amber close bracket for white). Called on
     * phase-beat -> turn-beat transitions. Also strips the blinking cursor.
     *
     * This does NOT delete any banners — past phase banners remain in the
     * history scroll; only the *active* visual state is cleared.
     */
    clear() {
        const container = document.getElementById('history');
        if (!container) return;
        const active = container.querySelectorAll('.phase-banner.active');
        active.forEach(function (el) {
            el.classList.remove('active');
            const cursor = el.querySelector('.phase-cursor');
            if (cursor && cursor.parentNode) cursor.parentNode.removeChild(cursor);
        });
        this._cancel();
    },
};

if (typeof window !== 'undefined') {
    window.PhaseBanner = PhaseBanner;
}

/**
 * TurnPump — the single-object reducer for gameplay turn state. All state
 * transitions flow through event() -> _advance(); external code reads getters.
 *
 * ==========================================================================
 * THE COMMAND-FEEDBACK SEQUENCE (canonical — combat-sequence storyboard
 * frames 2-4). This runs for EVERY skill / turn / phase:
 * ==========================================================================
 *
 *   1. ACCEPTANCE. The player submits -> the clock greys inline
 *      (ClockLine.freeze inPlace), input locks, and a "...thinking" indicator
 *      pins at the bottom. This registers that the command was ACCEPTED. See
 *      the `user_action` / `server_action` events.
 *
 *   2. OBJECTIVE READOUT (ch1 of THIS command). The player's goals roll into
 *      #history above the thinking indicator. The feed does not progress until
 *      ch1 or its void lands (`_ch1Owed`): the server answers every typed
 *      command with exactly one.
 *
 *   3. INDETERMINATE PAUSE. The "...thinking" pause spans until ch0 arrives. A
 *      deterministic MINIMUM (THINKING_MIN_FIRST_MS on a cycle's first
 *      narration, THINKING_MIN_BRIDGE_MS after) is enforced in _advanceInner so
 *      the acceptance beat always REGISTERS even when content arrives fast.
 *      This is a minimum, never a timeout-then-give-up.
 *
 *   4. RESULT. ch0 (the scene) streams in; the prompt unlocks on the two-signal
 *      gate. The load-bearing second signal is `turn_complete`, NOT
 *      `phase_clock`: completion is
 *      `ch0Terminal && serverDone && barrierReached && !aborted`. A bundled
 *      `next_phase_clock` supplies the reborn clock but never gates the unlock.
 *      (Under the slot-yielding beat the mechanics + phase_settled arrive
 *      first and ch0 + turn_complete land together, so the fold still completes
 *      only when ch0 does.)
 * ==========================================================================
 */
const TurnPump = {
    // [ch1-deadair-hold 2026-07-14] Deterministic MINIMUM "...thinking" pause
    // (step 3 of the command-feedback sequence above). The command-accepted
    // indicator must REGISTER even when ch1/ch0 arrive fast, so the first
    // narration of a cycle is held behind THINKING_MIN_FIRST_MS and subsequent
    // narrations behind THINKING_MIN_BRIDGE_MS, measured from thinkingStartMs.
    // Single source of truth for the two drain blocks in _advanceInner (ch1 +
    // ch0), replacing the former inline 3000/2000 literals. This is the
    // deterministic minimum; the server hold (never-void-until-ready) is the
    // orthogonal upper hold. See turn_loop.py::CH1_HOLD_CEILING_S.
    //
    // ======================================================================
    // [2026-07-27-001-ENGINE D6] THE THINKING CADENCE IS A FEATURE.
    // ======================================================================
    // The ratified cadence breaks the beat's latency into bite-sized packages
    // to give the illusion of liveness:
    //
    //   input -> thinking (3-5s)      -> ch1 (previous beat's dead-air fill)
    //         -> thinking (until ch2) -> ch2 inline
    //         -> thinking (until ch0) -> ch0
    //         -> unlock
    //
    // EACH SEGMENT MUST BE TERMINATED BY ITS CONTENT ARRIVING. One 60s
    // indeterminate wait and three content-punctuated waits totalling 60s are
    // the same latency and a completely different game. A segment that runs
    // long because its content never came is the failure this ticket kills --
    // and when content genuinely never arrives, the segment ends VISIBLY via
    // the existing `channel_void` void_class (`absence` | `failure`, rendered
    // by _renderVoidDegradation), never by silently extending.
    //
    // These are MINIMUMS, never timeouts. Nothing here gives up on content:
    // exceeding the minimum only makes the drain eligible to render, and the
    // three drain sites re-enter _advance() rather than voiding.
    //
    // FIRST is tuned to the middle of the ratified 3-5s band rather than its
    // floor. Under D1 (ch1 requested a full beat ahead) ch1 is normally already
    // in hand at submit, so this minimum IS the perceived length of segment 1 --
    // at the 3000ms floor the acceptance beat barely registers before the fill
    // starts typing. The band's CEILING is deliberately not encoded: the upper
    // bound on a segment is its content arriving, never a constant.
    //
    // Segment 2 (pre-ch2) carries NO minimum by design -- the interval-stream
    // drain renders each row the moment it arrives, and the acceptance beat has
    // already registered in segment 1. Segment 3 (pre-ch0) re-arms
    // thinkingStartMs at ch1's playback_complete and measures BRIDGE against it.
    THINKING_MIN_FIRST_MS: 4000,
    THINKING_MIN_BRIDGE_MS: 2000,

    // ch0 ARRIVAL ORDINALS. Every ch0 the pump receives gets the next ordinal;
    // `_ch0DoneThrough` is the highest one that has reached a terminal on screen
    // (rendered, covered, displaced or retired). "Has the scene the player was
    // owed at moment T reached them" is then `doneThrough >= arrived-at-T`,
    // whatever arrived after T. Monotonic for the page's life.
    _ch0Arrived: 0,
    _ch0DoneThrough: 0,
    _ch0PlayingOrdinal: 0,
    _ch0LastRendered: null,

    // [ch2 T-4.4] DECISIONS.md L7 — module-level carryover phase flag.
    // True while the client is rendering a phase-0 carryover tick. Used by
    // the ch2 renderer (ch2-renderer.js) to apply `.carryover-dim` to
    // row DOM elements. Set by a clock frame on phase-0/is_boundary; cleared
    // when the clock advances past phase 0 or switches to turn-beat.
    _inCarryoverPhase: false,

    /**
     * [ch2 T-4.4] Apply `.carryover-dim` to a row element when the pump is
     * currently in phase-0 carryover. Idempotent — re-applying is safe (the
     * class is added at most once). Use this as the single wiring point from
     * ch2 renderers so the flag + tag-driven class coexist without double-add.
     *
     * @param {HTMLElement|null} rowEl
     * @returns {HTMLElement|null} the same element (for chaining)
     */
    applyCarryoverDim(rowEl) {
        if (!rowEl || !TurnPump._inCarryoverPhase) return rowEl;
        if (rowEl.classList && !rowEl.classList.contains('carryover-dim')) {
            rowEl.classList.add('carryover-dim');
        } else if (rowEl.className !== undefined) {
            const classes = String(rowEl.className).split(/\s+/).filter(Boolean);
            if (!classes.includes('carryover-dim')) {
                classes.push('carryover-dim');
                rowEl.className = classes.join(' ');
            }
        }
        return rowEl;
    },

    /**
     * [Phase B B5] Snapshot the (actor, skill) pairs currently in flight
     * (per `pendingClock.inflight_skills`) so that lifecycle + resolution
     * events for those skills can be visually distinguished as carryover
     * during the next cycle. Called from user_action / server_action.
     *
     * The wire-level inflight_skills entries vary in shape across producers
     * (advance_executor uses `actor_name`/`skill_name`; some surfaces use
     * `name` for legacy ActionLine consumption). We accept either; the actor
     * is the local player's character (the per-player clock filters it that
     * way), so we fall back to GameServerConnection.state.characterName when
     * the entry omits an actor.
     */
    _snapshotCycleStartInflight() {
        const set = new Set();
        try {
            const c = this._state.pendingClock;
            const skills = (c && Array.isArray(c.inflight_skills)) ? c.inflight_skills : [];
            const fallbackActor = (typeof GameServerConnection !== 'undefined'
                && GameServerConnection.state && GameServerConnection.state.characterName)
                ? GameServerConnection.state.characterName : '';
            for (let i = 0; i < skills.length; i++) {
                const s = skills[i] || {};
                const actor = s.actor_name || s.actor || fallbackActor || '';
                const skill = s.skill_name || s.skill || s.name || '';
                if (skill) set.add(`${actor}::${skill}`);
            }
        } catch (_err) {
            // Defensive: a bad snapshot must not break the cycle.
        }
        this._state.cycleStartInflight = set;
    },

    /**
     * [Phase B B5] Renderer-facing predicate. Returns true when the given
     * (actor, skill) pair was already in flight at cycle start AND the pump
     * is currently inside a non-idle cycle. The renderer adds
     * `.lifecycle-carryover` to lifecycle tags + ch2 rows that match — see
     * the embedded `<style>` block in frontend/web/index.html for the rule.
     *
     * Defensive: a non-string actor/skill or unset cycleStartInflight Set
     * returns false. Idle status returns false (no live cycle to attribute
     * carryover against).
     */
    isCarryoverLifecycle(actor, skill) {
        if (!skill) return false;
        const s = this._state;
        if (s.status === 'idle') return false;
        if (!s.cycleStartInflight || typeof s.cycleStartInflight.has !== 'function') return false;
        const a = actor || '';
        return s.cycleStartInflight.has(`${a}::${skill}`);
    },

    _state: {
        status: 'idle',             // 'idle' | 'pending' | 'playing' | 'bridge'
        pendingClock: null,         // buffered phase_clock data
        // [2026-08-07-001-ENGINE] The room's beat interval in ms, as last
        // announced by the server. The backstop's window derives from it; a
        // hardcoded window cannot be right across cadence tiers. `null` until a
        // first clock arrives => the floor governs.
        beatCadenceMs: null,
        stallWatchdogWindowMs: null,
        lastRevealedPhase: null,    // phase number of last revealed clock (dedup BUG-8)
        initiator: null,            // 'user' | 'server' | 'idle' | null
        thinkingStartMs: null,      // when thinking animation started (for min-delay)
        hasPlayedAny: false,        // has any narration played this cycle
        // The monotonic BEAT CURSOR: the cycle_id of the last beat that
        // FINISHED. A frame stamped at or below it belongs to a beat that is
        // already over — it may still render, but it can never touch the live
        // record. SAME-SPACE ordering only (`_beatCompare` -> null across spaces): a room change
        // needs no reset because the old room's cursor is inert against the new
        // room's keys. Epoch-reset on the connection `reset` and on a same-room
        // server turn-number regression (`_resetBeatEpoch`).
        beatCursor: null,
        // [2026-08-08-015-ENGINE] SERVER-AUTHORITY LOSSES AWAITING A VERDICT.
        //
        // `{seq, cycleId}` per control-stream position the consumer advanced
        // past without ever seeing it (force-release or resend-miss). Appended
        // by `GameServerConnection._serverAuthorityNoteLoss`, drained by
        // `_adjudicateServerAuthority` at beat close.
        //
        // Why it exists: client_funnel.md §1 asserts a `server-authority` miss
        // is "accountable to the beat — a miss is a silent wedge, which is why
        // refusals must render", and NOTHING adjudicated that. channels.md §5
        // gave owed conditions to ch0-ch4 only. So the notice built to end a
        // silence could itself go missing silently — the same shape as a
        // dropped ch0, one layer up.
        //
        // ⚠️ TRACKED, NOT BLOCKING. This list feeds a VERDICT, never an abort:
        // `_cycleNoteAbort`'s `participates` set is untouched, and nothing here
        // can hold the ch0 gameplay unlock.
        _serverAuthorityLosses: [],
        // The ONE no-deadlock backstop. Armed at cycle start, re-armed on every
        // attributed-current frame, cleared on cycle exit. Fires _finishCycle
        // with the full record dump. Last-resort escape only; never a
        // completion signal (the two-signal unlock is unchanged).
        stallWatchdogTimer: null,
        // [2026-08-19-009-ENGINE] The OVERDUE-notice handle. Render-only twin of
        // the backstop above: armed by the same call, cleared by the same two
        // sites (`_finishCycle`, `reset`), and it never touches the record.
        stallNoticeTimer: null,
        // [2026-08-19-009-ENGINE] Has THIS cycle passed one heartbeat with no
        // progress? A FACT about the beat, decided once by the notice timer and
        // re-read every time the command block locks — see `_renderStallNotice`
        // for why the fact and its render cannot be one step.
        cycleOverdue: false,
        // [2026-07-29-010-QASTI] THE DEADLINE ON THE ch2 GATE. `ch2_playing` is
        // a hard drain gate (`_advanceInner` returns while it is set), so it is
        // the one latch in the pump that can hold the whole cycle on the word of
        // a DOM animation. It is armed by the same line that sets the flag and
        // cleared by the same line that clears it — the gate owns its own
        // release. This is not a sixth completion mechanism
        // ([2026-07-28-004-ENGINE]): it never touches the completion record and
        // never unlocks anything; it only guarantees the existing gate cannot be
        // held open by a signal that will never arrive.
        ch2PlaybackTimer: null,
        // Result of the last ingress classification: {cls, key} or null for a
        // frame that carries no beat attribution (control / lifecycle).
        lastAttribution: null,
        thinkingDelayTimer: null,   // thinking min-delay timer handle
        // P5 -- THE SERVER'S STATEMENT OF WHAT IS WANTED FROM THIS PLAYER
        // (`frame.md` 3b; `time-interval-phase-gameplay.md` 6).
        //
        // `{state, reason, stage, cycle_id, window, windowElapsed,
        // windowSubmitted}` as adopted from a `clock` frame's prompt (stage
        // `window`) or a `prompt-state` frame (stage `resolved`), or null before
        // the server has stated anything. The input line, the timer and the
        // action line read THIS; the client computes no eligibility.
        //
        // Null is NOT "asked": an unstated player has no input line.
        //
        // `windowElapsed` is the statement's OWN window reaching its end
        // (`noteWindowClosed`); the next statement replaces the object, so
        // nothing needs resetting.
        promptState: null,
        // [vp-hardening-2026-05-09 D1] Server-stamped phase identity for the
        // currently-active cycle. Set by a clock frame from its coordinate;
        // narration / interval_event / resolution_event / phase_settled all
        // share this id within a phase. Used by VP introspection
        // (window.__VP_PUMP_STATE) and by the out-of-order channel-arrival
        // warning (a channel landing with a NEWER cycle_id than the active
        // one is a real ordering bug).
        currentCycleId: null,
        // Per-channel last-drained cycle_id, tracked for the VP_PUMP_STATE
        // snapshot. Empty string means "no drain yet this session".
        channelLastDrained: {
            ch3_lifecycle: null,
            ch2_mechanics: null,
            ch1: null,
            ch0: null,
        },
        lastEvent: null,            // {name, ts, cycle_id} — last event() call
        // --- Channel slots for the unified-pump drain ---
        // The two interval streams land in FIFOs, drained by `_advance()`:
        //   ch3_lifecycle: FIFO of interval_event payloads (lifecycle-only)
        //   ch2_mechanics: FIFO of resolution_event payloads (one per resolution)
        // ch0 is a single slot (one scene per beat), consumed (nulled) the
        // moment the drain plays it. ch1 is a FIFO: it answers a COMMAND, and a
        // beat takes one command per free slot (`prompt.slot_act`), so a cycle
        // can hold one ch1 per command it took; each is shifted as it plays.
        //
        // These are TRANSPORT slots only. Whether a channel has RESOLVED is not
        // stored here — it is read off the one completion record via the derived
        // views (_ch0Terminal / _ch1Discharged / _cycleBarrierReached), so the
        // drain and the unlock can never disagree about the same fact.
        channels: {
            ch3_lifecycle: [],
            ch2_mechanics: [],
            ch0: null,
            ch1: [],
            // True while a single ch2-row animation is in flight. The drain pops
            // one entry per playback_complete({channel:2}).
            ch2_playing: false,
        },
        // --- [Phase B B5] Carryover lifecycle attribution ---
        // Snapshot of (actor, skill) pairs that were already in flight at the
        // moment this cycle started (user_action / server_action). Lifecycle +
        // resolution events whose (actor, skill) is in this Set belong to
        // pre-existing carryover skills, NOT to the just-submitted command —
        // renderers tag them with `.lifecycle-carryover` so the player can
        // visually attribute overlapping cycles. See
        // C:/Users/darre/.claude/projects/c--ReaversEngine/memory/phase-eligibility-and-cycle-model.md
        // for the cycle-overlap model. Cleared by _finishCycleTail.
        // Format: each entry is `${actor}::${skill_name}`.
        cycleStartInflight: new Set(),

        // --- THE completion record (one live instance, one cycle at a time) ---
        // Reset at cycle start (_resetCycleCompletion) and at cycle exit.
        // Every field below is a FACT about ONE beat; the drain and the unlock
        // both read it, neither keeps a private copy. See _resetCycleCompletion
        // for the field-by-field contract.
        cycleCompletion: {
            beatId: null,
            beatFloor: null,
            activePhaseKey: null,
            serverDone: false,
            ch0: { rendered: false, voided: false, renderReason: null },
            ch1: { commands: {}, voided: false },
            phases: {},
            aborted: false,
            attribution: { prior: 0, future: 0, unstamped: 0, refusedTerminals: 0, epochResets: 0 },
        },
    },

    // --- Event Handler (Reducer) ---

    /**
     * Emit a one-line state snapshot for transcript diffing. Grep with
     * `grep "Pump Audit"`. Read-only; single line by contract.
     *
     * Every channel term is a DERIVED VIEW of the one completion record, so the
     * audit line and the drain can never report different truths.
     */
    _auditSnapshot(eventName) {
        const s = this._state;
        const cc = s.cycleCompletion;
        const fsm = (typeof TerminalFSM !== 'undefined' && TerminalFSM.mode) ? TerminalFSM.mode : '?';
        const pendingClock = s.pendingClock ? 'yes' : 'no';
        const inCarryover = TurnPump._inCarryoverPhase === true;
        const ch3Len = (s.channels && Array.isArray(s.channels.ch3_lifecycle))
            ? s.channels.ch3_lifecycle.length : 0;
        const ch2Len = (s.channels && Array.isArray(s.channels.ch2_mechanics))
            ? s.channels.ch2_mechanics.length : 0;
        const ch0Slot = (s.channels && s.channels.ch0) ? 'yes' : 'no';
        const ch1Slot = this._ch1Queue().length ? 'yes' : 'no';
        const ch2Playing = !!(s.channels && s.channels.ch2_playing);
        console.log(
            `[Pump Audit] event=${eventName} status=${s.status} initiator=${s.initiator} ` +
            `cycle=${s.currentCycleId || 'none'} ` +
            `beat=${(cc && cc.beatId) || 'unbound'} cursor=${s.beatCursor || 'none'} ` +
            `ch0Slot=${ch0Slot} ch1Slot=${ch1Slot} ` +
            `ch0Terminal=${this._ch0Terminal()} ch0Rendered=${this._ch0Rendered()} ` +
            // [2026-07-29-001-QASTI] `ch1Owed` next to `ch1Discharged`, because
            // the prod audit line printed `ch1Discharged=false` on a cycle
            // nobody owed a ch1 — true, and the reason nothing moved, but
            // unreadable as such. The two together say WHY the drain is waiting.
            `ch1Discharged=${this._ch1Discharged()} ch1Owed=${this._ch1Owed()} ` +
            `barrier=${this._cycleBarrierReached()} ` +
            `ch2Playing=${ch2Playing} serverDone=${!!(cc && cc.serverDone)} ` +
            `pendingClock=${pendingClock} inCarryover=${inCarryover} ` +
            `ch3Len=${ch3Len} ch2Len=${ch2Len} fsm=${fsm}`
        );
    },

    event(name, data = {}) {
        // [Phase 0 Instrumentation] Pre-handler snapshot — logs pre-mutation state
        // AND the event name that arrived. One line per event. See _auditSnapshot.
        this._auditSnapshot(name);

        // [vp-hardening-2026-05-09 D2] Record this event for the snapshot.
        // Done BEFORE handler dispatch so even early-return branches surface
        // the event name in the post-handler snapshot below.
        const _eventCycleId = (data && data.cycle_id) ? data.cycle_id : null;
        this._state.lastEvent = {
            name: name,
            ts: (typeof performance !== 'undefined' && performance.now)
                ? performance.now() : Date.now(),
            cycle_id: _eventCycleId,
        };
        // [vp-hardening-2026-05-09 D2] Out-of-order channel arrival warning.
        // A channel that lands carrying a cycle_id NEWER than the active
        // cycle is a real ordering bug. Skip when current is null (boot /
        // pre-first-clock) or when the incoming has no cycle_id (legacy path
        // / unstamped narration).
        //
        // NUMERIC and same-space only (`_beatCompare`).
        if (this._beatCompare(_eventCycleId, this._state.currentCycleId) > 0) {
            const channelChannels = ['narration_received', 'interval_event',
                'resolution_event', 'phase_settled', 'ch1_missing', 'ch0_missing'];
            if (channelChannels.indexOf(name) !== -1) {
                console.warn(
                    `[TurnPump] out-of-order channel arrival: event=${name} ` +
                    `incoming_cycle_id=${_eventCycleId} active_cycle_id=${this._state.currentCycleId}`
                );
            }
        }
        // [vp-hardening-2026-05-09 D2] Push a synchronous post-handler
        // snapshot at the end of every event(), regardless of which branch
        // the dispatch took (return / break). try/finally wraps the existing
        // dispatch so the push runs on every code path. No-op when
        // __VP_INSTRUMENTATION_ENABLED is false.
        try {
            return this._dispatchEvent(name, data);
        } finally {
            this._pushVpSnapshot(name);
            // Prompt-state may flip on user_action / server_action / cycle
            // exit / playback_complete / frame_received. Snapshotting the
            // prompt on EVERY event is safe (DOM read is constant-time) and
            // covers all triggers without enumerating them.
            if (typeof window !== 'undefined' && window.__vpBuildPromptSnapshot
                    && window.__VP_INSTRUMENTATION_ENABLED) {
                const ps = window.__vpBuildPromptSnapshot();
                if (ps) {
                    ps.trigger = `pump_event:${name}`;
                    if (window.__vpPushPromptSnapshot) {
                        window.__vpPushPromptSnapshot(ps);
                    }
                }
            }
        }
    },

    /**
     * [vp-hardening-2026-05-09 D2] The original `event()` body. Renamed so
     * `event()` can wrap dispatch in a try/finally for the VP snapshot push.
     */
    _dispatchEvent(name, data = {}) {

        // Frames that must land even outside GAMEPLAY. The interval/mechanics
        // streams and the explicit channel voids buffer across a SPAWNING
        // boundary (otherwise the pump waits forever for a channel the server
        // already declared empty); welcome/spawn narration enters the pump as a
        // server-initiated cycle that must be able to start AND complete under
        // WELCOME_NARRATION / SPAWNING. Everything else is GAMEPLAY-only.
        //
        // ⚠️ AN ENVELOPED FRAME IS NEVER DROPPED BY MODE (P1). `frame_received`
        // is not in this list because it does not need to be: a frame is the
        // server's statement, and the client does not decide by its own mode
        // whether to hear it. This list is only for events that are not frames
        // yet, and it shrinks as their kinds ride the envelope.
        const ALWAYS_ALLOWED = ['reset',
                                'narration_received',
                                'interval_event', 'resolution_event', 'phase_settled',
                                'ch1_missing', 'ch0_missing',
                                'server_action', 'playback_complete'];
        // THE ONE ATTRIBUTION CHECK. Every beat frame is classified exactly
        // once, here, before anything reads or writes the completion record.
        // Runs ahead of the mode guard so frames buffered during non-GAMEPLAY
        // modes are attributed the same way as live ones.
        const attribution = this._attributeFrame(name, data);
        this._state.lastAttribution = attribution;
        // Rolling backstop: a frame that belongs to the beat in flight is
        // progress toward unlock and re-arms the watchdog to a fresh window. A
        // frame attributed to ANOTHER beat is not progress on THIS one and must
        // not extend the window (that is how a stall hides). No-op unless a
        // watchdog is armed (initiated cycle in flight).
        const _attributedElsewhere = !!attribution
            && (attribution.cls === 'prior' || attribution.cls === 'future');
        if (this._STALL_PROGRESS_EVENTS.includes(name) && !_attributedElsewhere
                && this._controlFrameIsProgress(name, data)) {
            this._resetStallWatchdog();
        }
        if (name !== 'frame_received' && TerminalFSM.mode !== 'GAMEPLAY'
                && !ALWAYS_ALLOWED.includes(name)) return;
        const s = this._state;
        const sid = GameServerConnection.state.sessionId || '?';
        // `live` is true when this frame may mutate the completion record:
        // the beat in flight, or a documented temporal exception (a frame the
        // server sends unstamped by contract — turn_complete, ch1, a synthetic
        // client-internal void).
        const live = !attribution || attribution.cls === 'current'
            || attribution.cls === 'unstamped';

        switch (name) {
            case 'user_action': {
                s.status = 'pending';
                s.initiator = 'user';
                s.hasPlayedAny = false;
                s.lastRevealedPhase = null;
                s.thinkingStartMs = Date.now();
                // Snapshot the (actor, skill) pairs already in flight at cycle
                // start so the renderer can mark lifecycle / resolution events
                // for those skills as carryover and visually separate them from
                // this cycle's command output.
                this._snapshotCycleStartInflight();
                // [2026-08-19-009-ENGINE] Retire BEFORE the wipe, so the loss is
                // counted on the record it actually happened to.
                this._ch0SpentForBeat = this._retireUnplayedCh0('user_action');
                s.channels.ch0 = null;
                s.channels.ch1 = [];
                s.channels.ch2_playing = false;
                this._clearTimer('ch2PlaybackTimer');
                // Fresh record. The cycle starts UNBOUND: the phase that will
                // resolve this command has no clock yet, so its identity does
                // not exist to be minted. It binds off the first server-stamped
                // content frame. User cycles always wait for ch1, so no
                // pre-discharge.
                this._resetCycleCompletion();
                this._clearTimer('thinkingDelayTimer');
                // The ONE backstop.
                this._armStallWatchdog();
                // Freeze in place so the clock's DOM node is retained (greyed
                // via `.idle`). No relocation on submit.
                ClockLine.freeze({ inPlace: true });
                WaitingIndicator.start();
                console.log(`[${sid}] TurnPump: user_action -> pending`);
                this._syncEmit('pump_user_action');
                break;
            }

            case 'server_action': {
                s.status = 'pending';
                s.initiator = 'server';
                s.hasPlayedAny = false;
                s.lastRevealedPhase = null;
                s.thinkingStartMs = Date.now();
                // Same carryover snapshot as user_action — a server-initiated
                // cycle (auto-look) overlaps any skills still in flight.
                this._snapshotCycleStartInflight();
                // [2026-08-19-009-ENGINE] THE LIVE SHAPE. A SPAWNING /
                // WELCOME_NARRATION arm is exactly what discarded the delivered
                // ch0 in the prod capture this ticket was opened on.
                this._ch0SpentForBeat = this._retireUnplayedCh0('server_action');
                s.channels.ch0 = null;
                s.channels.ch1 = [];
                s.channels.ch2_playing = false;
                this._clearTimer('ch2PlaybackTimer');
                // A server-initiated cycle answers no typed command, so it owes
                // no ch1 (`_ch1Owed`).
                this._resetCycleCompletion();
                this._clearTimer('thinkingDelayTimer');
                this._armStallWatchdog();
                // A command is being entered (server-driven) — freeze the clock
                // and start the thinking indicator so the UI matches user_action.
                ClockLine.freeze({ inPlace: true });
                WaitingIndicator.start();
                console.log(`[${sid}] TurnPump: server_action -> pending`);
                this._syncEmit('pump_server_action');
                break;
            }

            case 'idle_action': {
                s.initiator = 'idle';
                s.hasPlayedAny = false;
                s.lastRevealedPhase = null;
                // [2026-08-19-009-ENGINE] The idle arm does NOT null the slot,
                // but it DOES discard the record that would have consumed what
                // is sitting in it -- the same fact leaking through a different
                // hole. Retire it here too, so a delivered frame can never
                // outlive the only reader that could terminalize it.
                this._ch0SpentForBeat = this._retireUnplayedCh0('idle_action');
                s.channels.ch0 = null;
                // Fresh record for the idle passive cycle. No watchdog: the
                // prompt stays live, so there is nothing to unlock.
                this._resetCycleCompletion();
                console.log(`[${sid}] TurnPump: idle_action (passive, prompt stays live)`);
                this._syncEmit('pump_idle_action');
                break;
            }

            case 'turn_accepted': {
                // The server states the command's id; the ch1 that answers it
                // carries the same id (`channels.md` §1). The record owes one
                // ch1 per command it took: the command that armed the cycle, and
                // each act a free slot adds to the beat in flight
                // (`prompt.slot_act`, which does not restart the cycle).
                const owed = s.cycleCompletion && s.cycleCompletion.ch1.commands;
                if (owed && data.command_id && !owed[data.command_id]) {
                    owed[data.command_id] = { rendered: false, voided: false };
                }
                console.log(`[${sid}] TurnPump: turn_accepted (${data.action}) command=${data.command_id || 'none'}`
                    + ` ch1_owed=${owed ? Object.keys(owed).length : 0}`);
                break;
            }

            case 'narration_received': {
                const entry = {
                    text: data.text,
                    audioUrl: data.audioUrl,
                    // THE RECEIVED DURATION (`frame.md` P9) must ride the entry.
                    // This list is EXPLICIT, so a field handed to the pump and not
                    // named here is silently dropped -- and that is exactly what
                    // happened: the wire carried `audio_duration_ms`, handleNarration
                    // passed it in, and this entry left it behind, so every voiced
                    // beat fell back to the metadata probe. `null` = unmeasured.
                    audioDurationMs: (typeof data.audioDurationMs === 'number')
                        ? data.audioDurationMs
                        : null,
                    narrator: data.narrator,
                    context: data.context,
                    channel: data.channel,
                    requestId: data.requestId,
                    textStyle: data.textStyle || 'normal',
                    // ch1's address: the command it answers.
                    commandId: data.command_id || null,
                    // Cycle id rides on the narration entry so the DOM render
                    // path can stamp data-cycle-id on the streaming element.
                    cycleId: data.cycle_id || null,
                };

                // A ch1 that answers no command in flight is a late frame from
                // an earlier one: it is refused (and was counted, loudly, by
                // `_attributeFrame`) before it can open a cycle or take a slot.
                if (entry.channel === 1 && (!attribution || attribution.cls !== 'command')) {
                    return;
                }

                // Non-acting phase: narration arrives while the pump is idle.
                // Start a passive cycle so it renders above the prompt without
                // locking input or showing the thinking animation.
                //
                // The cycle owes no ch1 (no command was typed). THE RESET MUST
                // NOT UNBIND THE BEAT THE ARRIVING FRAME JUST BOUND
                // [2026-08-15-011-ENGINE].
                // `_attributeFrame` runs ahead of this switch and, on
                //     a `current` frame, BINDS `cc.beatId` off it — then this
                //     line replaced the record and threw that identity away. An
                //     unbound record is not neutral: the NEXT frame to arrive
                //     binds it instead, so when the frame in the slot eventually
                //     played, its `playback_complete` classified `prior` against
                //     a beat it pre-dated, `_terminalizeCh0` was refused, and the
                //     cycle could never complete on the scene it was showing.
                //     Restoring the binding (and pinning the floor to it — this
                //     beat IS this passive cycle's floor) keeps the completion
                //     attributable to the frame that opened it.
                if (s.status === 'idle' && this._isInputSuppressed()) {
                    s.initiator = 'idle';
                    s.hasPlayedAny = false;
                    this._resetCycleCompletion();
                    if (attribution && attribution.cls === 'current' && attribution.key) {
                        s.cycleCompletion.beatId = attribution.key;
                        s.cycleCompletion.beatFloor = attribution.key;
                    }
                    console.log(`[${sid}] TurnPump: non-acting phase passive cycle started`
                        + ` beat=${s.cycleCompletion.beatId || 'unbound'}`);
                }

                // Routing. ch1 plays first; ch0 is gated behind the
                // barrier. An unknown channel falls back to the ch0 slot so the
                // drain still completes, with a warning. Routing is TRANSPORT —
                // a prior-beat frame still lands and still renders; attribution
                // decides only what the completion record believes.
                const slot = (entry.channel === 1) ? 'ch1' : 'ch0';
                if (slot === 'ch0') {
                    entry.ch0Ordinal = ++this._ch0Arrived;
                    // A selection screen covers the terminal and the drain does
                    // not run under it, so a scene arriving now can never play.
                    // Cover it by name instead of parking it in the slot for the
                    // next arrival to DISPLACE.
                    if (this._selectionCovers()) {
                        this._coverCh0(entry, `mode=${TerminalFSM.mode}`, false);
                        return;
                    }
                }
                if (entry.channel !== 0 && entry.channel !== 1) {
                    // [2026-08-03-002-PROMPT] Raised warn -> error. The slot
                    // rule is `ch1 ? 'ch1' : 'ch0'`, so an unknown channel does
                    // not merely land somewhere odd — it lands in the ch0 slot
                    // and DISPLACES the scene sitting there. That is a
                    // full-screen render failure caused by a frame the client
                    // did not recognise, which is why ch4 was given its own
                    // `dialogue` type instead of riding `narration` on ch4.
                    console.error(`[${sid}] TurnPump: narration_received unknown ch${entry.channel} — routed to ch0 slot, DISPLACING any scene there. A narration frame on an unregistered channel is a client/registry mismatch (docs/Tome/systems/channels.md).`);
                }
                // [2026-07-29-001-QASTI] LOUD DISPLACEMENT. The ch0 slot IS
                // the drain gate, so an arrival into an occupied one overwrites
                // a scene the server counted delivered — and prod did exactly
                // that: the void scene silently replaced the death scene 0.8s
                // after it landed, which is why a wedged slot looked like an
                // empty one. The overwrite is kept (the slot contract is
                // deliberate) but it can never again be silent.
                //
                // ch1 queues one frame per command, in arrival order. Only a
                // second frame for the SAME command displaces: that is a loss.
                if (slot === 'ch1') {
                    const queue = this._ch1Queue();
                    const same = queue.findIndex(q => q.commandId === entry.commandId);
                    if (same >= 0) {
                        this._noteSlotDisplacement('ch1', queue[same], entry);
                        queue[same] = entry;
                    } else {
                        queue.push(entry);
                    }
                    s.channels.ch1 = queue;
                } else {
                    this._noteSlotDisplacement('ch0', s.channels.ch0, entry);
                    this._markCh0Done(s.channels.ch0);
                    s.channels.ch0 = entry;
                }
                const ch0Present = s.channels.ch0 ? 'yes' : 'no';
                const ch1Present = this._ch1Queue().length ? 'yes' : 'no';
                console.log(`[${sid}] TurnPump: narration_received ch${entry.channel} cycle=${entry.cycleId || 'none'} class=${attribution ? attribution.cls : 'n/a'} (ch0=${ch0Present} ch1=${ch1Present})`);
                // Positive intake+route trace: a silent drop shows "received
                // cycle X chN" with no matching rendered/drained line downstream.
                console.log(`[Delivery] cycle=${entry.cycleId || 'none'} ch=${entry.channel} phase=received|routed`);
                // [2026-07-29-001-QASTI] A content frame the player is OWED is
                // now enough to arm the one backstop. It used to be armed only
                // by user_action / server_action, so an idle pump holding an
                // undelivered scene was watched by nothing at all — which is
                // why the death scene's disappearance produced no watchdog line
                // to go with the missing render. `_finishCycle` disarms it on
                // the normal exit, exactly as before; this only closes the case
                // where there is no initiated cycle to have armed it.
                if (!s.stallWatchdogTimer) this._armStallWatchdog();
                this._advance();
                return;
            }

            case 'playback_complete': {
                s.status = s.status === 'playing' ? 'bridge' : s.status;
                if (data.channel === 0) {
                    // The typewriter finished. `rendered` is render-coupled, not
                    // arrival-coupled — and it goes through the ONE terminal
                    // writer, which refuses it if this ch0 belongs to a beat that
                    // is not the one in flight.
                    if (live) this._terminalizeCh0('rendered', data);
                    this._markCh0Done({ ch0Ordinal: this._ch0PlayingOrdinal });
                    // The retarget hold measures its readable dwell from here.
                    this._ch0LastRendered = { ordinal: this._ch0PlayingOrdinal, at: Date.now() };
                    // The scene has finished typing, so a ch4 line held for the
                    // end of its sentence prints now. The only coupling between
                    // the two channels, and it runs in the safe direction: ch4
                    // waits on ch0, never the reverse.
                    try {
                        if (typeof GameServerConnection !== 'undefined'
                                && typeof GameServerConnection._flushCh4Held === 'function') {
                            GameServerConnection._flushCh4Held('ch0 rendered');
                        }
                    } catch (_e) { /* a flush failure must never break the pump */ }
                } else if (data.channel === 1) {
                    // Attributed by command (`_attributeFrame`).
                    if (s.cycleCompletion && attribution && attribution.cls === 'command') {
                        s.cycleCompletion.ch1.commands[attribution.command].rendered = true;
                    }
                } else if (data.channel === 2) {
                    // ch2 has no per-cycle terminal (each resolution_event is one
                    // row; they play sequentially). Clear the in-flight flag so
                    // the drain can shift the next entry.
                    s.channels.ch2_playing = false;
                    this._clearTimer('ch2PlaybackTimer');
                } else {
                    // [2026-08-03-002-PROMPT] THE UNKNOWN-CHANNEL ALARM.
                    //
                    // This chain had NO `else`. A playback_complete for any
                    // channel the client did not know about fell through in
                    // total silence — no terminal, no latch, no log line — and
                    // that silence is exactly what made a server-only ch4 so
                    // dangerous: the frame would arrive, do nothing, and report
                    // nothing. THE LOUD-FAILURE LAW: a fallback must be LOUD, or
                    // it must not exist.
                    //
                    // Reaching here is not fatal — an unhandled channel simply
                    // has no per-cycle terminal to flip — but it always means
                    // the client is behind the channel registry (`combat.md`
                    // §10), so it is an ERROR and it names the channel. ch4 is
                    // NOT in this branch: it renders on its own `dialogue` type
                    // and never enters the pump's slot machinery.
                    console.error(
                        `[${sid}] playback_complete on UNKNOWN channel ` +
                        `${data.channel} (cycle=${data.cycle_id || 'none'}) — ` +
                        `this client has no arm for it, so the frame did ` +
                        `nothing and flipped nothing. Add it to the dispatch ` +
                        `chain, or it is invisible by construction. See the ` +
                        `channel registry in docs/Tome/systems/channels.md.`
                    );
                }
                // Only re-arm the thinking-bridge clock for narration channels.
                // A ch2 animation finishing is not "narration just played" from
                // the player's POV; re-arming on it inserted a dead pause before
                // ch0 (the drain measures its minimum against thinkingStartMs).
                if (data.channel === 0 || data.channel === 1) {
                    s.thinkingStartMs = Date.now();
                }
                if (s.status === 'bridge' && !this._ch0Rendered()) {
                    if (s.initiator === 'user') {
                        WaitingIndicator.start();
                    } else if (s.initiator === 'idle') {
                        // Passive thinking bridge: above the prompt, which stays live.
                        WaitingIndicator.start('passive');
                    }
                }
                console.log(`[${sid}] TurnPump: playback_complete ch${data.channel} cycle=${data.cycle_id || s.currentCycleId || 'none'} class=${attribution ? attribution.cls : 'n/a'} reason=${data.render_reason || 'playback'}, ch0Terminal=${this._ch0Terminal()} ch1Discharged=${this._ch1Discharged()}`);
                console.log(`[Delivery] cycle=${data.cycle_id || s.currentCycleId || 'none'} ch=${data.channel} phase=rendered`);
                this._syncEmit('pump_playback_complete');
                this._advance();
                return;
            }

            case 'server_done': {
                // turn_complete carries no cycle_id — the DOCUMENTED unstamped
                // exception. It is attributed temporally to the single live
                // record, so there is nothing to miss when it lands before the
                // next phase_clock (the ~0.8s ordering hazard): the record
                // persists across that arrival.
                if (s.cycleCompletion) s.cycleCompletion.serverDone = true;
                console.log(`[${sid}] TurnPump: server_done`);
                this._syncEmit('pump_server_done');
                this._advance();
                return;
            }

            case 'frame_received': {
                // P1: the ONE pump arm for an enveloped frame, already refused or
                // admitted at the wire door (`FrameRegistry.validateFrame`).
                if (data.kind === 'clock') {
                    this._onClockFrame(data);
                } else if (data.kind === 'prompt-state') {
                    this._onResolvedStatement(data);
                } else {
                    console.error(`[${sid}] TurnPump: frame kind '${data.kind}' has no pump arm`);
                }
                break;
            }

            case 'clock_expired': {
                this.event('idle_action');
                return;
            }

            case 'error': {
                console.warn(`[${sid}] TurnPump: error - ${data.message}`);
                this._clearTimer('thinkingDelayTimer');
                WaitingIndicator.stop();
                // Error path: exit the cycle cleanly. `_finishCycle()` is the
                // unconditional cycle-close — it nulls channel slots, runs the
                // drain tail (reveal clock + prompt), and dispatches the FSM hook.
                this._finishCycle();
                return;
            }

            // [2026-08-21-001-ENGINE] The `timeout` case is GONE, not disabled.
            //
            // Its only emitter was the 45s narration safety timer, and that
            // timer is now `_ch0PlaybackCeilingFired`, which hands over through
            // `_releaseNarrationGate('ceiling')` -- the same single
            // typewriter-complete callback a natural completion uses. Keeping a
            // second entry point that fires `playback_complete` would be a
            // rival authority over the ch0 terminal, which is exactly the shape
            // the two-signal unlock exists to prevent; keeping it EMPTY would
            // be a guard on code that does not run.
            //
            // Retiring it also exposed that this switch had NO default at all,
            // so an event name nobody handles was a silent no-op -- the same
            // hole the UNKNOWN-CHANNEL ALARM was added to close one level down.
            // See the `default:` at the bottom.

            case 'interval_event': {
                // ch3 lifecycle stream. The payload always queues for render;
                // only a CURRENT frame engages the live beat's barrier, so the
                // NEXT phase's mechanics can never make this beat's ch0 wait.
                if (live) this._cycleBarrierFor(this._barrierKeyFor(attribution)).engaged = true;
                s.channels.ch3_lifecycle.push(data);
                this._advance();
                return;
            }

            case 'resolution_event': {
                // ch2 mechanics/dice stream, one wire message per resolution.
                // Drains after ch3 in the merged interval stream. Same
                // attribution rule as interval_event.
                if (live) this._cycleBarrierFor(this._barrierKeyFor(attribution)).engaged = true;
                s.channels.ch2_mechanics.push(data);
                this._advance();
                return;
            }

            case 'effect_event': {
                // [2026-09-01-005-ENGINE] What a STATUS did this beat, on the
                // ch2 mechanics stream. Queued with the resolutions so the row
                // lands in transcript order beside them.
                //
                // ⚠️ DELIBERATELY DOES NOT ENGAGE THE CYCLE BARRIER, and that
                // asymmetry with `resolution_event` above is the point. A
                // resolution is the beat's content and the unlock legitimately
                // waits for it; a status tick is DISCLOSURE OF SOMETHING THAT
                // ALREADY HAPPENED -- the HP was written before this frame was
                // built. Engaging a barrier for it would let a lost or slow
                // effect row hold the beat the player is waiting on, which is
                // precisely the coupling ch4's D1 forbids one channel over.
                //
                // It still gets ordering, dedup and gap detection from ch2's
                // LEDGER_CONTENT_CHANNELS membership; it simply cannot make
                // ch0 wait.
                s.channels.ch2_mechanics.push(data);
                this._advance();
                return;
            }

            case 'ch1_missing': {
                // The server states this command's ch1 is not coming, and why.
                // Discharge without render so the drain proceeds to ch0.
                if (s.cycleCompletion && attribution && attribution.cls === 'command') {
                    s.cycleCompletion.ch1.commands[attribution.command].voided = true;
                }
                console.log(`[${sid}] TurnPump: ch1_missing reason=${data.reason || 'unspecified'}`);
                this._advance();
                return;
            }

            case 'ch0_missing': {
                // The server knows ch0 is not coming for this cycle (empty phase,
                // or an empty per-player map). A void is TERMINAL for ch0, so it
                // goes through the one terminal writer: it satisfies the GAMEPLAY
                // gate (terminal = rendered OR voided) but never the non-GAMEPLAY
                // arm, which reads `rendered` alone — a server skip is not a
                // played narration.
                if (live) this._terminalizeCh0('voided', data);
                console.log(`[${sid}] TurnPump: ch0_missing reason=${data.reason || 'unspecified'} class=${attribution ? attribution.cls : 'n/a'}`);
                this._advance();
                return;
            }

            case 'phase_settled': {
                // The ordering barrier. It arrives after every interval_event for
                // the phase has flushed and before ch0 is dispatched. The frame
                // carries its own cycle_id, so one the reorder buffer released
                // late still stamps the right phase — arrival order is irrelevant.
                if (live) {
                    const barrier = this._cycleBarrierFor(this._barrierKeyFor(attribution));
                    barrier.reached = true;
                    barrier.engaged = true;
                    // [2026-08-31-005-ENGINE] Record the BEAT AUTHOR'S verdict
                    // off the frame it is already relayed on. Only an explicit
                    // boolean STATES anything; anything else (absent, null, an
                    // un-migrated pod) leaves the record's `null` in place, so
                    // an unstated verdict can never be read as "dead".
                    const _cc = this._state.cycleCompletion;
                    if (_cc && _cc.beatVerdict) {
                        if (typeof data.ch0_owed === 'boolean') {
                            _cc.beatVerdict.ch0Owed = data.ch0_owed;
                        }
                        if (typeof data.beat_had_content === 'boolean') {
                            _cc.beatVerdict.beatHadContent = data.beat_had_content;
                        }
                    }
                }
                console.log(
                    `[${sid}] TurnPump: phase_settled ${data.turn_number || 0}:${data.phase_number} ` +
                    `class=${attribution ? attribution.cls : 'n/a'}`
                );
                this._advance();
                break;
            }

            case 'reset': {
                // Disconnect / cleanup disarms the one backstop.
                this._clearTimer('stallWatchdogTimer');
                this._clearTimer('stallNoticeTimer');
                s.cycleOverdue = false;
                this._clearTimer('thinkingDelayTimer');
                this._clearTimer('ch2PlaybackTimer');
                s.status = 'idle';
                s.pendingClock = null;
                s.initiator = null;
                s.thinkingStartMs = null;
                s.hasPlayedAny = false;
                // P5: a torn-down stream's statements describe beats the new
                // connection is not in. It is stated afresh (reconnect floor,
                // then its arrival beat).
                s.promptState = null;
                // Clear the transport slots so a reconnect does not start the
                // next cycle holding a torn-down stream's payloads. Nothing that
                // arrived before a teardown is still owed to the player.
                this._ch0DoneThrough = this._ch0Arrived;
                this._ch0LastRendered = null;
                s.channels = {
                    ch3_lifecycle: [],
                    ch2_mechanics: [],
                    ch0: null,
                    ch1: [],
                    ch2_playing: false,
                };
                TurnPump._inCarryoverPhase = false;
                if (typeof window !== 'undefined') window._inCarryoverPhase = false;
                s.cycleStartInflight = new Set();
                // [2026-08-08-015-ENGINE] A torn-down stream's control-seq
                // losses are not the next stream's debt. The reconnect
                // re-baselines the ledger from its first frame's stream_epoch,
                // so carrying these across would report a verdict for a stream
                // whose numbering no longer exists.
                s._serverAuthorityLosses = [];
                // A disconnect ends the beat epoch: clear the record AND the
                // cursor, so a torn-down stream's ids can never classify a fresh
                // session's frames as prior.
                this._resetBeatEpoch();
                s.currentCycleId = null;
                s.lastEvent = null;
                s.lastAttribution = null;
                s.channelLastDrained = {
                    ch3_lifecycle: null,
                    ch2_mechanics: null,
                    ch1: null,
                    ch0: null,
                };
                console.log(`[${sid}] TurnPump: reset`);
                this._syncEmit('pump_reset');
                break;
            }

            // [2026-08-21-001-ENGINE] THE UNKNOWN-EVENT ALARM.
            //
            // This switch had no default. An event name the reducer does not
            // handle did nothing, flipped nothing, and said nothing -- the same
            // silence the UNKNOWN-CHANNEL ALARM exists to break one level down
            // in `playback_complete`. It became reachable in practice the
            // moment the `timeout` case was retired above: a stale emitter
            // would now be a no-op rather than an error.
            //
            // Not fatal (an unhandled event simply has no reducer arm), but it
            // always means a caller and this switch disagree about the
            // vocabulary, so it is an ERROR and it names the event. Bounded so
            // a looping emitter complains once, not once per frame.
            default: {
                if (!this._unknownEventsWarned) this._unknownEventsWarned = new Set();
                if (!this._unknownEventsWarned.has(name)) {
                    this._unknownEventsWarned.add(name);
                    console.error(
                        `[${sid}] TurnPump: UNKNOWN EVENT '${name}' -- this ` +
                        `reducer has no arm for it, so the call did nothing and ` +
                        `flipped nothing. Either the emitter is stale or the ` +
                        `case is missing; a silent no-op here is invisible by ` +
                        `construction.`
                    );
                }
                break;
            }
        }
    },

    // --- Core Decision Function ---

    _advance() {
        try {
            return this._advanceInner();
        } finally {
            // [vp-hardening-2026-05-09 D2] Synchronous post-advance snapshot
            // captures status flips ('pending' -> 'playing' -> 'bridge')
            // happening inside the drain. event()'s try/finally also pushes
            // one; the duplication is intentional — VP page.evaluate(() =>
            // window.__VP_PUMP_STATE) sees post-mutation state regardless of
            // whether the trigger was an external event or a recursive
            // _advance() drain step.
            this._pushVpSnapshot('_advance');
        }
    },

    _advanceInner() {
        this._auditSnapshot('_advance');
        // The drain runs in GAMEPLAY and in the two server-initiated narration
        // modes. Welcome/spawn narration enters the pump through server_action,
        // so the drain has to reach the ch0 playback -> playback_complete ->
        // cycle-finish chain there too. Prompt creation at cycle exit is
        // independently gated by _shouldShowPrompt() (GAMEPLAY-only), so no
        // stray prompt leaks into a locked mode.
        if (TerminalFSM.mode !== 'GAMEPLAY'
                && TerminalFSM.mode !== 'WELCOME_NARRATION'
                && TerminalFSM.mode !== 'SPAWNING') {
            return;
        }
        const s = this._state;
        const sid = GameServerConnection.state.sessionId || '?';

        // 1. Something is playing -- wait
        if (s.status === 'playing') {
            if (!GameServerConnection._tokenDrainTimer) {
                s.status = 'bridge';
                this._syncEmit('pump_bridge_recovery');
            } else {
                return;
            }
        }

        // ch1 drain. ch1 (the objective readout for a command the player
        // typed) is STRUCTURALLY the first render of a cycle: before the
        // interval stream and before ch0. The FIFO's head is shifted on play,
        // so each delivered ch1 fires once, in arrival order; a slot act's ch1
        // plays after the one already queued.
        // Held while a ch2 dice animation is in flight; playback_complete
        // ({channel:2}) re-enters the drain.
        if (s.status !== 'playing' && !s.channels.ch2_playing && this._ch1Queue().length) {
            if (s.initiator === 'user' && s.thinkingStartMs) {
                const elapsed = Date.now() - s.thinkingStartMs;
                const minMs = s.hasPlayedAny ? this.THINKING_MIN_BRIDGE_MS : this.THINKING_MIN_FIRST_MS;
                if (elapsed < minMs) {
                    const delay = minMs - elapsed;
                    if (!s.thinkingDelayTimer) {
                        s.thinkingDelayTimer = setTimeout(() => {
                            s.thinkingDelayTimer = null;
                            this._advance();
                        }, delay);
                    }
                    return;
                }
                s.thinkingStartMs = null;
            }
            this._clearTimer('thinkingDelayTimer');
            const next = this._ch1Queue().shift();  // consume
            s.status = 'playing';
            s.hasPlayedAny = true;
            // [vp-hardening-2026-05-09 D2] Track last-drained ch1 cycle.
            if (next && next.cycleId) {
                s.channelLastDrained.ch1 = next.cycleId;
            }
            WaitingIndicator.stop();
            console.log(`[${sid}] TurnPump: playing ch1 (req=${next.requestId || 'none'})`);
            this._syncEmit('pump_playing');
            GameServerConnection._currentNarrationChannel = 1;
            GameServerConnection._playNarration(next);
            return;
        }

        // Structural ch1-before-intervals gate (M9). A typed command is always
        // answered with its ch1 or a ch1 void, so the interval stream HOLDS
        // until it discharges. Catch-net: the rolling watchdog.
        if (this._ch1Owed()) {
            return;  // M9: hold the interval stream for ch1 / its void
        }

        // [W1 P0-1] Unified interval-stream drain — ch3 lifecycle and ch2
        // mechanics interleaved by their (turn, interval) key instead of the
        // old drain-by-channel-FIFO (all lifecycle to empty, THEN mechanics).
        // The server emits per-interval order — lifecycle(i), resolution(i),
        // lifecycle(i+1), ... (turn_engine.py) — and the old per-channel
        // drain preserved it only when messages arrived one-at-a-time. Under
        // burst (or while a ch2 animation held the drain) lifecycle lines
        // piled up above and dice rows collected below — storyboard frames
        // 6/9/11 violation. The merged drain picks the lowest
        // (turn, interval) head across both FIFOs, lifecycle before mechanics
        // on a tie, so every dice row drains in its interval slot. Exact
        // WITHIN-interval placement (row immediately after the resolving
        // actor's `<active>` line, before same-interval peers) is
        // DOM-anchored in Ch2Renderer.renderResolutionEvent.
        //
        // While a ch2 animation is in flight the WHOLE stream holds (not just
        // ch2): later lifecycle lines must not leapfrog a still-animating
        // dice row. playback_complete({channel:2}) re-enters _advance().
        //
        // Feature flag `window.CH2_ANIMATE_ENABLED` (default true). When
        // false, ch2 renders synchronously — useful for VP determinism.
        // [Phase 6c] WaitingIndicator.repin() after each render keeps the
        // thinking-bridge at the bottom of #history.
        if (s.channels.ch2_playing) {
            // Animation in flight — defer the rest of the drain until
            // playback_complete({channel:2}) fires and re-enters _advance().
            return;
        }
        while (s.channels.ch3_lifecycle.length > 0 || s.channels.ch2_mechanics.length > 0) {
            const src = this._nextIntervalStreamSource();
            if (src === 'ch3') {
                const payload = s.channels.ch3_lifecycle.shift();
                try {
                    if (typeof window !== 'undefined' && window.Ch2Renderer
                            && typeof window.Ch2Renderer.renderLifecycleEvent === 'function') {
                        const container = document.getElementById('history');
                        if (container) window.Ch2Renderer.renderLifecycleEvent(payload, container);
                    } else {
                        console.warn(`[${sid}] ch3_lifecycle: Ch2Renderer.renderLifecycleEvent not available`);
                    }
                } catch (err) {
                    console.error(`[${sid}] ch3_lifecycle render failed:`, err);
                }
                // [vp-hardening-2026-05-09 D2] Track last-drained cycle for
                // the VP_PUMP_STATE snapshot. cycle_id is on the payload
                // (server stamps phase-level cycle_id on every interval_event).
                if (payload && payload.cycle_id) {
                    s.channelLastDrained.ch3_lifecycle = payload.cycle_id;
                } else if (payload && payload.audit_key) {
                    // Legacy fallback: derive phase-level cycle_id from the
                    // interval-level audit_key by stripping `.iII`.
                    s.channelLastDrained.ch3_lifecycle =
                        String(payload.audit_key).replace(/\.i\d+$/, '');
                }
                // [combat-validation-observability 3c] Positive per-drain trace.
                console.log(`[Delivery] cycle=${(payload && payload.cycle_id) || s.currentCycleId || 'none'} ch=3 phase=drained`);
                WaitingIndicator.repin();
                continue;
            }
            // src === 'ch2' — mechanics/dice resolution.
            const animateEnabled = (typeof window !== 'undefined')
                ? (window.CH2_ANIMATE_ENABLED !== false)  // default true
                : true;
            const payload = s.channels.ch2_mechanics.shift();

            // [2026-09-01-005-ENGINE] AN EFFECT ROW IS NOT A DIE. It rides the
            // ch2 queue so it stays in order with the resolutions around it,
            // but it must NOT enter the dice-animation gate below: that path
            // arms `_armCh2Deadline`, sets `ch2_playing`, and RETURNS to wait
            // for a `playback_complete` that a row with no dice will never
            // produce. Entering it would stall the merged drain on every
            // burning tick.
            //
            // Rendered synchronously and the drain CONTINUES in the same tick,
            // exactly like the ch3 branch above.
            if (payload && payload.type === 'effect_event') {
                try {
                    if (typeof window !== 'undefined' && window.Ch2Renderer
                            && typeof window.Ch2Renderer.renderEffectEvent === 'function') {
                        const container = document.getElementById('history');
                        if (container) window.Ch2Renderer.renderEffectEvent(payload, container);
                    } else {
                        // LOUD. A missing arm is a no-op, not an error -- the
                        // failure mode THE CHANNEL REGISTRY LAW's second gate
                        // exists to make visible.
                        console.warn(
                            `[${sid}] effect_event: Ch2Renderer.renderEffectEvent `
                            + 'not available — the status row is DROPPED.'
                        );
                    }
                } catch (err) {
                    console.error(`[${sid}] effect_event render failed:`, err);
                }
                WaitingIndicator.repin();
                continue;
            }
            // [vp-hardening-2026-05-09 D2] Track last-drained ch2 cycle.
            if (payload && payload.cycle_id) {
                s.channelLastDrained.ch2_mechanics = payload.cycle_id;
            } else if (payload && payload.audit_key) {
                s.channelLastDrained.ch2_mechanics =
                    String(payload.audit_key).replace(/\.i\d+$/, '');
            }
            // [combat-validation-observability 3c] Positive per-drain trace.
            console.log(`[Delivery] cycle=${(payload && payload.cycle_id) || s.currentCycleId || 'none'} ch=2 phase=drained`);
            if (animateEnabled) {
                s.channels.ch2_playing = true;
                // [2026-07-29-010-QASTI] Arm the gate's own deadline in the SAME
                // statement that closes the gate. Prod proved the pre-existing
                // in-animation safety timeout is not sufficient on its own: it
                // lives on the row, and the bug was binding to the WRONG row, so
                // the safety inherited the defect it existed to catch.
                this._armCh2Deadline(payload);
                try {
                    this._playCh2Mechanics(payload);
                } catch (err) {
                    // Catastrophic start failure — must NOT deadlock the
                    // drain. Clear the in-flight flag and fire
                    // playback_complete synthetically to keep the reducer
                    // moving.
                    console.error(`[${sid}] ch2 animation start failed:`, err);
                    s.channels.ch2_playing = false;
                    this._clearTimer('ch2PlaybackTimer');
                    this.event('playback_complete', { channel: 2 });
                }
                return;  // wait for playback_complete (or recursion above)
            }
            // Synchronous fallback (test determinism path). Render this
            // entry, then continue the merged drain in the same tick.
            try {
                if (typeof window !== 'undefined' && window.Ch2Renderer
                        && typeof window.Ch2Renderer.renderResolutionEvent === 'function') {
                    const container = document.getElementById('history');
                    if (container) window.Ch2Renderer.renderResolutionEvent(payload, container);
                } else {
                    console.warn(`[${sid}] ch2_mechanics: Ch2Renderer.renderResolutionEvent not available`);
                }
            } catch (err) {
                console.error(`[${sid}] ch2_mechanics render failed:`, err);
            }
            WaitingIndicator.repin();
        }

        // The ch2 barrier, read off the one record for the beat in flight. ch0
        // may not play before its OWN phase settles (ordering invariant); a
        // cycle with no interval pipeline never engages it, so ch0 drains
        // immediately there.
        const barrierReached = this._cycleBarrierReached();

        // Drain priority is ch1 -> ch3/ch2 interval stream -> ch0 -> clock+prompt
        // (storyboard M3/M9). ch0 cannot pass an owed ch1: the M9 gate above
        // already returned.
        // ch0 single-slot drain — after ch1 discharges AND the barrier clears.
        // The slot itself is the gate; it is consumed on play. Thinking-delay
        // rules apply.
        if (s.status !== 'playing' && s.channels.ch0 && barrierReached) {
            if (s.initiator === 'user' && s.thinkingStartMs) {
                const elapsed = Date.now() - s.thinkingStartMs;
                const minMs = s.hasPlayedAny ? this.THINKING_MIN_BRIDGE_MS : this.THINKING_MIN_FIRST_MS;
                if (elapsed < minMs) {
                    const delay = minMs - elapsed;
                    if (!s.thinkingDelayTimer) {
                        s.thinkingDelayTimer = setTimeout(() => {
                            s.thinkingDelayTimer = null;
                            this._advance();
                        }, delay);
                    }
                    return;
                }
                s.thinkingStartMs = null;
            }
            this._clearTimer('thinkingDelayTimer');
            const next = s.channels.ch0;
            s.channels.ch0 = null;  // consume
            s.status = 'playing';
            s.hasPlayedAny = true;
            this._ch0PlayingOrdinal = next.ch0Ordinal || this._ch0PlayingOrdinal;
            // [vp-hardening-2026-05-09 D2] Track last-drained ch0 cycle.
            if (next && next.cycleId) {
                s.channelLastDrained.ch0 = next.cycleId;
            }
            WaitingIndicator.stop();
            console.log(`[${sid}] TurnPump: playing ch0 (req=${next.requestId || 'none'})`);
            this._syncEmit('pump_playing');
            GameServerConnection._currentNarrationChannel = 0;
            GameServerConnection._playNarration(next);
            return;
        }

        // GAMEPLAY cycle completion. The one record is the sole authority:
        //   complete = ch0 terminal (rendered OR voided, for THIS beat)
        //           && serverDone   (this turn's turn_complete)
        //           && barrier reached for the beat in flight
        //           && !aborted     (no participating-channel gap)
        // ch0 stays the terminal beat: its drain above is gated by ch1 +
        // barrier, so the gate can never fire before ch0 resolves.
        //
        // A throw here LOGS and leaves — the rolling watchdog is the escape.
        // There is deliberately no second opinion to complete on: a fallback
        // predicate is how the pump ended up with two authorities that could
        // disagree, and a bounded honest stall beats a wrong unlock.
        let cycleComplete = false;
        try {
            cycleComplete = this._cycleNativeComplete();
        } catch (e) {
            console.error(`[${sid}] TurnPump: completion predicate threw; leaving escape to the watchdog`, e);
        }
        if (cycleComplete) {
            console.log(`[${sid}] TurnPump: cycle complete beat=${this._beatId() || 'unbound'} ch0Terminal=${this._ch0Terminal()} serverDone=${!!(s.cycleCompletion && s.cycleCompletion.serverDone)}`);
            WaitingIndicator.stop();
            this._finishCycle();
            return;
        }

        // Non-GAMEPLAY server-initiated completion. WELCOME_NARRATION and
        // SPAWNING never receive a turn_complete, so the same record is read in
        // a DIFFERENT mode: ch0 RENDERED alone. A void must not complete this
        // arm — an empty-narration skip is not a played narration. The FSM
        // listener consumes pump_finish_cycle to advance the mode.
        if (TerminalFSM.mode === 'WELCOME_NARRATION' || TerminalFSM.mode === 'SPAWNING') {
            let ngComplete = false;
            try {
                ngComplete = this._cycleNativeCompleteNonGameplay();
            } catch (e) {
                console.error(`[${sid}] TurnPump: non-gameplay completion predicate threw; leaving escape to the watchdog`, e);
            }
            if (ngComplete) {
                console.log(`[${sid}] TurnPump: server-initiated cycle complete (non-gameplay mode) ch0Rendered=${this._ch0Rendered()}`);
                WaitingIndicator.stop();
                this._finishCycle();
                return;
            }
        }

        // Otherwise: waiting for more data. When the server knows no ch0 is
        // coming it says so explicitly (channel_void / ch0_missing) and the
        // drain exits through the normal terminal path. If that signal never
        // arrives, the rolling watchdog is the catch-net.
    },

    // --- Cycle Completion ---

    /**
     * End the current cycle unconditionally. Called from exactly four sites:
     * the two `_advance()` cycle-complete branches (GAMEPLAY and non-GAMEPLAY),
     * the `error` handler, and the rolling watchdog's forced escape. In every
     * case the drain has reached an exit condition; the tail runs the clock
     * reveal + prompt creation + FSM hook.
     *
     * THE CURSOR ADVANCES HERE, and only here: a beat is "finished" exactly
     * when its cycle closes. That is what makes a later frame stamped for it
     * classifiable as PRIOR.
     */
    _finishCycle() {
        this._auditSnapshot('_finishCycle');
        const s = this._state;
        // Cycle exit disarms the one backstop.
        this._clearTimer('stallWatchdogTimer');
        this._clearTimer('stallNoticeTimer');
        s.cycleOverdue = false;
        this._clearTimer('thinkingDelayTimer');
        // [2026-07-29-010-QASTI] A cycle exit ends any ch2 animation this cycle
        // was waiting on. Leaving the latch set here is how the wedge OUTLIVED
        // its own escape: the prod watchdog fired at rel 845.33, force-exited
        // the cycle — and `ch2_playing` was STILL true on the way out, so the
        // re-entered drain returned at the same gate. Only the next
        // `user_action` cleared it (rel 845.75), i.e. the recovery depended on
        // the player typing rather than on the pump.
        this._clearTimer('ch2PlaybackTimer');
        if (s.channels) s.channels.ch2_playing = false;

        // Advance the monotonic beat cursor to the beat that just finished.
        // Never backwards WITHIN A SPACE: an out-of-order release must not
        // rewind the epoch. Across spaces the guard does not apply -- the ordinal
        // is that space's own counter, and an old space's cursor would leave the
        // new one with no ordering basis at all.
        const finishedBeat = this._beatId();
        if (finishedBeat && (!s.beatCursor
                || this._beatCompare(finishedBeat, s.beatCursor) === null
                || this._beatCompare(finishedBeat, s.beatCursor) > 0)) {
            s.beatCursor = finishedBeat;
        }

        s.status = 'idle';
        s.hasPlayedAny = false;
        s.initiator = null;
        s.thinkingStartMs = null;
        // P5: NO "safe default" is written here. What the player may do after
        // this cycle is the server's RESOLVED statement for the beat, already
        // adopted or on its way -- a client that re-asked the player here was
        // deciding eligibility, and it asked committed players too.
        // Clear the cycle-start inflight snapshot — the next cycle repopulates
        // it from the latest pendingClock.inflight_skills.
        s.cycleStartInflight = new Set();
        // [2026-08-07-001-ENGINE] ADJUDICATE BEFORE THE RECORD IS WIPED.
        // The beat is closing; this is the deterministic moment a channel that
        // was owed and never arrived becomes DROPPED. Must run before
        // `_resetCycleCompletion()` -- after it there is nothing left to judge.
        this._adjudicateOwedChannels();
        // Fresh (unbound) record. The next user/server/idle_action re-arms it.
        this._resetCycleCompletion();
        this._syncEmit('pump_finish_cycle');
        this._finishCycleTail();
    },

    /**
     * The beat's ledger as a close finds it, in the shape Frame declares
     * (`FrameRegistry.BEAT_LEDGER_FIELDS`): the cycle's arming, whether the
     * player typed (ch1's owed condition), the beat author's ch0 verdict, and
     * per judged content channel what is admitted and not yet rendered
     * (`in_hand`: the slot or the FIFO) and whether it reached a terminal
     * (`discharged`: rendered, or voided by the server). A read-only view of
     * the ONE record and the transport slots -- nothing is decided here.
     */
    _beatLedger() {
        const s = this._state;
        const cc = s.cycleCompletion;
        const ch = s.channels || {};
        const row = (inHand, discharged) => ({ in_hand: inHand, discharged: !!discharged });
        const verdict = cc && cc.beatVerdict ? cc.beatVerdict.ch0Owed : null;
        return {
            armed: s.initiator === 'user' || s.initiator === 'server',
            command_typed: s.initiator === 'user',
            ch0_owed: (typeof verdict === 'boolean') ? verdict : null,
            channels: {
                '0': row(ch.ch0 ? 1 : 0, cc && (cc.ch0.rendered || cc.ch0.voided)),
                '1': row(this._ch1Queue().length, cc && this._ch1Discharged()),
                '2': row((ch.ch2_mechanics || []).length, false),
                '3': row((ch.ch3_lifecycle || []).length, false),
            },
        };
    },

    /**
     * A CLOSE ASKS FRAME WHAT THE BEAT STILL OWES (thread 3, 2026-10-29).
     *
     * The two closes -- the newer-beat rescue in `_onClockFrame` and the
     * liveness backstop -- used to read `status === 'pending'` as "this beat's
     * content never came" and hand everything still queued to `_finishCycle`
     * as DROPPED. `pending` means nothing has PLAYED yet; it says nothing about
     * what has ARRIVED. On validation-727691b7a beat n6 the ch1 and four ch3
     * rows of a windup beat were in these slots, held behind the client's own
     * 4 s thinking minimum, when the server's next clock landed at 2.7 s; the
     * rescue closed the beat and reported all five DROPPED. The server was
     * right. The cause was here.
     *
     * `FrameRegistry.beatOwes` (the twin of `plug_core.frame.beat_owes`) splits
     * the question into the two facts it actually is:
     *   * ABSENT  -- owed by its kind's condition, never admitted, no terminal.
     *                DROPPED now, by the one verdict writer, and discharged on
     *                the record so nothing waits on it any longer.
     *   * IN HAND -- admitted and not yet rendered. The server did its part.
     *                The wait ENDS (the pacing hold is released, the beat's
     *                barrier is behind us) and the frame still plays, in order,
     *                through the one drain; the cycle then closes itself.
     *
     * `settle` is the caller saying THE SERVER HAS STATED THIS BEAT IS OVER (a
     * clock for a newer beat). Only then is what is absent discharged on the
     * record and the beat's barrier taken as reached -- those are statements
     * about the server's beat, and the backstop, which fires on a server that
     * said nothing, makes none of them: it reads, releases the hold, and lets
     * `_finishCycle`'s verdict judge what never arrived if the exit is forced.
     *
     * Returns true when something was in hand and the drain actually took it
     * -- the caller must NOT force the exit; false when nothing was in hand,
     * nothing moved, or the record cannot complete natively, so the caller
     * closes the cycle as it always did. GUARD ON THE GUARD: a refused ledger
     * or a throw returns false and the caller's unconditional exit still runs.
     */
    _endTheWait(cause, opts) {
        const settle = !!(opts && opts.settle);
        const s = this._state;
        const cc = s.cycleCompletion;
        const sid = (typeof GameServerConnection !== 'undefined'
            && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
        if (!cc) return false;
        let owes;
        try {
            owes = globalThis.FrameRegistry.beatOwes(this._beatLedger());
        } catch (err) {
            console.error(
                `[${sid}] TurnPump: beat.owes REFUSED cause=${cause} -- the ledger is ` +
                `not one Frame declares; forcing the exit as before`, err
            );
            return false;
        }
        const beat = this._beatId() || s.currentCycleId || 'unstamped';
        console.log(
            `[${sid}] TurnPump: beat.owes cause=${cause} beat=${beat} ` +
            `in_hand=[${owes.inHand.join(',')}] absent=[${owes.absent.join(',')}]`
        );
        if (settle) {
            for (const channel of owes.absent) {
                this._recordDrop(channel, beat,
                    `the beat is over (${cause}) and no ch${channel} ever arrived`);
                // Discharge on the record, through the writers that own each
                // latch, so the drain and the completion stop waiting on a
                // frame the server will never send.
                if (channel === 0) {
                    this._terminalizeCh0('voided', { cycle_id: cc.beatId, render_reason: cause });
                } else if (channel === 1) {
                    cc.ch1.voided = true;
                }
            }
        }
        if (owes.inHand.length === 0) return false;
        if (cc.aborted) {
            console.log(
                `[${sid}] TurnPump: beat.owes beat=${beat} has frames in hand but the ` +
                `cycle is aborted (a participating-channel gap); it cannot close ` +
                `itself, so the exit is forced`
            );
            return false;
        }
        if (settle) {
            // The beat is over on the server's side, so its phase is settled
            // whether or not the barrier frame was seen: a scene in hand must
            // not wait on a `phase_settled` that is behind us. (A lost ch9
            // frame is the ledger's to report, not this close's.)
            const key = cc.beatId || this._cycleActivePhaseKey();
            const barrier = key ? (cc.phases || {})[key] : null;
            if (barrier && barrier.engaged && !barrier.reached) {
                barrier.reached = true;
                console.log(`[${sid}] TurnPump: beat.owes beat=${beat} barrier taken as reached (${cause})`);
            }
        }
        // Release the pacing hold: what is in hand plays NOW, in the drain's
        // order (ch1, the interval stream, ch0), and `_cycleNativeComplete`
        // closes the cycle when the last of it has played.
        const heldBefore = JSON.stringify(this._beatLedger().channels);
        s.thinkingStartMs = null;
        this._clearTimer('thinkingDelayTimer');
        this._advance();
        // Did the drain TAKE anything? A frame still behind a gate this close
        // did not release (a dice animation in flight, a readout the server
        // never sent and the backstop may not void) has not moved, and a close
        // that returned "handled" over it would re-strand the pump. Nothing
        // moved -> the caller forces the exit, as it always did.
        const moved = s.status !== 'pending'
            || JSON.stringify(this._beatLedger().channels) !== heldBefore;
        if (!moved) {
            console.log(
                `[${sid}] TurnPump: beat.owes beat=${beat} frames in hand did not move ` +
                `on release (${cause}); the exit is forced`
            );
            return false;
        }
        return true;
    },

    /**
     * [2026-08-07-001-ENGINE] THE OWED-CHANNEL VERDICT. No seconds involved.
     *
     * Turn-manager is the only service that mints a beat, so a beat CLOSING is
     * turn-manager stating the prior beat is over. A channel that was owed to
     * that beat and never reached the player is therefore DROPPED -- determined,
     * not estimated. This replaces the stall watchdog's second firing mode,
     * which was answering the same question with a 90s wall clock shorter than
     * the beat it was judging ([2026-08-07-003-QASTI]).
     *
     * Per-channel conditions and budgets: [2026-08-07-001-ENGINE] and
     * `docs/Tome/systems/channels.md` §5.
     *
     * GUARD ON THE GUARD: this runs inside `_finishCycle`, so a throw here
     * would convert a normal cycle exit into a hang. Every branch is wrapped;
     * a missing verdict is survivable, a missing `_finishCycle` is not.
     */
    _adjudicateOwedChannels() {
        try {
            const s = this._state;
            const cc = s && s.cycleCompletion;
            if (!cc) return;
            // An UNARMED cycle was never owed anything: no submit, no ch0, no
            // ch1. Judging it would manufacture a drop on every passive beat.
            const armed = (s.initiator === 'user' || s.initiator === 'server');
            const beat = cc.beatId || s.currentCycleId || 'unstamped';
            const ch = s.channels || {};

            // --- ch0: owed on every armed beat that produced content ---------
            //
            // [2026-09-01-007-ENGINE] THE COMMENT ABOVE WAS THE SPEC AND THE
            // CONDITION DID NOT MEET IT. `armed` says a submit happened; it says
            // NOTHING about whether the beat produced anything. So a beat whose
            // AUTHOR stated `ch0_owed=false` -- a beat that deliberately owes
            // nothing and correctly draws no line -- was judged at close, found
            // wanting, and printed `The scene fails to render.` Measured on prod
            // 2026-09-01, world 428ef4ca, seat `voss`, room `coast-landing`:
            // `NO ch0 DISPATCHED -- reason=no_content ... recipients=1`, and the
            // player read a failure for a beat that had nothing to say.
            //
            // `space.md` 23-a point 2 (D20, RATIFIED): *"'Did this beat produce
            // content?' is stamped by the beat's AUTHOR -- turn-manager ... A
            // downstream renderer that infers the verdict from the action set is
            // 23.2's defect wearing a delta's clothes."* And 23 rule 1: zero
            // content is a legitimate ACCOUNT, *"never a failure shown to the
            // player."*
            //
            // NOTHING NEW IS PLUMBED. `ch0_owed` is already on `phase_settled`,
            // already parsed onto `cc.beatVerdict`, `_beatDeclaredDead()`
            // already reads it, and the **ch1** arm below already trusts it
            // ([2026-08-31-006-ENGINE]). This arm was the one that did not.
            //
            // ⚠️ THREE-VALUED, and the third value is the rolling-deploy safety.
            // `_beatDeclaredDead()` is strictly `=== false`: an UNSTATED verdict
            // (`null`, from a pod that has not shipped the stamp) behaves
            // EXACTLY as it does today and still drops. Suppressing a beat that
            // HAD a subject is SILENCE, which is the asymmetric failure this
            // subsystem has paid for over and over -- and the drop verdict is
            // the instrument [2026-07-29-014-QASTI] closed the silent-drop class
            // with, so it must stay loud everywhere it was loud before.
            if (armed && !this._beatDeclaredDead()
                    && !cc.ch0.rendered && !cc.ch0.voided) {
                this._recordDrop(0, beat,
                    'the beat closed with ch0 neither rendered nor voided');
            } else if (cc.ch0.rendered || cc.ch0.voided) {
                // The metric rides the beat that produced it, drop or not.
                const w = cc.ch0.inputWindowSeconds;
                if (w !== null && w !== undefined) {
                    console.log(`[Delivery] cycle=${beat} ch=0 input_window=${w}`);
                }
            }

            // --- ch1: owed when one was en route and never discharged --------
            if (armed && this._ch1Owed()) {
                this._recordDrop(1, beat,
                    'the beat closed with a ch1 still owed and undischarged');
            }

            // --- ch0 / ch1: WAREHOUSED past their own beat -------------------
            // [2026-08-07-001-ENGINE, retirement pass] The slot is not the
            // record. `_finishCycle` clears `ch2_playing` and NOTHING ELSE, so a
            // frame sitting in `channels.ch0` / `.ch1` SURVIVES cycle exit --
            // which is why the watchdog needed a second firing mode at all
            // ("the server delivered this frame and the player never saw it").
            //
            // The verdict above reads `cc.ch0.rendered/voided` and therefore
            // cannot see this: the frame arrived, was accounted, and simply was
            // never drained.
            //
            // ⚠️ ONLY a frame stamped for a PRIOR beat counts. One stamped for
            // the beat now closing may still drain -- the escape re-enters
            // `_advance()` immediately after this runs, deliberately, to give it
            // that last chance. Judging it here would manufacture a drop on
            // every healthy beat, which is the defect this whole arc is about.
            const outlived = (held) => {
                const heldBeat = held.cycleId || null;
                if (!heldBeat) return false;           // unstamped: incomparable, never invented
                return this._beatCompare(heldBeat, beat) < 0;   // current/future: may still drain
            };
            const declareLost = (slot, held) => {
                this._recordDrop(slot === 'ch1' ? 1 : 0, held.cycleId,
                    `a delivered ${slot} frame outlived its beat unrendered ` +
                    `(req=${held.requestId || 'none'})`);
            };
            if (ch.ch0 && outlived(ch.ch0)) {
                declareLost('ch0', ch.ch0);
                this._markCh0Done(ch.ch0);
                ch.ch0 = null;   // stop carrying a frame we have just declared lost
            }
            ch.ch1 = this._ch1Queue().filter((held) => {
                if (!outlived(held)) return true;
                declareLost('ch1', held);
                return false;
            });

            // --- ch2 / ch3: admitted into the FIFO, never drained ------------
            // These arrived (the ledger accounted them) and the drain never
            // rendered them -- the one class the ledger CANNOT see, because
            // nothing is missing from the seq stream.
            const stranded2 = (ch.ch2_mechanics || []).length;
            const stranded3 = (ch.ch3_lifecycle || []).length;
            // ch3 is the substrate ch2's rolls hang off, so when both strand in
            // one beat ch3 is the CAUSE and ch2 the CONSEQUENCE. Reporting them
            // as independent double-counts every ch3 loss.
            if (stranded3) {
                this._recordDrop(3, beat,
                    `${stranded3} interval-lifecycle frame(s) admitted and never rendered`);
            }
            if (stranded2) {
                this._recordDrop(2, beat,
                    `${stranded2} dice row(s) admitted and never rendered`
                    + (stranded3 ? ' (CONSEQUENCE of the ch3 drop above, not independent)' : ''));
            }

            // ch4 is not judged here: its owed line is judged in SECONDS by
            // GameServerConnection (`_enrolCh4Owed`), never by the beat.

            // --- ch9 server-authority: owed to the beat it was addressed to --
            this._adjudicateServerAuthority(beat);
        } catch (err) {
            try {
                console.warn('[TurnPump] owed-channel adjudication failed', err);
            } catch (_e) { /* never throw into the cycle exit */ }
        }
    },

    /**
     * [2026-08-08-015-ENGINE] SERVER-AUTHORITY'S OWED CONDITION.
     *
     * # The gap this closes
     *
     * channels.md §5 gave owed conditions to ch0-ch4 and stopped. client_
     * funnel.md §1 asserted that a `server-authority` miss is "accountable to
     * the beat — a miss is a silent wedge, which is why refusals must render" —
     * and nothing anywhere adjudicated it. So the refusal, the notice, the
     * queue position: every frame built to END a silence could itself go
     * missing in silence. Same shape as a dropped ch0, one layer up, and by
     * construction invisible because the whole point of the axis is that the
     * player is told nothing else.
     *
     * # Owed / dropped
     *
     * A ch9 position the SERVER committed to the control stream (it consumed a
     * seq, so it exists) and the client never saw is DROPPED. The losses are
     * recorded where they are detected — the ledger's force-release and
     * resend-miss paths, which already exempt positions the client did dispatch
     * (`ch.seen`), so a merely-reordered bootstrap frame is never counted.
     *
     * # The budget, and why it is +1
     *
     * **+1 beat: judged when the beat it was addressed to closes.** The same
     * budget as ch0, and deliberately tighter than ch4's +2.
     *
     *   * Not ZERO / mid-beat. The control stream repairs itself — NACK at
     *     LEDGER_NACK_COALESCE_MS (80ms), force-release at
     *     LEDGER_CONTROL_GAP_GRACE_MS (130ms) or LEDGER_GAP_GRACE_MS (300ms).
     *     ⚠️ Judging inside that window would RACE the CLOCK-NEVER-STALLS
     *     carve-out instead of observing its outcome, and would manufacture a
     *     drop for every reordered `room_state` — precisely the leg-eae8ce87
     *     shape `_ledgerAbortUnseenGap` was written to stop. The beat close is
     *     three orders of magnitude past every one of those graces (a measured
     *     beat is ~47s, [2026-08-08-007-QASTI]), so this never fights them.
     *   * Not +2. A server-authority frame is the answer to the command the
     *     player JUST issued. Reported two beats later it is not a notice, it
     *     is an epitaph — the player has typed again and moved on. ch4 earns
     *     +2 because dialogue is conversational and can legitimately land
     *     beside the next scene; a refusal cannot.
     *
     * # Tracked, never blocking
     *
     * ⚠️ This adds NO abort. `_cycleNoteAbort`'s `participates` set is
     * deliberately untouched (channels.md §3): a lost notice must not poison
     * the beat the player is waiting on. The cost of a false verdict is one
     * deduped line; the cost of a false abort is the wedge itself.
     *
     * # The base case that stops the recursion
     *
     * The line this renders is generated ON THE CLIENT and never crosses the
     * wire, so it is the one server-authority statement that cannot itself be
     * lost. Without that the guard would need a guard.
     */
    SERVER_AUTHORITY_BUDGET_BEATS: 1,

    _adjudicateServerAuthority(beat) {
        const s = this._state;
        const losses = s._serverAuthorityLosses;
        if (!losses || !losses.length) return;
        const survivors = [];
        const due = [];
        for (const loss of losses) {
            const lossBeat = (loss && loss.cycleId) ? String(loss.cycleId) : null;
            // A loss stamped for a beat that has NOT closed yet keeps waiting:
            // its own deadline has not arrived. Everything else — this beat, an
            // older beat, an unknowable or incomparable coordinate — is due now.
            // Judging the unknowable rather than carrying it forever is the same
            // call `_cycleNoteAbort` makes on a null coordinate, and it is the
            // safe direction here: the verdict is tracked, not blocking.
            if (lossBeat && this._beatCompare(lossBeat, beat) > 0) {
                survivors.push(loss);
                continue;
            }
            due.push(loss);
        }
        s._serverAuthorityLosses = survivors;
        if (!due.length) return;
        const seqs = due.map(l => (l && l.seq !== undefined ? l.seq : '?')).join(',');
        this._recordDrop(9, beat,
            `${due.length} server-authority control frame(s) committed to the ` +
            `stream and never seen (seq=${seqs}) — the beat closed without ` +
            `them [2026-08-08-015-ENGINE]`);
        // THE PLAYER IS TOLD. §1's whole claim is that a server-authority miss
        // is a SILENT wedge; converting it into a known degradation is the only
        // thing that makes the axis accountable. `_recordDrop` memoises per
        // (beat, channel), so a beat that loses six frames still prints ONE
        // line — the 812-wallpaper lesson from [2026-08-03-001-QASTI].
        //
        // The copy does not claim to know WHAT was lost, because it cannot: the
        // frame is gone and its type went with it.
        try {
            if (typeof GameServerConnection !== 'undefined'
                    && typeof GameServerConnection._renderSystemNotice === 'function') {
                // [FB-HUD-02] Stamped with, and anchored inside, the beat that
                // lost the frames.
                GameServerConnection._renderSystemNotice(
                    'A message from the game did not arrive.', 'degradation',
                    { cycleId: beat });
            }
        } catch (_e) { /* the verdict stands even if the notice cannot render */ }
    },

    /**
     * Record one channel's drop for one beat, and render it when the player is
     * owed a visible answer.
     *
     * ⚠️ AT MOST ONCE PER (beat, channel). [2026-08-03-001-QASTI] recorded
     * **812** "The scene fails to render." lines in a single racing session.
     * An honest degradation line printed 812 times is wallpaper, and wallpaper
     * is the same LOUD-FAILURE inversion as an alarm that never fires.
     */
    _recordDrop(channel, beat, why) {
        const s = this._state;
        if (!s._dropsRendered) s._dropsRendered = new Set();
        const key = `${beat}::ch${channel}`;
        if (s._dropsRendered.has(key)) return;
        s._dropsRendered.add(key);
        console.error(
            `[TurnPump] ch${channel} DROPPED — ${why}. beat=${beat} ` +
            `[2026-08-07-001-ENGINE]`
        );
        console.log(`[Delivery] cycle=${beat} ch=${channel} phase=dropped`);
        // Only ch0/ch1 have player-facing degradation copy HERE; a dropped
        // mechanics frame is recorded, not narrated at the player. ch4 and ch9
        // render their own copy at their call sites — a lost dialogue line, a
        // lost scene and a lost server statement are three different sentences,
        // and one renderer that tried to say all three would say none of them.
        if (channel === 0 || channel === 1) {
            try {
                if (typeof GameServerConnection !== 'undefined'
                        && typeof GameServerConnection._renderVoidDegradation === 'function') {
                    // Reuse the EXISTING renderer, deliberately. It already
                    // owns the failure-class copy ("The scene fails to render."
                    // / "The lead-in fails to render.") AND a per-(cycle_id,
                    // channel) dedup memo -- so a server-declared void and this
                    // drop verdict for the SAME beat cannot both print. A
                    // second renderer would have been a second producer of that
                    // line, which is precisely how 812 of them reached one
                    // session [2026-08-03-001-QASTI].
                    GameServerConnection._renderVoidDegradation(
                        channel, { cycle_id: beat });
                }
            } catch (_e) { /* the verdict is recorded even if the render fails */ }
        }
    },

    /**
     * Drain-exit tail: reveal the buffered clock, create the new input prompt,
     * and dispatch the FSM hand-off for server-initiated modes.
     *
     * No gate — the drain reaching this point IS the gate. Prompt creation is
     * independently filtered by `_shouldShowPrompt()` (GAMEPLAY-only + not
     * playing + no existing active input line), so no stray prompt leaks in
     * locked modes. The buffered-clock reveal here is the second half of the
     * two-signal unlock: clock and prompt are born together.
     */
    _finishCycleTail() {
        this._auditSnapshot('_finishCycleTail');

        this._revealClock();
        // ⚠️ ORDER IS LOAD-BEARING: `_revealClock()` is what makes this beat's
        // `acting` current (see the stamp there), so the suppression question
        // can only be asked after it.
        this._applyPromptGate();

        // [Phase 4/5] Server-initiated cycle exit drives the FSM mode
        // transition for WELCOME_NARRATION + SPAWNING. The pump is the single
        // authority for these transitions — prior to Phase 4 this was
        // scattered across _releaseNarrationGate / _checkSpawnGate bypass
        // paths. See PUMP_CLEANUP_HANDOFF.md Phase 4d.
        if (typeof TerminalFSM !== 'undefined') {
            const mode = TerminalFSM.mode;
            if (mode === 'WELCOME_NARRATION') {
                // Welcome cycle complete -> hand off to _releaseNarrationGate
                // which owns the WELCOME -> ROSTER_DISPLAY transition AND
                // re-dispatches any buffered character_choice message.
                if (typeof GameServerConnection !== 'undefined'
                        && typeof GameServerConnection._releaseNarrationGate === 'function') {
                    GameServerConnection._releaseNarrationGate();
                }
            } else if (mode === 'SPAWNING') {
                // Spawn cycle complete -> hand off to _checkSpawnGate which
                // owns the SPAWNING -> GAMEPLAY transition once spawn_complete
                // has landed (or defers if it hasn't).
                if (typeof GameServerConnection !== 'undefined'
                        && typeof GameServerConnection._checkSpawnGate === 'function') {
                    GameServerConnection._checkSpawnGate();
                }
            }
            // [2026-07-06-001-ENGINE / D1] The former GAMEPLAY branch that
            // dispatched the deferred walk-arrival look (_dispatchRoomEntryLook)
            // is REMOVED. Arrival is server-authoritative — the enter funnel
            // delivers the new room's scene; the client sends no follow-up look.
        }
    },

    /**
     * [2026-08-15-011-ENGINE] THE PROMPT GATE, asked once per beat that reaches
     * the screen. Suppressed -> render the WAITING state in the prompt's place;
     * otherwise open a command line if one is owed.
     *
     * ⚠️ CALLERS MUST HAVE REVEALED THE CLOCK FIRST. `_revealClock()` is where
     * the beat's WINDOW statement becomes the adopted `promptState`, so asking
     * before it reads the previous beat's answer.
     *
     * Two callers, deliberately: `_finishCycleTail` (a beat closed) and
     * a clock frame when the pump is already idle (a beat opened with no
     * cycle to close). The second exists because suppression is a PER-BEAT
     * verdict with no expiry of its own — without it, one `acting:false` beat
     * closed the command line and nothing short of a full cycle completing
     * could ever re-open it.
     *
     * No time is involved anywhere here: the gate is re-asked on a BEAT, which
     * is the only event that can change its answer.
     */
    _applyPromptGate() {
        if (this._isInputSuppressed()) {
            this._renderInputSuppressed();
            return;
        }
        // [2026-08-28] A SPENT WINDOW LOCKS TOO, AND IT IS A SEPARATE FACT.
        // Checked AFTER suppression so the `not-asked` copy still wins when
        // both are true — a player who was never asked should be told that,
        // not told their (nonexistent) action is resolving.
        if (this._isWindowClosed()) {
            this._renderWindowClosed();
            return;
        }
        // [2026-08-29-003-ENGINE] PAST BOTH REFUSALS, `[watching]` IS A LIE.
        //
        // Reaching this line means the server asked this player AND the window
        // is still open — the same two facts the prompt itself is born from. So
        // the action line's `[watching]`, which asserts the opposite, must go
        // with them. Live on prod 2026-08-29 it did not: the arrival beat
        // renders `[watching]` from its `acting:false` clock, the prompt opens
        // ~30s later off the ch0 terminal, and nothing ever re-rendered the row
        // — so the first turn every player sees says "watching" above a live
        // prompt.
        //
        // ⚠️ HERE, NOT IN THE `_shouldShowPrompt()` ARM BELOW. That arm also
        // answers "is a prompt OWED" (it declines when one already exists), and
        // the row's honesty does not depend on whether a NEW line is drawn.
        // Gating on it would leave `[watching]` standing on any beat that
        // re-asked the gate with a prompt already up.
        if (typeof ActionLine !== 'undefined'
                && typeof ActionLine.clearWatching === 'function') {
            ActionLine.clearWatching();
        }
        // P5: ASKED WITH NO WINDOW -- the gap after a beat closed, where the
        // room waits on input. There is nothing to count down, and a frozen
        // countdown from the closed beat would advertise a wait that is over.
        if (!this._state.promptState.window
                && typeof TerminalFSM !== 'undefined' && TerminalFSM.mode === 'GAMEPLAY'
                && typeof ClockLine !== 'undefined' && !ClockLine.isActive()
                && typeof ClockLine.showWaiting === 'function') {
            ClockLine.showWaiting();
        }
        if (this._shouldShowPrompt() && typeof createNewInputLine === 'function') {
            createNewInputLine();
        }
    },

    /**
     * Positive gate: returns true only when a new prompt line should appear.
     * Single source of truth — called from _finishCycle() and handleRoomState().
     */
    _shouldShowPrompt() {
        // Must be in GAMEPLAY (not SPAWNING, WORLD_MENU, etc.)
        if (typeof TerminalFSM !== 'undefined' && TerminalFSM.mode !== 'GAMEPLAY') return false;
        // Pump must be idle (not mid-cycle)
        if (this._state.status !== 'idle') return false;
        // THE SERVER'S `acting` IS AUTHORITY, NOT DECORATION
        // ([2026-08-12-011-ENGINE] / decision-log 2026-08-10).
        //
        // A character in a committed state is NOT PROMPTED. Turn-manager
        // resolves the eligible set once per beat and the same set drives
        // `acting`, the collection wait, the NPC fire and the timeout
        // substitution — so a suppressed actor was never asked, and offering
        // them a prompt invites a command the `no-skill-start` gate will then
        // refuse. Being invited to act and then told no is a worse experience
        // than not being asked, which is the ruling's whole point.
        //
        // Until now every live read of `acting` was COSMETIC (grey the clock
        // row, render `[watching]`), so the server could say `acting: false`
        // all along and the prompt appeared anyway. This is the enforcement.
        if (this._isInputSuppressed()) return false;
        // [2026-08-28] THE WINDOW MUST STILL BE OPEN.
        //
        // The gate above asks whether the server ASKED this player. It cannot
        // answer whether they can still REPLY, and the comment on this gate's
        // two callers says why: "the gate is re-asked on a BEAT, which is the
        // only event that can change its answer." Clock expiry is a second
        // event that changes it, and it is not a beat — the beat is still
        // resolving when the window shuts.
        if (this._isWindowClosed()) return false;
        // No duplicate prompts.
        // [2026-08-03-014-ENGINE stage 2] Asks the STATE, not the DOM. This is
        // job 2 of the three `.active-input-line` did at once, and it is the
        // one that fails silently: after stage 3 the element is permanent, so
        // `querySelector(...)` is TRUE forever and this guard would refuse to
        // ever create a prompt again. No throw, no log — just a terminal that
        // stops offering a command line.
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        if (CL ? CL.isOpen() : !!document.querySelector('.active-input-line')) return false;
        return true;
    },

    /**
     * A `clock` frame: the beat opens, carrying its WINDOW statement.
     *
     * The statement is buffered WITH the clock (`pendingClock.prompt`) and
     * becomes current when that clock is revealed -- one unit, so there is
     * nothing to pair.
     */
    _onClockFrame(frame) {
        const s = this._state;
        const sid = GameServerConnection.state.sessionId || '?';
        const data = this._clockFields(frame);
        // The beat's budget reaches this player only as their statement's
        // window (`time-interval-phase-gameplay.md` section 6).
        const duration = data.prompt.window ? data.prompt.window.duration_seconds : 0;
        const beatMode = data.beat_mode || 'turn';
        // [2026-08-07-001-ENGINE] THE ONLY writer of `beatCadenceMs`.
        // Guarded: a beat with no window leaves the previous reading standing
        // rather than collapsing the window to the floor.
        const _cadenceMs = Number(duration) * 1000;
        if (Number.isFinite(_cadenceMs) && _cadenceMs > 0) {
            s.beatCadenceMs = _cadenceMs;
        }
        const isBoundary = data.is_boundary === true;
        const auditKey = data.audit_key || null;

        // Dedup: skip if this BEAT was already revealed (BUG-8 fix).
        // [2026-08-05-027-ENGINE] The dedup key is the BEAT KEY when the
        // frame carries a parseable one, and only falls back to the
        // (turn, phase) tuple when it does not. The tuple is not a beat
        // identity: a lobby beat has no turn and no phase (both 0), so
        // under the tuple every void delivery after the first deduped
        // itself out of the reveal. The beat key IS the identity — that
        // is the whole of this arc.
        // NOTE: The boundary/banner animation fires on arrival (below), BEFORE
        // this dedup, because dedup gates the clock reveal only — back-to-back
        // boundaries within one cycle still legitimately produce banner fires.
        const incomingKey = this._clockDedupKey(data);
        const isDuplicate = (s.lastRevealedPhase === incomingKey);

        // [ch2 T-4.4] Carryover detection (DECISIONS.md L7).
        // phase_number === 0 && is_boundary === true means turn N+1 has begun
        // while a phased skill from turn N is still resolving. Render a
        // distinct banner variant so the player sees "this ONE tick is the
        // carryover resolution window" rather than a normal new-phase beat.
        const incomingPhaseNum = data.phase_number || 0;
        const isCarryoverTick = (incomingPhaseNum === 0 && isBoundary && beatMode === 'phase');
        if (isCarryoverTick) {
            TurnPump._inCarryoverPhase = true;
            if (typeof window !== 'undefined') {
                window._inCarryoverPhase = true;
            }
        } else if (isBoundary && beatMode === 'phase' && incomingPhaseNum >= 1) {
            // Phase advanced past the carryover window -- clear the flag.
            // Rows already rendered during the carryover keep their dim class
            // (historical); new rows won't get it.
            TurnPump._inCarryoverPhase = false;
            if (typeof window !== 'undefined') {
                window._inCarryoverPhase = false;
            }
        } else if (beatMode === 'turn') {
            // Switching back to turn-beat also exits the carryover window.
            TurnPump._inCarryoverPhase = false;
            if (typeof window !== 'undefined') {
                window._inCarryoverPhase = false;
            }
        }

        // [BASELINE-FIXES Issue 2b] The big PhaseBanner.play() typewriter at
        // phase boundaries has been retired — the `[PHASE:NN]` identity is now
        // carried by the inline input prompt (main.js createNewInputLine) and
        // the clock renders as the minimal `[NNs...]` regardless of beat mode.
        // We still call PhaseBanner.clear() on the phase-beat → turn-beat
        // transition so any stale `.phase-banner` nodes from a prior pass are
        // demoted (safe no-op if none exist). The PhaseBanner module itself
        // remains intact (tests still exercise it directly).
        if (isBoundary && beatMode === 'turn' && typeof window !== 'undefined' && window.PhaseBanner) {
            try {
                window.PhaseBanner.clear();
            } catch (e) {
                console.warn(`[${sid}] TurnPump: phase banner clear failed`, e);
            }
        }

        if (isDuplicate) {
            console.log(`[${sid}] TurnPump: ignoring duplicate clock reveal for ${incomingKey}`);
            return;
        }
        // phase_clock is the authoritative source for the ACTIVE phase
        // id. The envelope ships cycle_id alongside audit_key (identical
        // for phase-level messages); read cycle_id first.
        if (data.cycle_id) {
            s.currentCycleId = data.cycle_id;
        } else if (auditKey) {
            s.currentCycleId = auditKey;
        }
        // A clock for a phase BELOW the cursor means the server restarted
        // its turn numbering (pod re-election). Checked here, AFTER
        // currentCycleId is updated, because the clock is what opens a
        // beat — detecting it any later would let the new epoch's first
        // frames classify prior. See _noteEpochRegression.
        this._noteEpochRegression(data, 'phase_clock');
        // The active phase key is NOT the beat identity. The two-signal
        // unlock requires the NEXT phase_clock, so this re-points to
        // N+1 on essentially every beat while beat N is still
        // completing. It is the clock's coordinate; `cycleCompletion
        // .beatId` is the completion's. Keeping them separate is what
        // stopped the pump asking "has the NEXT beat's mechanics stream
        // closed?" to decide whether THIS beat's ch0 may render.
        if (s.cycleCompletion) {
            s.cycleCompletion.activePhaseKey = s.currentCycleId;
        }
        s.pendingClock = {
            phase_number: data.phase_number,
            phase_display: null,
            beat_mode: beatMode,
            is_boundary: isBoundary,
            audit_key: auditKey,
            total_phases: data.total_phases || 0,
            start_time: data.start_time || null,
            turn_number: data.turn_number || 0,
            cycle_id: data.cycle_id || null,
            inflight_skills: data.inflight_skills || [],
            // This beat's WINDOW statement -- adopted when the clock is revealed.
            prompt: data.prompt,
        };
        window._clockMode = beatMode;
        window._nextPhase = (s.pendingClock.phase_number || 0) + 1;
        window._turnNumber = s.pendingClock.turn_number || 0;
        // `[PHASE:NN]` for createNewInputLine(): 0-based, zero-padded.
        if (s.pendingClock.phase_number != null) {
            // Derive 0-based, zero-padded.
            const zeroBased = Math.max(0, (s.pendingClock.phase_number | 0) - 1);
            window._phaseDisplay = String(zeroBased).padStart(2, '0');
        }
        // Sync telemetry: detect turn rollover (phase 1 after higher phase)
        const incomingPhase = data.phase_number || 0;
        if (incomingPhase <= window._syncLastHighPhase && window._syncLastHighPhase > 0 && incomingPhase === 1) {
            window._syncTurn = (window._syncTurn || 0) + 1;
        }
        window._syncPhase = incomingPhase;
        window._syncLastHighPhase = Math.max(window._syncLastHighPhase || 0, incomingPhase);
        if (incomingPhase === 1) window._syncLastHighPhase = 1;
        window._syncClockMode = beatMode;
        console.log(
            `[${sid}] TurnPump: clock frame ${duration}s phase ${data.phase_number} ` +
            `prompt=${data.prompt.state}/${data.prompt.reason} ` +
            `turn ${data.turn_number || 0} beat_mode=${beatMode} ` +
            `boundary=${isBoundary} audit=${auditKey || 'none'}`
        );
        this._syncEmit('phase_clock_received');
        // Clock is the prompt's companion: visible only when prompt is active.
        // Buffer here, reveal in _finishCycle() when pump is idle and the new
        // prompt is born. Mid-narration clocks must NOT appear above a locked prompt.
        //
        // [BASELINE-FIXES Issue 3] Visual suppression during SPAWNING lives in
        // ClockLine._startClock (defense-in-depth). Server-side, GameServer
        // skips phase_clock broadcast while session.spawn_pending is truthy.
        //
        // [Phase 5] If pump is idle (post-drain), reveal immediately;
        // otherwise the buffered clock is consumed by _finishCycleTail
        // when the drain exits. The old two-signal unlock gate
        // (ch0DonePendingUnlock + clockPendingUnlock) was retired with
        // the escape hatches — cycle completion runs the tail
        // unconditionally now.
        // [2026-09-04-001-ENGINE] STRANDED-PUMP RESCUE. A clock that
        // announces a strictly-NEWER same-room beat is the server
        // stating the beat in flight is OVER. If the pump is still
        // `pending` on that older beat — armed but with NO content
        // rendering (a walk-arrival or resume beat whose ch0 never
        // terminalized) — its ch0 is never coming, and the idle-recovery
        // block below is SKIPPED because status !== 'idle'. So the prompt
        // stays locked across every subsequent beat: the 90s liveness
        // backstop force-exits one stale cycle, the next beat re-strands
        // the pump, and if the dead beat's ch0 never arrives (an
        // `ch0_absent_no_submission` void loop) it never recovers at all.
        // That is the live prompt-lock loop.
        //
        // Force the stale cycle closed NOW. `_finishCycle` is the same
        // unconditional close the watchdog uses; its tail reveals the
        // pendingClock JUST SET above and re-opens the prompt — so this
        // is the watchdog's own recovery, made event-driven instead of
        // waiting on a 90s wall clock (or forever).
        //
        // Narrow BY CONSTRUCTION, so a healthy cycle is untouched:
        //   * ONLY `pending` — `playing`/`bridge` means a real (slow) ch0
        //     is mid-render and MUST NOT be truncated. `pending` means
        //     nothing has PLAYED yet -- NOT that nothing arrived: a ch1
        //     and the rows behind it sit in their slots under the thinking
        //     minimum with the status still `pending` (thread 3,
        //     2026-10-29, validation-727691b7a beat n6). So before the
        //     exit is forced, `_endTheWait` asks Frame what the beat still
        //     owes: frames in hand play in order and the cycle closes
        //     itself; only a beat with nothing in hand is forced closed
        //     here, with what never arrived DROPPED;
        //   * ONLY when the server already FINISHED this beat
        //     (`serverDone` — its `turn_complete` landed). A beat still
        //     resolving has `serverDone` false and its content is still
        //     coming; the NEXT phase_clock the server pipelines ahead of
        //     it is NOT a rescue trigger. This is the bit that separates
        //     a DEAD beat (finished, no ch0, moved on) from a healthy one
        //     (still in flight), and it is why a live pipelined NEXT clock
        //     does not steal the live beat's identity;
        //   * ONLY a BOUND older beat, same room, STRICTLY newer incoming
        //     (`_beatCompare > 0`) — a phase re-point of the SAME beat is
        //     vs===0, a just-armed unbound cycle is skipped, cross-room is
        //     incomparable (null) and left to the existing paths.
        const _cc = s.cycleCompletion;
        if (s.status === 'pending' && _cc && _cc.beatId && _cc.serverDone) {
            const _newBeat = this._phaseKeyOf(data);
            const _vsStuck = this._beatCompare(_newBeat, _cc.beatId);
            if (_vsStuck !== null && _vsStuck > 0) {
                if (this._endTheWait('newer-beat', { settle: true })) {
                    console.log(
                        `[${sid}] TurnPump: the server moved on to ${_newBeat} with ` +
                        `beat ${_cc.beatId}'s frames in hand -- the wait ended, they ` +
                        `play in order, and the cycle reveals this clock at its tail`
                    );
                    return;
                }
                console.error(
                    `[${sid}] TurnPump: STRANDED-PUMP RESCUE — clock for ` +
                    `newer beat ${_newBeat} arrived while pending on dead ` +
                    `beat ${_cc.beatId} (nothing in hand); the server moved ` +
                    `on, so forcing cycle exit to re-open the prompt ` +
                    `[2026-09-04-001-ENGINE].`
                );
                this._finishCycle();
                return;
            }
        }
        if (s.status === 'idle') {
            this._revealClock();
            // [2026-08-15-011-ENGINE] SUPPRESSION IS A PER-BEAT VERDICT,
            // SO IT MUST BE RE-ASKED ON EVERY BEAT THAT REACHES THE
            // SCREEN — including one that arrives while the pump is
            // already idle.
            //
            // `_finishCycleTail` was the ONLY caller of the prompt gate,
            // so an `acting:false` beat closed the command line
            // (`_renderInputSuppressed` -> `CommandLine.close()`) and the
            // only way back was ANOTHER FULL CYCLE COMPLETING. When the
            // next beat arrives asked while the pump is idle, the reveal
            // above adopts that statement and NOTHING re-opened the
            // prompt: the player kept a disabled input against a beat the
            // server had just told them they could act in. Prod read
            // `prompt_locked=true` for the rest of the session on exactly
            // that transition.
            //
            // Same two lines as the tail, through one helper — a second
            // spelling of the gate is how the two would drift.
            this._applyPromptGate();
        }
    },

    /**
     * A `prompt-state` frame: a beat's RESOLVED statement -- what is wanted in
     * the gap after its collection closed. Adopted now; the gate is re-asked
     * only on an idle pump, since a cycle in flight asks it at its own tail.
     *
     * A statement for a beat OLDER than the one adopted is refused: the beat on
     * screen is newer.
     */
    _onResolvedStatement(frame) {
        const s = this._state;
        const sid = GameServerConnection.state.sessionId || '?';
        const ps = this._statementOf(frame.payload, 'resolved', frame.coordinate.cycle_id);
        const cur = s.promptState;
        if (cur && cur.cycle_id && ps.cycle_id) {
            const vs = this._beatCompare(ps.cycle_id, cur.cycle_id);
            if (vs !== null && vs < 0) {
                console.warn(`[${sid}] prompt_state.stale kind=prompt-state`
                    + ` cycle=${ps.cycle_id} behind adopted=${cur.cycle_id}`
                    + ' -- refused; the beat on screen is newer');
                return;
            }
        }
        this._adoptPromptState(ps, 'resolved');
        if (s.status === 'idle') {
            this._applyPromptGate();
            return;
        }
        // MID-COLLECTION, THE SERVER STATED THIS PLAYER AGAIN. The cycle in
        // flight is the beat's and stays; only the line answers the statement:
        // asked per slot -> the line reopens for the slots still free; anything
        // else -> it closes, and the pending lock copy stands. The client
        // computes no eligibility of its own (P5).
        if (this.askedPerSlot()) {
            this._reopenForSlots();
        } else if (ps.state !== 'asked') {
            this._renderInputSuppressed();
        }
    },

    /**
     * Is the adopted statement a PER-SLOT ask that is still open: `asked`,
     * naming the slots still free, with its window not yet elapsed? While it
     * is, a submit joins the cycle in flight instead of opening a new one, and
     * an empty line is the pass on the slots still free.
     */
    askedPerSlot() {
        const ps = this._state.promptState;
        return !!(ps && ps.state === 'asked' && Array.isArray(ps.slots)
            && ps.slots.length > 0 && ps.windowElapsed !== true);
    },

    /** Reopen the line for the slots still free, under the cycle in flight. */
    _reopenForSlots() {
        const sid = GameServerConnection.state.sessionId || '?';
        const ps = this._state.promptState;
        console.log(`[${sid}] prompt.reopen path=slots cycle=${ps.cycle_id || '-'}`
            + ` slots=${ps.slots.join(',')}`);
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        if (CL && typeof CL.isOpen === 'function' && CL.isOpen()) return;
        if (typeof TerminalFSM !== 'undefined' && TerminalFSM.mode !== 'GAMEPLAY') return;
        if (typeof createNewInputLine === 'function') createNewInputLine();
    },

    /**
     * The fields the clock path reads, off a validated `clock` frame. The
     * coordinate supplies the beat ids; the statement travels with the clock.
     */
    _clockFields(frame) {
        const p = frame.payload;
        const c = frame.coordinate;
        return {
            cycle_id: c.cycle_id || null,
            audit_key: c.audit_key || null,
            beat_mode: p.beat_mode,
            is_boundary: p.is_boundary,
            phase_number: p.phase_number,
            total_phases: p.total_phases,
            start_time: p.start_time,
            turn_number: p.turn_number,
            inflight_skills: p.inflight_skills,
            prompt: this._statementOf(p.prompt, 'window', c.cycle_id),
        };
    },

    /**
     * A statement as the pump holds it. `stage` and `cycle_id` are facts of the
     * FRAME that carried it -- a `clock`'s prompt is its window statement, a
     * `prompt-state` frame is a resolved one. The frame was validated at the
     * wire door, so nothing here guesses.
     */
    _statementOf(statement, stage, cycleId) {
        const w = statement.window;
        return {
            state: statement.state,
            reason: statement.reason,
            stage: stage,
            cycle_id: cycleId || null,
            window: w ? { start_time: w.start_time, duration_seconds: w.duration_seconds } : null,
            // THE PER-SLOT ASK (time-slot-mechanics.md 5): the free slots this
            // player is asked for, one act each; null is the whole ask.
            slots: Array.isArray(statement.slots) ? statement.slots.slice() : null,
            windowElapsed: false,
            windowSubmitted: true,
        };
    },

    /** Make a statement the one the prompt, timer and action line read. */
    _adoptPromptState(ps, via) {
        this._state.promptState = ps;
        console.log(`[TurnPump] prompt_state.adopted via=${via} stage=${ps ? ps.stage : '-'}`
            + ` cycle=${ps ? ps.cycle_id : '-'} state=${ps ? ps.state : 'UNSTATED'}`
            + ` window=${ps && ps.window ? ps.window.duration_seconds + 's' : 'none'}`);
    },

    /**
     * Is this player input-SUPPRESSED? True unless the server STATED `asked`.
     *
     * P5: one reader of the server's statement. An unstated player is not
     * asked -- the prompt is never invented from the absence of a frame.
     */
    _isInputSuppressed() {
        const ps = this._state.promptState;
        return !(ps && ps.state === 'asked');
    },

    /**
     * Has the adopted statement's WINDOW run out?
     *
     * Distinct from suppression: the player WAS asked, and the window the
     * server gave them has ended (`noteWindowClosed`). That difference decides
     * the lock copy -- `resolving gameplay…` versus `not-asked`.
     */
    _isWindowClosed() {
        const ps = this._state.promptState;
        return !!(ps && ps.windowElapsed === true);
    },

    /**
     * THE COLLECTION WINDOW FOR THIS BEAT HAS CLOSED — called by ClockLine when
     * the countdown reaches zero. [2026-08-28]
     *
     * ⚠️ THIS IS THE MISSING EVENT. `_applyPromptGate()` has exactly two
     * callers — a beat closing and a beat opening — and its own comment states
     * the limit: *"No time is involved anywhere here: the gate is re-asked on a
     * BEAT, which is the only event that can change its answer."* Clock expiry
     * changes the answer and is not a beat, so before this the gate was never
     * re-asked and the prompt stayed live over a spent beat.
     *
     * WHAT IT COSTS THE PLAYER WITHOUT THIS (prod, 2026-08-28): `[0s...]`
     * rendered above a live prompt; the player typed `go north`;
     * `ClockLine._injectIdle()` had already sent `idle`, so the beat was spent
     * and the typed command landed on the NEXT beat, 121 s later.
     *
     * ⚠️ IT DOES NOT CLEAR THE TEXT. `CommandLine.close()` is a state flag —
     * a value the player had already typed survives until they are asked
     * again, which is the existing contract for every other suppression.
     *
     * Idempotent: a second expiry for the same beat is a no-op.
     */
    noteWindowClosed(opts) {
        // The countdown ClockLine runs IS the adopted statement's window, so
        // its end is that statement's window ending. No window, or already
        // ended: nothing to close.
        const ps = this._state.promptState;
        if (!ps || !ps.window || ps.windowElapsed === true) return;
        ps.windowElapsed = true;
        // [2026-09-01-014-ENGINE] WHICH close this is decides the copy.
        // The ordinary close (expiry after a real countdown) has an `idle` on
        // the wire, so `resolving gameplay…` is true. A DEAD-ON-REVEAL close
        // ({submitted: false}) submitted NOTHING — the window elapsed before
        // the player saw it — and the ordinary copy would claim work on an
        // input that does not exist, the exact lie [2026-08-22-004-ENGINE]
        // removed one state over. `not-asked` is honest for it: nothing is
        // wanted from this player any more this beat.
        ps.windowSubmitted = !(opts && opts.submitted === false);
        this._applyPromptGate();
    },

    /**
     * Render the spent-window lock — the ORDINARY form, with NO reason.
     *
     * ⚠️ NOT `'not-asked'`. That copy ("No input needed this Phase") is FALSE
     * here: this player WAS asked and the time ran out. `resolving gameplay…`
     * is true — their `idle` is on the wire and the beat is resolving — so no
     * fourth copy string is authored and canon
     * (`time-interval-phase-gameplay.md` §7, which deliberately leaves the
     * ASSUMED / NOT-THEIRS distinction open) is not settled by shipping.
     */
    _renderWindowClosed() {
        if (typeof window === 'undefined') return;
        if (window.CommandLine && typeof window.CommandLine.close === 'function') {
            window.CommandLine.close();
        }
        if (typeof window.renderCommandBlockLock === 'function') {
            // A close with nothing submitted renders the not-asked copy —
            // see noteWindowClosed. The ordinary close keeps the no-reason
            // form (`resolving gameplay…`), which is true of it.
            const ps = this._state.promptState;
            window.renderCommandBlockLock(
                true,
                (ps && ps.windowSubmitted === false) ? 'not-asked' : undefined,
            );
        }
    },

    /**
     * Render the WAITING state IN THE PROMPT'S PLACE, never as an absence.
     *
     * ⚠️ THIS IS NOT OPTIONAL DECORATION — it is the other half of the gate
     * ([2026-08-12-011-ENGINE] DoD 3). `acting: false` was already routine
     * before any of this landed, and the client rendered it as a blank clock
     * rather than a state; wiring the gate WITHOUT this would convert a
     * cosmetic wrong-render into a terminal with no input line and no
     * explanation, which reads as a dead client.
     *
     * The state is already built and shipped: `#cmd-resolving` in
     * `frontend/web/index.html` carries the decision-log's exact string
     * (`resolving gameplay…`, HTML-escaped), it has CSS in `game-hud.css`, and
     * `renderCommandBlockLock()` is its sole mutator. Nothing new is authored
     * here — the two records that looked like two spellings are one element,
     * and the reason the string "grepped to nothing in frontend/web/src" is
     * that the markup lives in `index.html`, one directory up, with `&hellip;`
     * rather than three dots.
     */
    _renderInputSuppressed() {
        if (typeof window === 'undefined') return;
        // Closing is a state flag only — it clears no text and removes no
        // node, so a value the player had typed survives until they are asked
        // again. `close()` is idempotent by contract.
        if (window.CommandLine && typeof window.CommandLine.close === 'function') {
            window.CommandLine.close();
        }
        if (typeof window.renderCommandBlockLock === 'function') {
            window.renderCommandBlockLock(true, this._suppressedLockReason());
        }
    },

    /**
     * WHICH suppressed line to show: is the server working on MY action, or was
     * I never asked? [2026-08-22-004-ENGINE]
     *
     * Both facts are on the client: `initiator` says whether THIS player
     * submitted into the cycle in flight, and the adopted `promptState` says
     * whether the server asked them.
     *
     * THE LIE IT REMOVES. A player who submitted nothing was shown
     * `resolving gameplay…`, which claims their input is being processed when
     * nothing of theirs is in flight. QA read exactly that as an engine
     * dropout, and a player on prod read it as a lock — in the same session
     * where a GENUINE stall ([2026-08-22-019-ENGINE]) was producing the
     * identical screen. Correct behaviour that is indistinguishable from a
     * failure costs what a failure costs.
     *
     * ⚠️ IT DOES NOT DISTINGUISH *ASSUMED* FROM *NOT THEIRS*, AND MUST NOT.
     * Canon `systems/time-interval-phase-gameplay.md` §7 leaves that open and
     * forbids a service settling it by shipping; D5 of [2026-08-19-024-ENGINE]
     * holds the line. `not-asked` is deliberately true of both — the assumed
     * player already supplied their input, the not-theirs player was never
     * asked, and neither needs to type. Telling them apart requires the server
     * to say WHY it suppressed, which is a wire change behind a ruling that
     * has not happened.
     *
     * ⚠️ `initiator === 'user'` AND NOT `status !== 'idle'`. A cycle a NPC or
     * the room clock started is in flight too, and reporting it as "resolving
     * gameplay" to a player who typed nothing is the same lie in a second
     * costume. Only this player's own submission earns the ordinary line.
     */
    _suppressedLockReason() {
        return this._state.initiator === 'user' ? undefined : 'not-asked';
    },

    /**
     * THE BEAT'S RENDER BLOCK — where a write lands INSIDE the beat currently
     * in flight. [FB-HUD-02 + FB-HUD-03, ticket 2026-08-10-005-ENGINE]
     *
     * # The defect this exists to fix
     *
     * The server sends, per player, in this order (networking.py
     * `_deliver_player_cycle`, and it is DELIBERATE — "room_state must arrive
     * after ch0 and before turn_complete"):
     *
     *     ch1 -> ch0 -> ch4 -> room_state(ch9) -> turn_complete -> phase_clock
     *
     * `room_state` is not pumped; it renders the instant it is dispatched. So
     * its two visual products — the POI arrival image and the exits/occupants
     * panel — APPENDED below a ch0 that was already on screen. Two player
     * reports, one cause:
     *
     *   FB-HUD-02  "the last line in the terminal when input is unlocked should
     *              always be the end of the ch0 narration" — it was the
     *              room-state panel, every movement and every look beat.
     *   FB-HUD-03  "images should appear above ch1 and ch0 text inline" — the
     *              picture landed AFTER the prose it was supposed to establish.
     *
     * # What this does NOT do
     *
     * It does not delay, gate or reorder ANY frame's arrival, and it drops
     * nothing. The two-signal unlock (ch0 terminal + the next `phase_clock`) is
     * untouched; a slow or absent image cannot hold ch0 or the prompt, because
     * nothing here is ever waited on. This is placement only.
     *
     * # Why the anchor is READ OFF THE DOM rather than remembered
     *
     * The funnel already stamps `data-origin` / `data-channel` / `data-cycle-id`
     * on every node it admits (client_funnel.md §2). Reading those back is
     * derived state keyed on the SAME axis as the thing it describes, so it
     * cannot drift from what actually rendered — whereas a remembered node
     * reference is a second record that goes stale the moment a render path
     * changes, removes or re-parents a node.
     *
     * # Scope, and why `_beatId()` is the gate
     *
     * `cycleCompletion.beatId` is null between beats (nulled by
     * `_resetCycleCompletion`, which runs at cycle arm AND inside
     * `_finishCycle`) and bound only while a beat is in flight. So:
     *   - between beats there is no block, and every write appends. A frame
     *     that arrives after the beat closed belongs to the NEXT block, and
     *     hoisting it above a scene the player has already read would be worse
     *     than the defect.
     *   - `forBeat`, when supplied, must MATCH. A caller holding an older beat's
     *     coordinate is told "no anchor" rather than being silently anchored
     *     into somebody else's block.
     *
     * ⚠️ A ch1 frame is addressed by its command and carries NO delivery
     * `cycle_id` (channels.md §2), so its node is stamped with the pump's
     * `currentCycleId` while ch0's is stamped with the beat. BOTH spellings are
     * accepted here for that reason; matching only one would silently drop ch1
     * out of the block and put the image below it.
     *
     * @param {'establishing'|'trailing'} kind
     *        `establishing` — the TOP of the block: the first narration node of
     *        this beat (ch1 if there is one, else ch0). An image inserted here
     *        establishes the beat before the prose narrates it.
     *        `trailing` — immediately ABOVE this beat's ch0, so ch0 stays the
     *        last thing the player reads when the prompt unlocks.
     * @param {string} [forBeat] the beat the caller believes it is writing into.
     * @returns {Node|null} the node to insert BEFORE, or null to append.
     */
    _blockAnchor(kind, forBeat) {
        const beat = this._beatId();
        if (!beat) return null;                                   // no beat in flight
        if (forBeat && String(forBeat) !== String(beat)) return null;   // another beat's coordinate
        const history = (typeof document !== 'undefined' && document.getElementById)
            ? document.getElementById('history')
            : null;
        if (!history) return null;
        const kids = history.children;
        if (!kids || !kids.length) return null;
        const cur = this._state.currentCycleId ? String(this._state.currentCycleId) : null;
        let firstNarration = null;
        let ch0 = null;
        // Backwards from the tail: the block is always the last thing in
        // history, and the first PRIOR beat's narration node terminates the
        // walk — so this stays O(block), not O(session).
        for (let i = kids.length - 1; i >= 0; i--) {
            const n = kids[i];
            if (!n || typeof n.getAttribute !== 'function') continue;
            if (n.getAttribute('data-origin') !== 'ledger-content') continue;
            const ch = n.getAttribute('data-channel');
            // ch0/ch1 are the block's SPINE. ch2/ch3 rows are anchored inside
            // the interval stream by Ch2Renderer, and ch4 renders after the ch0
            // it accompanies by ruling ([2026-08-03-002-PROMPT] D1) — neither is
            // the top or the bottom of the block.
            if (ch !== '0' && ch !== '1') continue;
            const stamp = n.getAttribute('data-cycle-id');
            if (stamp !== beat && (cur === null || stamp !== cur)) break;   // a prior beat: block starts below
            firstNarration = n;
            if (ch === '0') ch0 = n;
        }
        return (kind === 'establishing') ? (firstNarration || ch0) : ch0;
    },

    // ===================================================================
    // THE COMPLETION AUTHORITY: identity, classification, terminal writer,
    // derived views, predicates. Everything below reads or writes exactly
    // one object — `_state.cycleCompletion`.
    // ===================================================================

    /**
     * The frame's OWN beat key `{space}:n{ordinal}`, or null. Deliberately has
     * NO fallback to the pump's active cycle: attribution must be decided by
     * what the SERVER stamped, never by what the client happens to be looking
     * at. A frame with no stamp is `unstamped`, which is a documented class, not
     * a guess. (`_phaseKeyOf` keeps the fallback for barrier-key derivation.)
     */
    _frameKeyOf(data) {
        if (data && typeof data === 'object') {
            if (data.cycle_id) return String(data.cycle_id);
            if (data.audit_key) return String(data.audit_key).replace(/\.i\d+$/, '');
        }
        return null;
    },

    /**
     * The clock-reveal dedup identity for one `phase_clock` payload.
     *
     * [2026-08-05-027-ENGINE]. Prefers the BEAT KEY, which is the beat's actual
     * identity; falls back to the `(turn, phase)` tuple only when the payload
     * carries no parseable key (a legacy or UNKEYED frame). The tuple alone is
     * not an identity — a lobby beat carries turn 0 / phase 0 forever, so every
     * void delivery after the first would dedup itself out of the reveal.
     *
     * An UNKEYED (`{space}:unkeyed-beat`) frame deliberately does NOT parse, so
     * it takes the tuple branch: the server has already said loudly that this
     * beat has no address, and inventing one here would be the same lie one
     * layer down.
     */
    _clockDedupKey(payload) {
        if (!payload) return 'null';
        const key = payload.audit_key || payload.cycle_id || null;
        if (key && this._parseBeatKey(key)) return key;
        return `${payload.turn_number || 0}:${payload.phase_number}`;
    },

    /**
     * THE BEAT ORDERING AXES, parsed NUMERICALLY -- never compared as text
     * (`"void:1000.01" > "void:999.01"` is FALSE; the ordinal is an unbounded
     * Redis INCR, so no zero-pad is durable).
     *
     *     {space}:n{ordinal}[.i{II}]
     *
     * Split on the LAST colon: a space name may contain one (`time:void`). The
     * interval suffix narrows the same beat, so it is the least-significant
     * axis (absent => 0).
     *
     * Returns `{space, axes}` or `null` when the key is unparseable.
     */
    _parseBeatKey(key) {
        if (!key) return null;
        const s = String(key);
        // ⚠️ SPLIT ON THE **LAST** COLON, NEVER THE FIRST. The space segment
        // is an opaque identity the server minted; this parser never assumes
        // its shape. The beat half never contains a colon, so the last one is
        // always the separator.
        const i = s.lastIndexOf(':');
        if (i <= 0) return null;
        const m = s.slice(i + 1).match(/^n(\d+)(?:\.i(\d+))?$/);
        if (!m) return null;
        return { space: s.slice(0, i), axes: [Number(m[1]), m[2] ? Number(m[2]) : 0] };
    },

    /** Bounded set so an unparseable key is loud ONCE, not once per frame. */
    _beatKeyWarned: null,

    _warnBeatKeyOnce(tag, a, b) {
        if (!this._beatKeyWarned) this._beatKeyWarned = new Set();
        const k = `${tag}|${a}|${b}`;
        if (this._beatKeyWarned.has(k)) return;
        if (this._beatKeyWarned.size > 200) this._beatKeyWarned.clear();
        this._beatKeyWarned.add(k);
        console.warn(
            `[TurnPump] BEAT KEY ${tag}: a=${a} b=${b} — these keys have NO ` +
            `defined order, so this frame is treated as INCOMPARABLE (never ` +
            `'prior'). Silently ordering them would dead-air a live beat.`
        );
    },

    /**
     * Compare two beat keys NUMERICALLY: -1 / 0 / 1, or **null** when they are
     * not order-comparable (another space, or an unparseable key). Every call
     * site treats `null` as "no ordering information", so such a frame can
     * never be classified `prior` -- the class that renders as dead air.
     */
    _beatCompare(a, b) {
        if (!a || !b) return null;
        const pa = this._parseBeatKey(a);
        const pb = this._parseBeatKey(b);
        if (!pa || !pb) {
            this._warnBeatKeyOnce('UNPARSEABLE', a, b);
            return null;
        }
        if (pa.space !== pb.space) return null;   // routine: another room
        for (let i = 0; i < 2; i++) {
            if (pa.axes[i] !== pb.axes[i]) return pa.axes[i] < pb.axes[i] ? -1 : 1;
        }
        return 0;
    },

    /**
     * Derive the phase-grain cycle key from a wire payload, falling back to the
     * pump's currently-active phase when the frame carries no stamp. Used for
     * barrier keying and logging — NOT for attribution (see `_frameKeyOf`).
     */
    _phaseKeyOf(data) {
        if (data && typeof data === 'object') {
            if (data.cycle_id) return String(data.cycle_id);
            if (data.audit_key) return String(data.audit_key).replace(/\.i\d+$/, '');
        }
        return (this._state && this._state.currentCycleId) || null;
    },

    /** The identity of the beat in flight, or null while the cycle is unbound. */
    _beatId() {
        const cc = this._state && this._state.cycleCompletion;
        return (cc && cc.beatId) || null;
    },

    /** Advance the done-through ordinal past `entry` (null-safe, never backwards). */
    _markCh0Done(entry) {
        const n = entry && entry.ch0Ordinal;
        if (n && n > this._ch0DoneThrough) this._ch0DoneThrough = n;
    },

    /**
     * A selection screen (the vessel offer, and the Episode board after it) is
     * up. It covers the terminal and the drain does not run under it.
     */
    _selectionCovers() {
        return typeof TerminalFSM !== 'undefined'
            && (TerminalFSM.mode === 'CHARACTER_CHOICE' || TerminalFSM.mode === 'ROSTER_DISPLAY');
    },

    /**
     * A ch0 that will not play because a selection covers it. A named terminal,
     * never a slot write: the next arrival would DISPLACE it, and the cycle it
     * bound would wait on it until the liveness backstop. `owed` marks a scene
     * the player was owed BEFORE the switch -- losing one of those is an error.
     */
    _coverCh0(entry, cause, owed) {
        this._markCh0Done(entry);
        const sid = (GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
        const line = `[${sid}] TurnPump: ch0 covered cause=${cause} ` +
            `cycle=${(entry && entry.cycleId) || 'unstamped'} req=${(entry && entry.requestId) || 'none'}`;
        if (owed) console.error(`${line} -- a scene the player was OWED did not play before the switch`);
        else console.log(line);
        console.log(`[Delivery] cycle=${(entry && entry.cycleId) || 'none'} ch=0 phase=covered`);
    },

    /**
     * THE SWITCH SETTLES THE PUMP. A controls retarget (mortal -> spark on death;
     * a finished chapter -> the board) puts a selection over the terminal and
     * the drain stops. Whatever is still in flight is resolved HERE, by name:
     * a ch0 in the slot is covered, a pending bridge delay is cancelled, and an
     * open cycle is finished -- so no frame is left for the next arrival to
     * displace and no cycle is left for the liveness backstop.
     *
     * The caller holds the switch until the owed scenes have played
     * (`GameServerConnection._rosterOfferBeatUnread`); this only settles what is
     * left once it proceeds. `owedThrough` is the arrival ordinal the hold was
     * waiting for: a covered scene at or below it is a loss, reported as one.
     */
    settleForRetarget(cause, owedThrough) {
        const s = this._state;
        const sid = (GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
        this._clearTimer('thinkingDelayTimer');
        const held = s.channels.ch0;
        if (held) {
            s.channels.ch0 = null;
            this._coverCh0(held, `retarget:${cause}`,
                !!owedThrough && (held.ch0Ordinal || 0) <= owedThrough);
        }
        if (s.status === 'idle' && !s.stallWatchdogTimer) return;
        const cc = s.cycleCompletion;
        if (cc && !cc.ch0.rendered && !cc.ch0.voided) {
            this._terminalizeCh0('voided', { cycle_id: cc.beatId, render_reason: 'retarget' });
        }
        console.log(`[${sid}] TurnPump: cycle settled for retarget cause=${cause} ` +
            `beat=${this._beatId() || 'unbound'} status=${s.status}`);
        this._finishCycle();
    },

    /**
     * [2026-08-19-009-ENGINE] A cycle is re-arming. Retire any ch0 sitting
     * DELIVERED-BUT-UNPLAYED in the single slot, and remember which beat it
     * belonged to.
     *
     * THE DEFECT THIS CLOSES, reproduced exactly from the live capture in
     * PR #1756 (prod 2026-09-01, seat `kael`, `common-room`):
     *
     *     ch0Slot=no ch0Terminal=false ch0Rendered=false
     *     ch1Discharged=true ch1Owed=false barrier=true serverDone=true
     *     cycle=n5  beat=n3  cursor=n2
     *
     * A ch0-only server re-arm (`server_action` with origin SPAWNING /
     * WELCOME_NARRATION -- a spawn or resume) fired while the ch0 for the beat
     * in flight was delivered and waiting its turn to type. The arm nulled
     * `channels.ch0` unconditionally, the fresh record then RE-BOUND to that
     * same beat off a later frame, and it waited for a ch0 that the server had
     * already delivered and marked `res=played`. Nothing re-sends it. The 90s
     * liveness backstop force-completed the cycle with `ch0Terminal=false`, and
     * the player sat locked for that whole window.
     *
     * ⚠️ IT WAS ALSO INVISIBLE. `attribution.displaced` read **0** through the
     * entire wedge: `_noteSlotDisplacement` fires when a delivered frame is
     * OVERWRITTEN on the delivery path, and never fired for a frame the ARM
     * threw away. The one instrument built to report a lost scene could not see
     * this one, which is why the ticket spent two weeks on the wrong suspects.
     *
     * Returns the retired frame's beat key, or null. Never throws: this runs on
     * the arm path, and a diagnosis that breaks the arm is worse than none.
     */
    _retireUnplayedCh0(reason) {
        try {
            const s = this._state;
            const held = s && s.channels && s.channels.ch0;
            if (!held) return null;
            // Count it on the OUTGOING record, where the loss actually happened,
            // and on the same counter the delivery path uses -- one number for
            // "a scene the player was owed and will never see", however it was
            // lost.
            this._noteSlotDisplacement('ch0', held, { requestId: reason });
            this._markCh0Done(held);
            // ⚠️ AND CARRY IT ACROSS THE RESET. `_noteSlotDisplacement` counts
            // on the record that is about to be REPLACED by this very arm, so
            // on its own the number would be born and destroyed in the same
            // breath -- a counter that can never be read is no better than the
            // silence it was meant to end. `_resetCycleCompletion` seeds the
            // fresh record from this flag, so the loss is visible on the record
            // a diagnosis will actually look at.
            this._ch0RetiredPending = true;
            const key = held.cycleId || null;
            const sid = (typeof GameServerConnection !== 'undefined'
                && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
            console.error(
                `[${sid}] TurnPump: ch0 RETIRED UNPLAYED by ${reason} — a ` +
                `DELIVERED scene was discarded before it typed. ` +
                `lost=${held.requestId || 'none'} lost_beat=${key || 'unstamped'}. ` +
                `The server will not resend it, so a record that re-binds to ` +
                `that beat is voided rather than left waiting [2026-08-19-009-ENGINE].`
            );
            return key;
        } catch (err) {
            try { console.warn('[TurnPump] ch0 retire report failed', err); }
            catch (_e) { /* observability only; never throw into the arm */ }
            return null;
        }
    },

    /**
     * Events that carry BEAT attribution. Everything else (clock, turn
     * lifecycle, cycle-start events) is control and is not classified — it
     * belongs to the pump, not to a beat.
     */
    _ATTRIBUTED_EVENTS: [
        'narration_received', 'playback_complete', 'interval_event',
        'resolution_event', 'phase_settled', 'ch1_missing', 'ch0_missing',
    ],

    /**
     * THE ONE ATTRIBUTION CHECK. Classifies one inbound frame and returns
     * `{cls, key}` (or null when the event carries no beat attribution).
     *
     *   command   — a ch1 frame whose `command_id` is a command this cycle
     *               took (`turn_accepted` stated it; a slot act adds one to
     *               the beat in flight). ch1 is addressed by its command,
     *               never by a beat (`channels.md` §2). `command` names it.
     *   current   — stamped for the beat in flight. The only beat-class that
     *               may mutate the record's completion terms. If the cycle is
     *               still UNBOUND, a current frame BINDS it: the beat that
     *               resolves a command is minted after the command is sent, so
     *               the first stamped frame at or above the floor names it.
     *   prior     — stamped at or below the cursor (a finished beat), below the
     *               floor (a phase older than the one the player acted in), or
     *               below the bound beat — or a ch1 answering another command.
     *               Never touches the record. Counted + logged LOUDLY.
     *   future    — stamped above the bound beat: the next phase's mechanics
     *               are on the wire while this beat's ch0 still types. The
     *               EXPECTED case in phase mode, so it is counted, not warned.
     *   unstamped — no key: `turn_complete` carries none by contract, and a
     *               client-synthesized void has no server seq. Attributed
     *               temporally to the live record.
     */
    _attributeFrame(name, data) {
        if (this._ATTRIBUTED_EVENTS.indexOf(name) === -1) return null;
        const s = this._state;
        const cc = s.cycleCompletion;
        const channel = (data && data.channel !== undefined) ? data.channel : null;
        const key = this._frameKeyOf(data);

        if (name === 'ch1_missing' || channel === 1) {
            const cmd = (data && data.command_id) || null;
            const owed = cc ? cc.ch1.commands : {};
            if (cc && cmd && Object.prototype.hasOwnProperty.call(owed, cmd)) {
                return { cls: 'command', key: key, ch1: true, command: cmd };
            }
            if (cc) cc.attribution.prior++;
            console.warn(
                `[TurnPump] ch1 REFUSED: event=${name} command=${cmd || 'none'} ` +
                `answers no command in flight (cycle command=` +
                `${Object.keys(owed).join(',') || 'none'}) — not rendered`
            );
            return { cls: 'prior', key: key, ch1: true };
        }
        if (!key) {
            if (cc) cc.attribution.unstamped++;
            return { cls: 'unstamped', key: null, ch1: false };
        }
        if (!cc) return { cls: 'unstamped', key: key, ch1: false };

        // The two bounds of the live beat, both SAME-ROOM only (a key from
        // another room has no order against these; `_beatCompare` -> null).
        const vsCursor = this._beatCompare(key, s.beatCursor);
        const vsFloor = this._beatCompare(key, cc.beatFloor);
        if ((vsCursor !== null && vsCursor <= 0) || (vsFloor !== null && vsFloor < 0)) {
            cc.attribution.prior++;
            console.warn(
                `[TurnPump] PRIOR frame: event=${name} key=${key} ` +
                `cursor=${s.beatCursor || 'none'} floor=${cc.beatFloor || 'none'} ` +
                `beat=${cc.beatId || 'unbound'} — renders if it has content, ` +
                `but cannot touch the live record`
            );
            return { cls: 'prior', key: key, ch1: false };
        }
        if (!cc.beatId) {
            cc.beatId = key;
            // [2026-08-19-009-ENGINE] A re-arm retired this beat's DELIVERED
            // ch0 unplayed; the server sent it once and will not resend, so
            // void it now rather than wait for the liveness backstop.
            // `_terminalizeCh0` stays the SOLE writer of the ch0 terminal.
            if (this._ch0SpentForBeat && key === this._ch0SpentForBeat) {
                this._ch0SpentForBeat = null;
                console.error(
                    `[TurnPump] ch0 VOIDED on bind: beat=${key} had its ` +
                    `delivered scene retired by a re-arm, so no ch0 can still ` +
                    `arrive for it [2026-08-19-009-ENGINE].`
                );
                this._terminalizeCh0('voided', { cycle_id: key });
            }
            return { cls: 'current', key: key, ch1: false };
        }
        if (key === cc.beatId) return { cls: 'current', key: key, ch1: false };
        // INCOMPARABLE counts as `future`, never `prior`: `prior` is the class
        // that renders as dead air, so "no ordering information" must not
        // fall into it.
        const vsBeat = this._beatCompare(key, cc.beatId);
        if (vsBeat === null || vsBeat > 0) {
            cc.attribution.future++;
            return { cls: 'future', key: key, ch1: false };
        }
        cc.attribution.prior++;
        console.warn(
            `[TurnPump] PRIOR frame (below bound beat): event=${name} ` +
            `key=${key} beat=${cc.beatId}`
        );
        return { cls: 'prior', key: key, ch1: false };
    },

    /**
     * The phase key a CURRENT (or temporally-attributed) frame stamps on the
     * barrier. A stamped frame owns its own key; an unstamped one falls back to
     * the beat in flight, then to the active phase.
     */
    _barrierKeyFor(attribution) {
        if (attribution && attribution.cls === 'current' && attribution.key) {
            return attribution.key;
        }
        return this._beatId() || this._cycleActivePhaseKey();
    },

    /**
     * THE ONE TERMINAL WRITER for ch0. `rendered` and `voided` are mutually
     * exclusive outcomes of one delivery, so exactly one of them may ever be
     * set, and only from here. First terminal wins; a second attempt is REFUSED
     * and counted as an anomaly.
     *
     * This mirrors the server delivery ledger's TERMINAL_CHANNEL_STATES guard.
     * Prod showed `rendered` and `voided` BOTH true on one record because two
     * code paths wrote the flags directly; a single writer makes that
     * unrepresentable rather than merely unlikely.
     *
     * Returns true when the transition was taken.
     */
    _terminalizeCh0(sense, data) {
        const cc = this._state && this._state.cycleCompletion;
        if (!cc) return false;
        if (cc.ch0.rendered || cc.ch0.voided) {
            cc.attribution.refusedTerminals++;
            console.warn(
                `[TurnPump] ch0 terminal REFUSED: already ` +
                `${cc.ch0.rendered ? 'rendered' : 'voided'}, attempted=${sense} ` +
                `beat=${cc.beatId || 'unbound'} frame=${this._frameKeyOf(data) || 'unstamped'}`
            );
            return false;
        }
        if (sense === 'rendered') cc.ch0.rendered = true;
        else cc.ch0.voided = true;
        // [2026-08-21-001-ENGINE] WHY this ch0 became terminal, stamped by the
        // one writer that owns the transition. 'playback' = the typewriter
        // finished; 'ceiling' = P2 released the gate at
        // CH0_PLAYBACK_CEILING_MS with the narration still running. Same latch,
        // different fact about the beat -- and the VP snapshot and the watchdog
        // dump both read it, so a ceiling unlock can never be counted as a
        // clean render.
        if (sense === 'rendered') {
            cc.ch0.renderReason = (data && data.render_reason) || 'playback';
        }
        // [2026-08-07-001-ENGINE] THE INPUT WINDOW -- the player's actual agency.
        //
        // Their decision time is the span between ch0 landing and the turn
        // timer expiring. A 120s turn whose ch0 arrives at t=118 leaves TWO
        // SECONDS to read, decide and type, and nothing recorded that. Taken at
        // the terminal because that is the moment the scene becomes readable;
        // taken for a VOID too, because a void the player can read is still the
        // start of their window.
        //
        // A near-zero or negative window is a DISTINCT defect from a dropped
        // ch0 -- the frame arrived and the player still had no turn.
        cc.ch0.inputWindowSeconds = this._inputWindowSeconds();
        return true;
    },

    /**
     * Seconds left on this beat's clock, right now. `null` when no clock is
     * known (never 0 -- an unknown window and an exhausted one are different
     * facts, and collapsing them is the silent-zero shape).
     */
    _inputWindowSeconds() {
        try {
            const s = this._state;
            const clock = s && s.pendingClock;
            if (!clock) return null;
            const remaining = this._computeRemaining(clock);
            return (typeof remaining === 'number' && isFinite(remaining))
                ? Math.round(remaining * 10) / 10
                : null;
        } catch (_e) {
            return null;   // observability must never break the terminal writer
        }
    },

    // --- Derived views. Read-only projections of the ONE record, so the drain
    // --- and the unlock can never hold different beliefs about one fact.

    /** ch0 reached a terminal outcome (delivered or explicitly voided). */
    _ch0Terminal() {
        const cc = this._state && this._state.cycleCompletion;
        return !!(cc && (cc.ch0.rendered || cc.ch0.voided));
    },

    /** ch0 was actually PLAYED (the non-GAMEPLAY sense; a void is not a render). */
    _ch0Rendered() {
        const cc = this._state && this._state.cycleCompletion;
        return !!(cc && cc.ch0.rendered);
    },

    /**
     * ch1 is discharged: every command the cycle took has its ch1 rendered or
     * voided (`channels.md` §5: one ch1 owed per command), or the server has
     * stated the beat over with ch1 absent (`cc.ch1.voided`). A cycle that took
     * no command is not discharged; whether it is OWED one is `_ch1Owed`'s.
     * Null-safe with a NON-BLOCKING default — a missing record must never wedge
     * the drain.
     */
    _ch1Discharged() {
        const cc = this._state && this._state.cycleCompletion;
        if (!cc) return true;
        if (cc.ch1.voided) return true;
        const owed = Object.values(cc.ch1.commands);
        return owed.length > 0 && owed.every(c => c.rendered || c.voided);
    },

    /** The ch1 FIFO: one frame per command, in arrival order. */
    _ch1Queue() {
        const ch = this._state && this._state.channels;
        return (ch && Array.isArray(ch.ch1)) ? ch.ch1 : [];
    },

    /**
     * [2026-07-29-001-QASTI] A delivered frame is being overwritten in its
     * single slot. Report it on the delivery rail and count it on the record,
     * so a lost scene is greppable next to the `received|routed` line that
     * promised it and shows up in the watchdog dump and the VP snapshot.
     *
     * No-throw: this is observability on the drain's hot path, and a diagnosis
     * that breaks the thing it diagnoses is worse than no diagnosis.
     */
    _noteSlotDisplacement(slot, previous, incoming) {
        try {
            if (!previous) return;
            const sid = (typeof GameServerConnection !== 'undefined'
                && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
            const cc = this._state && this._state.cycleCompletion;
            if (cc && cc.attribution) {
                cc.attribution.displaced = (cc.attribution.displaced || 0) + 1;
            }
            const chNum = slot === 'ch1' ? 1 : 0;
            console.error(
                `[${sid}] TurnPump: ${slot} DISPLACED — a delivered frame was ` +
                `overwritten before it rendered. lost=${previous.requestId || 'none'} ` +
                `lost_cycle=${previous.cycleId || 'unstamped'} ` +
                `incoming=${incoming && incoming.requestId ? incoming.requestId : 'none'} ` +
                `incoming_cycle=${(incoming && incoming.cycleId) || 'unstamped'} ` +
                `status=${this._state.status} initiator=${this._state.initiator} ` +
                `beat=${this._beatId() || 'unbound'}`
            );
            console.log(`[Delivery] cycle=${previous.cycleId || 'none'} ch=${chNum} phase=displaced`);
        } catch (err) {
            try {
                console.warn('[TurnPump] displacement report failed', err);
            } catch (_e) { /* observability only; never throw into the drain */ }
        }
    },

    /**
     * THE ONE QUESTION BOTH ch1 GATES ASK: is a ch1 still COMING to the cycle
     * in flight? A ch1 answers a command the player TYPED (`channels.md` §5),
     * so only a `user` cycle is owed one; the server answers every such command
     * with its readout or a void. Server-initiated, timer-idle and passive
     * cycles owe none. A ch1 already in the FIFO is owed until it drains.
     *
     * Null-safe with a NON-BLOCKING default, same as `_ch1Discharged`.
     */
    _ch1Owed() {
        if (this._ch1Discharged()) return false;
        const s = this._state;
        if (!s) return false;
        if (this._ch1Queue().length) return true;
        return s.initiator === 'user';
    },

    /**
     * Did the BEAT'S AUTHOR state that this beat produced nothing?
     *
     * `space.md` D20: the space decides and everyone downstream is TOLD. This
     * reads that verdict and never re-derives it -- in particular it does NOT
     * read `phase_had_content`, which counts INTERVAL rows and is false for
     * every walk, look and arrival ([2026-08-30-003-ENGINE] was filed proposing
     * exactly that predicate; `services/shared/beat_content.py` is why it was
     * not built that way).
     *
     * Three-valued by contract: only an explicit `false` is DEAD. `null` means
     * the beat never stated a verdict and must not be treated as one.
     */
    _beatDeclaredDead() {
        const cc = this._state && this._state.cycleCompletion;
        if (!cc || !cc.beatVerdict) return false;
        return cc.beatVerdict.ch0Owed === false;
    },

    /**
     * Reset the beat EPOCH. A fresh join, a disconnect, or a SAME-ROOM server
     * turn-numbering regression all mean no beat of this epoch has finished, so
     * nothing can be classified prior yet. One cursor, one rule, seeded in one
     * place — this is the pump's whole notion of "before my time", and it
     * subsumes the join-turn stale filter that used to answer a turn-scoped
     * version of the same question.
     *
     * DESTRUCTIVE: it clears the LIVE completion record, so it may only be
     * called where the beat in flight is genuinely dead. A room change is NOT
     * such a place (the walk beat is still closing when the destination's clock
     * lands) and neither is any per-turn frame — both destroyed the live record
     * mid-cycle and stalled every gameplay beat to the watchdog
     * [2026-07-28-004-QASTI]. Cross-room bounds are inert by construction
     * (`_beatCompare` -> null across spaces), which is what makes the room-change reset
     * unnecessary rather than merely unwise.
     */
    _resetBeatEpoch() {
        this._state.beatCursor = null;
        this._resetCycleCompletion();
        // An epoch reset invalidates BOTH of the classifier's bounds, not just
        // the cursor. `currentCycleId` can still hold a key from the dead epoch
        // (the previous room, or the pre-regression high water), and the record
        // constructor seeds the floor from it — which would classify every frame
        // of the NEW epoch as below-floor, the exact failure the reset cures.
        // The next cycle start re-establishes the floor from a live clock.
        this._state.cycleCompletion.beatFloor = null;
    },

    /**
     * A frame keyed STRICTLY BELOW the cursor, same space: the space's beat
     * counter went BACKWARDS. The ordinal is a durable per-space Redis INCR, so
     * this is a real fault (the counter was reset -- a world reset or a store
     * flush), never routine. Without a reset every live frame would classify
     * prior and every beat would degrade to the watchdog, so the epoch resets,
     * LOUDLY.
     *
     * Strictly below: a frame AT the cursor is a straggler from the beat that
     * just finished. Checked on the clock (which opens a beat) and on the two
     * lifecycle discard sites, which can race it. Returns true when it reset.
     */
    _noteEpochRegression(message, source) {
        const s = this._state;
        const cursor = s.beatCursor;
        if (!cursor) return false;
        const key = this._frameKeyOf(message);
        const vs = this._beatCompare(key, cursor);
        if (vs === null || vs >= 0) return false;
        const carried = (s.cycleCompletion && s.cycleCompletion.attribution
            && s.cycleCompletion.attribution.epochResets) || 0;
        console.error(
            `[TurnPump] BEAT COUNTER WENT BACKWARDS via ${source}: key=${key} is ` +
            `strictly below cursor=${cursor} in the same space. The space's ` +
            `ordinal was reset; resetting the beat epoch.`
        );
        this._resetBeatEpoch();
        s.cycleCompletion.attribution.epochResets = carried + 1;
        return true;
    },

    /**
     * The cursor rule, exposed for the two wire-dispatch sites that must drop a
     * frame from a beat that is already over (`turn_complete`, `action_result`).
     * Same comparison the ingress classifier uses — one rule, not a second
     * vocabulary.
     *
     * Conservative by construction: a frame the server did NOT stamp is never
     * discarded, and nothing is prior until a beat of this epoch has actually
     * finished. That preserves the BUG-16 fix (the join turn's own
     * turn_complete must reach the pump, or the cycle deadlocks in `bridge`
     * with all input dropped) without the retired join-turn arithmetic, which
     * could not answer the per-beat question at all.
     */
    _isPriorBeatFrame(message) {
        const cursor = this._state && this._state.beatCursor;
        if (!cursor) return false;
        const key = this._frameKeyOf(message);
        if (!key) return false;
        // `null` (another room, another key shape, unparseable) => NOT prior.
        // This gate DISCARDS a frame outright, so an unknown must never reach
        // it as `true`: that is how a live `turn_complete` gets eaten and the
        // cycle deadlocks in `bridge` with all input dropped.
        const vs = this._beatCompare(key, cursor);
        return vs !== null && vs <= 0;
    },

    // --- Interval-stream ordering helpers ---

    /**
     * Derive the (turn, phase, interval) ordering key from an interval_event
     * or resolution_event payload. Both wire shapes carry turn_number /
     * phase_number / interval_number; the interval-level audit_key
     * `{space}:n{ordinal}.iII` is the fallback. Returns null when no
     * interval identity is derivable (legacy payload) — the caller then
     * falls back to lifecycle-first channel ordering.
     */
    _intervalStreamKey(payload) {
        if (!payload || typeof payload !== 'object') return null;
        let turn = Number(payload.turn_number);
        let phase = Number(payload.phase_number);
        let interval = Number(payload.interval_number);
        if (!Number.isFinite(interval) && payload.audit_key) {
            // A null here degrades to lifecycle-first ordering, which silently
            // reorders the ch3/ch2 rows the player reads -- so it is loud.
            const p = this._parseBeatKey(payload.audit_key);
            if (p && p.axes[1] > 0) {
                interval = p.axes[1];
                // axes[0] (the beat ordinal) is the BEAT-ordering axis.
                if (!Number.isFinite(turn)) turn = p.axes[0];
                if (!Number.isFinite(phase)) phase = 0;
            } else if (!p) {
                this._warnBeatKeyOnce(
                    'UNPARSEABLE (interval stream)', payload.audit_key, '-',
                );
            }
        }
        if (!Number.isFinite(interval)) return null;
        return {
            turn: Number.isFinite(turn) ? turn : 0,
            phase: Number.isFinite(phase) ? phase : 0,
            interval: interval,
        };
    },

    /**
     * Pick which channel FIFO head drains next in the unified interval
     * stream. Ordering: ascending (turn, interval) — intervals are absolute
     * 1-12 within a turn, so (turn, interval) is a total order; on a tie the
     * ch3 lifecycle line precedes its own interval's ch2 dice row (the
     * `<active>` line must exist before the roll that anchors to it).
     * Payloads with no derivable key sort lifecycle-first (legacy parity
     * with the old ch3-then-ch2 drain).
     *
     * Returns 'ch3' | 'ch2' | null (both FIFOs empty).
     */
    _nextIntervalStreamSource() {
        const s = this._state;
        const h3 = s.channels.ch3_lifecycle.length > 0 ? s.channels.ch3_lifecycle[0] : null;
        const h2 = s.channels.ch2_mechanics.length > 0 ? s.channels.ch2_mechanics[0] : null;
        if (!h3 && !h2) return null;
        if (!h2) return 'ch3';
        if (!h3) return 'ch2';
        const k3 = this._intervalStreamKey(h3);
        const k2 = this._intervalStreamKey(h2);
        if (!k3 || !k2) return 'ch3';   // legacy fallback: lifecycle first
        if (k3.turn !== k2.turn) return (k3.turn < k2.turn) ? 'ch3' : 'ch2';
        if (k3.interval !== k2.interval) return (k3.interval < k2.interval) ? 'ch3' : 'ch2';
        return 'ch3';                   // same interval: lifecycle before dice
    },

    /**
     * Construct a fresh, UNBOUND completion record. Called at cycle start
     * (user / server / idle action), at cycle exit, and on epoch reset.
     *
     * Field contract:
     *   beatId          the beat's identity, bound off the first CURRENT
     *                   server-stamped content frame. Null = unbound; identity
     *                   is never minted at input, because the phase that will
     *                   resolve the command has no clock yet.
     *   beatFloor       the phase that was live when this cycle started. A beat
     *                   resolves in the phase the player acted in or a LATER
     *                   one, never an earlier one — so a frame stamped below
     *                   the floor cannot be this beat's, and must not bind it.
     *                   This is what keeps a CARRYOVER skill (a turn-N phased
     *                   skill still resolving while turn N+1 opens) from
     *                   stealing the new beat's identity with its old phase key.
     *                   It is the classifier's lower bound, the beat cursor is
     *                   its other one; one rule, two bounds.
     *   activePhaseKey  the newest phase_clock's key. The CLOCK's coordinate,
     *                   NOT the completion's — it points at N+1 while beat N is
     *                   still closing. Only a fallback for barrier keying.
     *   serverDone      this turn's turn_complete (unstamped, temporal).
     *   ch0 / ch1       terminal outcomes. ch0's two flags are written ONLY by
     *                   _terminalizeCh0. ch1 is per COMMAND: `commands` maps
     *                   each command the cycle took (`turn_accepted`) to its
     *                   {rendered, voided}; `voided` is the server stating the
     *                   beat over with ch1 absent (`_endTheWait`).
     *   phases          per-phase barrier map {engaged, reached}. Frames carry
     *                   their own cycle_id, so an out-of-order release still
     *                   stamps the right phase.
     *   aborted         a participating channel's frame was force-released or
     *                   resend-missed; the cycle can never be honestly ready.
     *   attribution     counters for the classes that did NOT mutate this
     *                   record. Silent absorption is the bug class; these make
     *                   it visible in the watchdog dump and to the VP.
     */
    _resetCycleCompletion() {
        // Consumed exactly once, by the record it is seeding
        // ([2026-08-19-009-ENGINE]); a later reset must not inherit a loss that
        // did not happen to it.
        const retired = !!this._ch0RetiredPending;
        this._ch0RetiredPending = false;
        this._state.cycleCompletion = {
            beatId: null,
            beatFloor: (this._state && this._state.currentCycleId) || null,
            activePhaseKey: null,
            serverDone: false,
            ch0: { rendered: false, voided: false, renderReason: null },
            // The commands this cycle took, stated by `turn_accepted`.
            ch1: { commands: {}, voided: false },
            // [2026-08-31-005-ENGINE] The BEAT AUTHOR'S verdict, relayed
            // verbatim on `phase_settled` (ch9). `null` is a REAL third state
            // meaning "not stated" and is never coerced to false -- an
            // un-migrated pod sends no verdict at all, and defaulting it would
            // declare every beat dead fleet-wide during a rolling deploy. The
            // server already ships these fields (networking.py copies the whole
            // phase_settled payload onto the frame); nothing read them.
            beatVerdict: { ch0Owed: null, beatHadContent: null },
            phases: {},
            aborted: false,
            attribution: {
                prior: 0, future: 0, unstamped: 0, refusedTerminals: 0, epochResets: 0,
                // [2026-07-28-012-ENGINE] aborts refused as belonging to another
                // cycle. Counted so a coordinate-scoped REFUSAL is as visible as
                // the abort it prevented.
                abortsElsewhere: 0,
                // [2026-07-29-001-QASTI] delivered frames overwritten in a
                // single slot before they rendered. A nonzero value here means
                // the player was owed a scene they will never see.
                //
                // [2026-08-19-009-ENGINE] SEEDED, not always zero. A scene
                // discarded by the ARM that is creating this very record was
                // counted on the record being replaced, so it would vanish with
                // it. Carry it onto the successor, which is the one anybody
                // debugging the resulting beat will actually read.
                displaced: retired ? 1 : 0,
            },
        };
    },

    /**
     * [2026-07-28-012-ENGINE] May an abort keyed on `key` touch the live record?
     *
     * The same two bounds the frame classifier uses (`_classifyFrame`): below
     * the beat cursor is a beat that already finished, below the floor is a
     * phase older than the one the player acted in. Neither can be this beat,
     * so neither may abort it. Both bounds are SAME-ROOM only — ordering across
     * rooms is meaningless (`_beatCompare` -> null), and an incomparable key is
     * treated as attributable rather than silently discarded.
     */
    _cycleAbortAttributable(key) {
        const s = this._state;
        const cc = s && s.cycleCompletion;
        if (!cc || !key) return true;
        const vsCursor = this._beatCompare(key, s.beatCursor);
        const vsFloor = this._beatCompare(key, cc.beatFloor);
        const belowCursor = (vsCursor !== null && vsCursor <= 0);
        const belowFloor = (vsFloor !== null && vsFloor < 0);
        return !(belowCursor || belowFloor);
    },

    /**
     * The newest phase_clock's key — the CLOCK's coordinate. Used only as the
     * barrier's fallback when the beat is still unbound (a turn-beat or
     * welcome/spawn cycle that never carries a stamped content frame).
     */
    _cycleActivePhaseKey() {
        const cc = this._state.cycleCompletion;
        if (cc && cc.activePhaseKey) return cc.activePhaseKey;
        return (this._state && this._state.currentCycleId) || null;
    },

    /** Lazily-init the per-phase barrier record for a phase key. */
    _cycleBarrierFor(phaseKey) {
        const cc = this._state.cycleCompletion;
        if (!cc.phases[phaseKey]) {
            cc.phases[phaseKey] = { engaged: false, reached: false };
        }
        return cc.phases[phaseKey];
    },

    /**
     * The ch2 barrier for the BEAT IN FLIGHT. Reached when that beat's
     * phase_settled landed, or when its ch2 pipeline never engaged at all
     * (turn-beat / welcome / spawn / look cycles emit no interval or
     * resolution). An unbound beat falls back to the active phase key; a cycle
     * with no key at all has no barrier to cross.
     *
     * Keyed on `beatId`, never on the newest clock: the two-signal unlock
     * REQUIRES the next phase_clock, so the active key re-points to N+1 on
     * essentially every beat before this cycle completes. Reading it would ask
     * "has the NEXT beat's mechanics stream closed?" to decide whether THIS
     * beat's ch0 may render — incoherent under the beat-order contract
     * (ch0(N) waits on ch2(N), never ch2(N+1)). It passed for a long time only
     * because N+1 had usually not engaged yet; the D4 re-seat removed that
     * accident and it became a hard stall.
     *
     * Null-safe: the watchdog reads this for diagnosis, and a torn-down record
     * must yield an answer, not a TypeError.
     */
    _cycleBarrierReached() {
        const cc = this._state.cycleCompletion;
        if (!cc) return true;
        const key = cc.beatId || this._cycleActivePhaseKey();
        if (!key) return true;                 // non-phased — no barrier to cross
        const b = (cc.phases || {})[key];
        if (!b) return true;                   // no frame for this phase -> reached
        return b.reached || (b.engaged === false);
    },

    /**
     * GAMEPLAY completion predicate — the sole authority.
     *
     *   complete = ch0 terminal && serverDone && barrier reached && !aborted
     *
     * Read-only. A throw is not caught here: the caller logs it and leaves the
     * escape to the rolling watchdog, because a fallback predicate is exactly
     * how the pump acquired two authorities that could disagree.
     */
    _cycleNativeComplete() {
        const cc = this._state.cycleCompletion;
        if (!cc) return false;
        if (cc.aborted) return false;
        return !!(this._ch0Terminal() && cc.serverDone && this._cycleBarrierReached());
    },

    /**
     * Non-GAMEPLAY (WELCOME_NARRATION / SPAWNING) completion predicate — the
     * same record read in a different mode. These modes never receive a
     * turn_complete, so completion is ch0 RENDERED alone. A void must NOT
     * complete this arm: an empty-narration skip is a server decision not to
     * speak, not a scene the player watched.
     */
    _cycleNativeCompleteNonGameplay() {
        const cc = this._state.cycleCompletion;
        if (!cc) return false;
        if (cc.aborted) return false;
        return this._ch0Rendered();
    },

    /**
     * [Phase 6f] Play a single ch2 mechanics resolution with the proto
     * animation (D4 spec). Renders the row via Ch2Renderer, then fades it
     * in over 300ms with a 4px translateY enter and a 200ms bracket pulse
     * tinted by outcome (success → green, failure → red, neutral → none).
     * Fires `playback_complete({channel: 2})` on transition-end OR after a
     * 500ms safety timeout — whichever lands first.
     *
     * Failure mode: if Ch2Renderer is missing OR the render produces no
     * row, fire playback_complete synchronously to keep the drain reducer
     * moving. The drain MUST never deadlock on a bad payload.
     *
     * Driven from `_advance()` when a ch2_mechanics entry is shifted off
     * the FIFO. The pump's `ch2_playing` flag gates the next pop until
     * playback_complete arrives.
     */
    _playCh2Mechanics(payload) {
        const sid = GameServerConnection.state.sessionId || '?';
        const container = (typeof document !== 'undefined')
            ? document.getElementById('history') : null;
        if (!container || typeof window === 'undefined' || !window.Ch2Renderer
                || typeof window.Ch2Renderer.renderResolutionEvent !== 'function') {
            console.warn(`[${sid}] ch2 animation: Ch2Renderer unavailable, skipping animation`);
            // Defensive: fire playback_complete so the drain advances.
            this.event('playback_complete', { channel: 2 });
            return;
        }

        // Render the row via the existing renderer. The renderer appends a
        // `.ch2-resolution-slot` wrapper containing one `.ch2-row` and RETURNS
        // that row ([2026-07-29-010-QASTI]).
        let produced = null;
        try {
            produced = window.Ch2Renderer.renderResolutionEvent(payload, container);
        } catch (err) {
            console.error(`[${sid}] ch2 render failed during animation start:`, err);
            this.event('playback_complete', { channel: 2 });
            return;
        }

        // [2026-07-29-010-QASTI] BIND TO THE ROW THIS CALL CREATED, never to
        // "the last .ch2-row in history".
        //
        // THE WEDGE, from the prod rail (session f6339dc0, rel 452.58): the
        // drain logged `[Delivery] cycle=warrens-approach:005.01 ch=2
        // phase=drained` and then NOTHING — no playback_complete, no safety
        // line, nothing — and `ch2_playing` stayed true for 273 s while every
        // subsequent `_advance()` returned at the ch2 gate. The player lost the
        // input line for three consecutive 60 s submits.
        //
        // The mechanism: `renderResolutionEvent` inserts DOM-ANCHORED (after
        // its interval's `<active>` line), so the new row is frequently NOT
        // last. The old lookup then handed the animation a previously-completed
        // row carrying `dataset.ch2Done='1'`, and BOTH completion paths below
        // early-return on exactly that flag — so the one signal that clears
        // `ch2_playing` was swallowed by the guard meant to protect it.
        //
        // The legacy lookup survives ONLY as a fallback for a renderer that
        // returns nothing (older stubs / test doubles), and even then a row
        // that is already terminal is REFUSED rather than re-bound.
        let row = (produced && produced.nodeType === 1) ? produced : null;
        if (!row) {
            const rows = container.querySelectorAll('.ch2-row');
            const candidate = rows[rows.length - 1];
            if (candidate && !candidate.dataset.ch2Done) row = candidate;
        }
        if (!row) {
            console.warn(`[${sid}] ch2 animation: renderer produced no row for ` +
                `${(payload && payload.audit_key) || 'unstamped'} — completing immediately ` +
                `rather than holding the drain on an animation that does not exist`);
            this.event('playback_complete', { channel: 2 });
            return;
        }
        if (row.dataset.ch2Done) {
            // Unreachable by construction now; LOUD if it ever happens again,
            // because this exact silence cost 273 s of dead terminal on prod.
            console.error(`[${sid}] ch2 animation: resolved row is ALREADY terminal ` +
                `(${(payload && payload.audit_key) || 'unstamped'}) — completing immediately. ` +
                `Binding an animation to a finished row is what wedged the pump ` +
                `[2026-07-29-010-QASTI].`);
            this.event('playback_complete', { channel: 2 });
            return;
        }

        // Initial state — set inline so the CSS transition applies on
        // class add. Without these, the row is already at opacity:1 and
        // there's nothing to transition.
        row.style.opacity = '0';
        row.style.transform = 'translateY(4px)';

        // Outcome-driven pulse class. Reads `step_outcome` (string) per
        // PHASE1-WIRE-CONTRACT, with a fallback to roll.outcome.
        const outcome = String(
            (payload && payload.step_outcome) ||
            (payload && payload.roll && payload.roll.outcome) ||
            ''
        );
        let pulseClass = 'ch2-pulse-neutral';
        if (/hit|crit|success|favor/i.test(outcome)) pulseClass = 'ch2-pulse-success';
        else if (/miss|fumble|failure|dissonance/i.test(outcome)) pulseClass = 'ch2-pulse-failure';
        row.classList.add(pulseClass);

        // [Phase 6f bugfix 2026-05-05] CSS class rules cannot override inline
        // styles. The earlier implementation set opacity:0 + translateY(4px)
        // inline (initial state), then added .ch2-enter expecting the class
        // to set opacity:1 + translateY(0). But inline always wins — the row
        // stayed invisible forever. Fix: clear the inline styles on the same
        // frame we add .ch2-enter so the class rule takes over and the
        // browser sees a property change (0 -> 1) to transition through.
        const triggerEnter = () => {
            row.classList.add('ch2-enter');
            row.style.opacity = '';
            row.style.transform = '';
        };
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
                requestAnimationFrame(triggerEnter);
            });
        } else {
            triggerEnter();
        }

        // Safety timeout — if transitionend doesn't fire (no CSS, headless
        // jsdom, browser quirk), advance the drain after 500ms.
        const safety = setTimeout(() => {
            if (row.dataset.ch2Done) return;
            row.dataset.ch2Done = '1';
            row.removeEventListener('transitionend', onEnd);
            console.log(`[${sid}] ch2 animation: safety timeout fired (transitionend missed)`);
            this.event('playback_complete', { channel: 2 });
        }, 500);

        const self = this;
        function onEnd(ev) {
            // Only react to the row's OWN transition. Nested children
            // (dice spin animations, reveal stage transitions, etc.) bubble
            // their own transitionend events; we ignore them.
            if (ev.target !== row) return;
            if (row.dataset.ch2Done) return;
            row.dataset.ch2Done = '1';
            clearTimeout(safety);
            row.removeEventListener('transitionend', onEnd);
            self.event('playback_complete', { channel: 2 });
        }
        row.addEventListener('transitionend', onEnd);
    },

    _revealClock() {
        const s = this._state;
        if (!s.pendingClock) return;
        const c = s.pendingClock;
        s.pendingClock = null;
        // Dedup key matches the clock frame handler's, through the SAME
        // helper — two spellings of one key is how one of them drifts.
        s.lastRevealedPhase = this._clockDedupKey(c);

        /* P5 -- THE REVEAL IS WHERE THIS BEAT'S STATEMENT BECOMES CURRENT.
         *
         * The clock frame carried its WINDOW statement; adopting it HERE, from
         * the clock being revealed, keeps the prompt from answering for a beat
         * the player is not yet looking at.
         *
         * ⚠️ A RESOLVED statement for the SAME beat is newer and wins: it says
         * the collection this clock opened is already over. */
        const cur = s.promptState;
        if (cur && cur.stage === 'resolved' && cur.cycle_id && cur.cycle_id === c.cycle_id) {
            console.log(`[TurnPump] prompt_state.kept stage=resolved cycle=${cur.cycle_id}`
                + ' -- its collection closed before this clock was revealed');
        } else {
            this._adoptPromptState(c.prompt, 'reveal');
        }

        /* ⭐ THE TIMER BELONGS TO THE PROMPT, NOT TO THE BEAT.
         * [2026-08-19-024-ENGINE D4]; canon `time-interval-phase-gameplay.md`
         * §6 shows the timer only when the player is ASKED: *"Where there is no
         * question there is no window to count down, so no timer is drawn and
         * none is announced."*
         *
         * The row STAYS and renders NOTHING when the player is not asked. Its
         * CONTENTS vacate; `ClockLine.vacate()` owns the no-reflow half. An
         * empty row is not an absence of signal -- it IS the signal (project
         * owner, 2026-08-20; FB-HUD-05's "reads as a dead client" was refuted).
         *
         * ⚠️ No countdown to a FUTURE phase: the prompt is 1:1 with the beat
         * (§6, ruled 2026-09-16). */
        if (typeof ClockLine !== 'undefined' && this._isInputSuppressed()) {
            // Vacate rather than skip: skipping leaves the PREVIOUS beat's
            // countdown frozen on a permanent row, a stale number reading as
            // current.
            if (typeof ClockLine.vacate === 'function') ClockLine.vacate();
        } else if (typeof ClockLine !== 'undefined') {
            this._drawClockRow(c);
        }

        // The action line answers "is anything holding me" (§6).
        if (typeof ActionLine !== 'undefined') {
            ActionLine.update(c.inflight_skills || [], !this._isInputSuppressed());
        }
        // The slot body answers "which of my slots" from the same entries:
        // each carries `slots` and `full_body` from the beat's own reading.
        if (typeof SlotBody !== 'undefined') {
            SlotBody.update(c.inflight_skills || []);
        }
    },

    /**
     * Draw the clock row for a revealed beat.
     *
     * P5: THE COUNTDOWN IS THE STATEMENT'S WINDOW. The duration and start time
     * come off the adopted `promptState.window`, never off the clock, so the
     * timer cannot show a window the server did not give this player.
     *
     * ⚠️ Callers own the `_isInputSuppressed()` question. This draws.
     */
    _drawClockRow(c) {
        if (typeof ClockLine !== 'undefined') {
            // [ch2 T-4.3] Pass audit_key so the clock element carries data-audit
            // for devtools-driven replay + L8 tagging.
            const ps = this._state.promptState;
            const w = ps ? ps.window : null;
            const duration = w ? w.duration_seconds : 0;
            const beatMode = c.beat_mode || 'turn';

            /* A BEAT WITH NO CLOCK BUDGET IS NOT A ZERO-SECOND COUNTDOWN.
             *
             * The lobby beat is emitted with `duration_seconds: 0` on purpose —
             * "beat_mode='lobby', no countdown, no phase schedule"
             * (`turn_engine.py` emit_lobby_terminal_entry / _hedge). Handing 0 to
             * `ClockLine.start()` renders `[0s...]`, and one tick later `_tick()`
             * sees `_remaining <= 0`, freezes, and `_injectIdle()` POSTS AN IDLE
             * ACTION — from the void, for a beat that never had a window. That
             * already fires today on the lobby HEDGE path, which passes
             * `phase_actors=[actor]` so the player reads `acting: true`.
             *
             * `[waiting...]` is the honest render: there is no countdown to show,
             * and the row says so rather than showing a number that is about to
             * spend a turn on the player's behalf. */
            const hasBudget = typeof duration === 'number' && duration > 0;
            if (!hasBudget || beatMode === 'lobby') {
                ClockLine.showWaiting();
            } else {
                // [Wave2 T2.1/T2.3] Pass server-emitted phase_display verbatim so
                // render matches wire boundary exactly (0-indexed, zero-padded).
                ClockLine.start(
                    duration,
                    c.phase_number,
                    beatMode,
                    c.total_phases,
                    w.start_time || c.start_time,
                    c.turn_number,
                    c.audit_key || null,
                    c.phase_display !== undefined ? c.phase_display : null,
                );
            }
        }
    },

    // --- Getter API (Read Side) ---

    getStatus() { return this._state.status; },
    isIdle() { return this._state.status === 'idle'; },
    isPending() { return this._state.status !== 'idle'; },
    isPromptLocked() { return this._state.status !== 'idle'; },

    getNarrationMode() {
        if (this._state.initiator === 'idle') return 'passive';
        if (this._state.status === 'idle') return 'passive';
        return 'active';
    },

    getClockState() {
        const c = this._state.pendingClock;
        if (!c) return null;
        return {
            remaining: this._computeRemaining(c),
            phase: c.phase_number,
            totalPhases: c.total_phases,
            mode: c.beat_mode,
            beat_mode: c.beat_mode,
            is_boundary: c.is_boundary === true,
            audit_key: c.audit_key || null,
        };
    },

    /** Seconds left on the buffered clock's WINDOW -- 0 when the player is not asked. */
    _computeRemaining(clock) {
        const w = clock.prompt ? clock.prompt.window : null;
        if (!w) return 0;
        const elapsed = (Date.now() - new Date(w.start_time).getTime()) / 1000;
        return Math.max(0, Math.round(w.duration_seconds - elapsed));
    },

    _clearTimer(name) {
        if (this._state[name]) {
            clearTimeout(this._state[name]);
            this._state[name] = null;
        }
    },

    // --- THE ONE ROLLING BACKSTOP -------------------------------------------
    // Armed for every initiated (locked) cycle and re-armed by every frame
    // attributed to the beat in flight. It fires only after its derived window
    // with zero such frames while the prompt is still locked, logs the whole
    // record LOUDLY, and force-exits via _finishCycle(). It NEVER feeds the
    // completion record: the two-signal unlock remains the sole authority.
    //
    // It is structural, not narration-conditional — that is what makes it
    // sufficient on its own. The retired action_timeout was narration-
    // conditional and had a live blind spot: it was cleared on turn_accepted,
    // narration then flowed so it never re-armed, and a server turn-epoch reset
    // mid-cycle left the player locked for 200s+ with no breaker at all.

    // [2026-08-07-003-QASTI] THE LAST PIECE OF THE ORIGINAL DEFECT. Retiring the
    // drop-adjudication fixed WHAT this timer judges; it did not fix WHEN it
    // fires. A flat 90s still sat BELOW the room's own ~120s beat interval, and
    // 4 of 7 measured prod gaps exceeded it -- so it force-exited beats that
    // were merely pending.
    //
    // The window now derives from the heartbeat the server announces on every
    // clock frame, which under the ONE TIMER ruling ([2026-08-03-028-ENGINE])
    // IS the phase timer. Turn-manager owns the seconds; the client reads them.
    //
    // ⚠️ THE STRICT PROGRESS GATE STAYS. `_controlFrameIsProgress` is NOT part
    // of the retired drop question -- it is the WEDGE ESCAPE. A pump bound to
    // beat N while the server has advanced to N+3 has a perfectly LIVE server
    // sending frames constantly; counting those as progress is what let a wedge
    // survive 273 seconds of dead terminal, recoverable only by the server
    // falling silent ([2026-07-29-010-QASTI]). Widening the window is the fix
    // for false-firing; loosening the gate would re-open that.
    STALL_WATCHDOG_FLOOR_MS: 90000,
    // Whole heartbeats of no-progress to tolerate. MUST be > 1: a window equal
    // to the cadence races the very beat it is waiting for.
    STALL_WATCHDOG_HEARTBEATS: 2,
    // A backstop that can never fire is not a backstop.
    STALL_WATCHDOG_CEILING_MS: 600000,

    /** The backstop window, in ms, derived per-arm from the room's heartbeat. */
    _stallWatchdogWindowMs() {
        const cadence = this._state && this._state.beatCadenceMs;
        if (!Number.isFinite(cadence) || cadence <= 0) return this.STALL_WATCHDOG_FLOOR_MS;
        return Math.min(
            this.STALL_WATCHDOG_CEILING_MS,
            Math.max(this.STALL_WATCHDOG_FLOOR_MS,
                     cadence * this.STALL_WATCHDOG_HEARTBEATS),
        );
    },

    // [2026-07-29-010-QASTI] Ceiling on ONE ch2 row animation. The animation
    // itself is ~300ms enter + 200ms pulse and carries its own 500ms in-row
    // safety; this is an order of magnitude above both, so it can only ever fire
    // when the animation's own completion never arrives at all.
    CH2_PLAYBACK_DEADLINE_MS: 5000,

    /**
     * Arm the deadline on the `ch2_playing` drain gate.
     *
     * The rule this enforces is the one the wedge broke: A GATE THAT CAN BE
     * CLOSED MUST HAVE SOMETHING ARMED THAT CAN OPEN IT. `ch2_playing` was the
     * one drain gate in the pump with no owner and no deadline — closed by the
     * drain, opened only by a DOM event — so when that event was swallowed the
     * pump reached a state in which nothing at all could fire.
     *
     * On expiry this fires the SAME `playback_complete({channel:2})` the
     * animation would have fired. It invents no completion: ch2 has no terminal
     * in the completion record, so releasing the gate cannot unlock anything the
     * two-signal authority did not already decide.
     */
    _armCh2Deadline(payload) {
        this._clearTimer('ch2PlaybackTimer');
        const key = (payload && (payload.audit_key || payload.cycle_id)) || 'unstamped';
        this._state.ch2PlaybackTimer = setTimeout(() => {
            this._state.ch2PlaybackTimer = null;
            if (!this._state.channels || !this._state.channels.ch2_playing) return;
            const sid = (typeof GameServerConnection !== 'undefined'
                && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
            console.error(
                `[${sid}] TurnPump: ch2 playback DEADLINE — the row animation for ${key} ` +
                `never signalled completion within ${this.CH2_PLAYBACK_DEADLINE_MS}ms and the ` +
                `drain gate is still closed. Releasing the gate. A ch2 gate held with no ` +
                `animation running is what froze the pump in status=bridge on prod ` +
                `[2026-07-29-010-QASTI].`
            );
            this._state.channels.ch2_playing = false;
            this.event('playback_complete', { channel: 2, synthetic: 'ch2_deadline' });
        }, this.CH2_PLAYBACK_DEADLINE_MS);
    },

    // Frames that count as "progress toward unlock" — receipt of any of these
    // re-arms the watchdog to a fresh full window, UNLESS ingress attributed it
    // to another beat (a prior/future frame is not progress on THIS one).
    _STALL_PROGRESS_EVENTS: [
        'turn_accepted', 'narration_received', 'playback_complete',
        'server_done', 'frame_received', 'interval_event', 'resolution_event',
        'phase_settled', 'ch1_missing', 'ch0_missing',
    ],

    /**
     * [2026-07-29-010-QASTI] A control frame that ANNOUNCES A NEWER BEAT than
     * the one the pump is still finishing is the server moving on, not progress
     * on this beat, so it must not extend the stall backstop -- otherwise a
     * server that keeps ticking keeps a wedged pump alive forever.
     *
     * Deliberately narrow:
     *   - an UNBOUND cycle (no beat yet) counts every control frame — a submit
     *     legitimately waits for its first stamped frame;
     *   - a different room is never comparable (`_beatCompare` -> null);
     *   - same-or-older keys still count, so a healthy cycle is untouched.
     * It changes NOTHING about attribution, the completion record, or the
     * unlock — only how long the backstop is willing to wait.
     */
    _controlFrameIsProgress(name, data) {
        // A resolved statement closes a beat; only a CLOCK frame is progress.
        if (name === 'frame_received' && data.kind !== 'clock') return false;
        const s = this._state;
        const cc = s.cycleCompletion;
        const beat = (cc && cc.beatId) || null;
        if (!beat) return true;
        // Content frames are already judged by `_attributeFrame`; this rule is
        // only for the unattributed control frames.
        if (this._ATTRIBUTED_EVENTS.indexOf(name) !== -1) return true;
        const key = (name === 'frame_received')
            ? this._phaseKeyOf(data.coordinate)
            : s.currentCycleId;
        if (!key) return true;
        // `null` (another room / another shape / unparseable) => still counts
        // as progress. This only decides how long the stall backstop waits;
        // erring toward "keep waiting" can never dead-air a beat.
        // A newer beat's control frame is EXPECTED while this beat's ch0 still
        // types (phase mode), so it is not warned -- it simply does not extend
        // the backstop.
        const vs = this._beatCompare(key, beat);
        return vs === null || vs <= 0;
    },

    // [2026-08-19-009-ENGINE] THE OVERDUE NOTICE fires at ONE heartbeat -- half
    // the backstop's own window, so it is DERIVED from the room's cadence and
    // not a second magic constant. One heartbeat of no progress means the beat
    // that should have advanced has not; that is the moment the WAITING state
    // stops being an honest "the world is thinking" and starts being a lie.
    // Render-only: it touches no completion term, unlocks nothing, and gates
    // nothing. See `_renderStallNotice`.
    STALL_NOTICE_FRACTION: 0.5,

    /** Arm (or re-arm) the stall watchdog with a fresh full window. */
    _armStallWatchdog() {
        this._clearTimer('stallWatchdogTimer');
        this._clearTimer('stallNoticeTimer');
        // A FRESH WINDOW MEANS PROGRESS HAPPENED, so the beat is no longer
        // overdue. Clearing here — and not only at cycle exit — is what makes
        // the label self-correcting: a slow beat that recovers stops claiming
        // it is late the moment a frame lands.
        this._state.cycleOverdue = false;
        const windowMs = this._stallWatchdogWindowMs();
        this._state.stallWatchdogWindowMs = windowMs;
        this._state.stallWatchdogTimer = setTimeout(() => {
            this._state.stallWatchdogTimer = null;
            this._stallWatchdogFired();
        }, windowMs);
        // [2026-08-19-009-ENGINE] DoD 2 -- "a client that cannot unlock must say
        // so". Armed and re-armed by the SAME call that arms the backstop, so it
        // can never outlive the cycle it describes: every site that re-arms one
        // re-arms the other, and `_finishCycle` / `reset` clear both together.
        this._state.stallNoticeTimer = setTimeout(() => {
            this._state.stallNoticeTimer = null;
            this._markCycleOverdue();
        }, Math.max(1, Math.round(windowMs * this.STALL_NOTICE_FRACTION)));
    },

    /**
     * Rolling reset: re-arm ONLY if already armed (i.e. a cycle is in flight).
     * Called on every cycle-relevant frame; a no-op otherwise.
     */
    _resetStallWatchdog() {
        if (!this._state.stallWatchdogTimer) return;
        this._armStallWatchdog();
    },

    /**
     * [2026-08-19-009-ENGINE] Swap the WAITING state to its OVERDUE form.
     *
     * `resolving gameplay...` was ONE string doing THREE jobs -- an ordinary
     * resolving beat, a player suppressed by `acting:false`, and a pump that
     * has stopped. The player had no way to tell a locked prompt from a slow
     * one, which is this ticket's LOUD-FAILURE half.
     *
     * Only speaks for a cycle that is actually in flight. On an idle pump the
     * prompt is either open or legitimately suppressed, and neither is overdue.
     *
     * ⚠️ RENDER-ONLY, and it must stay that way. It flips no latch, consumes no
     * slot and completes nothing -- the backstop below is what actually
     * recovers, and this only stops the wait from lying while that runs.
     */
    _markCycleOverdue() {
        const s = this._state;
        if (s.status === 'idle') return;
        s.cycleOverdue = true;
        const sid = (typeof GameServerConnection !== 'undefined'
            && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
        console.warn(
            `[${sid}] TurnPump: beat OVERDUE after ` +
            `${Math.round((s.stallWatchdogWindowMs || 0) * this.STALL_NOTICE_FRACTION) / 1000}s ` +
            `(status=${s.status} initiator=${s.initiator} ` +
            `beat=${this._beatId() || 'unbound'}). The backstop owns the recovery.`
        );
        this._renderStallNotice();
    },

    /**
     * Paint the OVERDUE label IF the cycle is overdue AND the block is locked.
     *
     * ⚠️ THE FACT AND THE RENDER ARE SEPARATE, and conflating them shipped a lie
     * in the first cut of this fix. "This cycle is overdue" is decided ONCE, by
     * the timer. "Is the label on screen" has to be re-asked every time the
     * block's lock state changes — and on a PASSIVE cycle the prompt is still
     * OPEN when the timer fires, so a one-shot render skipped, and a player who
     * typed a moment later got the ordinary `resolving gameplay` against a beat
     * that had already stopped. A state that is wrong for one heartbeat is still
     * a state that is wrong, and this whole ticket is about a WAITING render
     * that could not be trusted.
     *
     * Idempotent, and safe to call from anywhere the block has just locked.
     *
     * ⚠️ Never PROMOTES an open prompt into a locked one. This is a RELABEL of a
     * block that is already locked, never a new lock — a live prompt on a
     * passive cycle stays live, and taking it away would turn a render into a
     * lock, which is the defect wearing the fix's clothes.
     */
    _renderStallNotice() {
        const s = this._state;
        if (!s.cycleOverdue) return;
        if (s.status === 'idle') return;
        if (typeof window === 'undefined') return;
        if (typeof window.renderCommandBlockLock !== 'function') return;
        const CL = window.CommandLine;
        if (CL && typeof CL.isOpen === 'function' && CL.isOpen()) return;
        window.renderCommandBlockLock(true, 'overdue');
    },

    /**
     * Fired after `_stallWatchdogWindowMs()` with no cycle-relevant frame. Acts
     * whenever a CYCLE IS IN FLIGHT — a cycle that exited normally cleared the
     * timer in `_finishCycle`, so reaching here at all means one did not.
     *
     * ======================================================================
     * [2026-08-19-009-ENGINE] THE PREDICATE WAS KEYED ON THE WRONG AXIS.
     * ======================================================================
     * It used to read:
     *
     *     const lockedCycle = (s.initiator === 'user' || s.initiator === 'server')
     *         && s.status !== 'idle';
     *     if (!lockedCycle) return;
     *
     * and it rested on the premise stated in its own docstring — *"a
     * passive/idle cycle never locks the prompt"*. **THAT PREMISE IS FALSE, and
     * a wedged prod session is the proof.**
     *
     * A passive cycle locks the prompt the moment the player TYPES:
     * `main.js::processInput` closes the command line and disables the field
     * BEFORE dispatch, and `GameServerConnection.processInput` then answers
     * `TurnPump.isPending()` with `return true` — handled, nothing sent, and
     * `main.js` therefore does not re-open. So the prompt is shut by a cycle
     * whose `initiator` is `null` or `'idle'`, and the ONE thing that could have
     * re-opened it refused to look.
     *
     * ⚠️ AND EVERY OTHER RECOVERY SURFACE IS GATED ON `status === 'idle'` —
     * `_shouldShowPrompt` (:status check), the clock-frame prompt-gate
     * re-ask, and `handleRoomState`'s `server_action` re-arm. `status` is the
     * bit that is stuck, so a stuck cycle disables its own rescuers. This
     * backstop is the ONLY thing outside that gate, which is exactly why its
     * predicate may not carry a second, narrower condition.
     *
     * `initiator` answers *"who started this?"*. The question a BACKSTOP asks is
     * *"is a cycle in flight that nothing but me can exit?"*, and that fact's
     * own axis is `status`. Keying it on `initiator` was a COORDINATE LAW
     * violation: the guard read an axis the fact does not vary on.
     *
     * The intent the `initiator` test was standing in for — **do not fire on a
     * genuinely idle pump** — is PRESERVED, and is now stated directly:
     * `status === 'idle'` IS "there is no cycle to rescue". It is the same
     * intent on the right axis, not a dropped guard.
     *
     * Multi-occupancy is why this reached production: a SECOND actor in the room
     * is the only thing that produces a narration cycle to a player who did not
     * submit, and that is the only way to reach `initiator === null | 'idle'`.
     * At n=1 every cycle is user-initiated and was covered. No occupant-count
     * logic exists anywhere in this client, and none is wanted — the coupling
     * is causal, not conditional.
     */
    _stallWatchdogFired() {
        const s = this._state;
        const sid = (typeof GameServerConnection !== 'undefined'
            && GameServerConnection.state && GameServerConnection.state.sessionId) || '?';
        // A CYCLE IS IN FLIGHT AND ONLY THIS CAN EXIT IT. One term, on the axis
        // the fact actually varies on. See the block above before narrowing it.
        const stuckCycle = s.status !== 'idle';
        // [2026-08-07-001-ENGINE] THE SECOND FIRING MODE IS RETIRED.
        //
        // It existed because a delivered-but-never-rendered frame had no other
        // detector ([2026-07-29-001-QASTI]). It now has one, and a
        // DETERMINISTIC one: `_adjudicateOwedChannels` renders that verdict at
        // BEAT CLOSE, from the beat advance turn-manager already mints, with no
        // wall clock involved. Two authorities answering one question is how the
        // question gets answered differently.
        //
        // ⚠️ Retired only AFTER the verdict was extended to read the SLOT and
        // not merely the record — `_finishCycle` never cleared `channels.ch0`/
        // `.ch1`, so a warehoused frame outlived its beat and the record-only
        // check could not see it. Removing this mode before closing that gap
        // would have dropped the coverage on the floor.
        //
        // What survives below is a LIVENESS backstop and nothing more: if the
        // server has gone silent entirely, no beat ever advances, so no verdict
        // can ever fire and the player would sit locked forever. That is the one
        // question a timer is still the right instrument for.
        if (!stuckCycle) {
            // DECLINED, and the timer already nulled itself before calling us.
            // That is safe HERE and only here: an idle pump has no cycle to
            // rescue, and the arm sites (`user_action`, `server_action`, and
            // `narration_received`'s owed-content arm) all re-arm on the next
            // frame. Leaving it disarmed on ANY other decline would spend the
            // backstop silently -- which is half of what this ticket was.
            this._clearTimer('stallNoticeTimer');
            return;
        }
        // (thread 3, 2026-10-29) ASK FRAME FIRST. A cycle in flight with the
        // beat's frames IN HAND is not a silent server: the drain is what is
        // stuck, behind a hold or a gate this close can release. `_endTheWait`
        // drops only what never arrived, releases the hold, and lets what is in
        // hand play in order; the cycle then closes itself. The backstop re-arms
        // for one more window so a server that truly went quiet after that is
        // still caught. Nothing in hand -> the unconditional exit below, as ever.
        if (this._endTheWait('backstop')) {
            console.log(
                `[${sid}] TurnPump: LIVENESS BACKSTOP deferred -- the beat's frames ` +
                `were in hand and are playing; re-armed for one more window`
            );
            this._armStallWatchdog();
            return;
        }
        // PRINT THE TERM THAT ACTUALLY FAILED. A stall report that lists only
        // satisfied gates is a silent zero wearing a status report — the prod
        // stall printed four green latches while the one unsatisfied term was
        // invisible. So the whole record goes on the line: the beat it bound to,
        // the cursor it was measured against, every completion term, the
        // per-phase barrier, and how many frames were classified into some OTHER
        // beat (a nonzero `prior` here means the scene the player is owed was
        // answered by a frame from a beat that is already over).
        //
        // GUARD ON THE GUARD: this runs BEFORE `_finishCycle()`, so anything it
        // throws converts the pump's last-resort unstick into a permanent hang —
        // strictly worse than the stall it reports. A missing diagnosis is
        // survivable; a missing `_finishCycle` is not.
        const cc = s.cycleCompletion;
        let dump = 'unavailable';
        try {
            const phaseDump = (cc && cc.phases)
                ? (Object.keys(cc.phases).map(k =>
                    `${k}{engaged:${cc.phases[k].engaged},reached:${cc.phases[k].reached}}`
                  ).join(' ') || 'none')
                : 'none';
            const at = (cc && cc.attribution) || {};
            dump =
                `beat_id=${(cc && cc.beatId) || 'unbound'} ` +
                `beat_cursor=${s.beatCursor || 'none'} ` +
                `beat_floor=${(cc && cc.beatFloor) || 'none'} ` +
                `barrier_reached=${this._cycleBarrierReached()} ` +
                `barrier_key=${this._beatId() || this._cycleActivePhaseKey() || 'none'} ` +
                `active_key=${this._cycleActivePhaseKey() || 'none'} ` +
                `ch0_rendered=${!!(cc && cc.ch0 && cc.ch0.rendered)} ` +
                `ch0_voided=${!!(cc && cc.ch0 && cc.ch0.voided)} ` +
                `ch0_terminal=${this._ch0Terminal()} ` +
                `ch1_discharged=${this._ch1Discharged()} ` +
                `ch1_owed=${this._ch1Owed()} ` +
                `server_done=${!!(cc && cc.serverDone)} ` +
                `aborted=${!!(cc && cc.aborted)} ` +
                `attributed_elsewhere={prior:${at.prior || 0},future:${at.future || 0},` +
                `unstamped:${at.unstamped || 0},refused_terminals:${at.refusedTerminals || 0},` +
                `epoch_resets:${at.epochResets || 0},` +
                `aborts_elsewhere:${at.abortsElsewhere || 0},` +
                `displaced:${at.displaced || 0}} ` +
                `phases=[${phaseDump}]`;
        } catch (err) {
            dump = `unavailable (${err && err.message ? err.message : 'unknown'})`;
        }
        console.error(
            `[${sid}] TurnPump: LIVENESS BACKSTOP fired — no beat has advanced ` +
            `for ${(s.stallWatchdogWindowMs || this.STALL_WATCHDOG_FLOOR_MS) / 1000}s ` +
            `(cadence=${Number.isFinite(s.beatCadenceMs) ? s.beatCadenceMs / 1000 + 's' : 'unheard'}) ` +
            `while a cycle is in flight ` +
            `(status=${s.status} initiator=${s.initiator} ` +
            `ch0_slot=${s.channels.ch0 ? 'slot' : 'none'} cycle=${s.currentCycleId || 'none'} ` +
            `| RECORD ${dump}). ` +
            `Forcing cycle exit — last-resort escape; completion authority unchanged.`
        );
        // [2026-08-07-001-ENGINE] The per-frame UNDELIVERED report is RETIRED
        // with the mode that owned it. Naming a stuck frame is now
        // `_adjudicateOwedChannels`'s job, and it does it better: at beat close,
        // per channel, with a verdict and a player-facing render rather than a
        // console line no player reads. Reporting it here as well would give one
        // lost frame two different names in one bundle.
        //
        // The escape below still re-enters the drain, so a frame in a slot gets
        // its last chance exactly as before — what changed is who ADJUDICATES it.
        // [2026-07-29-010-QASTI] DoD 3: a pump that stopped while the prompt was
        // LOCKED is a dead station from the chair — no spinner resolution, no
        // message, nothing. A console error is loud to us and silent to the
        // player, so the escape also says so ON SCREEN, in the existing
        // mechanical degradation vocabulary (render-honesty 3a).
        //
        // [2026-08-07-001-ENGINE] The `if (lockedCycle)` guard that used to wrap
        // this is GONE, not forgotten: with the warehoused mode retired, the
        // early return above means we only reach here on a cycle in flight.
        // Keeping a condition that can no longer be false reads as though the
        // other branch still exists.
        try {
            if (typeof GameServerConnection !== 'undefined'
                    && typeof GameServerConnection._renderSystemNotice === 'function') {
                // [FB-HUD-02] Runs BEFORE `_finishCycle`, so the beat is still
                // bound: a ch0 that DID render stays the last line, and one
                // that never rendered leaves no anchor and this appends.
                GameServerConnection._renderSystemNotice(
                    'The turn stopped responding. Returning your prompt.',
                    'degradation', { cycleId: this._beatId() });
            }
        } catch (_e) { /* the escape must run even if the notice cannot */ }
        this._syncEmit('pump_stall_watchdog');
        // Invoked directly rather than through event() so a stall in a
        // non-GAMEPLAY mode (a welcome/spawn server_action cycle) cannot be
        // dropped by the mode guard.
        if (typeof WaitingIndicator !== 'undefined') WaitingIndicator.stop();
        this._finishCycle();
        // [2026-07-29-001-QASTI] The escape resets the record, which clears the
        // debt that wedged the drain. Re-enter it once so a frame that is still
        // in its slot gets an honest last chance to REACH THE PLAYER rather
        // than being force-exited around. `_finishCycle` does not clear the
        // channels, so without this the frame stays warehoused past its own
        // watchdog.
        this._advance();
    },

    /** Update sync telemetry pump state and emit log line. */
    _syncEmit(source) {
        if (typeof window !== 'undefined') {
            window._syncPumpState = this._state.status;
        }
        if (typeof logSync === 'function') logSync(source);
    },

    /**
     * Build a synchronous read-only snapshot of pump state for the VP harness.
     * Every per-channel term is the DERIVED VIEW of the one completion record,
     * so the VP reads exactly what the drain decided on. Safe to call any time;
     * no-throw, defensive against partial state during init.
     */
    _buildVpSnapshot() {
        const s = this._state;
        const cc = s.cycleCompletion;
        const fsm = (typeof TerminalFSM !== 'undefined' && TerminalFSM.mode)
            ? TerminalFSM.mode : null;
        return {
            current_cycle_id: s.currentCycleId || null,
            beat_id: (cc && cc.beatId) || null,
            beat_cursor: s.beatCursor || null,
            fsm_mode: fsm,
            status: s.status,
            initiator: s.initiator,
            prompt_locked: s.status !== 'idle',
            channels: {
                ch3_lifecycle: {
                    queue_depth: (s.channels && Array.isArray(s.channels.ch3_lifecycle))
                        ? s.channels.ch3_lifecycle.length : 0,
                    last_drained_cycle: (s.channelLastDrained && s.channelLastDrained.ch3_lifecycle) || null,
                },
                ch2_mechanics: {
                    queue_depth: (s.channels && Array.isArray(s.channels.ch2_mechanics))
                        ? s.channels.ch2_mechanics.length : 0,
                    last_drained_cycle: (s.channelLastDrained && s.channelLastDrained.ch2_mechanics) || null,
                    phase_settled: this._cycleBarrierReached(),
                    playing: !!(s.channels && s.channels.ch2_playing),
                    // [2026-07-29-010-QASTI] `playing:true` with nothing armed
                    // to clear it IS the wedge. The prod snapshot showed
                    // `playing:true, queue_depth:0` three times over 123s and
                    // there was no field that said whether anything could ever
                    // change it. Now there is.
                    deadline_armed: !!s.ch2PlaybackTimer,
                },
                ch1: {
                    slot_present: this._ch1Queue().length > 0,
                    queue_depth: this._ch1Queue().length,
                    resolved: this._ch1Discharged(),
                    last_drained_cycle: (s.channelLastDrained && s.channelLastDrained.ch1) || null,
                },
                ch0: {
                    // [2026-08-21-001-ENGINE] 'playback' | 'ceiling' | null.
                    // A ceiling-driven unlock must be visible to the VP and to
                    // /survey as its own outcome, never as a clean render.
                    render_reason: (s.cycleCompletion && s.cycleCompletion.ch0
                        && s.cycleCompletion.ch0.renderReason) || null,
                    slot_present: !!(s.channels && s.channels.ch0),
                    resolved: this._ch0Terminal(),
                    last_drained_cycle: (s.channelLastDrained && s.channelLastDrained.ch0) || null,
                    played: this._ch0Rendered(),
                },
            },
            server_done: !!(cc && cc.serverDone),
            // Frames the ingress classifier attributed to some OTHER beat. A
            // nonzero `prior` on a stalled cycle is the whole diagnosis.
            attribution: (cc && cc.attribution)
                ? { prior: cc.attribution.prior, future: cc.attribution.future,
                    unstamped: cc.attribution.unstamped,
                    refused_terminals: cc.attribution.refusedTerminals,
                    epoch_resets: cc.attribution.epochResets,
                    aborts_elsewhere: cc.attribution.abortsElsewhere || 0 }
                : null,
            last_attribution: s.lastAttribution || null,
            has_played_any: !!s.hasPlayedAny,
            pending_clock: !!s.pendingClock,
            in_carryover_phase: TurnPump._inCarryoverPhase === true,
            last_event: s.lastEvent || null,
        };
    },

    /**
     * [combat-validation-observability 1b] Cycle-keyed rollup of the delivery
     * ledger. `window.__LEDGER_SHADOW` is keyed by channel+seq (per-channel
     * resolutions/decisions); this helper re-buckets those by `cycle_id`
     * ({room}:NNN.PP) so the storyboard survey can read ONE row per turn.phase
     * and auto-detect `sent-but-never-rendered`.
     *
     * Read-only, no-throw. cycle_id is a name-space composite, NOT a UUID.
     * Returns an array of rows (also published to window.__LEDGER_CYCLE):
     *   { cycle_id,
     *     ch0:{sent,seq,resolution}, ch1:{...}, ch2:{...}, ch3:{...},
     *     phase_clock: sent?,                       // ch9 control frame present
     *     phase_settled:{sent,phase_had_content},
     *     serverDone, cycle_complete }
     */
    _ledgerCycleLedger() {
        const rows = {};   // cycle_id -> row
        try {
            const shadow = (typeof window !== 'undefined' && window.__LEDGER_SHADOW)
                ? window.__LEDGER_SHADOW : null;
            if (!shadow || typeof shadow !== 'object') return [];

            // Map the numeric ledger channel to the row's named slot.
            const CH_SLOT = { 0: 'ch0', 1: 'ch1', 2: 'ch2', 3: 'ch3' };

            const rowFor = (cid) => {
                const key = cid || '_unkeyed';
                if (!rows[key]) {
                    rows[key] = {
                        cycle_id: (cid || null),
                        ch0: null, ch1: null, ch2: null, ch3: null,
                        phase_clock: false,
                        phase_settled: null,
                        serverDone: false,
                        cycle_complete: false,
                    };
                }
                return rows[key];
            };

            for (const chKey of Object.keys(shadow)) {
                const sh = shadow[chKey] || {};
                const chNum = Number(chKey);
                const resolutions = Array.isArray(sh.resolutions) ? sh.resolutions : [];
                for (const r of resolutions) {
                    if (!r || typeof r !== 'object') continue;
                    const row = rowFor(r.cycle_id);
                    if (chNum === LEDGER_CONTROL_CHANNEL) {
                        // Control channel (ch9): lifecycle frames. A 'void'
                        // resolution on control is the empty-phase phase_settled.
                        row.phase_clock = true;
                        row.phase_settled = {
                            sent: true,
                            phase_had_content: r.resolution !== 'void',
                        };
                        continue;
                    }
                    const slot = CH_SLOT[chNum];
                    if (!slot) continue;
                    // Keep the LAST resolution seen for the channel within a
                    // cycle (a re-resolution wins); record sent + seq + outcome.
                    row[slot] = {
                        sent: true,
                        seq: r.seq,
                        resolution: r.resolution || null,
                    };
                }
            }

            // Fold in the live pump's per-cycle completion truth where the
            // active cycle matches (best-effort; the shadow rows persist past
            // the live state, so this only enriches the current row).
            try {
                const s = this._state;
                const cc = s && s.cycleCompletion;
                if (s && s.currentCycleId && rows[s.currentCycleId]) {
                    const row = rows[s.currentCycleId];
                    row.serverDone = !!(cc && cc.serverDone);
                    row.cycle_complete = !!(cc && cc.aborted === false && cc.serverDone);
                }
            } catch (_e) { /* best-effort enrichment */ }
        } catch (_e) { /* never throw from a read-only rollup */ }

        const out = Object.keys(rows).map((k) => rows[k]);
        try {
            if (typeof window !== 'undefined') window.__LEDGER_CYCLE = out;
        } catch (_e) { /* best-effort publish */ }
        return out;
    },

    /**
     * [vp-hardening-2026-05-09 D2] Push a snapshot to window.__VP_PUMP_STATE +
     * history. No-op if instrumentation is disabled (the helper itself
     * checks the flag). Called from _advance() and inside event() at end of
     * each handler so post-event state is visible synchronously.
     */
    _pushVpSnapshot(eventName) {
        if (typeof window === 'undefined' || !window.__vpPushPumpSnapshot) return;
        if (!window.__VP_INSTRUMENTATION_ENABLED) return;
        const snap = this._buildVpSnapshot();
        snap.event = eventName || null;
        window.__vpPushPumpSnapshot(snap);
    },
};

// Make TurnPump available globally
if (typeof window !== 'undefined') {
    window.TurnPump = TurnPump;
}

const GameServerConnection = {
    // Connection state
    // NOTE: isConnected, isInGame, inCreationFlow, inputLocked, awaitingFlowCompletion
    // are now FSM-derived via shim getters installed by _installFSMShims().
    state: {
        connectionType: null, // 'direct' or 'proxied'
        currentWorld: null,
        connectionDetails: null,
        websocket: null,
        proxySocket: null,
        lastHeartbeat: null,
        reconnectAttempts: 0,
        maxReconnectAttempts: 10,  // Increased for stateless recovery
        reconnectTimer: null,
        isReconnecting: false,
        heartbeatTimer: null,
        heartbeatCheckTimer: null,
        // [fnf queue/warming UX] True while held by the admission controller
        // warming GANDALF pods. Suppresses the heartbeat-timeout watchdog so a
        // ~2min cold start can't tear down the held websocket. Cleared on the
        // `connected` frame (promotion) or on disconnect.
        queueHold: false,
        // Stateless GameServer tracking
        gameserverName: null,
        gameserverGeneration: null,
        isStateless: false,
        // Buffered messages for connection state
        bufferedMessages: [],  // Messages to display before next prompt
        // Creation flow state tracking
        currentFlowStep: null          // Current creation flow step
    },

    // Compatibility shim: init() installs FSM-derived getters on state
    _installFSMShims() {
        const state = this.state;

        // isConnected: true when FSM is not DISCONNECTED
        Object.defineProperty(state, 'isConnected', {
            get() { return TerminalFSM.mode !== 'DISCONNECTED'; },
            set(v) {
                // Legacy setter - ignored; transitions drive this now
                console.log('GameServerConnection.state.isConnected setter (ignored, FSM drives state)');
            },
            configurable: true
        });

        // isInGame: true when in game states
        Object.defineProperty(state, 'isInGame', {
            get() { return TerminalFSM.isInGame(); },
            set(v) {
                console.log('GameServerConnection.state.isInGame setter (ignored, FSM drives state)');
            },
            configurable: true
        });

        // inCreationFlow: true when in any creation-related state
        Object.defineProperty(state, 'inCreationFlow', {
            get() { return TerminalFSM.isFlowActive(); },
            set(v) {
                console.log('GameServerConnection.state.inCreationFlow setter (ignored, FSM drives state)');
            },
            configurable: true
        });

        // inputLocked: true when input should be blocked
        Object.defineProperty(state, 'inputLocked', {
            get() { return !TerminalFSM.isInputOpen(); },
            set(v) {
                console.log('GameServerConnection.state.inputLocked setter (ignored, FSM drives state)');
            },
            configurable: true
        });

        // awaitingFlowCompletion: true when in SPAWNING or CREATION_FLOW
        Object.defineProperty(state, 'awaitingFlowCompletion', {
            get() { return TerminalFSM.mode === 'SPAWNING' || TerminalFSM.mode === 'CREATION_FLOW'; },
            set(v) {
                console.log('GameServerConnection.state.awaitingFlowCompletion setter (ignored, FSM drives state)');
            },
            configurable: true
        });
    },

    // Connection configuration
    config: {
        directConnectionTimeout: 10000, // 10 seconds
        reconnectBaseDelay: 1000, // 1 second base delay
        reconnectMaxDelay: 30000, // 30 seconds max delay
        heartbeatInterval: 10000, // 10 seconds (send ping every 10s to prevent timeout)
        // 120s tolerance: a laggy/flaky link (mobile, remote) can stall well past
        // 60s during a narration compute without the pipe being truly dead. A too-
        // tight watchdog self-inflicts drops on top of real network blips. Pair with
        // the relay's matching 120s ws-ping tolerance; the socket survives brief
        // stalls, and a genuine break still reconnects (+ resumes, place kept).
        heartbeatTimeout: 120000, // 120 seconds without heartbeat = connection lost
        // Production: ONE WSS to the Cloud Run websocket-proxy relay
        // (services/websocket_proxy). The relay looks up the world's allocated
        // gameserver, resolves its pod via k8s DNS, and proxies frames both ways
        // in-memory (no Firestore, no SSE). The frontend opens a single socket
        // and sends the same connect frame it uses on the local direct path; the
        // gameserver verifies the JWT and runs the admission/queue gate, whose
        // queue_position frames relay back over THIS socket unchanged.
        // Local dev: empty -> the direct ws://localhost:7655 path below is used.
        // Set by main.js from the platform seam's wsBase() -- this file stays
        // import-free because suites run its source as a classic script.
        wsProxyUrl: '',
        tokenRenderIntervalMs: 30, // ms between rendering each token (0 = immediate, 30 = smooth typewriter)
    },

    // Initialize connection manager
    init() {
        this._installFSMShims();
        if (typeof HeaderFSM !== 'undefined') {
            HeaderFSM.init();
        }
        if (typeof ClockLine !== 'undefined') {
            ClockLine.init();
        }
        if (typeof ActionLine !== 'undefined') {
            ActionLine.init();
        }
        // Reset play queue state when FSM enters DISCONNECTED
        if (typeof FSMEvents !== 'undefined') {
            FSMEvents.on('terminal:cleanup', () => {
                TurnPump.event('reset');
                this._ledgerReset();
            });
        }
        // [2026-07-27-003-QASTI BUG 2] Arm the passive audio-unlock gesture hook.
        // WHY HERE: init() is called from main.js::initializeGame(), which runs
        // on DOMContentLoaded — BEFORE login, before world join, and a long way
        // before the first narration. So the listener is installed in time to
        // catch whatever the player touches FIRST (the login button on a phone),
        // which is the whole point: a player who never finds the SOUND toggle or
        // the "TAP TO ENABLE SOUND" affordance must still get audio.
        this._armAudioUnlockGesture();
        this.setupHeartbeat();
        this.setupReconnectionLogic();
        console.log('GameServer Connection Manager initialized (FSM-driven)');
    },

    // Establish connection to GameServer
    async connect(connectionDetails) {
        const sessionId = this.state.sessionId || Math.random().toString(36).substring(2, 10);
        this.state.sessionId = sessionId;
        this.state.connectionDetails = connectionDetails;

        // Transition FSM to CONNECTING (if not already past DISCONNECTED)
        if (TerminalFSM.mode === 'DISCONNECTED') {
            TerminalFSM.transition('connect_requested', {
                reconnectAttempts: this.state.reconnectAttempts,
                maxReconnectAttempts: this.state.maxReconnectAttempts
            });
        }

        // Only reset reconnect attempts if this is a fresh connection (not a reconnection)
        if (!this.state.isReconnecting) {
            this.state.reconnectAttempts = 0;
            console.log(`🆕 [${sessionId}] Fresh connection attempt`);

            this.logMessage('Establishing GameServer Connection');
            this.logMessage('');
            this.logMessage(`World: ${connectionDetails.world_name}`);
            // Server IP:port not shown - stateless connection via the WSS relay
            this.logMessage('');
        } else {
            console.log(`🔄 [${sessionId}] Reconnection attempt ${this.state.reconnectAttempts + 1}/${this.state.maxReconnectAttempts}`);
        }

        // Environment-based connection strategy
        if (typeof Environment !== 'undefined' && Environment.isLocal()) {
            console.log(`🔌 [${sessionId}] Local environment detected - using direct WebSocket`);
            await this.attemptDirectWebSocket(connectionDetails);
        } else {
            console.log(`🔌 [${sessionId}] Production environment - using single WSS relay`);
            await this.attemptProxiedConnection(connectionDetails);
        }
    },


    // Production connection: ONE WebSocket to the Cloud Run websocket-proxy relay.
    // The relay binds to the world's allocated gameserver and proxies frames both
    // ways. JWT verification and the admission/queue gate live on the gameserver
    // and relay back over this same socket (queue_position frames -> the QUEUED
    // FSM surface). No HTTP-POST, no SSE, no Firestore connection-state.
    async attemptProxiedConnection(connectionDetails) {
        try {
            const connectionId = Math.random().toString(36).substring(2, 10);

            const base = (this.config.wsProxyUrl || '').replace(/\/+$/, '');
            if (!base) {
                console.error(`❌ [${connectionId}] no relay base for this build (platform wsBase() returned empty)`);
                this.logMessage('Connection is misconfigured (missing relay URL).');
                this.logMessage('Please contact support.');
                return;
            }
            const wsUrl = `${base}/${connectionDetails.world_id}`;
            console.log(`🔌 [${connectionId}] Connecting via WSS relay: ${wsUrl}`);
            this.logMessage('Connecting to the world...');

            this.state.websocket = new WebSocket(wsUrl);

            this.state.websocket.onopen = () => {
                console.log(`✅ [${connectionId}] WSS relay connection established`);
                // `.catch` because setupProxiedConnection is async since
                // [2026-08-16-010-ENGINE] (it awaits a fresh ID token). An
                // unhandled rejection here would leave the socket OPEN with no
                // connect frame ever sent — a silent hang, which is the class
                // this whole ticket exists to remove.
                this.setupProxiedConnection(connectionDetails).catch((e) => {
                    console.error(
                        `❌ [${connectionId}] connect frame was never sent: `
                        + `${e && e.message ? e.message : e}`
                    );
                });
            };

            this.state.websocket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    this.handleGameServerMessage(message);
                } catch (error) {
                    console.error(`❌ [${connectionId}] Failed to parse relay message:`, error);
                }
            };

            this.state.websocket.onclose = (event) => {
                console.log(`🔌 [${connectionId}] WSS relay connection closed (code: ${event?.code})`);
                this.handleConnectionLoss('proxied');
            };

            this.state.websocket.onerror = (error) => {
                console.error(`❌ [${connectionId}] WSS relay error:`, error);
            };

        } catch (error) {
            console.error('Relay connection error:', error);
            this.logMessage('Failed to establish a connection to the world.');
            this.logMessage('Please check your network connection and try again.');
        }
    },

    // Setup the relay connection once the socket is OPEN: send the connect frame
    // (same shape as the direct/local path) and start the heartbeat. Inbound
    // messages arrive via the socket's onmessage handler (wired above) ->
    // handleGameServerMessage. The server-side handshake / admission gate drives
    // the FSM transitions and the "welcome" output, so we don't print them here.
    // ASYNC since [2026-08-16-010-ENGINE]: the connect frame carries a token
    // that must be REFRESHED before it is sent, and an unawaited promise would
    // serialize as `{}` — a connect that fails auth in a NEW way. The caller
    // (`onopen`) attaches a .catch; nothing downstream of the send depends on
    // this resolving.
    async setupProxiedConnection(connectionDetails) {
        const sessionId = this.state.sessionId || Math.random().toString(36).substring(2, 10);
        console.log(`🎮 [${sessionId}] Setting up relay connection for world ${connectionDetails.world_id}`);

        this.state.connectionType = 'proxied';
        // isConnected and isInGame are now FSM-derived (shim getters)
        this.state.currentWorld = connectionDetails;
        this.state.lastHeartbeat = Date.now();
        this.state.sessionId = sessionId;
        this.state.isReconnecting = false;  // Successfully connected, clear reconnection flag
        this.state.reconnectAttempts = 0;   // Reset reconnection counter on successful connection

        // Clear any pending reconnect timer
        if (this.state.reconnectTimer) {
            clearTimeout(this.state.reconnectTimer);
            this.state.reconnectTimer = null;
        }

        // Send the connect frame. The relay forwards it to the gameserver, which
        // verifies + runs the admission gate (queue_position frames relay back
        // over this socket).
        this.state.websocket.send(JSON.stringify(await this._connectFrame(connectionDetails)));

        // Wire + push the persisted sound preference now the WS is open
        // (2026-06-25-004-ENGINE Mitigation 2). init() is idempotent, so a
        // reconnect re-asserts the player's choice to the (possibly fresh)
        // server session — keeping the text-only-ch0 capacity lever correct.
        if (typeof SoundToggle !== 'undefined') {
            try { SoundToggle.init(this); } catch (e) { /* non-fatal */ }
        }

        console.log(`✅ [${sessionId}] Relay connection ready; awaiting server handshake`);

        // Start heartbeat mechanism
        this.startHeartbeat();
    },

    /**
     * THE connect frame, for the relay and the direct socket alike.
     * Server-authoritative identity: player_id is derived from the JWT
     * server-side; firebase_token is sent for verification. A route chosen on
     * connect and a shared link's join token ride here, and the GameServer
     * resolves them to an entry (game_server/entry.py).
     *
     * `sound` is the player's voice preference. It decides the seat's admission
     * class before the gate runs: a voice-off seat is admitted on narrate warmth
     * and never takes a voiced slot (services/shared/admission_queue.py).
     */
    async _connectFrame(connectionDetails) {
        return {
            type: 'connect',
            world_id: connectionDetails.world_id,
            connection_token: connectionDetails.connection_token,
            firebase_token: await this._getFirebaseToken(),
            sound: this._voicePref(),
            ...(this._forceNewCharacterFlag() ? { new_character: true } : {}),
            ...this._routeChoiceField(),
            ...(connectionDetails.join_token ? { join_token: connectionDetails.join_token } : {}),
        };
    },

    /** The stored voice preference, 'on' | 'off'; 'on' when SoundToggle is absent. */
    _voicePref() {
        if (typeof SoundToggle === 'undefined') return 'on';
        return SoundToggle.isSoundOn() === false ? 'off' : 'on';
    },

    // Attempt direct WebSocket connection (local development only)
    async attemptDirectWebSocket(connectionDetails) {
        try {
            const connectionId = Math.random().toString(36).substring(2, 10);
            // [2026-06-25-002-ENGINE] WS port is overridable for isolated
            // port-offset validation stacks (MP Phase-2). Order: ?ws_port=
            // query param > window.__GAMESERVER_WS_PORT__ > default 7655.
            // Production/shared-stack behavior is unchanged (default 7655).
            let _wsPort = 7655;
            try {
                const _qp = new URLSearchParams(window.location.search).get('ws_port');
                if (_qp) _wsPort = parseInt(_qp, 10);
                else if (window.__GAMESERVER_WS_PORT__) _wsPort = parseInt(window.__GAMESERVER_WS_PORT__, 10);
            } catch (e) { /* default */ }
            console.log(`🔌 [${connectionId}] Attempting direct WebSocket to localhost:${_wsPort}`);

            // Construct WebSocket URL
            const wsUrl = `ws://localhost:${_wsPort}`;
            console.log(`🔗 [${connectionId}] WebSocket URL: ${wsUrl}`);

            // Create WebSocket connection
            this.state.websocket = new WebSocket(wsUrl);

            // Connection opened
            this.state.websocket.onopen = () => {
                console.log(`✅ [${connectionId}] Direct WebSocket connection established`);
                // `.catch` for the same reason as the proxied path: an
                // unhandled rejection would leave the socket open with no
                // connect frame ever sent.
                this.setupDirectWebSocket(connectionDetails).catch((e) => {
                    console.error(
                        `❌ [${connectionId}] connect frame was never sent: `
                        + `${e && e.message ? e.message : e}`
                    );
                });
            };

            // Message received
            this.state.websocket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    this.handleGameServerMessage(message);
                } catch (error) {
                    console.error('❌ Failed to parse WebSocket message:', error);
                }
            };

            // Connection closed
            this.state.websocket.onclose = () => {
                console.log(`🔌 [${connectionId}] WebSocket connection closed`);
                TerminalFSM.transition('disconnect');
            };

            // Connection error
            this.state.websocket.onerror = (error) => {
                console.error(`❌ [${connectionId}] WebSocket error:`, error);
                this.logMessage('Failed to connect to GameServer');
                this.logMessage('Make sure GameServer is running on port 7655');
            };

        } catch (error) {
            console.error(`❌ Direct WebSocket connection error:`, error);
            this.logMessage('Failed to establish WebSocket connection');
            this.logMessage('Please check that GameServer is running');
        }
    },

    // Setup direct WebSocket connection state
    // ASYNC since [2026-08-16-010-ENGINE], for the same reason as
    // `setupProxiedConnection`: the connect frame carries a token that must be
    // refreshed before it is sent. There are TWO setup funnels — proxied and
    // direct — and both send a connect frame; awaiting in only one is the
    // half-migration this codebase keeps filing.
    async setupDirectWebSocket(connectionDetails) {
        const sessionId = this.state.sessionId || Math.random().toString(36).substring(2, 10);
        console.log(`🎮 [${sessionId}] Setting up direct WebSocket for world ${connectionDetails.world_id}`);

        this.state.connectionType = 'direct';
        // isConnected and isInGame are now FSM-derived (shim getters)
        this.state.currentWorld = connectionDetails;
        this.state.lastHeartbeat = Date.now();
        this.state.isReconnecting = false;
        this.state.reconnectAttempts = 0;

        // Send the connect frame; the server returns the authoritative session_id.
        this.state.websocket.send(JSON.stringify(await this._connectFrame(connectionDetails)));

        // Wire + push the persisted sound preference now the WS is open
        // (2026-06-25-004-ENGINE Mitigation 2). init() is idempotent, so a
        // reconnect re-asserts the player's choice to the (possibly fresh)
        // server session — keeping the text-only-ch0 capacity lever correct.
        if (typeof SoundToggle !== 'undefined') {
            try { SoundToggle.init(this); } catch (e) { /* non-fatal */ }
        }

        // Don't transition FSM here - handleStatelessConnection will do it
        // Don't display initial messages here - they'll come from handleStatelessConnection

        console.log(`✅ [${sessionId}] Direct WebSocket ready for gameplay`);
    },

    // Get Firebase ID token for GameServer auth (real token enables multi-player)
    /**
     * The Firebase ID token for a `connect` frame — FRESH, not the snapshot.
     *
     * [2026-08-16-010-ENGINE] THE 60-MINUTE WEDGE. This used to return
     * `UnifiedAuthService.getCurrentToken()`, which is `state.authToken` — a
     * value written ONLY inside `onAuthStateChanged`. That callback fires on
     * sign-in / sign-out / user-change; it does NOT fire on token refresh
     * (Firebase has a separate `onIdTokenChanged`, and nothing subscribes to
     * it). So the app served a token snapshotted at sign-in for the whole life
     * of the page, while the SDK quietly refreshed its own copy every ~55 min.
     * A Firebase ID token expires at 60 minutes.
     *
     * ⚠️ THE RENEWAL WAS NEVER MISSING — IT WAS NEVER CALLED.
     * `UnifiedAuthService.refreshToken()` already wraps
     * `currentUser.getIdToken(forceRefresh)`, writes the result back to
     * `state.authToken` and notifies listeners. `world-commands.js` takes a
     * fresh token per request. The WS
     * connect path was the ONE funnel that did not — the same
     * N-funnels-one-doesn't shape as [2026-08-16-007-ENGINE], in the same
     * session.
     *
     * `forceRefresh` is deliberately LEFT FALSE: the SDK returns the cached
     * token while it is still valid and refreshes only near expiry, so this is
     * not a network call per connect. Forcing it would trade a wedge for a
     * round-trip on every reconnect.
     *
     * ⚠️ THE FALLBACK IS THE OLD SNAPSHOT, AND THAT IS DELIBERATE. If the
     * refresh throws (offline, clock skew, revoked session) the snapshot is
     * still the best credential we hold — it may be valid, and sending it lets
     * the SERVER decide. Sending nothing would refuse a connection the server
     * might have accepted. It is logged at ERROR either way: a token we could
     * not refresh is a fact worth seeing in a dump bundle.
     */
    async _getFirebaseToken() {
        if (typeof UnifiedAuthService === 'undefined'
                || !UnifiedAuthService.isAuthenticated()) {
            return 'local-dev-token';
        }
        try {
            const fresh = await UnifiedAuthService.refreshToken();
            if (fresh) return fresh;
            console.error(
                'GameServerConnection: refreshToken() resolved EMPTY; falling '
                + 'back to the cached token. A connect on a stale token is the '
                + '60-minute wedge ([2026-08-16-010-ENGINE]).'
            );
        } catch (e) {
            console.error(
                'GameServerConnection: ID-token refresh FAILED before connect '
                + `(${e && e.message ? e.message : e}); falling back to the `
                + 'cached token. If this connect is refused, THIS is why.'
            );
        }
        return UnifiedAuthService.getCurrentToken() || 'local-dev-token';
    },

    /**
     * Deliberate opt-in escape hatch for forcing a FRESH character on connect,
     * even when the account already owns a durable character in the world.
     *
     * The default connect path RESUMEs the owned durable character in dev exactly
     * as in prod ([2026-07-08-002-ENGINE] removed the dev-mode always-create stub).
     * A tester who wants a clean creation run instead adds ``?newchar=1`` to the
     * URL; the server honors ``new_character`` only in development, so this can
     * never drop a durable character in prod. Absent the param this returns false
     * and the connect frame is byte-for-byte unchanged.
     */
    /**
     * The route chosen on connect: ``?episode=<name>&plot_line=<name>`` -- the
     * board's choice, given before the board has a screen. The server deals
     * the plot line's characters and enters the route's start room; a name
     * that matches no route is REFUSED, fatally (``entry-refused``). Absent the
     * params the connect frame is byte-for-byte unchanged.
     */
    _routeChoiceField() {
        let params;
        try {
            params = new URLSearchParams(window.location.search);
        } catch (_e) {
            return {};
        }
        const out = {};
        for (const key of ['episode', 'plot_line']) {
            const value = (params.get(key) || '').trim();
            if (value) {
                out[key] = value;
            }
        }
        return out;
    },

    _forceNewCharacterFlag() {
        try {
            const v = new URLSearchParams(window.location.search).get('newchar');
            return v === '1' || v === 'true';
        } catch (_e) {
            return false;
        }
    },

    /**
     * [channel-delivery-ledger Step 3] Active per-channel admission state.
     *
     * Lazily-initialized map: channel -> {
     *   last_contiguous_seq, stream_epoch, baselined,
     *   seen:Set<seq>, buffer:Map<seq,message>, graceTimer
     * }. Distinct from window.__LEDGER_SHADOW (the observe-only introspection
     * record): this is the ACTING ordering state consulted by _ledgerAdmit.
     */
    _ledgerAdmitState() {
        if (!this._ledgerChannels || typeof this._ledgerChannels !== 'object') {
            this._ledgerChannels = {};
        }
        return this._ledgerChannels;
    },

    _ledgerChannelState(channel) {
        const all = this._ledgerAdmitState();
        let ch = all[channel];
        if (!ch) {
            ch = {
                last_contiguous_seq: 0,
                stream_epoch: null,
                baselined: false,
                seen: new Set(),
                buffer: new Map(),
                graceTimer: null,
                // [Step 4] NACK coalescing timer (fires before grace) + the
                // set of seqs already NACKed (so we don't re-NACK on every
                // subsequent ahead-frame for the same gap). Cleared when the
                // gap fills or the channel re-baselines.
                nackTimer: null,
                nacked: new Set(),
                // [Step 4] Debounced ACK timer + last seq we ACKed (prune-only).
                ackTimer: null,
                acked_through: 0,
                // [Step 5] Frame class this channel's stream carries
                // ('content' | 'lifecycle-control'). Set on first admit; picks
                // the per-class force-release grace (clock-never-stalls). The
                // control channel (9) may interleave lifecycle-control AND
                // bootstrap frames on the same seq stream — bootstrap frames are
                // NOT admitted (they pass through), so this stays 'lifecycle-
                // control' for the control channel's ordered positions.
                frameClass: null,
            };
            all[channel] = ch;
        }
        return ch;
    },

    /**
     * [channel-delivery-ledger Step 5] Frame-class taxonomy classifier.
     *
     * A frame's CLASS — not just its channel number — decides its ledger
     * treatment. This replaces the binary "content channels 0-3 vs everything
     * else" gate and is the single extension point for the reserved
     * `consequential` class (PERSIST-C): a future world-state event class slots
     * in as one branch here + one treatment row in `_ledgerAdmit`, with NO
     * retrofit of the admission core.
     *
     * Returns one of:
     *   'content'           — ch0-3 narration/mechanics/lifecycle-fx. FULL
     *                         ordering treatment (order + void + reorder/dedup +
     *                         NACK/resend + force-release @ LEDGER_GAP_GRACE_MS).
     *   'lifecycle-control' — turn_complete/phase_clock/phase_settled on the
     *                         control channel. Admitted + ordered + recoverable,
     *                         but with the SHORT force-release grace
     *                         (LEDGER_CONTROL_GAP_GRACE_MS) — clock never stalls.
     *   'bootstrap'         — room_state/spawn_complete on the control channel.
     *                         Pass-through (connection setup; recovery is the
     *                         step-4 handshake/resync, not in-cycle ordering).
     *   null                — non-pump / unstamped / unknown control type:
     *                         pass straight through (today's behavior).
     *
     * RESERVED (PERSIST-C, build NOTHING): a future `consequential` class for
     * persistence world-state events (death / item-destruction / arc-beat —
     * ticket 2026-05-22-005-PERSISTENCE §4) would be one more branch returning
     * 'consequential' + its treatment row. It is intentionally UNWIRED here.
     */
    _ledgerFrameClass(message) {
        if (!message || typeof message !== 'object') return null;
        const seq = message.seq;
        const channel = message.channel;
        // Unstamped frames carry no ordering authority — pass through.
        if (typeof seq !== 'number' || typeof channel !== 'number') return null;
        if (LEDGER_CONTENT_CHANNELS.has(channel)) return 'content';
        if (channel === LEDGER_CONTROL_CHANNEL) {
            // P4 for the enveloped kinds: the ledger class is the KIND's rule,
            // read off the registry -- never a wire-type list kept beside it.
            const envelopeRules = (message.type === globalThis.FrameRegistry.ENVELOPE_TYPE)
                ? globalThis.FrameRegistry.rulesFor(message.kind) : null;
            if (envelopeRules) return envelopeRules.ledger_class;
            const t = message.type;
            if (LEDGER_LIFECYCLE_CONTROL_TYPES.has(t)) return 'lifecycle-control';
            // `bootstrap` covers room_state/spawn_complete AND action_result —
            // all pass-through-but-seq-accounted (see LEDGER_BOOTSTRAP_CONTROL_TYPES).
            // action_result MUST be seq-accounted: it consumes a ch9 seq the
            // server stamps (action_result then turn_complete on the same ch9
            // stream per phase), so leaving it `null`-class would phantom-gap the
            // next lifecycle-control frame and fire a needless NACK->resend EVERY
            // phase even on a clean run. It is a ch9 seq hazard, not a free pass.
            if (LEDGER_BOOTSTRAP_CONTROL_TYPES.has(t)) return 'bootstrap';
            // RESERVED branch (PERSIST-C): a `consequential` world-state frame
            // would be matched here. None exist today — UNWIRED on purpose.

            // ------------------------------------------------------------
            // [2026-07-28-012-ENGINE] THE UNCLASSED SEQ-CONSUMER ALARM.
            //
            // Reaching here means the server stamped a channel-9 SEQUENCE
            // NUMBER on a type this classifier does not know. The guard at the
            // top of this method already proved `seq` is a number, so there is
            // no such thing as a harmless fallthrough on ch9: every frame here
            // OCCUPIES a position in the control stream.
            //
            // This used to `return null`, and the comment right here warned
            // that doing so "phantom-gaps ch9". It was correct and it was
            // decorative: `spawn` sat unclassed for the life of the ledger and
            // deadlocked the first beat of every new life, through five silent
            // steps — no watermark advance, a phantom gap, a NACK, a
            // force-release, an aborted cycle — and not one line of output said
            // "I did not know what this frame was". THE LOUD-FAILURE LAW: a
            // fallback must be LOUD, or it must not exist.
            //
            // So: default to `bootstrap` — the treatment that is SAFE for an
            // unknown frame (dispatched immediately, never buffered behind a
            // hole, but seq-ACCOUNTED so contiguity closes and the clock never
            // stalls) — and scream, once per type. A wrong-but-accounted
            // classification degrades ordering for one frame; an unaccounted
            // one silently destroys the whole beat.
            //
            // Known offenders at the time of writing: `spawn` and
            // `action_refused` — both since classed above (`spawn` in
            // [2026-07-28-012-ENGINE], `action_refused` in
            // [2026-08-08-009-QASTI]). That is why this is a CLASS fix and
            // not a one-type one: the alarm remains for the NEXT type.
            // ------------------------------------------------------------
            if (!LEDGER_UNCLASSED_CONTROL_TYPES.has(t)) {
                LEDGER_UNCLASSED_CONTROL_TYPES.add(t);
                try {
                    if (typeof window !== 'undefined') {
                        window.__LEDGER_UNCLASSED_CONTROL_TYPES =
                            Array.from(LEDGER_UNCLASSED_CONTROL_TYPES);
                    }
                } catch (_e) { /* observability must never break admission */ }
                console.error(
                    `[ledger] UNCLASSED CONTROL FRAME: type='${t}' arrived on `
                    + `channel ${LEDGER_CONTROL_CHANNEL} carrying seq=${seq}. It `
                    + 'CONSUMES a control-stream position but is in neither '
                    + 'LEDGER_LIFECYCLE_CONTROL_TYPES nor '
                    + 'LEDGER_BOOTSTRAP_CONTROL_TYPES. Defaulting to `bootstrap` '
                    + '(dispatched + seq-accounted) so it cannot phantom-gap the '
                    + 'stream — but this is a BUG: add it to the correct set. '
                    + 'See [2026-07-28-012-ENGINE].'
                );
            }
            return 'bootstrap';
        }
        return null;
    },

    /**
     * [channel-delivery-ledger Step 5] Per-class force-release grace.
     * Content gets the generous LEDGER_GAP_GRACE_MS (a late reorder fills first);
     * lifecycle-control gets the aggressive LEDGER_CONTROL_GAP_GRACE_MS so the
     * clock advances within ~one render tick even mid-repair (clock-never-stalls).
     */
    _ledgerGraceForClass(frameClass) {
        return frameClass === 'lifecycle-control'
            ? LEDGER_CONTROL_GAP_GRACE_MS
            : LEDGER_GAP_GRACE_MS;
    },

    /**
     * [channel-delivery-ledger Step 3/5] Active admission layer.
     *
     * Consulted by handleGameServerMessage for seq-bearing frames whose
     * `_ledgerFrameClass` is `content` (ch0-3) OR `lifecycle-control`
     * (turn_complete/phase_clock/phase_settled on channel 9 — NEW in step 5).
     * `bootstrap` (room_state/spawn_complete) and `null` (non-pump / unknown
     * control) pass straight through. Returns { admit, release[] }:
     *   - admit=true  -> caller dispatches `message` now, then dispatches each
     *                    `release[]` message in seq order through the same switch.
     *   - admit=false -> frame was buffered (out-of-order) or dropped (dup /
     *                    stale-epoch); caller does NOT dispatch it.
     *
     * Algorithm (spec §"The admission layer"):
     *   1. Stale epoch (< current)        -> drop.
     *   2. New/first epoch (> current)    -> re-baseline last_contiguous=seq-1
     *                                        (PHANTOM-GAP FIX), then fall through.
     *   3. Duplicate (<= contiguous|seen) -> drop.
     *   4. Exactly next (==contiguous+1)  -> admit + drain contiguous successors.
     *   5. Ahead (>contiguous+1)          -> buffer + arm bounded grace.
     *
     * The grace + NACK machinery is shared between content and lifecycle-control;
     * ONLY the force-release grace DURATION differs (per `_ledgerGraceForClass` —
     * the clock-never-stalls carve-out gives control a much shorter backstop).
     * A gap that never fills is force-released by the grace timer
     * (_ledgerForceRelease). Fail-soft + kill-switch are applied by the CALLER
     * (handleGameServerMessage wraps this in try/catch and checks
     * LEDGER_ACTIVE_ORDERING) so this method can assume it's only called when
     * active and is free to throw on genuine corruption.
     */
    _ledgerAdmit(message) {
        const passThrough = { admit: true, release: [] };
        if (!LEDGER_ACTIVE_ORDERING) return passThrough;
        if (!message || typeof message !== 'object') return passThrough;

        const seq = message.seq;
        const channel = message.channel;
        // [Step 5] Classify by FRAME CLASS, not raw channel. Only `content` and
        // `lifecycle-control` are admitted/ordered. `bootstrap` (room_state/
        // spawn_complete/action_result) and `null` (no seq, unknown control
        // type, non-pump) pass straight through — but `bootstrap` is ALSO
        // seq-accounted (below) so it never phantom-gaps the shared ch9 stream.
        const frameClass = this._ledgerFrameClass(message);

        // [Step 5] BOOTSTRAP SEQ-ACCOUNTING (clock-never-stalls correctness).
        //
        // bootstrap frames (room_state/spawn_complete/action_result) pass straight
        // through to dispatch — they are idempotent / carry their own turn-scope
        // filter, and must NOT be buffered behind a control hole.
        // BUT they share the control channel's monotonic seq counter with
        // lifecycle-control frames (the server stamps action_result/room_state
        // then turn_complete on the same channel-9 stream within one phase
        // delivery).
        // If a bootstrap frame's seq were simply ignored by the admission state,
        // the NEXT lifecycle-control frame (bootstrap_seq + 1) would look like a
        // gap and (briefly) stall the clock + needlessly NACK. So we ACCOUNT for
        // the bootstrap seq's position — advance contiguity / fill a buffered
        // hole exactly like a void — while ALWAYS dispatching it immediately
        // (never buffering it: a connection-setup frame must not wait on a hole).
        if (frameClass === 'bootstrap'
            && channel === LEDGER_CONTROL_CHANNEL
            && typeof seq === 'number') {
            try {
                const ch = this._ledgerChannelState(channel);
                // Baseline the control channel from the first bootstrap frame if
                // it hasn't been baselined yet (no handshake adopted), so we don't
                // read it as a giant phantom gap.
                if (!ch.baselined) {
                    ch.last_contiguous_seq = seq - 1;
                    ch.stream_epoch = (typeof message.stream_epoch === 'number')
                        ? message.stream_epoch : 0;
                    ch.baselined = true;
                }
                if (seq === ch.last_contiguous_seq + 1) {
                    // Occupies the next position: advance + drain any buffered
                    // lifecycle-control successors that are now contiguous.
                    ch.seen.add(seq);
                    ch.last_contiguous_seq = seq;
                    const release = this._ledgerDrainBuffer(ch, channel);
                    if (ch.buffer.size === 0) {
                        this._ledgerCancelGrace(ch);
                        this._ledgerCancelNack(ch);
                        ch.nacked = new Set();
                    }
                    this._ledgerScheduleAck(channel, ch);
                    this._ledgerShadowDecision(channel, seq, 'bootstrap-accounted',
                        { released: release.length, last_contiguous: ch.last_contiguous_seq });
                    // [Fold X-2] Resolve the drained lifecycle-control successors
                    // (the bootstrap frame itself is bootstrap-class, not a
                    // content/control delivered position — no resolution row).
                    for (const rm of release) {
                        this._ledgerRecordResolution(channel, rm.seq,
                            this._ledgerResolutionOf(rm), 'lifecycle-control',
                            (rm && (rm.cycle_id || rm.audit_key)) || null);
                    }
                    // Dispatch the bootstrap frame itself now (admit), then any
                    // drained lifecycle-control successors in seq order.
                    return { admit: true, release };
                }
                // Out-of-order or behind: still dispatch the bootstrap frame
                // (idempotent, must not wait), and mark its seq seen so a later
                // contiguity advance drains past it.
                ch.seen.add(seq);
                // [2026-07-28-012-ENGINE] ...and TRY TO CLOSE CONTIGUITY NOW.
                //
                // Marking `seen` alone was not enough. `_ledgerDrainBuffer`
                // already advances past seen-but-not-buffered positions, but
                // nothing CALLED it on this arm — so an out-of-order bootstrap
                // frame sat `seen` while `last_contiguous` stayed put, and the
                // next lifecycle-control frame read the position it had already
                // filled as a hole. Measured on prod leg eae8ce87: `room_state`
                // seq 98 arrived after `phase_clock` 97, was passed through
                // here, and was then NACKed and force-released as an `aborted`
                // gap — a frame the client had already rendered.
                //
                // Draining here is safe on every path this arm serves: a
                // duplicate/behind seq drains nothing (lc+1 is already past it),
                // and an ahead seq only closes the run when every intervening
                // position is genuinely present.
                const release = this._ledgerDrainBuffer(ch, channel);
                if (ch.buffer.size === 0) {
                    this._ledgerCancelGrace(ch);
                    this._ledgerCancelNack(ch);
                    ch.nacked = new Set();
                }
                this._ledgerScheduleAck(channel, ch);
                this._ledgerShadowDecision(channel, seq, 'bootstrap-passthrough',
                    { released: release.length, last_contiguous: ch.last_contiguous_seq });
                for (const rm of release) {
                    this._ledgerRecordResolution(channel, rm.seq,
                        this._ledgerResolutionOf(rm), 'lifecycle-control',
                        (rm && (rm.cycle_id || rm.audit_key)) || null);
                }
                return { admit: true, release };
            } catch (_e) { /* fail-soft: just pass through below */ }
            return { admit: true, release: [] };
        }

        if (frameClass !== 'content' && frameClass !== 'lifecycle-control') {
            return passThrough;
        }

        const ch = this._ledgerChannelState(channel);
        // Record the class on the channel state so the gap path picks the
        // correct (per-class) force-release grace. The control channel's
        // ordered stream is uniformly 'lifecycle-control'.
        ch.frameClass = frameClass;
        const frameEpoch = (typeof message.stream_epoch === 'number')
            ? message.stream_epoch : (ch.stream_epoch !== null ? ch.stream_epoch : 0);

        // 1. Stale epoch — a pre-reconnect straggler. Drop.
        if (ch.baselined && ch.stream_epoch !== null && frameEpoch < ch.stream_epoch) {
            this._ledgerShadowDecision(channel, seq, 'dropped-stale-epoch',
                { frame_epoch: frameEpoch, channel_epoch: ch.stream_epoch });
            return { admit: false, release: [] };
        }

        // 2. New / first epoch — re-baseline (PHANTOM-GAP FIX). seq persists
        //    across reconnect, so a fresh connection's first frame is seq=N>>1;
        //    baselining last_contiguous to seq-1 makes it contiguous instead of
        //    a giant false gap. Clear buffer/seen + cancel any pending grace.
        if (!ch.baselined || frameEpoch > (ch.stream_epoch === null ? -Infinity : ch.stream_epoch)) {
            this._ledgerCancelGrace(ch);
            this._ledgerCancelNack(ch);
            // [Step 4d] If a connect-time handshake already authoritatively
            // baselined this channel for THIS epoch (adopted the server
            // watermark, no replay), honor that baseline instead of inferring
            // seq-1 from the first frame: keep the higher of (watermark,
            // seq-1) so a fresh page that adopted watermark=W treats the next
            // frame W+1 as contiguous, and an out-of-order first frame doesn't
            // regress the authoritative baseline. Falls back to seq-1 when no
            // handshake baseline exists for this epoch (pure step-3 behavior).
            const handshakeBase = (ch._handshake_epoch === frameEpoch)
                ? ch.last_contiguous_seq : null;
            ch.last_contiguous_seq = (handshakeBase !== null)
                ? Math.max(handshakeBase, seq - 1)
                : seq - 1;
            ch.stream_epoch = frameEpoch;
            ch.baselined = true;
            ch.seen = new Set();
            ch.buffer = new Map();
            ch.nacked = new Set();
            this._ledgerShadowDecision(channel, seq, 'baselined',
                { stream_epoch: frameEpoch, baseline_contiguous: ch.last_contiguous_seq });
            // fall through to ordering on the freshly-baselined channel
        }

        // 3. Duplicate — idempotent drop (resend overlap lands here in step 4).
        if (seq <= ch.last_contiguous_seq || ch.seen.has(seq)) {
            this._ledgerShadowDecision(channel, seq, 'dropped-dup',
                { last_contiguous: ch.last_contiguous_seq });
            return { admit: false, release: [] };
        }

        // 4. Exactly next — admit + drain contiguous successors from the buffer.
        if (seq === ch.last_contiguous_seq + 1) {
            ch.seen.add(seq);
            ch.last_contiguous_seq = seq;
            const release = this._ledgerDrainBuffer(ch, channel);
            // A drain that fully empties the buffer clears any pending gap
            // grace AND the NACK timer (the gap that triggered them is filled —
            // possibly BY a resend, which lands here and is indistinguishable
            // from a late reorder; either way the position is satisfied).
            if (ch.buffer.size === 0) {
                this._ledgerCancelGrace(ch);
                this._ledgerCancelNack(ch);
                ch.nacked = new Set();
            }
            // [Step 4b] ACK (prune-only): the contiguous watermark advanced,
            // so schedule a debounced ledger_ack to free the server ring.
            this._ledgerScheduleAck(channel, ch);
            // [Step 5 — Fold X-2] Resolve the admitted position (+ each drained
            // successor): a void frame resolves `void`, content resolves
            // `played`. Carries the frame class so the survey extractor buckets
            // by class (content vs lifecycle-control). An empty-phase
            // phase_settled (phase_had_content === false) is the natural
            // lifecycle-control `void`.
            this._ledgerRecordResolution(channel, seq,
                this._ledgerResolutionOf(message), frameClass,
                (message && (message.cycle_id || message.audit_key)) || null);
            for (const rm of release) {
                this._ledgerRecordResolution(channel, rm.seq,
                    this._ledgerResolutionOf(rm), frameClass,
                    (rm && (rm.cycle_id || rm.audit_key)) || null);
            }
            this._ledgerShadowDecision(channel, seq, 'admitted',
                { released: release.length, last_contiguous: ch.last_contiguous_seq });
            return { admit: true, release };
        }

        // 5. Ahead (gap) — buffer + arm bounded grace + arm a NACK coalescing
        //    tick (Step 4c). The NACK asks the server to resend the missing
        //    span; if the resend arrives it fills the gap (drain path above).
        //    The step-3 grace force-release REMAINS the ultimate backstop if the
        //    NACK->resend round-trip never completes — no-deadlock preserved.
        ch.buffer.set(seq, message);
        ch.seen.add(seq);
        this._ledgerArmGrace(ch, channel);
        this._ledgerArmNack(ch, channel);
        this._ledgerShadowDecision(channel, seq, 'buffered',
            { gap_from: ch.last_contiguous_seq + 1, gap_to: seq - 1 });
        return { admit: false, release: [] };
    },

    /**
     * [channel-delivery-ledger Step 3] Drain contiguous successors from the
     * channel's reorder buffer, advancing last_contiguous_seq for each.
     * Voids (step 2) occupy a seq and satisfy contiguity exactly like content —
     * they're stored in the buffer like any frame and drained the same way.
     * Returns the released messages in seq order.
     *
     * [Step 5] Also advances PAST any `seen`-but-not-buffered position — a
     * bootstrap control frame (room_state/spawn_complete) that was dispatched
     * immediately and only seq-accounted (never buffered). Without this, a
     * lifecycle-control frame whose immediate predecessor was a bootstrap would
     * leave a permanent phantom hole in the control stream.
     */
    _ledgerDrainBuffer(ch, channel) {
        const release = [];
        let next = ch.last_contiguous_seq + 1;
        // Advance over a contiguous run that may freely interleave buffered
        // frames (released) and seen-but-not-buffered bootstrap positions
        // (already dispatched — just advance contiguity past them).
        while (ch.buffer.has(next) || (ch.seen && ch.seen.has(next))) {
            if (ch.buffer.has(next)) {
                const m = ch.buffer.get(next);
                ch.buffer.delete(next);
                release.push(m);
            }
            ch.last_contiguous_seq = next;
            next += 1;
        }
        return release;
    },

    _ledgerArmGrace(ch, channel) {
        if (ch.graceTimer) return; // one grace per outstanding gap
        // setTimeout may be absent in a bare unit harness; guard so the active
        // path never throws there (tests drive _ledgerForceRelease directly).
        if (typeof setTimeout !== 'function') return;
        // [Step 5] CLOCK-NEVER-STALLS: lifecycle-control uses the SHORT grace so
        // the clock advances within ~one render tick even mid-repair; content
        // keeps the generous grace so a late reorder fills first. The class was
        // stamped onto `ch` by _ledgerAdmit's gap path.
        const graceMs = this._ledgerGraceForClass(ch.frameClass);
        ch.graceTimer = setTimeout(() => {
            ch.graceTimer = null;
            this._ledgerForceRelease(channel);
        }, graceMs);
    },

    _ledgerCancelGrace(ch) {
        if (ch.graceTimer && typeof clearTimeout === 'function') {
            clearTimeout(ch.graceTimer);
        }
        ch.graceTimer = null;
    },

    /**
     * [channel-delivery-ledger Step 4c] Arm the NACK coalescing tick for a
     * buffered gap. After LEDGER_NACK_COALESCE_MS (shorter than the grace
     * window), if the gap is still open, emit a `ledger_nack` for the missing
     * span so the server resends from its retain ring. The coalescing tick
     * avoids NACKing a frame that was merely momentarily out of order (it fills
     * first on a single WS). One NACK timer per channel; re-armed only after it
     * fires or the gap fills.
     */
    _ledgerArmNack(ch, channel) {
        if (ch.nackTimer) return; // one coalescing tick per outstanding gap
        if (typeof setTimeout !== 'function') return;
        ch.nackTimer = setTimeout(() => {
            ch.nackTimer = null;
            this._ledgerSendNack(channel);
        }, LEDGER_NACK_COALESCE_MS);
    },

    _ledgerCancelNack(ch) {
        if (ch.nackTimer && typeof clearTimeout === 'function') {
            clearTimeout(ch.nackTimer);
        }
        ch.nackTimer = null;
    },

    /**
     * [channel-delivery-ledger Step 4c] Emit a `ledger_nack` for the currently
     * open gap on `channel`. The missing span is (last_contiguous+1 ..
     * minBufferedSeq-1) — the contiguous hole the buffer is waiting on. Seqs
     * already NACKed are not re-requested (resend may still be in flight). If
     * the buffer drained in the meantime (no gap), this is a no-op.
     */
    _ledgerSendNack(channel) {
        try {
            const ch = this._ledgerAdmitState()[channel];
            if (!ch || ch.buffer.size === 0) return;
            const bufferedSeqs = Array.from(ch.buffer.keys()).sort((a, b) => a - b);
            const firstBuffered = bufferedSeqs[0];
            const from = ch.last_contiguous_seq + 1;
            const to = firstBuffered - 1;
            if (to < from) return; // no actual hole
            // Collect only the seqs in the hole we haven't already NACKed.
            const missing = [];
            for (let s = from; s <= to; s++) {
                if (!ch.nacked.has(s)) {
                    missing.push(s);
                    ch.nacked.add(s);
                }
            }
            if (missing.length === 0) return; // already requested this span
            const sent = this.sendDirectMessage({
                type: 'ledger_nack',
                channel,
                missing,
                stream_epoch: ch.stream_epoch,
                timestamp: new Date().toISOString(),
            });
            this._ledgerShadowDecision(channel, from, 'nack-sent',
                { missing, sent });
        } catch (e) {
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                console.warn('[ledger-nack] send skipped:', e);
            }
        }
    },

    /**
     * [channel-delivery-ledger Step 4b] Schedule a debounced prune-only ACK.
     * Coalesces a burst of in-order frames into one `ledger_ack` carrying the
     * channel's contiguous watermark. Prune-only — never drives retransmit.
     */
    _ledgerScheduleAck(channel, ch) {
        if (typeof setTimeout !== 'function') {
            // No timer harness (unit) — send immediately so the behavior is
            // observable without a fake clock.
            this._ledgerSendAck(channel);
            return;
        }
        if (ch.ackTimer) return; // debounce: one pending ACK per channel
        ch.ackTimer = setTimeout(() => {
            ch.ackTimer = null;
            this._ledgerSendAck(channel);
        }, LEDGER_ACK_DEBOUNCE_MS);
    },

    _ledgerSendAck(channel) {
        try {
            const ch = this._ledgerAdmitState()[channel];
            if (!ch) return;
            const through = ch.last_contiguous_seq;
            // Only ACK forward progress (idempotent prune; re-ACKing the same
            // watermark is harmless but pointless).
            if (through <= ch.acked_through) return;
            const sent = this.sendDirectMessage({
                type: 'ledger_ack',
                channel,
                through,
                stream_epoch: ch.stream_epoch,
                timestamp: new Date().toISOString(),
            });
            if (sent) ch.acked_through = through;
            this._ledgerShadowDecision(channel, through, 'ack-sent', { through, sent });
        } catch (e) {
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                console.warn('[ledger-ack] send skipped:', e);
            }
        }
    },

    /**
     * [channel-delivery-ledger Step 3] No-deadlock force-release.
     *
     * Invoked by the grace timer when a buffered gap has not filled within
     * LEDGER_GAP_GRACE_MS. Step 3 has no resend, so a truly-lost frame's gap
     * never fills — we advance last_contiguous PAST the gap, drain whatever is
     * buffered (in seq order, skipping any further holes by jumping to the next
     * buffered seq), record each jumped gap `unfilled`, and dispatch the
     * released frames through the normal switch. Result: never worse than today
     * on loss (the lost frame is simply gone; the pump proceeds on its latches),
     * strictly better on dup/reorder.
     *
     * Returns the released messages (for unit-test assertion); also dispatches
     * them via _dispatchReleased when a live message switch is available.
     */
    _ledgerForceRelease(channel) {
        const all = this._ledgerAdmitState();
        const ch = all[channel];
        if (!ch || ch.buffer.size === 0) {
            if (ch) { this._ledgerCancelGrace(ch); this._ledgerCancelNack(ch); }
            return [];
        }
        this._ledgerCancelGrace(ch);
        this._ledgerCancelNack(ch);

        // Buffered seqs in ascending order. For each buffered frame the span
        // (last_contiguous+1 .. seq-1) is the unfilled gap — record it, jump
        // last_contiguous to the buffered seq, capture the message, and release
        // it. Repeating handles a buffer with internal holes (multiple gaps).
        const seqs = Array.from(ch.buffer.keys()).sort((a, b) => a - b);
        const released = [];
        for (const s of seqs) {
            if (s > ch.last_contiguous_seq + 1) {
                this._ledgerShadowDecision(channel, s, 'gap-unfilled-released',
                    { from: ch.last_contiguous_seq + 1, to: s - 1 });
                // [Step 5 — Fold X-2] Each force-released-past position is a
                // committed-but-undelivered frame -> resolves `aborted`. The
                // frame existed (it had a seq) but never arrived; the consumer
                // advanced past it. (Distinct from `void`, which is an
                // intentional empty position the server explicitly sent.)
                //
                // [Step 6 rewrite] A force-released (lost-and-unrecovered) gap
                // on a participating channel aborts the active cycle's native
                // completion (never falsely ready). Shadow-only when flag OFF.
                //
                // [2026-07-28-012-ENGINE] ...UNLESS THE POSITION WAS `seen` —
                // see `_ledgerAbortUnseenGap`.
                this._ledgerAbortUnseenGap(
                    channel, ch, ch.last_contiguous_seq + 1, s - 1);
            }
            const m = ch.buffer.get(s);
            ch.buffer.delete(s);
            ch.last_contiguous_seq = s;
            if (m && typeof m === 'object') {
                released.push(m);
                // The buffered frame itself DID arrive — resolve it normally.
                this._ledgerRecordResolution(channel, s,
                    this._ledgerResolutionOf(m), ch.frameClass,
                    (m && m.cycle_id) || null);
            }
        }

        // Dispatch the released frames through the normal handler path.
        for (const m of released) {
            this._dispatchReleased(m);
        }
        return released;
    },

    /**
     * Dispatch a single released frame through the normal handler path. The
     * frame has already passed admission (it's contiguous now), so re-entering
     * handleGameServerMessage would re-admit it harmlessly (seq <= contiguous ->
     * dropped-dup) — but to avoid that round-trip and the shadow double-count we
     * route straight to the type switch via a re-dispatch flag.
     */
    _dispatchReleased(message) {
        try {
            this._ledgerReleasing = true;
            this.handleGameServerMessage(message);
        } finally {
            this._ledgerReleasing = false;
        }
    },

    /**
     * [channel-delivery-ledger Step 4c] Handle a server `ledger_resend_miss`.
     *
     * The server replied that a NACKed seq is no longer retained (pruned / TTL
     * / never existed) — so it can never be filled by a resend. Force-release
     * that specific position so the client doesn't wait for it: advance
     * last_contiguous past the missed seq (recording it `unfilled`), then drain
     * any buffered successors that became contiguous. This keeps the
     * no-deadlock guarantee even when resend can't help. Idempotent: a miss for
     * a seq already passed is a no-op.
     */
    _ledgerResendMiss(channel, seq) {
        try {
            // [Step 5] Accept the control channel too: a missed lifecycle-control
            // frame (e.g. a turn_complete the ring no longer holds) must also
            // force-release so the clock/turn lifecycle never deadlocks.
            const gated = LEDGER_CONTENT_CHANNELS.has(channel)
                || channel === LEDGER_CONTROL_CHANNEL;
            if (typeof seq !== 'number' || !gated) return;
            const ch = this._ledgerChannelState(channel);
            // Already past this position (filled or force-released) — no-op.
            if (seq <= ch.last_contiguous_seq) {
                this._ledgerShadowDecision(channel, seq, 'resend-miss-stale', {});
                return;
            }
            // Advance past the missed position. If there's a span between the
            // current contiguous and the missed seq, that whole span is
            // unfillable — record it unfilled and jump past it.
            // [2026-07-28-012-ENGINE] Same `seen` exemption as the force-release
            // path: a dispatched bootstrap position in the span is not a loss.
            const missCycle = this._ledgerActiveCycleId();
            if (seq > ch.last_contiguous_seq + 1) {
                // [Step 5 — Fold X-2] committed-but-undeliverable -> `aborted`.
                this._ledgerAbortUnseenGap(
                    channel, ch, ch.last_contiguous_seq + 1, seq - 1, missCycle);
            }
            ch.last_contiguous_seq = seq;
            ch.nacked.delete(seq);
            this._ledgerRecordUnfilled(channel, seq, seq);
            // [Step 5 — Fold X-2] the explicitly-missed position resolves
            // `aborted` (the server confirmed it can't be delivered).
            this._ledgerRecordResolution(channel, seq, 'aborted', ch.frameClass, missCycle);
            this._cycleNoteAbort(channel, missCycle); // [Step 6 rewrite] the explicitly-missed position
            // [2026-08-08-015-ENGINE] The server has CONFIRMED it cannot resend
            // this position, so on the control stream it is a settled
            // server-authority loss, not a maybe.
            this._serverAuthorityNoteLoss(channel, seq, missCycle);
            this._ledgerShadowDecision(channel, seq, 'resend-miss-released',
                { last_contiguous: ch.last_contiguous_seq });
            // Drain any buffered successors now contiguous, dispatching them.
            const release = this._ledgerDrainBuffer(ch, channel);
            for (const m of release) {
                this._dispatchReleased(m);
            }
            if (ch.buffer.size === 0) {
                this._ledgerCancelGrace(ch);
                this._ledgerCancelNack(ch);
            }
            // Forward progress -> ACK the new watermark.
            this._ledgerScheduleAck(channel, ch);
        } catch (e) {
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                console.warn('[ledger] resend-miss handling skipped:', e);
            }
        }
    },

    // Mirror a real admission decision into the introspection shadow so VP can
    // assert ordering behavior (admitted / buffered / dropped-dup / released /
    // gap-unfilled-released / baselined / dropped-stale-epoch). Best-effort.
    _ledgerShadowDecision(channel, seq, decision, detail) {
        try {
            if (!window.__LEDGER_SHADOW || typeof window.__LEDGER_SHADOW !== 'object') {
                window.__LEDGER_SHADOW = {};
            }
            const sh = window.__LEDGER_SHADOW[channel] || (window.__LEDGER_SHADOW[channel] = {});
            if (!Array.isArray(sh.decisions)) sh.decisions = [];
            sh.decisions.push({ seq, decision, ...(detail || {}) });
            // cap the decision log so a long session doesn't grow unbounded
            if (sh.decisions.length > 500) sh.decisions.splice(0, sh.decisions.length - 500);
        } catch (e) { /* shadow must never disrupt admission */ }
    },

    /**
     * [channel-delivery-ledger Step 5 — Fold X-2] Resolution of a delivered
     * frame:
     *   'void'   — an explicit empty position the server sent on purpose
     *              (channel_void on content; an empty-phase phase_settled,
     *              phase_had_content === false, on lifecycle-control).
     *   'played' — content actually delivered.
     * The `aborted` resolution (committed-but-failed: a force-released or
     * resend-miss position the consumer advanced past) is recorded directly by
     * the force-release / resend-miss paths, NOT here.
     */
    _ledgerResolutionOf(message) {
        if (!message || typeof message !== 'object') return 'played';
        if (message.type === 'channel_void' || message.kind === 'void') return 'void';
        // An empty-phase phase_settled is the natural lifecycle-control void.
        if (message.type === 'phase_settled' && message.phase_had_content === false) {
            return 'void';
        }
        return 'played';
    },

    /**
     * [channel-delivery-ledger Step 5 — Fold X-2] Record a position's resolution
     * + frame class into the shadow so VP / the prompt-survey extractor can
     * bucket exact `played` / `void` / `aborted` counts BY CLASS — replacing the
     * fuzzy `incomplete_loops` (no-`delivered`) heuristic.
     *
     * CLIENT-SIDE ONLY (consumer-fast-follow note): the server-side `delivered`
     * survey emit currently carries played/void for CONTENT channels only
     * (step 4, turn_loop._send_narration). Control frames are broadcast via
     * networking._async_broadcast_* without a `delivered` emit, so server-side
     * control resolution would need a producer-side completeness marker — out of
     * step-5 scope (would be a turn-manager touch / follow-up ticket). This
     * client shadow makes the control resolution OBSERVABLE now; xstitch owns
     * folding it into the server `delivered` record + the extractor.
     */
    /**
     * [2026-07-28-012-ENGINE] The cycle coordinate an ABORT is attributed to.
     *
     * A force-released / resend-missed position has NO frame in hand, so its
     * own `cycle_id` is unknowable; the best available coordinate is the cycle
     * that was live when the consumer advanced past it. This used to be
     * re-derived independently inside `_ledgerRecordResolution` and then
     * DROPPED on the floor by `_cycleNoteAbort`, which took no cycle at all.
     * THE COORDINATE LAW: derive it ONCE, at the site that knows, and THREAD
     * it to every consumer.
     */
    /**
     * [2026-07-28-012-ENGINE] Resolve a force-released / resend-missed gap span
     * `[from..to]`, EXEMPTING positions the client already dispatched.
     *
     * A bootstrap control frame is dispatched immediately and only
     * seq-accounted, so it lives in `ch.seen` and never in the reorder buffer.
     * Both gap paths used to declare the whole span undelivered without
     * consulting `seen` — which meant a frame the client had ALREADY RENDERED
     * could be recorded `unfilled` and abort the cycle. "Delivered" and "lost"
     * are not allowed to look the same on the ledger. Measured on prod leg
     * eae8ce87: `room_state` seq 98 arrived out of order, was passed through
     * here, and was then NACKed and force-released as an `aborted` gap.
     *
     * Contiguous runs of genuinely-missing positions are coalesced back into
     * one `unfilled` span, so a span with no `seen` positions in it records
     * EXACTLY what it recorded before this change — the no-loss path is
     * byte-identical, and only a mixed span splits.
     *
     * Resolutions and aborts stay per-position: they are keyed by seq.
     */
    _ledgerAbortUnseenGap(channel, ch, from, to, cycleId) {
        const gapCycle = (cycleId === undefined) ? this._ledgerActiveCycleId() : cycleId;
        let runStart = null;
        const flushRun = (end) => {
            if (runStart === null) return;
            this._ledgerRecordUnfilled(channel, runStart, end);
            runStart = null;
        };
        for (let g = from; g <= to; g++) {
            if (ch.seen && ch.seen.has(g)) {
                flushRun(g - 1);
                this._ledgerShadowDecision(channel, g, 'gap-position-already-seen',
                    { note: 'dispatched bootstrap frame; not a loss' });
                continue;
            }
            if (runStart === null) runStart = g;
            this._ledgerRecordResolution(channel, g, 'aborted', ch.frameClass, gapCycle);
            this._cycleNoteAbort(channel, gapCycle);
            // [2026-08-08-015-ENGINE] ...and, on the control stream, remember it
            // for the beat-close VERDICT. This position is past the `seen`
            // exemption above, so it is a genuine loss and not a reorder.
            this._serverAuthorityNoteLoss(channel, g, gapCycle);
        }
        flushRun(to);
    },

    _ledgerActiveCycleId() {
        try {
            if (typeof TurnPump !== 'undefined'
                    && TurnPump._state && TurnPump._state.currentCycleId) {
                return String(TurnPump._state.currentCycleId);
            }
        } catch (_e) { /* best-effort */ }
        return null;
    },

    _ledgerRecordResolution(channel, seq, resolution, frameClass, cycleId) {
        try {
            if (!window.__LEDGER_SHADOW || typeof window.__LEDGER_SHADOW !== 'object') {
                window.__LEDGER_SHADOW = {};
            }
            const sh = window.__LEDGER_SHADOW[channel] || (window.__LEDGER_SHADOW[channel] = {});
            if (!Array.isArray(sh.resolutions)) sh.resolutions = [];
            // [combat-validation-observability 1b] Stamp the cycle_id (the
            // {room}:NNN.PP join key) onto each resolution so the cycle rollup
            // (_ledgerCycleLedger) can group played/void/aborted per turn.phase.
            // Prefer the frame's own cycle_id (passed by the caller from the
            // message in hand); for an aborted gap with no frame, fall back to
            // the drain's notion of the active cycle. null when unknowable.
            let _cid = (cycleId === undefined || cycleId === null) ? null : String(cycleId);
            if (_cid === null) {
                try {
                    if (typeof TurnPump !== 'undefined'
                            && TurnPump._state && TurnPump._state.currentCycleId) {
                        _cid = String(TurnPump._state.currentCycleId);
                    }
                } catch (_e) { /* best-effort */ }
            }
            sh.resolutions.push({
                seq,
                resolution,                       // 'played' | 'void' | 'aborted'
                frame_class: frameClass || null,  // 'content' | 'lifecycle-control'
                cycle_id: _cid,                   // {room}:NNN.PP join key (name-space, NOT a UUID)
            });
            if (sh.resolutions.length > 500) {
                sh.resolutions.splice(0, sh.resolutions.length - 500);
            }
        } catch (e) { /* shadow must never disrupt admission */ }
    },

    /**
     * [channel-delivery-ledger Step 6 — rewrite] Mark the active cycle's native
     * completion record ABORTED when a PARTICIPATING channel's frame was
     * force-released / resend-missed (a committed-but-undeliverable gap the
     * consumer advanced past). Only ch0 (channel 0) and the control channel
     * (ch9 turn lifecycle) participate in the GAMEPLAY unlock, so only those
     * abort the cycle — a lost ch1 / ch2 / ch3 frame must NEVER block the
     * unlock (ch1 is deferred-next-turn; ch2/ch3 are pre-barrier render rows).
     *
     * Runs on GameServerConnection (the abort paths are GSC methods) but mutates
     * the TurnPump record via an explicit `TurnPump.` reference (the cross-object
     * pattern the wiring guard pins). The native predicate reads `!aborted` as
     * the sole completion authority (6d). Best-effort + fail-soft.
     *
     * [2026-07-28-012-ENGINE] NOW CYCLE-SCOPED. `cycleId` is the coordinate the
     * abort belongs to, threaded from `_ledgerActiveCycleId()` at the release
     * site. Before this, the method took no coordinate at all and wrote
     * `aborted = true` onto WHATEVER record happened to be live — so a gap
     * belonging to a DEPARTED cycle could poison the beat the player is
     * currently waiting on, and did (observed: leg eae8ce87's abort rows carry
     * `void:000.00` and `processional-mouth:001.01` while the record they set
     * was the arrival beat's). THE COORDINATE LAW: a fact is only as
     * trustworthy as the coordinate it is keyed on.
     *
     * The attribution test is the pump's EXISTING two-bound rule (below the
     * beat cursor = a beat that already finished; below the floor = a phase
     * older than the one the player acted in). One rule, two bounds — not a
     * second, private notion of "does this belong to me".
     *
     * A refused abort is COUNTED and WARNED, never silently dropped: silent
     * absorption is the bug class this whole record exists to make visible.
     * `abortsElsewhere` surfaces in the stall-watchdog dump.
     */
    /**
     * [2026-08-08-015-ENGINE] Record a SERVER-AUTHORITY loss for later verdict.
     *
     * Called from the two places that know a control position is gone — the
     * force-release sweep and the resend-miss handler — beside `_cycleNoteAbort`
     * and under the same `seen` exemption, so a bootstrap frame the client
     * already dispatched is never counted as lost.
     *
     * ⚠️ THIS IS NOT A SECOND ABORT. `_cycleNoteAbort` decides whether a gap
     * blocks the ch0 unlock; this decides whether the player is TOLD a server
     * statement went missing. channels.md §3 keeps those two gates apart on
     * purpose, and they have been confused before. Nothing written here can
     * hold a beat.
     *
     * Only channel 9 is recorded: `server-authority` is a ch9 axis by
     * definition (client_funnel.md §1), and a content-channel loss already has
     * its own owed condition in channels.md §5.
     */
    _serverAuthorityNoteLoss(channel, seq, cycleId) {
        try {
            if (channel !== LEDGER_CONTROL_CHANNEL) return;
            if (typeof TurnPump === 'undefined' || !TurnPump._state) return;
            const s = TurnPump._state;
            if (!Array.isArray(s._serverAuthorityLosses)) {
                s._serverAuthorityLosses = [];
            }
            // Bounded. A pathological stream must not grow this without limit;
            // the verdict is per-beat and reads the COUNT, so an older entry
            // beyond the cap has already been represented by one that is still
            // here.
            if (s._serverAuthorityLosses.length >= 200) return;
            s._serverAuthorityLosses.push({
                seq,
                cycleId: (cycleId === undefined || cycleId === null)
                    ? null : String(cycleId),
            });
        } catch (_e) { /* accounting must never disrupt admission */ }
    },

    _cycleNoteAbort(channel, cycleId) {
        try {
            // [2026-08-03-002-PROMPT] ⚠️ CH4 IS DELIBERATELY ABSENT FROM THIS
            // TEST, and adding it would be a contract violation, not a fix.
            //
            // This gate is NOT ledger enrollment (that is
            // LEDGER_CONTENT_CHANNELS, where ch4 IS enrolled). It asks a
            // narrower question: does a gap on this channel ABORT the ch0
            // gameplay unlock? Only channels the unlock depends on may say yes.
            //
            // D1 of the ch4 delivery contract is that ch0 NEVER waits on ch4
            // ("i don't think we should hold ch0 calls for ch4 returns"). A ch4
            // in this set would let a lost dialogue line poison the beat the
            // player is waiting on — reintroducing the coupling the whole
            // channel split exists to remove, on the channel least able to
            // afford it. Ch4 still gets ordering, dedup and NACK/resend from its
            // LEDGER_CONTENT_CHANNELS membership; it simply cannot abort ch0.
            // READ OFF THE REGISTRY (`frame.md` P10). This was
            // `(channel === 0 || channel === LEDGER_CONTROL_CHANNEL)` inline --
            // the BLOCKING gate spelled at its own call site, where the next
            // channel's arrival would not find it. The registry's
            // `aborts_unlock` field is the same decision with one home; ch4 is
            // false there for the reason stated above, and a row is what
            // changes it, not an edit here.
            const participates =
                LEDGER_ABORT_PARTICIPATING_CHANNELS.has(channel);
            if (!participates) return;
            if (typeof TurnPump === 'undefined'
                    || !TurnPump._state || !TurnPump._state.cycleCompletion) {
                return;
            }
            const cc = TurnPump._state.cycleCompletion;
            const key = (cycleId === undefined || cycleId === null) ? null : String(cycleId);
            // An unknowable coordinate keeps today's behaviour: abort. Refusing
            // on `null` would weaken the no-deadlock guarantee on exactly the
            // frames we know least about.
            if (key && !TurnPump._cycleAbortAttributable(key)) {
                cc.attribution.abortsElsewhere =
                    (cc.attribution.abortsElsewhere || 0) + 1;
                console.warn(
                    `[ledger] ABORT REFUSED: ch${channel} gap at cycle=${key} is not `
                    + `attributable to the live beat (beat=${cc.beatId || 'unbound'} `
                    + `floor=${cc.beatFloor || 'none'} `
                    + `cursor=${TurnPump._state.beatCursor || 'none'}) — a departed `
                    + 'cycle\'s gap must not poison the beat in flight.'
                );
                return;
            }
            cc.aborted = true;
        } catch (_e) { /* best-effort */ }
    },

    /**
     * [channel-delivery-ledger Step 3] Tear down active admission state on
     * disconnect/cleanup. Cancels any outstanding grace timers (so a buffered
     * gap doesn't fire a force-release into a dead connection) and drops the
     * per-channel state. A subsequent connection re-baselines from its first
     * frame's stream_epoch (the phantom-gap fix), so no carry-over is needed.
     */
    _ledgerReset() {
        try {
            const all = this._ledgerChannels;
            if (all && typeof all === 'object') {
                // [Step 4d] Snapshot the per-channel contiguous watermark BEFORE
                // clearing so a WS-blip reconnect (page intact, this same object
                // graph alive) can ledger_resync against it. A fresh page never
                // has this snapshot, so it adopts the server handshake watermark
                // as baseline instead (no replay). Persisted on the manager
                // object across the reset (which only clears _ledgerChannels).
                const snapshot = {};
                for (const k of Object.keys(all)) {
                    this._ledgerCancelGrace(all[k]);
                    // [Step 4] also cancel the NACK + ACK timers so a buffered
                    // gap/pending-ACK never fires into a dead connection.
                    this._ledgerCancelNack(all[k]);
                    if (all[k].ackTimer && typeof clearTimeout === 'function') {
                        clearTimeout(all[k].ackTimer);
                    }
                    all[k].ackTimer = null;
                    if (all[k].baselined) {
                        snapshot[k] = all[k].last_contiguous_seq;
                    }
                }
                if (Object.keys(snapshot).length) {
                    this._ledgerLastWatermarks = snapshot;
                }
            }
        } catch (e) { /* best-effort */ }
        this._ledgerChannels = {};
        this._ledgerReleasing = false;
    },

    /**
     * [channel-delivery-ledger Step 4d] Apply the connect-time watermark
     * handshake from the `connected` frame's `ledger` block.
     *
     *   ledger = { stream_epoch, watermarks: { channel: highestSentSeq } }
     *
     * Two paths, distinguished by whether this is a reconnect (page + in-memory
     * state alive) vs a fresh page load:
     *
     *   FRESH PAGE (no prior watermark snapshot): adopt each server watermark as
     *   the channel's authoritative baseline (last_contiguous = watermark) for
     *   the handshake epoch and go FORWARD — do NOT replay history. This makes
     *   step-3's first-frame bootstrap server-authoritative and closes the
     *   phantom-gap note. The next live frame (watermark+1) is contiguous.
     *
     *   WS-BLIP RECONNECT (isReconnecting AND we have a prior snapshot): send
     *   `ledger_resync` with our per-channel watermarks; the server replays
     *   everything above each from its retain ring (re-stamped current epoch).
     *   Adopt our own snapshot as the baseline so the replayed frames are
     *   contiguous from where we left off.
     */
    _ledgerApplyHandshake(ledger) {
        try {
            if (!LEDGER_ACTIVE_ORDERING) return;
            if (!ledger || typeof ledger !== 'object') return;
            const watermarks = ledger.watermarks || {};
            const epoch = (typeof ledger.stream_epoch === 'number')
                ? ledger.stream_epoch : null;
            const snapshot = this._ledgerLastWatermarks;
            const isReconnect = !!this.state.isReconnecting && snapshot
                && Object.keys(snapshot).length > 0;

            if (isReconnect) {
                // Adopt our own pre-blip watermarks as the baseline so replayed
                // frames are contiguous, then ask the server to replay above them.
                for (const k of Object.keys(snapshot)) {
                    const channel = Number(k);
                    // [Step 5] Resync the control channel (9) too, not just
                    // content (0-3): a WS-blip mid-turn must replay above the
                    // last-seen turn_complete/phase_clock so the lifecycle resumes.
                    if (!LEDGER_CONTENT_CHANNELS.has(channel)
                        && channel !== LEDGER_CONTROL_CHANNEL) continue;
                    const ch = this._ledgerChannelState(channel);
                    ch.last_contiguous_seq = snapshot[k];
                    ch.stream_epoch = epoch;
                    ch.baselined = true;
                    ch._handshake_epoch = epoch;
                }
                const sent = this.sendDirectMessage({
                    type: 'ledger_resync',
                    watermarks: snapshot,
                    stream_epoch_prev: epoch,
                    timestamp: new Date().toISOString(),
                });
                this._ledgerShadowDecision(-1, 0, 'resync-sent',
                    { watermarks: snapshot, sent });
            } else {
                // Fresh page: adopt server watermarks as the forward baseline.
                for (const k of Object.keys(watermarks)) {
                    const channel = Number(k);
                    // [Step 5] Adopt the control channel (9) watermark too so a
                    // fresh page's first lifecycle-control frame (watermark+1) is
                    // contiguous instead of a giant phantom gap.
                    if (!LEDGER_CONTENT_CHANNELS.has(channel)
                        && channel !== LEDGER_CONTROL_CHANNEL) continue;
                    const wm = watermarks[k];
                    if (typeof wm !== 'number') continue;
                    const ch = this._ledgerChannelState(channel);
                    ch.last_contiguous_seq = wm;
                    ch.stream_epoch = epoch;
                    ch.baselined = true;
                    // Tag the epoch this authoritative baseline applies to so
                    // _ledgerAdmit's re-baseline honors it (max(watermark,seq-1))
                    // rather than blindly inferring seq-1 from the first frame.
                    ch._handshake_epoch = epoch;
                    this._ledgerShadowDecision(channel, wm, 'handshake-baseline',
                        { watermark: wm, stream_epoch: epoch });
                }
            }
            // Consume the snapshot — a subsequent disconnect re-snapshots.
            this._ledgerLastWatermarks = null;
        } catch (e) {
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                console.warn('[ledger] handshake apply skipped:', e);
            }
        }
    },

    _ledgerRecordUnfilled(channel, from, to) {
        try {
            if (!window.__LEDGER_SHADOW || typeof window.__LEDGER_SHADOW !== 'object') {
                window.__LEDGER_SHADOW = {};
            }
            const sh = window.__LEDGER_SHADOW[channel] || (window.__LEDGER_SHADOW[channel] = {});
            if (!Array.isArray(sh.unfilled)) sh.unfilled = [];
            sh.unfilled.push({ from, to });
        } catch (e) { /* best-effort */ }
    },

    // Handle messages from GameServer
    /**
     * [channel-delivery-ledger Step 1] Shadow-record ordering metadata.
     *
     * Records per-channel (last_seq, stream_epoch, last_contiguous_seq, gaps,
     * out_of_order, received_count) into window.__LEDGER_SHADOW for VP/dev
     * introspection. SHADOW MODE: observe only. Does NOT reorder, NACK,
     * block, or touch TurnPump._state / the reducer / any render path.
     * Reliability mechanisms (reorder/NACK/resend) arrive in later steps.
     *
     * Always-on (the structure is tiny); not gated behind
     * __VP_INSTRUMENTATION_ENABLED so a clean local run can be inspected
     * without arming the heavier pump-state instrumentation.
     */
    _ledgerShadowRecord(message) {
        try {
            if (!message || typeof message !== 'object') return;
            const seq = message.seq;
            // Unstamped frames (no seq) are ignored — fail-soft parity with
            // the server's fail-soft stamping. Step 1 must never throw here.
            if (typeof seq !== 'number') return;

            const channel = (typeof message.channel === 'number')
                ? message.channel : -1;
            const streamEpoch = (typeof message.stream_epoch === 'number')
                ? message.stream_epoch : null;
            // [channel-delivery-ledger Step 2] Frame kind. A `channel_void`
            // frame carries kind:'void' and OCCUPIES a content-channel seq —
            // it advances contiguity exactly like a content frame, but is
            // flagged so step 3 can see the position was satisfied by a void
            // (skip) rather than content (play). Any frame without an explicit
            // kind is treated as content.
            const kind = (message.type === 'channel_void' || message.kind === 'void')
                ? 'void' : 'content';

            if (!window.__LEDGER_SHADOW || typeof window.__LEDGER_SHADOW !== 'object') {
                window.__LEDGER_SHADOW = {};
            }
            const shadow = window.__LEDGER_SHADOW;

            let ch = shadow[channel];
            if (!ch) {
                ch = {
                    last_seq: 0,
                    stream_epoch: streamEpoch,
                    last_contiguous_seq: 0,
                    gaps: [],
                    out_of_order: [],
                    received_count: 0,
                    // [Step 2] seqs satisfied by an explicit void frame.
                    voids: [],
                    // [Step 2] kind of the most-recently recorded frame.
                    last_kind: null,
                };
                shadow[channel] = ch;
            }

            // New connection generation: a higher stream_epoch resets the
            // contiguity baseline for this channel (records the transition,
            // does not act on it).
            if (streamEpoch !== null && ch.stream_epoch !== null
                && streamEpoch > ch.stream_epoch) {
                ch.stream_epoch = streamEpoch;
                ch.last_contiguous_seq = 0;
            } else if (ch.stream_epoch === null) {
                ch.stream_epoch = streamEpoch;
            }

            // Backfill fields onto a channel object that may have been created
            // by an earlier record (defensive; the shadow is long-lived across
            // many frames AND across reconnects -- `window.__LEDGER_SHADOW`
            // outlives the socket).
            //
            // ⚠️ `gaps` and `out_of_order` USED TO BE MISSING FROM THIS LIST while
            // `voids` and `last_kind` were in it, and the two `.push` calls below
            // are unguarded. Observed live 2026-08-25 on a reconnecting session:
            //
            //   [ledger-shadow] record skipped (type=channel_void ch=1 seq=1):
            //     TypeError: Cannot read properties of undefined (reading 'push')
            //
            // The throw is caught, so it costs no playback -- but the record is
            // DROPPED, and it drops exactly the frames the ledger analysis needs
            // most: the ones straddling a reconnect, where seq restarts and
            // contiguity is the whole question ([2026-08-24-038-ENGINE]). A
            // measurement instrument with a hole at the moment of interest reads
            // as a clean stream. Backfill every array the recorder writes.
            if (!Array.isArray(ch.voids)) ch.voids = [];
            if (!Array.isArray(ch.gaps)) ch.gaps = [];
            if (!Array.isArray(ch.out_of_order)) ch.out_of_order = [];
            if (typeof ch.received_count !== 'number') ch.received_count = 0;
            if (typeof ch.last_seq !== 'number') ch.last_seq = 0;
            if (typeof ch.last_contiguous_seq !== 'number') ch.last_contiguous_seq = 0;
            if (ch.last_kind === undefined) ch.last_kind = null;

            ch.received_count += 1;
            ch.last_kind = kind;
            // A void OCCUPIES this seq position (advances contiguity below like
            // any content frame); record the seq it satisfied for step-3.
            if (kind === 'void') ch.voids.push(seq);

            if (seq > ch.last_contiguous_seq + 1) {
                // Gap: positions (last_contiguous+1 .. seq-1) missing so far.
                ch.gaps.push({
                    from: ch.last_contiguous_seq + 1,
                    to: seq - 1,
                    observed_at: seq,
                });
                ch.last_contiguous_seq = seq;
            } else if (seq <= ch.last_contiguous_seq) {
                // Out-of-order / duplicate (a later-step resend would land here).
                ch.out_of_order.push({ seq, prev_contiguous: ch.last_contiguous_seq });
            } else {
                // Exactly contiguous.
                ch.last_contiguous_seq = seq;
            }

            if (seq > ch.last_seq) ch.last_seq = seq;
        } catch (e) {
            // Shadow recording must never disrupt message handling.
            // [Step 5 — Finding #3] A non-Error throw (e.g. a bare `{}` rejected
            // value on the void/nack path) logs as an opaque empty object and is
            // useless for triage. Normalize to a message string + the frame
            // type/channel/seq context so the WARN is actionable instead of `{}`.
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                const reason = (e instanceof Error)
                    ? (e.stack || e.message)
                    : (typeof e === 'object' ? JSON.stringify(e) : String(e));
                const ctx = (message && typeof message === 'object')
                    ? `type=${message.type} ch=${message.channel} seq=${message.seq}`
                    : 'no-message';
                console.warn(`[ledger-shadow] record skipped (${ctx}):`, reason || '<empty>');
            }
        }
    },

    handleGameServerMessage(message) {
        // The media origin (platform seam): inside the Discord Activity, frame
        // media URLs are rewritten onto the URL-mapped /media prefix. The web
        // build publishes no rehoster, and its frames pass untouched.
        const rehostMedia = typeof window !== 'undefined' ? window.__REAVERS_REHOST_MEDIA__ : undefined;
        if (rehostMedia) message = rehostMedia(message);

        // Connection-liveness: every received frame counts as a heartbeat,
        // regardless of admission outcome (a dropped dup still proves the link
        // is alive).
        this.state.lastHeartbeat = Date.now();

        // [2026-07-02-007] client_logs_ack is a control reply for a browser
        // /dump-logs routed over the WS (see GameDiagnostics.dumpTestLogs). It
        // is NOT a renderable/pump frame: consume it here (deliver to the
        // waiting one-shot resolver) before any ledger shadow/admission runs,
        // never dispatch it to the render switch.
        if (message && message.type === 'client_logs_ack') {
            this._resolveClientLogsAck(message);
            return;
        }

        // [2026-07-27-002-QASTI] vp_capture_ack is the server's per-frame reply
        // for a screen capture routed over the WS. Like client_logs_ack it is a
        // control reply, NOT a renderable/pump frame -- consume it here before
        // any ledger shadow/admission runs, never dispatch it to the render
        // switch. It feeds the probe's delivered/failed counters, which is the
        // only thing that makes a silently-dropping probe observable.
        if (message && message.type === 'vp_capture_ack') {
            this._recordVpCaptureAck(message);
            return;
        }

        // [channel-delivery-ledger Step 3] Force-released frames re-enter here
        // already-admitted (they're contiguous now); skip shadow + admission and
        // dispatch directly to avoid a re-admit round-trip / shadow double-count.
        if (this._ledgerReleasing) {
            this._dispatchGameServerMessage(message);
            return;
        }

        // [channel-delivery-ledger Step 4c] A `ledger_resend_miss` is a control
        // reply (NOT a renderable frame): the server can't resend the NACKed
        // seq (pruned/absent), so force-release that position to preserve the
        // no-deadlock guarantee. Consumed here; never dispatched to the switch.
        if (message && message.type === 'ledger_resend_miss') {
            this._ledgerResendMiss(message.channel, message.seq);
            return;
        }

        // [channel-delivery-ledger Step 1] Shadow-record the frame's ordering
        // metadata (channel/seq/stream_epoch) for introspection. Pure
        // observation — records gaps + out-of-order but does NOT reorder,
        // NACK, block, or alter playback. The pump reducer never reads this.
        // See .claude_specs/engine/channel-delivery-ledger/step-1-seq-stamping.md
        this._ledgerShadowRecord(message);

        // [channel-delivery-ledger Step 3] Active admission. Only seq-bearing
        // frames on content channels 0-3 are gated; everything else (no seq,
        // channel 9 control, non-pump messages) passes straight through. The
        // shadow recorder above keeps running regardless. Fail-soft: any throw
        // (or LEDGER_ACTIVE_ORDERING=false) -> dispatch the frame directly
        // (today's behavior). On admit, dispatch the frame, then each drained
        // successor in seq order through the same switch. On NOT-admit the frame
        // was buffered (out-of-order) or dropped (dup / stale-epoch) -> return
        // without dispatching.
        let decision;
        try {
            decision = this._ledgerAdmit(message);
        } catch (e) {
            if (window.__VP_INSTRUMENTATION_ENABLED) {
                console.warn('[ledger-admit] fail-soft (direct dispatch):', e);
            }
            decision = { admit: true, release: [] };
        }
        if (!decision || decision.admit !== false) {
            this._dispatchGameServerMessage(message);
        } else {
            return; // buffered or dropped — do not dispatch
        }
        if (decision.release && decision.release.length) {
            for (const released of decision.release) {
                this._dispatchGameServerMessage(released);
            }
        }
    },

    /**
     * [channel-delivery-ledger Step 3] The wire-message type switch. Split out
     * of handleGameServerMessage so the admission layer can route both the
     * admitted frame and any reorder-buffer releases through the IDENTICAL
     * dispatch. Behavior of every case is unchanged from before step 3.
     */
    _dispatchGameServerMessage(message) {
        const sessionId = this.state.sessionId || 'unknown';

        console.log(`📬 [${sessionId}] Handling message type: ${message.type}`);

        switch (message.type) {
            case 'connected':
                // Stateless GameServer connection response
                console.log(`✅ [${sessionId}] Connected to stateless GameServer`);
                this.handleQueuePromotion();
                this.handleStatelessConnection(message);
                break;

            case 'queue_position':
                // [fnf queue/warming UX] The admission controller is HOLDING us on
                // this same websocket while GANDALF's GPU pods warm from
                // scale-to-zero. Frame: {position, total_waiting, eta_seconds,
                // message, world_id}. Sent on hold AND on every position change.
                // There is NO dedicated promotion frame -- admission resumes the
                // normal connect flow, so the eventual `connected` frame above is
                // what tears the queue surface down (handleQueuePromotion).
                console.log(
                    `[${sessionId}] Queue position: ${message.position}/${message.total_waiting} ` +
                    `eta=${message.eta_seconds}s msg="${message.message || ''}"`
                );
                this.handleQueuePosition(message);
                break;

            case 'queue_admitted':
                // [2026-08-06-006-QASTI] CHANNEL REGISTRY LAW. The server has
                // emitted this on every held entry since the admission gate
                // landed; this client had no arm, so it hit `default:` and the
                // one moment the warming surface exists to communicate -- "you
                // are through" -- was the moment it went mute and started
                // showing a position the player no longer occupied.
                //
                // This is the END OF THE LINE, not the opening of the world.
                // `connected` (above) still owns promotion + teardown.
                console.log(
                    `[${sessionId}] Queue admitted -- the line is over; ` +
                    `awaiting 'connected' to open the world`
                );
                this.handleQueueAdmitted(message);
                break;

            case 'auth_success':
                console.log(`✅ [${sessionId}] Authentication successful`);
                this.logMessage('Authentication successful');
                // isInGame is FSM-derived; auth_success alone does not change mode
                break;

            case 'auth_failure':
                console.error(`❌ [${sessionId}] Authentication failed: ${message.reason}`);
                this.logMessage(`Authentication failed: ${message.reason}`);
                this.disconnect();
                break;

            case 'world_state':
                this.logMessage('World state loaded');
                this.handleWorldState(message.data);
                break;

            case 'game_message':
                // [2026-08-08-007-ENGINE / client_funnel.md section 5 leak 2]
                // DORMANT. No producer for `game_message` exists anywhere in
                // services/ or game_server/. This arm rendered arbitrary server
                // prose with no channel, no cycle and no ledger enrolment - the
                // ch9 leak's exact shape, waiting for a producer.
                //
                // It REFUSES rather than being deleted outright, because the
                // arrival of a producer is NEWS: a silent drop here would be
                // indistinguishable from a frame that was never sent.
                this._refuseDormantProse('game_message', message.text);
                break;

            case 'game_response':
                // Legacy response format - log and ignore
                console.log(`[${sessionId}] Legacy game_response received, ignored`);
                break;

            case 'turn_accepted':
                // Server acknowledged the action.
                console.log(`[${sessionId}] Turn accepted: ${message.action}, command=${message.command_id}`);
                TurnPump.event('turn_accepted', { action: message.action, command_id: message.command_id });
                break;

            case 'frame': {
                // P1/P2: THE ONE DOOR for an enveloped frame. It is checked
                // against the generated schema the server minted it with; a
                // frame that fails is named and never rendered.
                const refused = globalThis.FrameRegistry.validateFrame(message);
                if (refused) {
                    console.error(`[${sessionId}] frame.refused kind=${message.kind} `
                        + `seq=${message.seq}: ${refused}`, message);
                    break;
                }
                const _st = message.kind === 'clock' ? message.payload.prompt
                    : message.kind === 'prompt-state' ? message.payload : null;
                console.log(
                    `[${sessionId}] frame kind=${message.kind} seq=${message.seq} ` +
                    `cycle=${message.coordinate.cycle_id || 'none'}` +
                    (_st ? ` state=${_st.state} reason=${_st.reason} ` +
                        `window=${_st.window ? _st.window.duration_seconds + 's' : 'none'}` : '')
                );
                const _arm = this._ENVELOPE_ARMS[message.kind];
                if (_arm === 'pump') {
                    TurnPump.event('frame_received', message);
                } else if (_arm === 'board') {
                    this.handleEpisodeBoard(message);
                } else {
                    console.error(`[${sessionId}] frame kind=${message.kind} has no client arm `
                        + `(a registry row with no arm here is invisible)`, message);
                }
                break;
            }

            case 'action_result':
                // Handle action result from GameServer (Phase 4)
                console.log(`[${sessionId}] Action result received:`, message.result);
                if (typeof logSync === 'function') logSync('action_result');
                this.handleActionResult(message);
                break;

            case 'action_refused':
                // [render-honesty 3a §3] The Turn Manager gate refused this
                // command (e.g. no-skill-start). Render a visible, mechanical
                // system line so the player learns their command was rejected
                // WITHIN this beat instead of staring at a silent 91s wedge.
                // Render-ONLY: the refused command's cycle completes through the
                // existing machinery (turn_complete unlocks the prompt; the
                // gated empty_phase ch0 void flips the ch0 latch when no scene
                // is coming). This handler must NOT drive the pump — doing so
                // could pre-void a ch0 that IS on its way for a spectated scene.
                console.log(
                    `[${sessionId}] Action refused: action=${message.action || ''} ` +
                    `reason=${message.refusal_reason || 'unknown'} ` +
                    `turn=${message.turn_number} phase=${message.phase_number}`
                );
                this._renderActionRefused(message);
                break;

            case 'action_notice':
                // [2026-08-08-014-ENGINE] THE COMMAND HAPPENED, AND ONE THING
                // ABOUT IT DID NOT LAND. Distinct from `action_refused`, which
                // says the command did NOT happen — collapsing the two would
                // tell a player who just spoke aloud that they were refused.
                //
                // Measured on prod `ch0instr-1b26151e`: `say hello to grimwald
                // the unresolvable` delivered a ch4 frame with `listener: null`
                // and a ch0 reading "your words hanging for a moment in the
                // smoke-thick air" — with nothing anywhere saying the person
                // named was not there. This arm is the half of that fix that
                // reaches the screen; without it the server work is a no-op
                // (channels.md section 3: a channel/type with no client arm is
                // not an error, it is invisible).
                //
                // Render-ONLY, exactly like the refusal arm: it must NOT drive
                // the pump. The utterance is still on the narration surface, so
                // a real ch0 IS coming for this beat.
                console.log(
                    `[${sessionId}] Action notice: action=${message.action || ''} ` +
                    `reason=${message.notice_reason || 'unknown'} ` +
                    `detail=${message.notice_detail || ''} ` +
                    `turn=${message.turn_number} phase=${message.phase_number}`
                );
                this._renderActionNotice(message);
                break;

            case 'ch4_owed':
                // channels.md section 5: a player's utterance was ACCEPTED, so a
                // ch4 line is owed to them. Rides channel 4, which never aborts
                // the unlock, and puts NOTHING on the screen: it enrols an
                // obligation judged in seconds, or settles at once against a
                // line that already arrived.
                console.log(
                    `[${sessionId}] ch4 OWED: speaker=${message.speaker || '?'} ` +
                    `cycle=${message.cycle_id || 'none'} ` +
                    `turn=${message.turn_number} phase=${message.phase_number}`
                );
                this._enrolCh4Owed(message);
                break;

            // [W1 P2 combat_log retirement, 2026-07-09] The legacy
            // `combat_log` wire message type is RETIRED. A stale server
            // still sending it degrades gracefully: it falls through to the
            // non-throwing default branch (log-and-drop). ch2 dice mechanics
            // ride `resolution_event`; lifecycle rides `interval_event`.

            case 'interval_event':
                // [Phase 1 refactor] Per PHASE1-WIRE-CONTRACT.md, interval_event
                // now carries LIFECYCLE-ONLY entries (ch3 stream); resolution
                // mechanics moved to the new `resolution_event` wire message.
                // Entries shape: {actor, skill?, state}. Pre-sorted by server
                // (initiative DESC, tiebreaker DESC). TurnPump pushes the
                // payload into `channels.ch3_lifecycle` and drains it as the
                // first step of drain priority.
                console.log(
                    `[${sessionId}] Interval event: turn=${message.turn_number} phase=${message.phase_number} ` +
                    `i=${message.interval_number} audit=${message.audit_key || 'none'} ` +
                    `entries=${(message.entries || []).length}`
                );
                TurnPump.event('interval_event', message);
                break;

            case 'resolution_event':
                // [Phase 1 refactor] Per PHASE1-WIRE-CONTRACT.md, the new
                // mechanics-only wire stream (ch2). One message per resolution.
                // Payload: {type, room_name, turn_number, phase_number,
                //           interval_number, audit_key, actor, skill, target,
                //           initiative, roll, step_outcome}.
                // TurnPump pushes into `channels.ch2_mechanics` and drains
                // synchronously after ch3 in drain-priority order. Phase 6
                // will swap the sync drain for an animated playback.
                console.log(
                    `[${sessionId}] Resolution event: turn=${message.turn_number} phase=${message.phase_number} ` +
                    `i=${message.interval_number} actor=${message.actor} skill=${message.skill} ` +
                    `audit=${message.audit_key || 'none'}`
                );
                TurnPump.event('resolution_event', message);
                break;

            case 'effect_event':
                // [2026-09-01-005-ENGINE] ch2 mechanics: what a STATUS did this
                // beat. One message per beat carrying every bearer's rows that
                // actually moved something (HP or a derived stat). Payload:
                // {type, room_name, turn_number, phase_number, interval_number,
                //  audit_key, cycle_id, effects:[{bearer, bearer_display,
                //  effect_name, route, hp_delta, stat, stat_delta, ...}]}.
                console.log(
                    `[${sessionId}] Effect event: turn=${message.turn_number} phase=${message.phase_number} ` +
                    `i=${message.interval_number} audit=${message.audit_key || 'none'} ` +
                    `effects=${(message.effects || []).length}`
                );
                TurnPump.event('effect_event', message);
                break;

            case 'phase_settled':
                // [Wave2 T2.1] Phase-close barrier message. Arrives AFTER all
                // `interval_event`s for the phase have flushed, BEFORE ch0
                // narration for the phase is dispatched. See §5 ordering
                // guarantees in _INTERVAL_WIRING_MODEL.md.
                console.log(
                    `[${sessionId}] Phase settled: turn=${message.turn_number} phase=${message.phase_number} ` +
                    `had_content=${message.phase_had_content !== false} ` +
                    `resolved_intervals=[${(message.resolved_intervals || []).join(',')}]`
                );
                TurnPump.event('phase_settled', message);
                break;

            case 'channel_void': {
                // The void (`plug_core.frame.build_void_frame`). The server
                // stamps it on the CONTENT channel it voids (0 = ch0 scene,
                // 1 = ch1 commentary) so it occupies a seq, interleaved with that
                // channel's real narration. It translates to the pump's
                // `ch1_missing` / `ch0_missing` drain events, which discharge
                // the channel's latch.
                // The void's seq is already shadow-recorded with kind:'void' by
                // _ledgerShadowRecord above (advancing contiguity).
                const voidChannel = message.channel;
                console.log(
                    `[${sessionId}] channel_void: channel=${voidChannel} ` +
                    `class=${message.void_class || 'unspecified'} ` +
                    `reason=${message.reason || 'unspecified'} ` +
                    `seq=${message.seq != null ? message.seq : 'none'} ` +
                    `cycle=${message.cycle_id || 'none'}`
                );
                // [render-honesty 3a §4] A FAILURE-class void renders a visible
                // degradation line so the player SEES the render fault instead
                // of byte-identical silence; an ABSENCE-class void keeps today's
                // behavior (latch flip only, no output). This is purely
                // ADDITIVE — the latch/drain flip below is UNCHANGED for both
                // classes. Deduped per cycle+channel so a co-incident second
                // void (e.g. narrate_failed from the phase-result relay AND an
                // empty_phase void from the phase_settled relay) renders once.
                if (message.void_class === 'failure') {
                    this._renderVoidDegradation(voidChannel, message);
                }
                if (voidChannel === 1) {
                    TurnPump.event('ch1_missing', message);
                } else {
                    // channel 0 (and any unexpected content channel) -> ch0 latch.
                    TurnPump.event('ch0_missing', message);
                }
                break;
            }

            case 'player_joined':
                // [2026-08-08-007-ENGINE] DORMANT - no producer. See game_message.
                this._refuseDormantProse('player_joined',
                    `${message.player_name} joined the world`);
                break;

            case 'player_left':
                // [2026-08-08-007-ENGINE] DORMANT - no producer. See game_message.
                this._refuseDormantProse('player_left',
                    `${message.player_name} left the world`);
                break;

            case 'world_event':
                // [2026-08-08-007-ENGINE] DORMANT - no producer. See game_message.
                // The most dangerous of the four: `description` is a free
                // prose field, so a producer appearing would put unaccounted
                // narrative straight on screen.
                this._refuseDormantProse('world_event', message.description);
                break;

            case 'heartbeat':
            case 'pong':
                // Server heartbeat/pong response - connection is alive
                console.log(`💓 [${sessionId}] Heartbeat/pong received from GameServer`);
                break;

            case 'session_superseded': {
                // [2026-07-24-005-QASTI] One live session per uid is GLOBAL
                // across all worlds. This account became active in another
                // session (another device, or a different world), which
                // superseded this one. The server refused a command that
                // arrived on this now-foreign controls edge rather than route
                // it into the other session's vessel. Present the clean
                // "taken over elsewhere" sign-off — NEVER the post-death
                // SELECTION screen, and NEVER a raw crash line. Character and
                // world state live in Mongo, independent of this socket.
                //
                // [2026-07-29-003-ENGINE] NAME THE OWNER. An unqualified
                // "active elsewhere" is unfalsifiable: on 2026-07-29 a dead,
                // HELD session held the account and the player — who was not
                // playing anywhere else — had no way to tell that from a
                // genuine second device. The server now sends an `owner`
                // coordinate (world / actor / gameserver / state); render it.
                // `state: 'DISCONNECTED'` is the ghost tell.
                const supMsg = message.message ||
                    'This account is now active in another session.';
                const owner = message.owner || {};
                console.warn(`[${sessionId}] session superseded:`, supMsg, owner);
                this.logMessage('');
                this.logMessage('-------------------------------------------');
                this.logMessage('  This account is now active elsewhere.');
                if (owner.world) {
                    this.logMessage(`  World:     ${owner.world}`);
                }
                if (owner.actor) {
                    this.logMessage(`  Character: ${owner.actor}`);
                }
                if (owner.state === 'DISCONNECTED') {
                    this.logMessage('  That session is DISCONNECTED — if that');
                    this.logMessage('  was you, it will release shortly.');
                }
                this.logMessage('  This play session has ended here.');
                this.logMessage('  Your character and story are safe.');
                this.logMessage('-------------------------------------------');
                this.logMessage('');
                this.disconnect();
                break;
            }

            case 'error': {
                // The GameServer sends human-readable error text in `message`
                // (the convention used everywhere it emits a `type:'error'`
                // frame, and by `gandalf_error`). Historically this handler
                // read `message.error`, which is never populated -- so every
                // server error rendered as "undefined" and the real text was
                // invisible (ticket 2026-05-25-002-ENGINE Defect 3). Read
                // `message.message`, fall back to legacy `message.error`.
                const errorText = message.message || message.error || 'Unknown error';
                const isFatal = message.fatal === true;
                console.error(`❌ [${sessionId}] GameServer error:`, errorText, `fatal: ${isFatal}`);
                // One-session-per-player kick: when the same player connects from
                // another device, the server (networking.py) boots the older socket
                // with {message:'Replaced by new connection', fatal:true}. This is
                // intended final behavior (last-connection-wins); present it as a
                // clear, intentional sign-off, NOT a raw "GameServer error: ..." line
                // (which reads as a crash to the booted user). No data is at risk --
                // character/world state lives in Mongo, independent of the socket.
                if (isFatal && errorText === 'Replaced by new connection') {
                    this.logMessage('');
                    this.logMessage('-------------------------------------------');
                    this.logMessage('  You have signed in on another device.');
                    this.logMessage('  This play session has ended here.');
                    this.logMessage('  Your character and story are safe.');
                    this.logMessage('-------------------------------------------');
                    this.logMessage('');
                    this.disconnect();
                    break;
                }
                this.logMessage(`GameServer error: ${errorText}`);
                if (isFatal) {
                    this.disconnect();
                }
                break;
            }

            case 'creation_flow':
                // Handle creation flow messages (Phase 3.1.3a)
                console.log(`🎭 [${sessionId}] Creation flow step: ${message.step}`);
                this.handleCreationFlow(message);
                break;

            case 'spawn':
                // Handle spawn message (character created and spawned)
                console.log(`[${sessionId}] Character spawned`);
                this.handleSpawn(message);
                break;

            case 'narration':
                // Handle narration from GameServer (v25 consumer-pull delivery)
                console.log(`[${sessionId}] Narration message received`);
                this.handleNarration(message);
                break;

            case 'dialogue':
                // [2026-08-03-002-PROMPT] Ch4 — THE DIALOGUE ARM.
                //
                // This case is the whole reason client and server had to land
                // together. Before it existed, a ch4 frame reached this switch
                // and fell to `default:` — generated, dispatched, invisible, no
                // error: the exact symptom the ch4 build set out to fix,
                // reproduced by shipping only half of it.
                //
                // It is its own `type` rather than `narration` on channel 4 by
                // design: the narration handler slots anything that is not ch1
                // into the ch0 slot, so a dialogue frame would have OVERWRITTEN
                // the scene the player was reading.
                this.handleDialogue(message);
                break;

            case 'spawn_complete':
                // Server signals all spawn-related async work is done.
                // Only set the flag -- _checkSpawnGate() is the single decision point.
                console.log(`[${sessionId}] Spawn complete signal received`);
                this._spawnServerDone = true;
                this._checkSpawnGate();
                break;

            case 'turn_complete':
                // The discard RACES the clock, so it carries the epoch guard
                // itself. phase_clock and turn_complete are separate cases with
                // no ordering guarantee; the documented ~0.8s hazard has
                // turn_complete landing FIRST; and a bundled `next_phase_clock`
                // rides INSIDE this frame and is dispatched BELOW the discard —
                // so discarding a regressed turn_complete would take its own
                // clock down with it and the reset would never fire at all.
                TurnPump._noteEpochRegression(message, 'turn_complete');
                // Drop a turn_complete belonging to a beat that already
                // finished — the pump's one cursor rule, not a private filter.
                if (TurnPump._isPriorBeatFrame(message)) {
                    console.log(`[${sessionId}] Discarding turn_complete from a finished beat (cycle ${message.cycle_id} <= cursor ${TurnPump._state.beatCursor})`);
                    break;
                }
                console.log(`[${sessionId}] Turn complete (server done)`);
                if (typeof logSync === 'function') logSync('turn_complete');
                TurnPump.event('server_done');
                break;

            case 'turn_ready':
                // Legacy compatibility: treat as turn_complete
                console.log(`[${sessionId}] Legacy turn_ready -> treating as turn_complete`);
                TurnPump.event('server_done');
                break;

            case 'room_state':
                console.log(`[${sessionId}] Room state received: ${message.room_name} turn=${message.turn_number} phase=${message.phase?.status}`);
                this.handleRoomState(message);
                break;

            case 'transform_reveal': {
                /* [2026-09-01-019-ENGINE] A room member's form TRANSITIONED
                 * and the server resolved authored reveal art (a frame only
                 * ships when art exists — the no-asset case never reaches the
                 * wire). The render path is the arrival splash's, whole:
                 * same funnel write, same placeholder, same honest load/error
                 * logging, same `data-canon-pending` stamp (the open
                 * image-as-content canon question is inherited, not resolved).
                 *
                 * ⚠️ ITS OWN DEDUPE CURSOR. The splash cursor keys renders on
                 * slug change; sharing the POI cursor would make a reveal
                 * re-trigger the POI splash on the next same-POI walk. The
                 * reveal moment is server-detected (a transition, never
                 * state), so this cursor only guards consecutive repeats. */
                console.log(
                    `[${sessionId}] transform_reveal: ` +
                    `${message.character_name} -> ${message.form_name}`
                );
                this._renderArrivalImage(message, '_lastTransformRevealSlug');
                break;
            }

            case 'spend_result': {
                /* [2026-08-15-001-GDD] T-9/T-10. THE CONFIRM BUTTON'S ANSWER.
                 *
                 * ⚠️ THIS ARM IS HALF THE FEATURE, NOT PLUMBING. The server
                 * answers every `spend_attributes` — success, refusal, or a
                 * degraded partial — and an answer with no arm here is a NO-OP,
                 * never an error. The player would press confirm, watch
                 * "Spending..." sit there forever, and press it again against a
                 * bank that may already have been debited. That is THE CHANNEL
                 * REGISTRY LAW's shape one layer down: producer shipped,
                 * payload shipped, arm absent.
                 *
                 * The render lives on HudScreens because the surface that asked
                 * is the surface that must answer — the refusal belongs beside
                 * the rows the player was pressing, not in the scroll box. */
                console.log(
                    `[${sessionId}] spend_result ok=${message.ok} ` +
                    `code=${message.code || '-'} ` +
                    `allocations=${JSON.stringify(message.allocations || null)}`
                );
                if (typeof HudScreens !== 'undefined'
                        && typeof HudScreens.onSpendResult === 'function') {
                    HudScreens.onSpendResult(message, document);
                } else {
                    /* LOUD. A spend that COMMITTED and cannot be rendered is
                     * worse than one that failed: the player's points are gone
                     * and the screen still shows them banked. */
                    console.error(
                        `[${sessionId}] spend_result arrived (ok=${message.ok}) ` +
                        'but HudScreens has no `onSpendResult` — the answer ' +
                        'cannot be rendered. Load-order or version mismatch ' +
                        'between hud-screens.js and gameserver-connection.js.'
                    );
                }
                break;
            }

            case 'sound_ack': {
                // [2026-07-27-003-QASTI BUG 4] The server ACKs every `set_sound`
                // (networking.py). The client had no case for it, so every ack
                // fell through to `Unknown GameServer message type` — noise that
                // reads like a protocol defect and buries real unknowns.
                //
                // It is also the ONE authoritative statement of the server-side
                // pref, so we reconcile: if the server's view disagrees with the
                // local persisted pref, say so LOUDLY. A silent disagreement here
                // means the client mutes/unmutes against a server that is doing
                // the opposite with include_voice.
                const serverSoundOn = String(message.sound || 'on').toLowerCase() !== 'off';
                // Voice ON refused: no voiced slot was free, so the server kept
                // this seat text. Agree with it and say why; never re-send.
                if (message.refused) {
                    if (typeof SoundToggle !== 'undefined'
                        && typeof SoundToggle.refuseOn === 'function') {
                        SoundToggle.refuseOn();
                    }
                    if (message.message) this.logMessage(message.message);
                    console.log(`[${sessionId}] sound_ack: voice ON refused (${message.refused})`);
                    break;
                }
                let localSoundOn = serverSoundOn;
                try {
                    if (typeof SoundToggle !== 'undefined'
                        && typeof SoundToggle.isSoundOn === 'function') {
                        localSoundOn = SoundToggle.isSoundOn() !== false;
                    }
                } catch (_e) { /* unreadable pref -> no divergence claim */ }
                console.log(
                    `[${sessionId}] sound_ack: server=${serverSoundOn ? 'on' : 'off'} ` +
                    `local=${localSoundOn ? 'on' : 'off'}`
                );
                if (serverSoundOn !== localSoundOn) {
                    console.warn(
                        `[${sessionId}] sound pref DIVERGED: server=${serverSoundOn ? 'on' : 'off'} ` +
                        `local=${localSoundOn ? 'on' : 'off'} — the server's include_voice ` +
                        'decision will not match what this client plays'
                    );
                    this._emitVoiceEvent('warn', {
                        event: 'sound_pref_diverged',
                        server_sound_on: serverSoundOn,
                        local_sound_on: localSoundOn,
                    });
                }
                break;
            }

            case 'gandalf_error':
                // GANDALF failure is an error state, not silent.
                console.warn(`[${sessionId}] GANDALF error:`, message.message);
                this.logMessage(`[System] ${message.message || 'Narration service error'}`);
                TurnPump.event('error', { message: message.message });
                break;

            default:
                // [2026-08-03-002-PROMPT] Raised log -> error. THIS is the line
                // a server-only ch4 would have hit: a frame the server built,
                // stamped, ledgered and delivered, landing here and vanishing
                // with a grey console.log among thousands. An unhandled wire
                // type is always a client/server contract mismatch — it is
                // never routine — and the whole ch4 build exists because that
                // class of silence cost a feature nobody could see.
                console.error(`❓ [${sessionId}] UNHANDLED GameServer message type: '${message.type}' — the server sent it, this client has no arm for it, so it did nothing and reported nothing. This is a contract mismatch, not noise.`, message);
        }
    },

    // Handle world state data
    handleWorldState(worldData) {
        if (worldData.world_name) {
            this.logMessage(`World: ${worldData.world_name}`);
        }

        if (worldData.connected_players !== undefined) {
            this.logMessage(`Players connected: ${worldData.connected_players}`);
        }
    },

    // Handle action result from GameServer.
    // action_result carries mechanical data. Narration arrives separately via play queue.
    // Input unlocks when ch0 playback finishes (frontend controls input unlock).
    handleActionResult(message) {
        const sessionId = this.state.sessionId || 'unknown';

        // Same epoch guard as the turn_complete discard, for the same reason:
        // action_result is BOOTSTRAP-class (dispatched immediately, never held
        // behind a control hole), so it can reach this discard before the
        // regressed epoch's clock does.
        TurnPump._noteEpochRegression(message, 'action_result');
        // Drop an action_result belonging to a beat that already finished —
        // the same cursor rule the pump's ingress classifier applies.
        if (TurnPump._isPriorBeatFrame(message)) {
            console.log(`[${sessionId}] Discarding action_result from a finished beat (cycle ${message.cycle_id} <= cursor ${TurnPump._state.beatCursor})`);
            return;
        }

        if (!message.result) {
            console.warn(`[${sessionId}] Action result message has no result field`);
            // Stay in GAMEPLAY -- TurnPump handles turn completion
            return;
        }

        const result = message.result;

        // Log action type for debugging
        if (result.action_type) {
            console.log(`[${sessionId}] Action type: ${result.action_type}, success: ${result.success}`);
        }

        // Idle injection display: ClockLine._injectIdle() now handles rendering
        // the "] idle" line when the clock expires. No DOM insertion needed here.

        // [2026-08-08-007-ENGINE / client_funnel.md section 5 leak 1] An error is
        // a CONTROL-PLANE FACT the server asserts, so it renders - as
        // server-authority, on the same sink as the refusal line, carrying no
        // channel. What changed is that it is no longer ANONYMOUS: it used to go
        // through `logMessage` as `.message ai-response`, byte-identical to a
        // /debug dump and to LLM prose, and it was the one arm here that was
        // never even mode-gated.
        //
        // It MUST keep rendering. Suppressing it is the silent wedge the
        // server-authority axis exists to prevent.
        if (!result.success && result.error) {
            this._renderSystemNotice(String(result.error), 'action-error');
            // Stay in GAMEPLAY -- TurnPump handles turn completion
            return;
        }

        // In GAMEPLAY/SPAWNING, narration arrives via WebSocket play queue (ch0/ch1).
        // Suppress raw action_result text to avoid duplicating what narration covers.
        // Only show structured data as fallback if no narration is expected.
        const narrationExpected = (TerminalFSM.mode === 'GAMEPLAY' || TerminalFSM.mode === 'SPAWNING');

        // Check for batched action errors in phase-result format.
        // Top-level result.success is true for the phase, but individual actions
        // may have failed (NLU crash, skill error, etc.). Log for diagnostics --
        // the TurnPump no-ch0 recovery path will show a fallback message if
        // the backend also fails to synthesize ch0.
        if (result.actions && narrationExpected) {
            this._surfaceOwnBuiltinRows(result.actions);
            for (const action of result.actions) {
                const execResult = action.execution_result;
                if (execResult && execResult.success === false) {
                    // [Thread 0 / T0-B] A row nobody submitted is not an error
                    // — not on the screen (the gate inside
                    // `_surfaceNpcActionFailure`) and not in the console
                    // either. The same loudness asymmetry ai_service already
                    // ruled on for this exact row: alarming on a correct
                    // outcome is how the channel gets filtered and the real
                    // failures go dark with it. Still LOGGED, at log level, so
                    // the beat is accounted for and never merely absent.
                    if (this._isEngineTimeoutSubstitution(action)) {
                        console.log(`[${sessionId}] No submission this beat for ${action.character_name || 'unknown'} (engine clock substitution) — not an error, nothing rendered`);
                        continue;
                    }
                    console.warn(`[${sessionId}] Batched action error: ${action.character_name || 'unknown'} - ${execResult.error || 'unknown error'}`);
                    this._surfaceNpcActionFailure(action, execResult);
                }
            }
        }

        /* [2026-08-08-007-ENGINE / client_funnel.md section 5 leak 1] HIGHEST RISK.
         *
         * `action_result` is a ch9 CONTROL frame. It carried LLM PROSE in
         * `result.narration`, and the only thing standing between that prose and
         * the screen was `narrationExpected` - THE FSM MODE, NOT THE CHANNEL.
         * So outside GAMEPLAY it rendered unconditionally, in exactly the window
         * where the player has no ledger, no beat and no way to notice it went
         * unaccounted. Structurally the same leak PR #978 closed on `room_state`,
         * behind a different door.
         *
         * ch0 owns scene prose on every path. There is nothing for this to be a
         * fallback FOR, so it is a TRIPWIRE, not a suppression: if `narration`
         * reappears on this frame a producer has regressed, and that must be
         * LOUD rather than dropped on the floor.
         */
        if (result.narration) {
            console.warn(
                '[CHANNEL] ch9 BOUNDARY VIOLATION: action_result carried a ' +
                'content payload (narration, len=' + String(result.narration).length + ') ' +
                'action_type=' + (result.action_type || 'unknown') + '. ' +
                'channels.md section 4: ch9 may not carry content. NOT RENDERED - ' +
                'scene prose belongs to ch0, which the delivery ledger accounts for.'
            );
        }

        // Route display based on action_type
        // In gameplay, suppress raw text -- narration pipeline covers the scene.
        if (result.action_type === 'builtin') {
            this._displayBuiltinResult(result);
        } else if (!narrationExpected) {
            // Only show arbitration/skill/unmatched text outside narration pipeline
            if (result.action_type === 'arbitration') {
                this._displayArbitrationResult(result);
            } else if (result.action_type === 'skill') {
                this._displaySkillResult(result);
            } else if (result.action_type === 'unmatched') {
                this._displayUnmatchedResult(result);
            }
        }

        // Display system messages (always shown -- these are system-level)
        if (result.system_message) {
            this.logMessage('[System] ' + result.system_message);
        }

        // Handle fallback responses
        if (result.fallback) {
            console.log(`[${sessionId}] Fallback response used:`, result.error);
        }

        // During SPAWNING, action_results arrive (reawaken narration, auto-look) but the
        // spawn flow manages its own FSM transitions via spawn_complete. Skip here.
        if (TerminalFSM.mode === 'SPAWNING') {
            console.log(`[${sessionId}] Action result during SPAWNING - displayed, skipping FSM transition`);
            return;
        }

        // [2026-07-06-001-ENGINE / D1] Arrival is SERVER-AUTHORITATIVE. The
        // client no longer records a deferred room-entry `look` on a movement
        // action_result. When the player walks into a new room, the server's
        // membership enter funnel fires the arrival scene (ch0) + the new room's
        // phase_clock and pushes them down the normal narration-receive path.
        // The client only RECEIVES them; it never re-submits a follow-up look.
        // (Removed: the _pendingRoomEntryLook intent-record + _dispatchRoomEntryLook
        // deferred re-dispatch — the phantom second submit that caused W0.7.)

        // Stay in GAMEPLAY. TurnPump handles narration delivery and turn completion.
        console.log(`[${sessionId}] Action result displayed, play queue handles narration`);
    },

    /**
     * Handle room_state message: sync TurnPump + clock, display room description.
     *
     * room_state is sent on spawn complete, movement arrival, and reconnect.
     * It is NOT a turn cycle -- it does not go through TurnPump event system.
     * It is a one-shot sync message that sets initial frontend state.
     */
    /**
     * Render the POI arrival splash when the player arrives at a NEW POI.
     * [2026-07-23-002-GDD]
     *
     * THE CLIENT OWNS ARRIVAL DETECTION. The server ships `arrival_image` for
     * whatever room the player is in; we render only when `poi_slug` CHANGES
     * from the one last rendered. That yields the ruled behaviour exactly --
     * fires on every arrival, in BOTH directions (descending and climbing the
     * warrens), with no dedupe -- and keeps the server free of a per-player POI
     * cursor, which would have needed a durable (player, world, poi) key to
     * survive a pod re-election.
     *
     * ABSENCE IS NOT "no image": the look-refresh room_state path ships no
     * arrival_image at all. Never clear on absence.
     *
     * UI VISUAL CANON: square (1:1) slot, 1px line frame, ascii placeholder
     * until the art lands (§4); no glow, no gradient, no soft-pulse (§1).
     */
    _renderArrivalImage(message, cursorField) {
        const asset = message && message.arrival_image;
        if (!asset || !asset.url || !asset.poi_slug) return;

        /* [2026-09-01-019-ENGINE] The transform reveal reuses this whole
         * path but keeps ITS OWN dedupe cursor — sharing the POI cursor
         * would cross-trigger the two splashes. Default preserves the POI
         * behaviour byte-identically. */
        const _cursor = cursorField || '_lastArrivalPoiSlug';
        if (this[_cursor] === asset.poi_slug) return;
        this[_cursor] = asset.poi_slug;

        const history = document.getElementById('history');
        if (!history) return;

        const slot = document.createElement('div');
        slot.className = 'message poi-arrival-image';
        slot.style.aspectRatio = '1 / 1';
        slot.style.maxWidth = '320px';
        slot.style.border = '1px solid var(--color-line, #333)';
        slot.style.display = 'flex';
        slot.style.alignItems = 'center';
        slot.style.justifyContent = 'center';
        slot.style.overflow = 'hidden';
        slot.textContent = `[ scene — ${asset.poi_slug} ]`;

        const img = document.createElement('img');
        img.alt = asset.alt || asset.poi_slug;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.addEventListener('load', () => {
            slot.textContent = '';
            slot.appendChild(img);
            // [2026-08-08-008-QASTI] THE RENDER LOG IS HONEST NOW: it fires on
            // the load event, carrying naturalWidth as its own evidence. The
            // old line logged "rendered" unconditionally at INSERT time — so
            // the console said "rendered" whether or not anything rendered,
            // while three CORS lines per asset screamed on requests that then
            // succeeded (<img> without crossorigin does not require CORS).
            // Noise on success, silence on failure: the validation pass
            // nearly filed "POI art is broken" off that console, and only
            // measuring naturalWidth in the DOM caught the truth.
            console.log(
                `[POI-IMAGE] rendered poi=${asset.poi_slug} `
                + `naturalWidth=${img.naturalWidth} url=${asset.url}`
            );
        });
        img.addEventListener('error', () => {
            // LOUD-FAILURE LAW: a CONFIGURED url that fails to load is a bug and
            // must surface. The ascii placeholder stays so the failure is
            // visible on screen rather than rendering as an empty gap.
            // ([2026-08-08-008-QASTI]: this warning is PINNED to be reachable —
            // a guard that has only ever been silent is indistinguishable from
            // one that cannot fire.)
            console.warn(
                `[POI-IMAGE] failed to load ${asset.url} for ${asset.poi_slug}`
            );
        });
        img.src = asset.url;

        if (typeof window !== 'undefined' && window.__vpAnnotate) {
            // The VP frame-capture grades on render kind — a transform reveal
            // is not a POI arrival and must not be counted as one.
            window.__vpAnnotate(
                slot,
                (message && message.type === 'transform_reveal')
                    ? 'transform_reveal_image' : 'poi_arrival_image',
                null
            );
        }

        /* [2026-08-08-007-ENGINE] ⚠️ PROVISIONAL CLASSIFICATION — AN OPEN CANON
         * QUESTION, DELIBERATELY NOT RULED HERE.
         *
         * `room_state.arrival_image` and its `[ scene — slug ]` caption arrive
         * on a **ch9** frame. Whether an IMAGE ASSET counts as "content" under
         * channels.md §4 is a canon call for /gdd, not a code call for this PR
         * (client_funnel.md §5 row 4 flags it and explicitly does not rule it).
         *
         * So this migration changes NOTHING about whether or how the splash
         * renders. It routes the write through the one door — which is the whole
         * point of a funnel, and is what stops this being the next anonymous
         * payload — and stamps `data-canon-pending` so the open question is
         * legible ON THE NODE rather than only in a document. It is classed
         * `server-authority` for now because that is the axis the frame it rides
         * on already belongs to.
         *
         * ⛔ If /gdd rules it content, the fix is a CHANNEL, not a re-label here.
         */
        const _F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!_F) return;
        /* [FB-HUD-03] THE PICTURE ESTABLISHES THE BEAT, SO IT GOES ON TOP OF IT.
         *
         * `room_state` is on the wire AFTER ch0 by server design, so appending
         * put the splash below the prose it was meant to establish — the exact
         * report ("images streamed to the game terminal should come before the
         * ch1 and ch0 text for the turn"). The anchor is the block's first
         * narration node; with none yet, `null` appends, which already IS the
         * top of the block.
         *
         * ⚠️ This is PLACEMENT ONLY. Nothing waits on the image: the anchor is
         * resolved at insert time from what has already rendered, so a slow or
         * absent asset can never hold ch0 or the prompt.
         */
        const _anchor = (typeof TurnPump !== 'undefined')
            ? TurnPump._blockAnchor('establishing') : null;
        console.log(
            `[BLOCK] arrival-image poi=${asset.poi_slug} placement=` +
            `${_anchor ? 'above-narration' : 'append'} beat=` +
            `${(typeof TurnPump !== 'undefined' && TurnPump._beatId()) || 'unbound'}`
        );
        _F.insert(slot, {
            origin: _F.ORIGIN.SERVER_AUTHORITY,
            parent: history,
            before: _anchor,
            dataset: { 'data-canon-pending': 'arrival-image' }
        });

        // [2026-08-08-008-QASTI] Distinct vocabulary, deliberately NOT
        // "rendered": the slot is inserted and the image is still loading.
        // "rendered" is claimed only by the load listener above, with pixels.
        console.log(
            `[POI-IMAGE] slot inserted poi=${asset.poi_slug} (image loading) `
            + `url=${asset.url}`
        );
    },

    handleRoomState(message) {
        if (TerminalFSM.mode === 'SPAWNING') {
            this._bufferedRoomState = message;
            return;
        }

        // [2026-07-23-002-GDD] POI arrival splash. Structured half of the
        // frame — stays. Only the prose left in the ch9 content-leak fix.
        this._renderArrivalImage(message);
        const sessionId = this.state.sessionId || 'unknown';
        const phase = message.phase || {};
        const exits = message.exits || [];
        const occupants = message.occupants || [];

        // 1. NO ROOM PROSE IS RENDERED FROM room_state. ch9 CARRIES NO CONTENT.
        //
        //    docs/Tome/systems/channels.md section 4, on ch9: "may not carry
        //    content. It is lifecycle and control; a content payload here is
        //    unaccounted by construction."
        //
        //    room_state is a ch9 frame. It used to carry the room's authored
        //    prose in `description`, and this arm rendered it into #history as
        //    a `.message narration-stream` node. On a movement beat that made
        //    THREE consecutive paragraphs describing one room — measured live
        //    on play.reavers.io: the ch9 baseline at history row 24, ch0 frames
        //    at rows 25 and 27.
        //
        //    (It was NOT printing every beat — the room-identity dedup worked,
        //    `rendered=false reason=same-room`. The defect was REDUNDANCY, not
        //    repetition, and the redundant copy is the one on the wrong
        //    channel.)
        //
        //    ch0 is the sole owner of room prose, and it covers every arrival
        //    path server-side: `turn_engine.fire_arrival_scene` queues the
        //    arrival look for spawn / resume / void-entry, and the movement
        //    turn's own resolution narrates the destination. There is no client
        //    `look` dispatch on any path (ADR 2026-07-06-001-ENGINE) and no
        //    baseline for ch0 to be a fallback FOR.
        //
        //    THE FIX IS AT THE PRODUCER: no builder emits `description` any
        //    more (game_server/networking.py, and ai_service `_handle_look`
        //    upstream of it). The check below is a TRIPWIRE, not a suppression
        //    — if a `description` ever reappears on this frame it means a
        //    producer regressed and re-opened the leak, and that must be LOUD
        //    rather than silently dropped on the floor.
        if (message.description) {
            console.warn(
                `[CHANNEL] ch9 BOUNDARY VIOLATION: room_state carried a `
                + `content payload (description, len=${String(message.description).length}) `
                + `for room=${message.room_name || 'unknown'}. channels.md §4: `
                + `ch9 may not carry content. NOT RENDERED — a producer has `
                + `regressed; room prose belongs to ch0 alone.`
            );
        }

        // [WS-2 issue-7] Exits + occupants are scene STATE, not prose. They are
        // rendered as a distinct structured affordance above the prompt (HUD-
        // style, like the clock) from the structured room_state fields — NOT as
        // `Exits: ...` / `Present: ...` text lines stapled into the #history
        // .message narration stream. The ch0 narration already weaves the exits
        // into prose; this is the addressable structured surface for them.
        // [2026-08-15-001-GDD] T-5: `castable_skills` rides the SAME frame and
        // is passed straight through. This is the payload the HUD fan-out
        // already stamps (`_stamp_castable_skills`) — one source, not a second
        // read, and the server's omit-on-failure contract is honoured inside.
        // [2026-08-13-005-ENGINE] The CATEGORY and the LEARNABLE pool ride the
        // same frame and are passed straight through with the set they
        // describe. Three keys, three independent absence signals — the server
        // omits each on its own failure and the panel honours each separately.
        this._renderRoomStatePanel(
            exits, occupants, message.castable_skills,
            message.castable_skill_buckets, message.latent_skills,
            message.skill_chips,
        );

        // 2. Initialize TurnPump from phase status (only in GAMEPLAY)
        //
        // ⚠️ INITIALIZE, NOT RE-INITIALIZE — AND THAT IS WHY THE `isPending()`
        // GUARD BELOW EXISTS [2026-08-10-005-ENGINE / FB-HUD-10].
        //
        // `TurnPump.event('server_action')` is DESTRUCTIVE: it calls
        // `_resetCycleCompletion()`, nulls `channels.ch0` / `channels.ch1`,
        // re-arms the stall watchdog and freezes the clock. That is correct for
        // a frame arriving when the client has NO cycle (spawn / reconnect /
        // resume, where the server is mid-resolution and the UI must show it),
        // and it is exactly the wrong thing to do to a cycle already in flight
        // — it is the same wipe note 4 below records as
        // [2026-07-28-004-QASTI]: ch1's discharge and ch0's terminal cleared
        // mid-beat, so an ARRIVED ch0 sits unplayed until the 90s watchdog.
        // Note 4 closed the epoch-reset door; this arm is the SECOND door to
        // the same room, reached through `server_action` instead.
        //
        // It was latent while room_state was pushed only on arrival / look /
        // roster-delta, where the phase is usually `collecting`. FB-HUD-10
        // widens the push to any change in the room FACT SET (a health band
        // crossing now pushes), so a frame built mid-resolution is no longer a
        // rare race. A frame whose job is to refresh FURNITURE must never
        // reset the BEAT the player is waiting on.
        if (TerminalFSM.mode === 'GAMEPLAY') {
            const status = phase.status || 'idle';

            /* ⭐ [2026-08-19-024-ENGINE D4] THE SECOND CLIENT FUNNEL FOR THE
             * CLOCK ROW, and it needs the same gate as `_revealClock`.
             *
             * COORDINATE LAW rule 3: two funnels, one stamping the fact, is
             * always a bug — and this module has already filed that shape twice
             * on room_state alone (`arrival_image`, the display names). A
             * `room_state` carries `phase.timer_seconds` and NO per-player
             * statement, so on its own it cannot answer the question. It does
             * not have to: the adopted `promptState` is what the prompt gate
             * consults below, and the timer and the prompt are the SAME decision
             * — canon §6 shows them together in *Asked* and hides them together
             * otherwise. Reading one statement for both is what keeps the row
             * and the prompt from disagreeing on a resume. */
            const timerSuppressed = (typeof TurnPump !== 'undefined')
                && TurnPump._isInputSuppressed();
            if (timerSuppressed && typeof ClockLine !== 'undefined'
                && typeof ClockLine.vacate === 'function') {
                ClockLine.vacate();
            }

            if (status === 'collecting') {
                // Room clock is ticking, player can type their action
                // Start clock with remaining_seconds — unless this beat is not
                // theirs, in which case the row stays vacated (above).
                if (typeof ClockLine !== 'undefined' && phase.remaining_seconds > 0
                    && !timerSuppressed) {
                    const clockMode = (message.beat_mode === 'phase') ? 'phase' : 'turn';
                    ClockLine.start(
                        phase.timer_seconds || phase.remaining_seconds,
                        phase.number || 1,
                        clockMode,
                        phase.total || 1,
                        phase.start_time || null,
                        message.turn_number || 0,
                        message.audit_key || null,
                    );
                }
            } else if (status === 'resolving' || status === 'narrating') {
                // `resolving`: actions submitted, processing — show the thinking
                // indicator. `narrating`: phase resolved, narration in flight —
                // TurnPump will get `narration_received`. Both arms did the
                // identical thing, so they are one branch; keeping them apart
                // only made it look as though they might diverge.
                //
                // ⚠️ ONLY WHEN THE PUMP IS IDLE. A pump that already has a cycle
                // in flight KNOWS it is resolving — it does not need a ch9
                // snapshot to tell it, and re-arming would wipe the record of
                // the beat it is mid-way through delivering. See the note above
                // this block.
                if (typeof TurnPump !== 'undefined' && !TurnPump.isPending()) {
                    TurnPump.event('server_action');
                }
            }
            // status === 'idle': no clock, no pump action. Player waits for first phase_clock.
        }

        // Show waiting indicator if no clock exists yet (player just joined, waiting for first phase)
        //
        // [2026-08-19-024-ENGINE D4] ...but never over a VACATED row. `[waiting...]`
        // means "no countdown to show YET" — a beat with no budget. A player who
        // is not being asked is a different state and has already been answered:
        // nothing is coming for them this beat, and saying "stand by" would be
        // announcing the wait canon §6 forbids advertising.
        if (TerminalFSM.mode === 'GAMEPLAY' && typeof ClockLine !== 'undefined'
            && !ClockLine.isActive()
            && !(typeof TurnPump !== 'undefined' && TurnPump._isInputSuppressed())) {
            const phaseStatus = (phase && phase.status) || 'idle';
            if (phaseStatus === 'idle') {
                ClockLine.showWaiting();
            }
        }

        // 3. BUG-15: Create initial prompt if none exists -- through THE prompt
        // gate, the one opener of the GAMEPLAY prompt. A second spelling of it
        // here is how the two drift.
        if (TerminalFSM.mode === 'GAMEPLAY') {
            TurnPump._applyPromptGate();
        }

        // 4. room_state is a PER-TURN SNAPSHOT, not an epoch signal, and it must
        //    never touch the completion record. The server sends it on every
        //    look / movement turn and sends it MID-CYCLE by design —
        //    networking.py `_deliver_player_cycle`: "room_state must arrive
        //    after ch0 and before turn_complete (the documented client order)".
        //    Resetting the beat epoch here cleared ch1's discharge, ch0's
        //    terminal, serverDone, the bound beat and the cursor on EVERY
        //    gameplay beat, while that beat was still closing; nothing
        //    re-discharges ch1 on a server-driven phase advance, so both ch1
        //    gates then held for the life of the cycle and a ch0 that had
        //    ARRIVED sat unplayed in its slot until the 90s watchdog
        //    [2026-07-28-004-QASTI].
        //
        //    THE ONE RECORD HAS ONE LIFECYCLE: created at cycle start, cleared
        //    at cycle end. The classifier's epoch bounds are seeded by the
        //    connection `reset` (join / disconnect / reconnect all tear the
        //    stream down first) and self-healed by `_noteEpochRegression` when a
        //    frame key sorts strictly below the cursor — which covers a server
        //    turn-number restart AND a room change into a lower-sorting room.

        // 5. Store sync tracking variables for telemetry
        if (typeof window !== 'undefined') {
            window._syncTurn = message.turn_number || 0;
            window._syncPhase = phase.number || 0;
            window._syncClockMode = message.beat_mode || 'unknown';
        }
        if (typeof logSync === 'function') logSync('room_state');

        // [2026-07-29-014-ENGINE] Fan the frame out to the in-game HUD.
        //
        // ADDITIVE AND TERMINAL: this is the LAST thing handleRoomState does,
        // it is a broadcast with no return value, and it is wrapped so a
        // subscriber that throws can never take the room_state handler down
        // with it. The HUD is a reader of this frame; it is not in its path.
        //
        // An event rather than a direct call on purpose — the pump must not
        // acquire a dependency on a presentation surface that is behind a flag
        // and may not exist.
        try {
            if (typeof document !== 'undefined'
                    && typeof CustomEvent === 'function') {
                document.dispatchEvent(new CustomEvent('reavers:room-state', {
                    detail: message,
                }));
            }
        } catch (hudErr) {
            console.warn('[room_state] HUD fan-out failed (non-fatal):', hudErr);
        }

        console.log(
            `[${sessionId}] Room state applied: ${message.room_name} ` +
            `turn=${message.turn_number} phase=${phase.number}/${phase.total} ` +
            `status=${phase.status} remaining=${phase.remaining_seconds}s`
        );
    },

    /**
     * [WS-2 issue-7] Render exits + occupants as a structured, HUD-style panel
     * — NOT as `Exits: ...` / `Present: ...` prose lines in the #history
     * .message stream.
     *
     * This is a singleton affordance: each room_state rebuilds it in place (it
     * reflects current scene state, like the clock). It consumes the full
     * structured room_state fields (direction, destination, passage/lock state,
     * occupant display_name, occupant vitality) rather than collapsing them to
     * a single label.
     *
     * It is deliberately NOT a `.message` element and never produces a line
     * beginning `Exits: ` / `Present: ` — that is the render-contract break the
     * ExitsRenderedAsStringDetector guards against.
     *
     * ── [FB-HUD-01] IT LIVES IN `#cmd-state` NOW, NOT `#history` ─────────────
     *
     * FB-HUD-02 anchored this panel ABOVE the beat's ch0 so it would stop being
     * the last thing the player read before typing. That was the right fix for
     * a panel inside the scroll box — but it was treating a symptom, and the
     * item said so: *"making it permanent furniture below the input line is
     * FB-HUD-01."*
     *
     * The panel was never narration and never belonged in the scroll box. It is
     * the room's CURRENT state, and current state that scrolls away is stale
     * state the player has to go looking for. In `#cmd-state` it is pinned
     * below the input line, rebuilt on every `room_state`, and it cannot land
     * after ch0 BY CONSTRUCTION rather than by anchor arithmetic — which is why
     * `_blockAnchor('trailing')` is gone from here rather than kept "just in
     * case". A dead anchor that only stays dead because nothing calls it is the
     * loaded-gun shape this codebase keeps filing.
     *
     * ⚠️ REFUSES RATHER THAN FALLING BACK. If `#cmd-state` is absent there is
     * no render, and it says so. Falling back to `#history` would silently
     * restore the exact scroll-away defect this item exists to remove, and it
     * would look like a working panel while doing it.
     */
    _renderRoomStatePanel(exits, occupants, skills, buckets, latent, chips) {
        const host = document.getElementById('cmd-state');
        if (!host) {
            console.warn(
                '[room_state] NO RENDER: #cmd-state is absent, so the pinned ' +
                'exits/occupants/skills footer has nowhere to land. Refusing ' +
                'rather than falling back to #history — a panel in the scroll ' +
                'box is the FB-HUD-01 defect, not a degraded mode.'
            );
            return;
        }

        const exitList = Array.isArray(exits) ? exits : [];
        const occList = Array.isArray(occupants) ? occupants : [];

        /* [2026-08-15-001-GDD] T-5. THE CASTABLE SET IS REMEMBERED ACROSS
         * FRAMES, and it has to be.
         *
         * The panel is a SINGLETON rebuilt from scratch on every call, and not
         * every caller carries skills — the builtin-`look` arm below rebuilds
         * it from an `action_result` that has no `castable_skills` on it at
         * all. Rebuilding from that frame alone would DROP the skills group
         * until the next `room_state`, which is a group blinking out for
         * reasons the player cannot see.
         *
         * ⚠️ ABSENT IS "NO REFRESH", NOT "NONE". Same contract the server
         * stamps it under: it OMITS `castable_skills` on a failed read and
         * never sends `[]` (the starter kit is universal, so an empty castable
         * set is a failed read, not a character without skills). So an absent
         * key keeps the last list; it never clears it. */
        if (Array.isArray(skills) && skills.length) {
            this._lastCastableSkills = skills;
        }
        const skillList = Array.isArray(this._lastCastableSkills)
            ? this._lastCastableSkills : [];

        /* [2026-08-13-005-ENGINE] THE CATEGORY MAP — remembered on the same
         * terms as the set it labels, because it arrives on the same key's
         * frame and the panel is rebuilt from callers that carry neither.
         *
         * ⚠️ ABSENT IS "NO REFRESH", and an absent map is HARMLESS: the chips
         * render as bare names, which is exactly what they did before this
         * existed. It is never emptied to `{}` server-side (the map is TOTAL
         * over the castable set), so there is no answered-empty case to hold
         * open here — only "we were told" and "we were not". */
        if (buckets && typeof buckets === 'object'
                && !Array.isArray(buckets)) {
            this._lastSkillBuckets = buckets;
        }
        const bucketMap = (this._lastSkillBuckets
                && typeof this._lastSkillBuckets === 'object')
            ? this._lastSkillBuckets : {};

        /* [2026-08-18-014-ENGINE] `latent` IS STILL RECEIVED AND IS NO LONGER
         * RENDERED. The project owner removed the `learnable` group entirely
         * on 2026-08-18 — the whole pool and its label — so the tri-state
         * memory this used to keep ([2026-08-12-042-ENGINE]: `[]` clears where
         * an empty `skills` is ignored) has nothing left to feed and is gone
         * with it.
         *
         * ⚠️ THE PARAMETER STAYS ON THE SIGNATURE ON PURPOSE. The server still
         * sends the key and `handleRoomState` still passes it; keeping it makes
         * the non-render DELIBERATE and visible here, where dropping it would
         * read as a key someone forgot to wire. If the pool is ever shown
         * again, restore the tri-state memory WITH it — an unanswered read
         * (`undefined`) keeping the last pool, and an answered `[]` clearing
         * it, are not the same thing and holding a stale pool names a skill the
         * player has just been granted.
         */

        /* THE SKILLS ROW IS THE SERVER'S CHIP SET (owner, 2026-10-30). The
         * universe authors which skills sit on the chips (`skill.chip`) and the
         * word each reads (`skill.chip-label`); the server sends the row as
         * `skill_chips`, `{skill, display_name}` in order, drawn on what the
         * character may attempt. Nothing here filters, sorts or names a skill:
         * every skill stays castable and parseable whatever the row shows.
         *
         * Remembered on the castable set's terms: absent keeps the last row; an
         * array is an answer, and `[]` (nothing on the chips) is drawn as one.
         * A row never told is not drawn and is said: a castable set with no
         * chip set is a server that did not send it. */
        if (Array.isArray(chips)) {
            this._lastSkillChips = chips.filter((c) => c && typeof c === 'object'
                && typeof c.skill === 'string' && typeof c.display_name === 'string');
        }
        const chipList = Array.isArray(this._lastSkillChips) ? this._lastSkillChips : null;
        if (chipList === null && skillList.length > 0) {
            console.error('[room_state] skill_chips NOT TOLD: the castable set arrived '
                + 'without the chip set, so the skills row is not drawn.');
        }

        // Remove the prior singleton so the panel reflects current scene state.
        // Each chip row's scroll offset is kept by its group, so a rebuild
        // landing mid-scroll does not throw the player back to the row's start.
        const prior = host.querySelector('.room-state-panel');
        const keptScroll = {};
        if (prior) {
            prior.querySelectorAll('.room-state-group').forEach((g) => {
                keptScroll[g.className] = g.scrollLeft || 0;
            });
            prior.remove();
        }

        /* Nothing to surface -> no panel (and the stale one is already gone).
         *
         * ⚠️ `skillList` IS IN THIS CONDITION ON PURPOSE ([2026-08-15-001-GDD]
         * T-6). The guard used to read `exits === 0 && occupants === 0`, and
         * adding a third group without widening it would have made the skills
         * line VANISH in exactly the rooms with no exits and no occupants —
         * silently, in the case nobody clicks, which is how a feature ships
         * dark and looks fine. There are such rooms.
         *
         * ⚠️ `latentList` WAS IN THIS CONDITION AND IS NOW OUT, because the
         * `learnable` group no longer renders. Leaving it would keep an
         * EMPTY panel alive on a pool nothing draws. */
        if (exitList.length === 0 && occList.length === 0
                && skillList.length === 0 && chipList === null) return;

        // Scene state is system chrome, not narration: the panel is NOT a
        // .message element and carries no narration/cycle annotation, so it is
        // never captured as a narration line.
        const panel = document.createElement('div');
        panel.className = 'room-state-panel';

        if (exitList.length > 0) {
            const exitsGroup = document.createElement('div');
            exitsGroup.className = 'room-state-group room-state-exits';

            const label = document.createElement('span');
            label.className = 'room-state-label';
            label.textContent = 'exits';
            exitsGroup.appendChild(label);

            const chips = document.createElement('span');
            chips.className = 'room-state-chips';
            exitList.forEach((exit) => {
                chips.appendChild(this._buildExitChip(exit));
            });
            exitsGroup.appendChild(chips);
            exitsGroup.appendChild(this._buildChipRowTail());
            panel.appendChild(exitsGroup);
        }

        if (occList.length > 0) {
            const occGroup = document.createElement('div');
            occGroup.className = 'room-state-group room-state-occupants';

            const label = document.createElement('span');
            label.className = 'room-state-label';
            label.textContent = 'here';
            occGroup.appendChild(label);

            const chips = document.createElement('span');
            chips.className = 'room-state-chips';
            occList.forEach((occ) => {
                chips.appendChild(this._buildOccupantChip(occ));
            });
            occGroup.appendChild(chips);
            occGroup.appendChild(this._buildChipRowTail());
            panel.appendChild(occGroup);
        }

        /* SKILLS — LAST, AFTER `here`  ([2026-08-15-001-GDD] §5, T-5).
         *
         * Ruled: the skills group renders BELOW both room facts, so `exits` and
         * `here` stay adjacent. They describe the room; this describes you.
         *
         * ⚠️ THE WHOLE ROW, NO TRUNCATION. Every chip of the set sits on one
         * line and SCROLLS left and right; the amber tail says chips lie past
         * the edge. There is no `+N` and no destination one could point at.
         *
         * ⚠️ NOT SORTED, NOT FILTERED. The order is the server's, and so is the
         * category [2026-08-13-005-ENGINE]: the bucket is read out of the
         * server's map by name and is NEVER inferred here.
         *
         * An answered-empty row still draws its label and SAYS it: a group
         * missing altogether means, and only means, the set was not read. */
        if (chipList !== null) {
            const skillGroup = document.createElement('div');
            skillGroup.className = 'room-state-group room-state-skills';

            const label = document.createElement('span');
            label.className = 'room-state-label';
            label.textContent = 'skills';
            skillGroup.appendChild(label);

            if (chipList.length === 0) {
                // Prose about the absence of chips, never a chip itself.
                const empty = document.createElement('span');
                empty.className = 'room-state-empty';
                empty.textContent = 'none on the chips';
                skillGroup.appendChild(empty);
            } else {
                const chips = document.createElement('span');
                chips.className = 'room-state-chips';
                chipList.forEach((chip) => {
                    chips.appendChild(this._buildSkillChip(
                        chip, bucketMap[chip.skill] || null,
                    ));
                });
                skillGroup.appendChild(chips);
                skillGroup.appendChild(this._buildChipRowTail());
            }
            panel.appendChild(skillGroup);
        }

        /* [2026-08-18-014-ENGINE] THE `learnable` GROUP WAS HERE AND IS GONE.
         *
         * It rendered `character.latent-skills` as its own group below
         * `skills` ([2026-08-13-005-ENGINE]). The project owner removed it
         * outright on 2026-08-18, with the per-chip category text, as part of
         * decluttering this footer for phone widths: measured at 320px it was
         * two more rows on a panel already taking 61% of the screen, and it
         * listed things the player cannot do at all.
         *
         * ⚠️ IT IS DELETED, NOT COMMENTED OUT OR LEFT DARK BEHIND A FALSE
         * CONDITION — and the CSS it hung on (`.room-skill-chip.is-latent`,
         * `.room-skill-tag`) goes with it in index.html and in the style-guide
         * preview. A rule with no producer is the `.waiting-ch0-bridge` shape:
         * an advertised affordance with nothing behind it.
         *
         * ⚠️ IF IT COMES BACK, IT COMES BACK WHOLE. A latent skill is NOT
         * CASTABLE, and the distinction was carried by THREE non-colour
         * signals on purpose so that losing any one still left the fact
         * standing: its own group and `learnable` label, the words `not yet
         * learned` on every chip, and a DASHED border against the solid one
         * every castable chip has. Restoring the group without all three
         * re-opens the safety problem, not just the styling one — a player who
         * reads a latent skill as castable types it and dispatch refuses them.
         */

        // [2026-08-08-007-ENGINE] ORIGIN: server-authority. Exits and occupants
        // are scene STATE, not prose - the structured half of a ch9 frame, and
        // the half that is CORRECT to carry there. Same affordance family as the
        // clock and the action line, so it carries no channel.
        //
        // This is the load-bearing counterexample to "only ledger channels may
        // render". Deleting ch9 from the screen wholesale would take this panel
        // and the refusal line with it.
        const _F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!_F) return;
        /* [FB-HUD-01] Still through the door, and still `server-authority` —
         * moving the panel out of the scroll box changes WHERE it renders, not
         * WHAT it is. `#cmd-state` is passed as the parent explicitly (the
         * funnel honours `opts.parent`), so this is not a new write path.
         *
         * No `before`: the host holds exactly this one singleton, so there is
         * no ordering question left to answer — which is the point.
         */
        _F.insert(panel, {
            origin: _F.ORIGIN.SERVER_AUTHORITY,
            parent: host
        });

        this._wireRoomStateChips(host);
        panel.querySelectorAll('.room-state-group').forEach((g) => {
            if (keptScroll[g.className]) g.scrollLeft = keptScroll[g.className];
            g.addEventListener('scroll', () => this._markChipRow(g), { passive: true });
            this._markChipRow(g);
        });
        this._paintChipPressed(host);

        /* `skills` is the castable set; `chips` is what the row drew, `untold`
         * when no chip set ever arrived. `chips=0` is an answer; `skills=0` is
         * the read that never answered. */
        console.log(
            `[TEXT] actor=ROOM_STATE_PANEL placement=pinned-footer prompt=structured ` +
            `channel=system exits=${exitList.length} occupants=${occList.length} ` +
            `skills=${skillList.length} ` +
            `chips=${chipList === null ? 'untold' : chipList.length} ` +
            `categorised=${skillList.filter(
                (s) => !!bucketMap[String(s)]).length}`
        );
        console.log(
            `[BLOCK] room-state-panel placement=cmd-state ` +
            `beat=${(typeof TurnPump !== 'undefined' && TurnPump._beatId()) || 'unbound'}`
        );
    },

    /* ── THE CHIPS ARE BUTTONS (release-0.1 "The interface") ──────────────────
     *
     * Every footer chip is a `<button>` that lays its text into the command
     * line: `data-kind` says how (`verb` a skill, `object` an occupant, `move`
     * an exit) and `data-fill` says what. The board this copies is
     * frontend/web/preview/episode-sequence/ (`compose()`, "The footer's rules").
     *
     *   - The line holds one verb and one object, verb first. A verb press
     *     replaces the verb and keeps the object; an object press replaces the
     *     object and keeps the verb; a move is the whole line.
     *   - A press never sends, and a locked prompt takes no press.
     *   - A chip shows pressed while its text is on the line. The draft is the
     *     line only while the field still holds it: typing by hand, or sending,
     *     ends it.
     */

    /** One chip button. An empty `fill` is a chip with nothing to lay: disabled. */
    _buildChipButton(kind, fill) {
        const chip = document.createElement('button');
        chip.setAttribute('type', 'button');
        chip.setAttribute('data-kind', kind);
        if (fill) chip.setAttribute('data-fill', fill);
        else chip.disabled = true;
        return chip;
    },

    /** The amber `›` at a row's right edge, shown while chips lie past it. */
    _buildChipRowTail() {
        const tail = document.createElement('span');
        tail.className = 'room-state-tail';
        tail.setAttribute('aria-hidden', 'true');
        tail.textContent = '›';
        return tail;
    },

    /** A row says whether it overflows, is scrolled, and sits at its end; the
     *  CSS draws the tail and the label's edge from those. */
    _markChipRow(g) {
        const over = g.scrollWidth > g.clientWidth + 1;
        g.classList.toggle('is-overflowing', over);
        g.classList.toggle('is-scrolled', g.scrollLeft > 0);
        g.classList.toggle('is-at-end', g.scrollLeft + g.clientWidth >= g.scrollWidth - 1);
    },

    /** The chip draft while it is still what the line holds, or null. */
    _chipDraftOnLine() {
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        const field = CL ? CL.inputField() : null;
        const d = this._chipDraft;
        return (d && field && field.value === d.text) ? d : null;
    },

    /** THE ONE FUNNEL for a chip press: the next draft from the one on the line. */
    _composeChip(kind, fill) {
        const on = this._chipDraftOnLine() || { verb: null, object: null, move: null };
        let d;
        // A move, or an ask's answer, is the whole line.
        if (kind === 'move' || kind === 'line') d = { verb: null, object: null, move: fill };
        else if (kind === 'verb') d = { verb: fill, object: on.object, move: null };
        else d = { verb: on.verb, object: fill, move: null };
        d.parts = d.move ? [d.move] : [d.verb, d.object].filter(Boolean);
        d.text = d.parts.join(' ');
        return d;
    },

    /** Write the draft into the field the way a keystroke does: the field takes
     *  the value and its own `input` listeners (resize, caret, VP snapshot) run. */
    _layChipDraft(d) {
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        const field = CL ? CL.inputField() : null;
        if (!field) {
            console.error('[chip] fill refused: no .input-field in #command-line');
            return false;
        }
        this._chipDraft = d;
        this._chipFilling = true;
        try {
            field.setRangeText(d.text, 0, field.value.length, 'end');
            field.dispatchEvent(new Event('input', { bubbles: true }));
        } finally {
            this._chipFilling = false;
        }
        console.log(`[chip] fill path=pressed text=${JSON.stringify(d.text)}`);
        return true;
    },

    /** Mark the chips whose text is on the line, and only those. */
    _paintChipPressed(host) {
        const d = this._chipDraftOnLine();
        const on = d ? d.parts : [];
        host.querySelectorAll('.room-state-chip[data-fill]').forEach((chip) => {
            if (on.indexOf(chip.getAttribute('data-fill')) !== -1) {
                chip.setAttribute('aria-pressed', 'true');
            } else {
                chip.removeAttribute('aria-pressed');
            }
        });
    },

    /** The line was emptied without an `input` event (a send): the draft ends
     *  and no chip stays pressed over the empty line. */
    noteCommandLineCleared() {
        this._chipDraft = null;
        const host = (typeof document !== 'undefined') ? document.getElementById('cmd-state') : null;
        if (host) this._paintChipPressed(host);
    },

    /** Bind the footer's presses ONCE per host, and the hand-typing reset once
     *  per field (both are permanent markup; the panel inside is rebuilt). */
    _wireRoomStateChips(host) {
        const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
        const field = CL ? CL.inputField() : null;
        if (field && !field._chipDraftBound) {
            field._chipDraftBound = true;
            field.addEventListener('input', () => {
                if (this._chipFilling) return;
                this._chipDraft = null;
                this._paintChipPressed(host);
            });
        }
        if (host._chipsWired) return;
        host._chipsWired = true;
        // A chip must not take focus: the field's blur handler SENDS a non-empty
        // line (the soft keyboard's Done), so a press that blurred it would send.
        host.addEventListener('mousedown', (e) => {
            if (e.target.closest && e.target.closest('.room-state-chip')) e.preventDefault();
        });
        host.addEventListener('click', (e) => {
            const chip = e.target.closest ? e.target.closest('.room-state-chip[data-fill]') : null;
            if (!chip || chip.disabled) return;
            if (!CL || CL.isLocked()) {
                console.log('[chip] fill path=locked -- the prompt is closed; nothing laid');
                return;
            }
            if (this._layChipDraft(this._composeChip(
                    chip.getAttribute('data-kind'), chip.getAttribute('data-fill')))) {
                this._paintChipPressed(host);
            }
        });
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('resize', () => {
                host.querySelectorAll('.room-state-group').forEach((g) => this._markChipRow(g));
            });
        }
    },

    /**
     * One skill chip: THE NAME, ALONE. [2026-08-18-014-ENGINE]
     *
     * ⚠️ THE CATEGORY IS NO LONGER PRINTED, AND THE FACT IS NOT LOST. Until
     * 2026-08-18 this appended a `.room-skill-tag` reading `· universal` /
     * `· signature` / `· borne`. Measured against the shipped sheet at 320px
     * (`tools/measure-room-state-panel.py`), the tag roughly DOUBLED a chip:
     * the widest was 232px against a 299px content box, so no two chips could
     * ever share a row and the group rendered ONE SKILL PER LINE — eight lines
     * for Lysa's kit, 61% of the phone. The project owner's report was that
     * the line "overflows and becomes confusing on a phone"; the tag is what
     * made it that wide.
     *
     * The server's fact is still ON THE NODE as `data-bucket` — the machine
     * -readable half stays, only the printed half goes. So this remains a
     * RENDERING change: nothing about which bucket a skill is in moved, and
     * the client still does not decide it.
     *
     * ⚠️ THE CATEGORY IS READ, NEVER DERIVED — unchanged and still absolute.
     * `bucket` is whatever the server's map said and nothing else. There is no
     * de-slugging, no lookup table of skill names, and no inference from a
     * name's shape — the same rule `HudScreens.placeName` follows and the same
     * one the FB-HUD-08 finding names explicitly: prettify and classify on the
     * BACKEND. A client that guessed would be a second, disagreeing authority
     * on a fact the player is using to learn their own character.
     *
     * ⚠️ THE `latent` ARM IS GONE BECAUSE ITS CALLER IS. The `learnable` group
     * was removed by the same ruling, so nothing passes `'latent'` here any
     * more, and an arm kept alive for a caller that does not exist is the
     * loaded-gun shape this file keeps filing (`.waiting-ch0-bridge`). IF THE
     * LEARNABLE POOL EVER RENDERS AGAIN it must come back with ALL THREE of
     * its non-colour carriers together — its own group and label, the words
     * `not yet learned`, and the dashed `.is-latent` border. A latent skill is
     * NOT CASTABLE; a player who reads one as something they can do now types
     * it and dispatch refuses them. Re-adding one carrier without the others
     * is the regression, not the absence.
     */
    _buildSkillChip(entry, bucket) {
        /* `entry` is one `skill_chips` row: the chip reads its display word and
         * lays it lower-cased (`Attack` lays `attack`), the word the skill's own
         * match parses; `data-skill` keeps the skill it names. */
        const word = String(entry.display_name);
        const chip = this._buildChipButton('verb', word.toLowerCase());
        chip.className = 'room-state-chip room-skill-chip';
        chip.setAttribute('data-skill', String(entry.skill));

        const name = document.createElement('span');
        name.className = 'room-skill-name';
        // textContent, never innerHTML: the word arrives from the wire.
        name.textContent = word;
        chip.appendChild(name);

        // The provenance still travels, it just no longer takes up room.
        if (bucket) chip.setAttribute('data-bucket', String(bucket));

        return chip;
    },

    /* Build one structured exit chip from a structured exit dict (or string).
     * It lays `go <direction>`, never a destination: the travel verb resolves
     * one word against the exit's direction or its display name, and the
     * token read hands a second `to` phrase to the model, so `go north to the
     * <room>` is a command the parser has no home for. An exit with no
     * direction lays nothing. */
    _buildExitChip(exit) {
        const direction = typeof exit === 'string'
            ? exit : ((exit && typeof exit === 'object' && exit.direction) || '');
        const chip = this._buildChipButton('move', direction ? `go ${direction}` : '');
        chip.className = 'room-state-chip room-exit-chip';

        if (typeof exit === 'string') {
            const dir = document.createElement('span');
            dir.className = 'room-exit-dir';
            dir.textContent = exit;
            chip.appendChild(dir);
            return chip;
        }

        const obj = (exit && typeof exit === 'object') ? exit : {};
        const isLocked = obj.is_locked === true;
        const passageState = obj.passage_state || '';

        const dir = document.createElement('span');
        dir.className = 'room-exit-dir';
        dir.textContent = direction || 'unknown';
        chip.appendChild(dir);

        /* THE CHIP IS THE DIRECTION. NO DESTINATION (project owner, 2026-08-24):
         * "We should show exits only not destinations. [east] is complete as is
         * and we can fit multiple chips onto the exit line."
         *
         * `direction -> destination` ran to 218px on a phone, so ONE chip filled
         * a row and a three-exit room spent THREE ROWS plus an orphaned `exits`
         * label -- 91.8px of a 780px viewport, taken from the narration the
         * player is reading. Direction-only chips are ~50px and several sit on
         * one line beside their label.
         *
         * ⚠️ THIS IS THE FOOTER CHIP ONLY. `destination_visible` still governs
         * whether the ch0 NARRATION may name the adjacent room, and that flag is
         * NOT decorative: it was triaged per-room from each room's authored
         * `scene-shape.sightlines` down to 17 true / 75 false, on the ruling that
         * an exit describes ITSELF from the side you are standing on and the
         * occupant usually cannot know what the next room is called
         * ([2026-08-08/09], PRs #1009 + #1013). Nothing here touches that. Do not
         * "tidy up" by deleting `destination_visible` from the wire -- the prose
         * side reads it.
         *
         * ⚠️ `locked` AND `passage_state` STAY. They are STATE, not destination:
         * a locked exit is one the player cannot use, and hiding that trades a
         * saved pixel for a wasted turn. "State is data, not prose" is the same
         * ruling that put `passage_state` on the ch0 line.
         */

        if (isLocked) {
            chip.classList.add('is-locked');
            const lock = document.createElement('span');
            lock.className = 'room-exit-state room-exit-locked';
            lock.textContent = 'locked';
            chip.appendChild(lock);
        } else if (passageState && passageState !== 'open') {
            chip.classList.add('is-' + passageState);
            const state = document.createElement('span');
            state.className = 'room-exit-state';
            state.textContent = passageState;
            chip.appendChild(state);
        }

        return chip;
    },

    /* Read an occupant's vitality as a {current, max} pair, or null.
     *
     * ── [FB-HUD-01 / FB-HUD-06] WHY THIS RETURNS null AND NEVER ZERO ─────────
     *
     * `current_hp` / `max_hp` are `null` when the read did not resolve — the
     * server composes them through the shared `build_vitality` authority and
     * says so honestly rather than substituting a number. That honesty is only
     * worth anything if the client preserves it: a chip printing `0HP` or
     * `-/-` renders a READ FAILURE to the player as a creature one hit from
     * death, and the player would act on it. THE LOUD-FAILURE LAW — a failure
     * must not return a plausible zero.
     *
     * So every arm that cannot produce two finite numbers returns null, and the
     * caller omits the segment entirely. `0` current HP is NOT such an arm: a
     * dead or downed occupant genuinely reads `0HP/12HP`, which is why this
     * tests `Number.isFinite` rather than truthiness. `max <= 0` IS such an arm
     * — there is no creature whose maximum health is zero, so that is a broken
     * read wearing a number.
     *
     * Two shapes are accepted because the wire carries both: the flat
     * `current_hp`/`max_hp` pair, and the `vitality` block whose keys are
     * shape-identical to what `hud-character.js::buildVitality` already
     * renders. Neither is preferred over the other on principle — `vitality`
     * is consulted first only because it is the composed one.
     */
    _occupantVitality(occ) {
        if (typeof occ !== 'object' || occ === null) return null;

        const v = (typeof occ.vitality === 'object' && occ.vitality !== null)
            ? occ.vitality : null;

        const cur = v && v.current !== undefined ? v.current : occ.current_hp;
        const max = v && v.max !== undefined ? v.max : occ.max_hp;

        if (!Number.isFinite(cur) || !Number.isFinite(max)) return null;
        if (max <= 0) return null;
        // `coherent: false` travels with the pair. The server composes it in the
        // shared `build_vitality` authority and DELIBERATELY does not clamp:
        // "an impossible pair is a data disagreement, and hiding it behind a
        // plausible number is how the 16/15 defect survived to reach a player."
        // Carry it so the chip can mark it — see `_buildOccupantChip`.
        return {
            current: cur,
            max: max,
            coherent: v && v.coherent === false ? false : true,
        };
    },

    /* Build one structured occupant chip from a structured occupant dict (or
     * string) — `Goblin Warrior:6HP/12HP`, or the bare name when vitality is
     * unreadable (see `_occupantVitality`).
     *
     * ⚠️ THIS FUNCTION WAS THE SINGLE POINT OF LOSS. The HP was on the wire and
     * `current_hp` / `max_hp` had ZERO references anywhere in the client: every
     * occupant was collapsed to `display_name || name || 'unknown'` here, so
     * the fact arrived, was parsed, and was thrown away one line from the
     * screen. Nothing failed; the chip just said less than it knew.
     */
    _buildOccupantChip(occ) {
        let name;
        if (typeof occ === 'object' && occ !== null) {
            name = occ.display_name || occ.name || 'unknown';
        } else {
            name = String(occ);
        }
        // The object it lays: a player by their name, anyone else as a noun
        // with `the` (target resolution drops the article).
        const isPlayer = typeof occ === 'object' && occ !== null && occ.is_player === true;
        const chip = this._buildChipButton('object', isPlayer ? name : `the ${name}`);
        chip.className = 'room-state-chip room-occupant-chip';

        const nameEl = document.createElement('span');
        nameEl.className = 'room-occupant-name';
        nameEl.textContent = name;
        chip.appendChild(nameEl);

        const vit = this._occupantVitality(occ);
        if (vit) {
            const hp = document.createElement('span');
            hp.className = 'room-occupant-hp';
            // The colon is CONTENT, not a gap: the owner's layout spells the
            // chip `[Goblin Warrior:6HP/12HP]`, and a flex gap would render it
            // as two loose words. `.room-occupant-chip` sets `gap: 0` to match.
            hp.textContent = `:${vit.current}HP/${vit.max}HP`;
            chip.appendChild(hp);
            if (vit.current < vit.max) chip.classList.add('is-wounded');
            // ⚠️ MARK AN INCOHERENT PAIR, DO NOT CLAMP IT. The server already
            // refuses to clamp and ships `coherent: false` so a renderer can say
            // so; `hud-character.js::buildVitality` appends its `!` marker and
            // this chip did NOT — so the SAME fact was marked on the character
            // screen and silent in the `here:` line. A player reported
            // `Goblin Warrior:16HP/10HP` from this chip and had no way to know
            // it was a disagreement rather than a rule they did not understand
            // (live prod, 2026-08-12; `garrick` reads {current:16, max:15,
            // coherent:false} on the same build). Three surfaces, two shipped.
            if (vit.coherent === false) {
                chip.classList.add('is-incoherent');
                const bang = document.createElement('span');
                bang.className = 'room-occupant-hp-incoherent';
                bang.textContent = '!';
                bang.title = 'HP pair disagrees (current exceeds max) — '
                    + 'reported as read, not corrected';
                chip.appendChild(bang);
            }
        }

        /* WHAT IS ON THIS ACTOR, AFTER HP ([2026-08-29-007-ENGINE]).
         *
         * "I feel like correct results should spool to the goblin chip in the
         * HUD after HP" (project owner). AFTER the HP segment and before the
         * unread mark, because that is the reading order of the chip: who,
         * how hurt, what is on them, and whether we can vouch for any of it.
         *
         * ⭐ THE CHIP IS THE AUTHORITY; PROSE IS COLOUR. A status has
         * DURATION, and ch0 is an EVENT surface — "the deep drags you under"
         * is narrated once and says nothing three beats later about whether
         * the grip is still on. Miss the line and the state is invisible
         * forever. This says it every frame without repeating itself, which
         * is exactly what an event surface cannot do.
         *
         * ⛔ OMIT ON UNKNOWN, NEVER PRINT AN EMPTY LIST. `statuses` is `null`
         * whenever the server's SES probe did not answer, and `[]` only when
         * it answered and nothing is on. Rendering the two the same way would
         * publish "this goblin is unaffected" off a 500 — the same lie
         * `0HP` was, one field over ([2026-08-16-004-QASTI]). Null renders
         * nothing at all, and the `?` mark below already says the row is
         * unvouched.
         *
         * ⚠️ NAMES ARE PRINTED VERBATIM. They are universe-authored canonical
         * names (`gripped`, `parry-armed`); de-hyphenating or title-casing
         * them here would stand up a second display authority beside the
         * server's.
         */
        const statuses = (typeof occ === 'object' && occ !== null
                          && Array.isArray(occ.statuses))
            ? occ.statuses.filter(s => typeof s === 'string' && s.length)
            : null;
        if (statuses && statuses.length) {
            const fx = document.createElement('span');
            fx.className = 'room-occupant-statuses';
            // Same colon-is-content rule as the HP segment above: the chip
            // sets `gap: 0`, so separators are written, never spaced.
            fx.textContent = `:${statuses.join(',')}`;
            fx.title = `Active: ${statuses.join(', ')}`;
            chip.appendChild(fx);
            chip.classList.add('has-statuses');
        }

        /* ⚠️ [2026-08-16-004-QASTI] AN UNREAD OCCUPANT MUST NOT LOOK LIKE A
         * FULLY-KNOWN ONE.
         *
         * Omitting the HP segment on an unreadable vitality is right (above,
         * [FB-HUD-01]) and is NOT SUFFICIENT, because the bare-name chip is
         * ALSO what a perfectly healthy occupant renders as when nobody asked
         * for its HP. Omission cannot distinguish "we did not ask" from "we
         * asked and could not read it", so the honest render and the broken one
         * are the same pixels.
         *
         * That ambiguity had a body count. On prod (`paxval-68fd3934`,
         * 2026-08-16) `house-passenger-wisp` rendered here as an ordinary live
         * occupant while the server could not read its sheet at all; the player
         * targeted it for ten consecutive beats and was killed from behind by a
         * live goblin. The server knew — it now says so on the row — and this
         * is the arm, without which the fact dies one line from the screen for
         * the SECOND time in this same function.
         *
         * ⚠️ KEYED ON THE SERVER'S STATEMENT, NEVER ON A NULL VITALITY.
         * Inferring "unread" from `current_hp === null` would re-derive
         * client-side a fact the server already owns, and would mark every chip
         * nobody asked HP for. Two fields, because they are two different
         * failures: `unresolved` = we could not read the SHEET; `liveness.state
         * === 'unknown'` = we could not read the DEATH LATCH. Either one means
         * the player is looking at a target we cannot vouch for.
         */
        const unresolved = (typeof occ === 'object' && occ !== null)
            ? occ.unresolved : null;
        const liveness = (typeof occ === 'object' && occ !== null
                          && typeof occ.liveness === 'object' && occ.liveness !== null)
            ? occ.liveness : null;
        const livenessUnknown = liveness && liveness.state === 'unknown';
        if (unresolved || livenessUnknown) {
            chip.classList.add('is-unread');
            const mark = document.createElement('span');
            mark.className = 'room-occupant-unread';
            mark.textContent = '?';
            mark.title = livenessUnknown
                ? 'This occupant’s condition could not be read '
                  + `(${(liveness && liveness.basis) || 'unknown'}) — `
                  + 'treat it as unverified'
                : 'This occupant’s sheet could not be read '
                  + `(${unresolved}) — treat it as unverified`;
            chip.appendChild(mark);
        }

        return chip;
    },

    // Format exit data (handles both strings and structured dicts)
    _formatExit(exit) {
        if (typeof exit === 'string') return exit;
        if (typeof exit === 'object' && exit !== null) {
            const dir = exit.direction || 'unknown';
            const dest = exit.destination;
            return dest ? `${dir} (to ${dest})` : dir;
        }
        return String(exit);
    },

    // Format occupant data (handles both strings and structured dicts)
    _formatOccupant(occupant) {
        if (typeof occupant === 'string') return occupant;
        if (typeof occupant === 'object' && occupant !== null) {
            return occupant.name || occupant.entity_name || 'unknown';
        }
        return String(occupant);
    },

    /* A BUILTIN TYPED DURING PLAY ARRIVES AS ONE ROW OF A PHASE RESULT.
     *
     * `inventory`, `skills` and `stats` resolve inside the phase, so their
     * answer is a row of a batched `action_result` (`result.actions[]`), never
     * the top-level `action_type: 'builtin'` frame `_displayBuiltinResult` was
     * written for. The row names its builtin (`execution_result.builtin_type`);
     * this renders the VIEWER'S OWN such rows through the same arm, and no one
     * else's (the batch carries every character's action).
     */
    _surfaceOwnBuiltinRows(actions) {
        const viewer = this.state && this.state.playerId;
        for (const action of actions || []) {
            const execResult = action && action.execution_result;
            if (!execResult || !execResult.builtin_type) continue;
            if (!viewer || action.player_id !== viewer) continue;
            this._displayBuiltinResult({
                builtin_type: execResult.builtin_type,
                result: execResult,
            });
        }
    },

    // Display builtin command results (look, inventory, skills, help)
    // [2026-08-08-007-ENGINE] The `narrationExpected` parameter is GONE. It was
    // the FSM-mode gate that stood between ch9 prose and the screen, and a mode
    // is not a channel — keeping it as a vestigial argument would leave the next
    // reader believing this function still has a content gate. It does not; it
    // has a tripwire.
    _displayBuiltinResult(result) {
        const data = result.result;
        if (!data) return;

        /* [FB-HUD-08 / 2026-08-10-005-ENGINE] THE SERVER SAYS "I COULD NOT READ
         * THIS", AND THIS FUNCTION HAD NO ARM FOR IT.
         *
         * `BuiltinHandler` distinguishes UNKNOWN from EMPTY deliberately and at
         * some cost — `builtin_commands.py::_handle_skills` logs an error and
         * returns `{"skills": [], "error": "Your skills could not be read just
         * now."}` when the castable read does not answer, with the reason
         * written in its own comment: *"rendering an unresolved kit as an empty
         * list would tell the player they know nothing, which is a confident lie
         * and indistinguishable from the real thing"* ([2026-08-12-032-ENGINE]).
         * `_handle_inventory` does the same with `{"items": [], "gold": 0,
         * "error": ...}`.
         *
         * The arms below branched on `data.skills.length` and `data.items.length`
         * ALONE. `error` was never read. So the one case the server went out of
         * its way to mark as unanswerable rendered as **"You have no skills."**
         * and **"Your inventory is empty." / "Gold: 0"** — the exact sentences it
         * refused to let itself say, reinstated at the last hop. A plausible zero
         * (THE LOUD-FAILURE LAW), and the ship-two-of-three shape the channel law
         * names: the producer existed, the payload existed, THE ARM DID NOT.
         *
         * ⚠️ IT ALSO CONTRADICTED THE OTHER RENDERER OF THE SAME FACT. The
         * character screen is documented as forbidden from this sentence —
         * hud-screens.js:397-402, *"the sentence is deliberately NOT 'you have no
         * skills'"* — so the terminal and the screen could state opposite things
         * about one castable set on one beat.
         *
         * ⚠️ THE SERVER'S STRING IS **NOT** RENDERED VERBATIM, AND THE REASON IS
         * SPECIFIC. `data.error` is not one authored sentence — it is a union.
         * `_handle_skills` writes a player-ready one, but `BuiltinHandler.handle`
         * wraps every handler and returns `f"Failed to handle {type}: {e}"` on any
         * exception, and `_handle_stats` returns `f"stat block unreadable: {e!r}"`
         * — a Python exception **repr** in the player's face. Passing the field
         * through would ship the good case and the leak together, and the leak is
         * the one that shows up at a booth. Same rule the screens are held to:
         * *no engine identifier reaches the player's eye* (hud-screens.js:98) —
         * THE PLAYER GETS PLAIN LANGUAGE, THE CONSOLE GETS THE REASON.
         *
         * That is not a second authority for the REASON (the reason stays the
         * server's, intact, in the warning below). It is a client-owned sentence
         * for the one thing the client is deciding: what an unanswerable read
         * looks like on screen. */
        if (data.error) {
            console.warn(
                `[builtin] ${result.builtin_type} is UNKNOWN, not empty — the ` +
                `server could not answer and said so: ${data.error}. Rendering an ` +
                `honest absence; an empty list here would be a plausible zero. ` +
                `[FB-HUD-08 / 2026-08-12-032-ENGINE]`
            );
            this.logMessage(
                BUILTIN_UNREADABLE[result.builtin_type] || BUILTIN_UNREADABLE._
            );
            return;
        }

        if (result.builtin_type === 'look') {
            /* [2026-08-08-007-ENGINE / leak 1, second door] ROOM PROSE OFF A ch9
             * FRAME. Same tripwire as `result.narration` above, and the same
             * reason: this was gated on `narrationExpected` (the FSM mode) and
             * so rendered freely outside GAMEPLAY.
             *
             * ⚠️ The `if (narrationExpected) return;` early-out is GONE ON
             * PURPOSE. It used to skip the structured panel too, which meant the
             * gate that suppressed the prose also suppressed the exits - the
             * half of a ch9 frame that is correct to carry. The prose now never
             * renders in any mode, and the panel always does.
             */
            if (data.description) {
                console.warn(
                    '[CHANNEL] ch9 BOUNDARY VIOLATION: action_result(builtin/look) ' +
                    'carried a content payload (result.description, len=' +
                    String(data.description).length + '). channels.md section 4: ch9 ' +
                    'may not carry content. NOT RENDERED - room prose belongs to ch0.'
                );
            }
            // [WS-2 issue-7] Exits + occupants render as the structured
            // room-state panel (same affordance as handleRoomState), NOT as
            // `Exits: ` / `You see: ` prose lines in the .message stream.
            // ⚠️ NO THIRD ARGUMENT, DELIBERATELY. A builtin `look` result
            // carries no `castable_skills`, and passing `[]`/`null` here would
            // CLEAR the skills group on every look. Omitting the argument is
            // the "no refresh this beat" signal the panel already honours, so
            // the group survives a look and refreshes on the next room_state.
            this._renderRoomStatePanel(data.exits || [], data.occupants || []);
        } else if (result.builtin_type === 'inventory') {
            if (data.items && data.items.length > 0) {
                this.logMessage('Inventory: ' + data.items.join(', '));
            } else {
                this.logMessage('Your inventory is empty.');
            }
            if (data.gold !== undefined) {
                this.logMessage('Gold: ' + data.gold);
            }
        } else if (result.builtin_type === 'skills') {
            if (data.skills && data.skills.length > 0) {
                this.logMessage('Skills: ' + data.skills.join(', '));
            } else {
                this.logMessage('You have no skills.');
            }
        } else if (result.builtin_type === 'help') {
            if (data.commands) {
                data.commands.forEach(cmd => this.logMessage(cmd));
            }
        } else {
            /* [FB-HUD-08] A BUILTIN WITH NO ARM IS A COMMAND THAT ANSWERS WITH
             * SILENCE, AND `stats` IS ONE TODAY.
             *
             * `check_builtin` matches `stats` / `character` / `char` /
             * `my stats` (builtin_commands.py:63-66) and `_handle_stats` does
             * real work — it was rewritten in July specifically to stop
             * reporting a plausible-zero stat block, and its own docstring ends
             * *"…and the sheet membership the display layer iterates."* THERE IS
             * NO DISPLAY LAYER. The chain ran end to end and terminated in this
             * `if/else` with no arm, so the player typed a supported command and
             * the terminal said nothing at all.
             *
             * That is the CHANNEL REGISTRY LAW's shape one layer down: producer
             * shipped, payload shipped, arm absent — and a missing arm is a
             * NO-OP, never an error. So this arm exists to make it an error.
             *
             * ⚠️ IT IS LOUD IN THE CONSOLE AND SILENT ON SCREEN, DELIBERATELY.
             * Inventing a stat readout here would be designing a second
             * character sheet next to the one the HUD character screen already
             * renders from ONE projection — the exact two-renderers-one-fact
             * drift hud-screens.js:446-453 exists to prevent. The render (and
             * whether `stats` should simply OPEN that screen) is a design call,
             * filed rather than guessed. Same posture as `_refuseDormantProse`
             * above: say so rather than vanish. */
            console.error(
                `[builtin] NO CLIENT ARM for builtin_type='${result.builtin_type}'. ` +
                `The server answered and this client rendered NOTHING — the ` +
                `player typed a supported command and got silence. Keys on the ` +
                `payload: ${Object.keys(data).join(', ') || '(none)'}. ` +
                `[FB-HUD-08 / 2026-08-10-005-ENGINE]`
            );
        }
    },

    // Display arbitration results (dice roll info)
    _displayArbitrationResult(result) {
        const data = result.result;
        if (!data) return;

        if (data.roll !== null && data.roll !== undefined) {
            const outcome = data.success ? 'Success!' : 'Failed.';
            this.logMessage('[Roll: ' + data.roll + ' vs DC ' + data.dc + '] ' + outcome);
        }

        // Show the arbitration result message if no narration covered it
        if (!result.narration && data.message) {
            this.logMessage(data.message);
        }
    },

    // Display skill execution results
    _displaySkillResult(result) {
        const data = result.result;
        if (!data) return;

        // If no narration was generated, show the raw result message
        if (!result.narration && data.message) {
            this.logMessage(data.message);
        }
    },

    // Display unmatched action results
    _displayUnmatchedResult(result) {
        const data = result.result;

        // Show the unmatched message
        if (data && data.message) {
            if (!result.narration) {
                this.logMessage(data.message);
            }
        } else if (!result.narration) {
            this.logMessage('I don\'t understand that command.');
        }
    },

    // Handle creation flow messages (Phase 3.1.3a + 3.1.3b Voice + 3.1.3c Images)
    handleCreationFlow(message) {
        const sessionId = this.state.sessionId || 'unknown';
        const step = message.step;
        const content = message.content;
        const narrator = message.narrator;
        const audioUrl = message.audio_url;

        console.log(`[${sessionId}] Creation flow - step: ${step}, narrator: ${narrator || 'none'}, audio: ${audioUrl ? 'yes' : 'no'}`);

        // Track current flow step for prompt deferral
        this.state.currentFlowStep = step;

        // Display narrator prefix if present (except for character_choice with images)
        if (narrator && step !== 'character_choice') {
            const narratorNames = {
                'mother_time': 'Mother Time',
                'father_light': 'Father Light',
                'lord_dark': 'Lord Dark'
            };
            const displayName = narratorNames[narrator] || narrator;
            this.logMessage(`\n[${displayName}]`);
        }

        // Handle specific steps
        switch (step) {
            case 'flow_starting':
                // Flow is starting - voice generation in progress
                // Do NOT signal flow complete - wait for character_choice
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Creation flow starting (voice generation in progress)`);
                break;

            case 'welcome':
                // Welcome message - narrator header text shown immediately.
                // Narration audio+text arrives later via WebSocket "narration" message.
                if (content) {
                    this.logMessage(content);
                }
                this._creationNarrationPending = true;
                this._armWelcomeNarrationTimeout(sessionId, 'welcome');
                console.log(`[${sessionId}] Welcome - awaiting narration via WebSocket (90s timeout)`);
                break;

            case 'character_choice':
                // Character alignment choice with portraits
                // Gate behind narration - don't show choices until welcome narration completes.
                // If still in WELCOME_NARRATION, buffer the message. _releaseNarrationGate
                // will transition to ROSTER_DISPLAY and re-dispatch this message.
                if (TerminalFSM.mode === 'WELCOME_NARRATION') {
                    console.log(`[${sessionId}] Buffering character_choice until narration completes`);
                    this._pendingCharacterChoice = message;
                    break;
                }
                // An empty roster frame is NOT a selectable menu — it is a
                // failure (the GameServer roster/flow-context silent zero,
                // 2026-07-24-004-QASTI: the offer succeeded but the hand was
                // lost in-process). Render it as an explicit error + retry
                // affordance, NEVER the dead-end "choose 1, 2, or 3" waiting
                // prompt with zero options (the observed wedge). The server now
                // marks these frames with error/roster_empty; also treat a bare
                // empty characters[] as the same failure for defense in depth.
                const _rosterEmpty = message.roster_empty === true
                    || message.error === true
                    || !(message.characters && message.characters.length > 0);
                if (_rosterEmpty) {
                    console.error(`[${sessionId}] Empty character_choice frame — no vessels on the wire (roster/flow-context failure); rendering error+way-back, NOT a waiting menu`);
                    this._renderEmptyRosterFrame(message, content);
                    this._rosterOfferFailed = true;
                    // Open an input line so the player is never frozen with no
                    // affordance, but this is an error state, not a numeric
                    // character selection.
                    TerminalFSM.transition('roster_displayed');
                    TurnPump.settleForRetarget('empty-roster', 0);
                    break;
                }
                // A hand that would cover a scene still owed to the player waits
                // for it (THE RETARGET HOLD); otherwise it is shown now.
                if (this._shouldHoldRosterOffer()) {
                    this._holdRosterOffer(message);
                    break;
                }
                this._switchToRoster(message, 'offer', 0);
                break;

            case 'creating':
                // Character is being created
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Character creation in progress`);
                break;

            case 'reawaken':
                // Alignment narrator speaks the reawaken text
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Reawaken message from ${narrator}${audioUrl ? ' with audio' : ''}`);
                break;

            case 'void_description':
                // Void description before reawaken
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Void description displayed`);
                break;

            case 'destination_description':
                // Village square description after spawn
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Destination description displayed`);
                break;

            case 'invalid_choice':
                // Invalid input - stay in CHARACTER_CHOICE for another attempt
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Invalid choice entered`);
                TerminalFSM.transition('invalid_choice');
                break;

            case 'error':
                // Error in creation flow
                if (content) {
                    this.logMessage(content);
                }
                console.error(`[${sessionId}] Creation flow error`);
                // During character choice, treat error like invalid_choice
                if (TerminalFSM.mode === 'CHARACTER_CHOICE') {
                    TerminalFSM.transition('invalid_choice');
                } else if (TerminalFSM.mode === 'CREATION_FLOW') {
                    TerminalFSM.transition('flow_step_complete');
                }
                break;

            default:
                // Default: display content if present
                if (content) {
                    this.logMessage(content);
                }
                console.log(`[${sessionId}] Unknown creation flow step: ${step}`);
        }

        // Play audio narration if present (Phase 3.1.3b)
        if (audioUrl) {
            // Creation-flow narration (welcome/reawaken) is ch0-class, no cycle id.
            this.playNarrationAudio(audioUrl, 0, { cycleId: null, channel: 0 });
        }
    },

    // =====================================================================
    // THE RETARGET HOLD -- a selection never covers a scene the player is owed.
    //
    // A `character_choice` that lands in GAMEPLAY is a controls retarget: the
    // player's mortal is gone (death) and a selection is about to cover the
    // terminal. The drain does not run under a selection, so the switch waits
    // for the scenes that were already on their way when the offer arrived --
    // the fatal beat, and the void scene the death continuation sent behind it
    // -- and then SETTLES the pump (`TurnPump.settleForRetarget`) before the
    // mode changes. A completed chapter returning the party to the Episode
    // board is the same switch, so nothing here reads "death".
    //
    // WHAT THE HOLD WAITS FOR is fixed at the moment the offer arrives: every
    // ch0 received by then (`owedThrough`, an arrival ordinal), their voice,
    // and the beat in flight. Scenes that arrive later (the void's lobby beats
    // keep coming while the player chooses) never extend it.
    //
    // RELEASED by, whichever comes first:
    //   (a) ACKNOWLEDGEMENT -- the player presses Enter. They may always skip.
    //   (b) READ -- the owed scenes are done, voice finished, and
    //       ROSTER_HOLD_READ_MS has passed since the last of them rendered.
    //   (c) STALL -- ROSTER_HOLD_CEILING_MS with NO delivery progress (typed
    //       characters, voice position, a scene reaching its terminal). A long
    //       voiced scene is progress the whole way through; a stuck audio
    //       element or a wedged cycle is not.
    //   (d) MAX -- ROSTER_HOLD_MAX_MS from arrival, unconditionally. The one
    //       exit that holds whatever the delivery state claims.
    // =====================================================================

    // Readable dwell after the owed scenes finish delivering.
    ROSTER_HOLD_READ_MS: 6000,
    // No-progress ceiling: this long with nothing typed, voiced or completed.
    ROSTER_HOLD_CEILING_MS: 30000,
    // Absolute bound from the offer's arrival.
    ROSTER_HOLD_MAX_MS: 120000,

    /** True when a `character_choice` would cover a scene still owed to the player. */
    _shouldHoldRosterOffer() {
        if (typeof TerminalFSM === 'undefined' || TerminalFSM.mode !== 'GAMEPLAY') return false;
        return !!this._rosterOfferBeatUnread(this._retargetOwed());
    },

    /** What the player is owed right now: every ch0 received, and the beat in flight. */
    _retargetOwed() {
        const s = TurnPump._state;
        const cc = s && s.cycleCompletion;
        const inFlight = !!(cc && s.status !== 'idle' && !cc.ch0.rendered && !cc.ch0.voided);
        return { owedThrough: TurnPump._ch0Arrived, beat: inFlight ? (cc.beatId || 'unbound') : null };
    },

    /**
     * The REASON the owed delivery is incomplete, or null when it is done.
     * A reason, not a boolean, so the release line says what it waited on.
     */
    _rosterOfferBeatUnread(owed) {
        const p = TurnPump;
        const s = p._state;
        if (p._ch0DoneThrough < owed.owedThrough) {
            if (this._tokenDrainTimer) return 'typing';
            return s.status === 'playing' ? 'playing' : 'owed';
        }
        try {
            const cur = (typeof window !== 'undefined') ? window.currentNarration : null;
            if (cur && !cur.paused && !cur.ended) return 'voice';
        } catch (_e) { /* audio introspection is best-effort */ }
        if (owed.beat) {
            const cc = s.cycleCompletion;
            if (cc && s.status !== 'idle' && (cc.beatId || 'unbound') === owed.beat
                    && !cc.ch0.rendered && !cc.ch0.voided) {
                return 'beat-in-flight';
            }
        }
        const last = p._ch0LastRendered;
        if (last && last.ordinal <= owed.owedThrough
                && (Date.now() - last.at) < this.ROSTER_HOLD_READ_MS) {
            return 'reading';
        }
        return null;
    },

    /** Delivery progress, as a comparable string: moves while anything owed advances. */
    _retargetProgress() {
        let voicePos = '';
        try {
            const cur = (typeof window !== 'undefined') ? window.currentNarration : null;
            if (cur && !cur.paused && !cur.ended) voicePos = Math.floor((cur.currentTime || 0) * 2);
        } catch (_e) { /* best-effort */ }
        const typed = this._streamingElement ? (this._streamingElement.textContent || '').length : '';
        return `${TurnPump._ch0DoneThrough}|${TurnPump._ch0PlayingOrdinal}|${typed}|${voicePos}`;
    },

    /** Enter the hold: tell the player, fix what it waits for, arm the exits. */
    _holdRosterOffer(message) {
        const sessionId = this.state.sessionId || 'unknown';
        if (this._rosterHold) {
            // A later offer replaces the payload; what the hold waits for, and
            // its bounds, stay those of the FIRST offer.
            this._rosterHold.message = message;
            return;
        }
        const owed = this._retargetOwed();
        const reason = this._rosterOfferBeatUnread(owed) || 'unknown';
        const now = Date.now();
        this._rosterHold = {
            message, owed, reason, startedAt: now,
            progress: this._retargetProgress(), progressAt: now,
        };
        console.warn(
            `[${sessionId}] [roster-hold] HOLDING the vessel offer: a scene is still ` +
            `reaching the player (reason=${reason} owed_through=${owed.owedThrough} ` +
            `beat=${owed.beat || 'none'}).`
        );
        try {
            this._renderSystemNotice(
                'Press ENTER when you are ready to take up a vessel.', 'roster-hold');
        } catch (_e) { /* the hold must arm even if the notice cannot render */ }
        this._rosterHold.maxTimer = setTimeout(() => {
            this._releaseRosterOffer('max');
        }, this.ROSTER_HOLD_MAX_MS);
        this._scheduleRosterHoldRecheck();
    },

    /** The rolling re-check: release, stall out, or wait another tick. */
    _scheduleRosterHoldRecheck() {
        if (!this._rosterHold) return;
        if (this._rosterHold.recheckTimer) clearTimeout(this._rosterHold.recheckTimer);
        this._rosterHold.recheckTimer = setTimeout(() => {
            if (!this._rosterHold) return;
            this._rosterHold.recheckTimer = null;
            this._maybeReleaseRosterHold('read');
        }, 400);
    },

    /**
     * Release if the owed delivery is done; release on a stall; otherwise
     * re-arm. `acknowledged` releases unconditionally.
     */
    _maybeReleaseRosterHold(reason) {
        const hold = this._rosterHold;
        if (!hold) return false;
        if (reason === 'acknowledged') {
            this._releaseRosterOffer(reason);
            return true;
        }
        const unread = this._rosterOfferBeatUnread(hold.owed);
        if (!unread) {
            this._releaseRosterOffer(reason || 'read');
            return true;
        }
        hold.reason = unread;
        const now = Date.now();
        const progress = this._retargetProgress();
        if (progress !== hold.progress) {
            hold.progress = progress;
            hold.progressAt = now;
        } else if (now - hold.progressAt >= this.ROSTER_HOLD_CEILING_MS) {
            this._releaseRosterOffer('stall');
            return true;
        }
        this._scheduleRosterHoldRecheck();
        return false;
    },

    /** Perform the deferred offer. */
    _releaseRosterOffer(reason) {
        const hold = this._rosterHold;
        if (!hold) return;
        this._rosterHold = null;
        if (hold.maxTimer) clearTimeout(hold.maxTimer);
        if (hold.recheckTimer) clearTimeout(hold.recheckTimer);
        const sessionId = this.state.sessionId || 'unknown';
        const line = `[${sessionId}] [roster-hold] RELEASED after ${Date.now() - hold.startedAt}ms ` +
            `(reason=${reason}, last_wait=${hold.reason})`;
        if (reason === 'stall' || reason === 'max') {
            console.error(`${line} -- the owed delivery never finished; the offer is ` +
                `shown anyway so the player is never stranded by this hold.`);
        } else {
            console.log(line);
        }
        // A scene skipped by the player's own Enter is their choice; one lost to
        // a forced release is a loss, and `settleForRetarget` reports it so.
        const forced = (reason === 'stall' || reason === 'max');
        this._switchToRoster(hold.message, reason, forced ? hold.owed.owedThrough : 0);
    },

    /**
     * THE ARM OF EVERY ENVELOPED KIND (`frame.md` P2). `pump`: the TurnPump's
     * one frame arm. `board`: the void's selection, beside the hand. A kind the
     * registry envelopes and this table lacks is refused loudly at the door;
     * `test_every_enveloped_kind_has_a_client_arm` fails the build first.
     */
    _ENVELOPE_ARMS: {
        'clock': 'pump',
        'prompt-state': 'pump',
        'episode-board': 'board',
    },

    /** Whether a selection message is the Episode board (else it is a hand). */
    _isEpisodeBoard(message) {
        return !!message && message.type === 'frame' && message.kind === 'episode-board';
    },

    /**
     * The Episode board (`episodes.md` 7.2): the void's routes, answered by a
     * number before the hand. It takes the hand's path exactly -- held behind
     * the welcome narration and the retarget hold, shown by `_switchToRoster`,
     * answered in CHARACTER_CHOICE -- so the void has one way to ask.
     */
    handleEpisodeBoard(message) {
        const sessionId = this.state.sessionId || 'unknown';
        this.state.currentFlowStep = 'episode_board';
        if (TerminalFSM.mode === 'WELCOME_NARRATION') {
            console.log(`[${sessionId}] Buffering episode board until narration completes`);
            this._pendingCharacterChoice = message;
            return;
        }
        if (this._shouldHoldRosterOffer()) {
            this._holdRosterOffer(message);
            return;
        }
        this._switchToRoster(message, 'offer', 0);
    },

    /** Re-dispatch a selection the welcome narration held: a board or a hand. */
    _dispatchSelection(message) {
        if (this._isEpisodeBoard(message)) {
            this.handleEpisodeBoard(message);
        } else {
            this.handleCreationFlow(message);
        }
    },

    /**
     * The board in the terminal (the shell renders its own when it is active):
     * the board view as a panel at the foot of the history, or, with no view
     * loaded, numbered lines. Either way a player reads titles from data -- a
     * null one reads unnamed, never the entity name.
     */
    displayEpisodeBoard(message) {
        const view = this._terminalBoardView();
        if (view) {
            view.showBoard(message.payload);
            return;
        }
        const routes = message.payload.routes;
        const unnamed = (t) => (t === null ? '(unnamed)' : t);
        this.logMessage(routes.length === 1 ? 'One road is open to you:' : 'The roads before you:');
        this.logMessage('');
        routes.forEach((r) => {
            const where = (r.t === null) ? unnamed(r.episode_title)
                : `${unnamed(r.episode_title)} (T${r.t})`;
            const state = (r.state === 'full')
                ? '  -- full: everyone it lists is in play in this world'
                : `  -- ${r.free} can play`;
            this.logMessage(`[${r.choice}] ${where} -- ${unnamed(r.title)}${state}`);
        });
        this.logMessage('');
        const nums = routes.map((r) => String(r.choice));
        const phrase = nums.length <= 2 ? nums.join(' or ')
            : `${nums.slice(0, -1).join(', ')}, or ${nums[nums.length - 1]}`;
        this.logMessage(`Choose your road (${phrase}):`);
    },

    /**
     * The terminal's board view, its panel moved to the foot of the history so
     * a re-offered board is never left above newer lines. Null without a DOM
     * or a loaded view (a test harness): the caller prints lines instead.
     */
    _terminalBoardView() {
        const V = (typeof window !== 'undefined') ? window.EpisodeBoardView : null;
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        const history = (typeof document !== 'undefined') ? document.getElementById('history') : null;
        if (!V || !history) return null;
        if (!F) {
            console.error('[board] no TerminalFunnel: the board is printed as lines');
            return null;
        }
        if (!this._boardHost) {
            this._boardHost = document.createElement('div');
            this._boardHost.className = 'eb-host';
            this._boardView = V.createBoardView(document, this._boardHost, (text) => {
                // A panel outlives its question once the void is answered: a
                // click then is no answer, and the panel goes.
                if (typeof TerminalFSM !== 'undefined' && TerminalFSM.mode !== 'CHARACTER_CHOICE') {
                    console.warn(`[board] a click in ${TerminalFSM.mode} answers nothing; retiring the panel`);
                    this._retireTerminalBoard();
                    return;
                }
                this.processInput(text);
            });
        }
        if (this._boardHost.parentNode === history) {
            F.reposition(this._boardHost, { parent: history });
        } else {
            F.insert(this._boardHost, { origin: F.ORIGIN.GAME_STATE_TRANSITION, parent: history });
        }
        return this._boardView;
    },

    /** Leave the board's panel behind once the void is answered (a claim, a disconnect). */
    _retireTerminalBoard() {
        if (this._boardHost) {
            this._boardHost.remove();
            this._boardHost = null;
            this._boardView = null;
        }
    },

    /**
     * THE ONE SWITCH into a void selection: render the board or the hand,
     * change mode, then settle the pump so nothing in flight is left under the
     * selection. Every path that shows a board or a `character_choice` hand
     * ends here.
     */
    _switchToRoster(message, cause, owedThrough) {
        const sessionId = this.state.sessionId || 'unknown';
        this._rosterOfferFailed = false;
        if (this._isEpisodeBoard(message)) {
            console.log(`[${sessionId}] Awaiting a road from the Episode board`);
            this.displayEpisodeBoard(message);
        } else {
            console.log(`[${sessionId}] Awaiting character choice (1, 2, or 3)`);
            this.displayCharacterChoices(message);
        }
        if (typeof TerminalFSM !== 'undefined') {
            TerminalFSM.transition('roster_displayed');
        }
        TurnPump.settleForRetarget(cause, owedThrough);
    },

    /** Drop a held offer without performing it (disconnect / teardown). */
    _clearRosterHold() {
        const hold = this._rosterHold;
        if (!hold) return;
        this._rosterHold = null;
        if (hold.maxTimer) clearTimeout(hold.maxTimer);
        if (hold.recheckTimer) clearTimeout(hold.recheckTimer);
        console.warn('[roster-hold] cleared without releasing (connection teardown)');
    },

    // Display character choices with portraits (Phase 3.1.3c)
    // Layout: simple intro, then each card as [N] Title / portrait / stats
    // Skips message.content to avoid duplicate stats from build_roster_choice_text()
    displayCharacterChoices(message) {
        const sessionId = this.state.sessionId || 'unknown';
        console.log(`[${sessionId}] Displaying character choices with portraits`);
        // A hand dealt for a route is the board's last step: headed as its plot
        // line, with the way back to the board.
        if (message.cast) {
            const view = this._terminalBoardView();
            if (view) {
                view.showHand(message);
                return;
            }
            this.logMessage(message.cast.title === null ? '(unnamed)' : message.cast.title);
            if (message.cast.heading) this.logMessage(message.cast.heading);
        } else {
            this._retireTerminalBoard();
        }

        // Simple intro line (replaces verbose message.content that duplicated stats)
        // [2026-07-29-012-QASTI] DERIVED FROM THE ROSTER BEING RENDERED. The
        // post-death offer is frequently ONE vessel; "Three souls await rebirth"
        // over a single card is a visible contradiction on the most emotionally
        // loaded screen in the game. Copy never asserts a count the payload does
        // not carry.
        const _n = (message.characters && message.characters.length) || 0;
        this.logMessage(_n === 1
            ? 'One soul awaits rebirth:'
            : `${_n} souls await rebirth. Choose wisely:`);
        this.logMessage('');

        // Display each character card: title, image, description (stats)
        const imageLoadPromises = [];

        message.characters.forEach((char, index) => {
            // Space before title creates visual grouping
            this.logMessage('\u00A0');

            // Title above image
            this.logMessage(`[${char.choice}] ${char.title}`);

            // Image (inline) - track loading
            if (char.image_url) {
                const loadPromise = this.displayCharacterImage(char.image_url, char.title);
                imageLoadPromises.push(loadPromise);
            }

            // Description (stats from format_roster_character_data) below image
            if (char.description) {
                this.logMessage(char.description);
            }
        });

        // Prompt
        if (message.prompt) {
            this.logMessage('');
            this.logMessage(message.prompt);
        }

        // Wait for all images to load (or fail gracefully) before signaling complete
        Promise.all(imageLoadPromises).then(() => {
            console.log(`[${sessionId}] All character images loaded`);
        }).catch(err => {
            console.warn(`[${sessionId}] Some character images failed to load:`, err);
        });
    },

    // Display a character portrait image (Phase 3.1.3c)
    displayCharacterImage(imageUrl, altText) {
        return new Promise((resolve) => {
            const history = document.getElementById('history');
            if (!history) {
                resolve();
                return;
            }

            const imgContainer = document.createElement('div');
            imgContainer.className = 'character-portrait-container';

            const img = document.createElement('img');
            img.src = imageUrl;
            img.className = 'character-portrait';
            img.alt = altText || 'Character Portrait';
            img.loading = 'eager';  // Load immediately

            // Handle load success
            img.onload = () => {
                console.log(`Character portrait loaded: ${imageUrl}`);
                resolve();
            };

            // Handle load failure gracefully
            img.onerror = () => {
                console.warn(`Character portrait failed to load: ${imageUrl}`);
                // Add placeholder text instead
                imgContainer.innerHTML = `<div class="character-portrait-placeholder">[Portrait: ${altText}]</div>`;
                resolve();
            };

            imgContainer.appendChild(img);

            // Actor model: two-path placement based on prompt lock state.
            // [2026-08-03-014-ENGINE stage 2] state via CommandLine, position
            // via the anchor -- see the split at WaitingIndicator.start().
            const _cl = (typeof window !== 'undefined') ? window.CommandLine : null;
            const activeInput = _cl ? _cl.anchor(history) : history.querySelector('.active-input-line');
            const promptOpen = _cl ? _cl.isOpen() : !!activeInput;
            const passiveMode = (typeof TurnPump !== 'undefined') ? TurnPump.getNarrationMode() === 'passive' : false;
            let before = null;
            if (promptOpen && activeInput && passiveMode) {
                const clockEl = (typeof ClockLine !== 'undefined') ? ClockLine.getElement() : null;
                before = (clockEl && clockEl.parentNode === history) ? clockEl : activeInput;
            }

            // [2026-08-08-007-ENGINE] ORIGIN: game-state-transition. A roster
            // PORTRAIT during character creation - there is no world, no beat
            // and no clock yet, so it carries neither a channel nor a cycle.
            const _F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
            if (_F) {
                _F.insert(imgContainer, {
                    origin: _F.ORIGIN.GAME_STATE_TRANSITION,
                    parent: history,
                    before: before
                });
            }
        });
    },

    // Play narration audio via GANDALF SSE audio channel
    // Audio chunks are queued and played sequentially for seamless narration
    // Resolve a GANDALF audio URL to something the browser can fetch.
    //  - absolute http(s):// (v52 signed GCS URL) -> use as-is (prod: direct GCS fetch)
    //  - already-proxied /gandalf/... -> use as-is
    //  - bare relative /audio/... (local dev / legacy) -> prefix /gandalf for the vite proxy
    resolveAudioUrl(url) {
        if (!url) return url;
        if (/^https?:\/\//i.test(url)) return url;      // absolute signed URL
        if (url.startsWith('/gandalf')) return url;      // already proxied
        return `/gandalf${url}`;                          // local-dev vite proxy fallback
    },

    // =========================================================================
    // Voice-drop observability (2026-07-11-008-ENGINE)
    // =========================================================================
    // Voice drops are structurally invisible: a missing audio_url is near-silent,
    // a stuck audio queue never logs, audio outcome is decoupled from the pump/VP,
    // and a load failure throws away the HTTP status (404 upload-race vs 403
    // signed-URL expiry vs network). These helpers give every drop mode ONE
    // stable, greppable, cycle-stamped structured event on the console ->
    // logHistory -> /api/client-logs dump rail (game-diagnostics.js) so /survey
    // can grep a single token ([VoiceDrop]) and VP scenarios can assert on the
    // window.__VOICE_OUTCOMES ring buffer.
    //
    // GUARDRAIL: these are OBSERVATIONAL ONLY. They never touch the TurnPump
    // reducer, the drain latches, or gate semantics — playback_complete still
    // fires on typewriter completion (audio outcome is a breadcrumb, not a gate).
    _VOICE_OUTCOMES_MAX: 50,
    VOICE_QUEUE_STALL_MS: 20000,   // audio-queue stall window (distinct from the 90s pump watchdog)

    // =========================================================================
    // P2 -- THE ch0 PLAYBACK CEILING  [2026-08-21-001-ENGINE]
    // user ruling, 2026-08-21/22
    // =========================================================================
    //
    // TWO CEILINGS, TWO AXES. They are both 45s and they are NOT the same
    // parameter; conflating them is how this was first mis-specified.
    //
    //   P1  bounds voice GENERATION/ARRIVAL. Server-side, in
    //       `game_server/gandalf_client.py::VOICE_HOLD_CEILING_SECONDS`. Past
    //       it the frame ships text-only and the voice is DROPPED.
    //       A 55s clip that ARRIVES in 10s never touches P1 -- clip DURATION
    //       was never P1's business.
    //
    //   P2  (this one) bounds narration PLAYBACK. Client-side. At the ceiling
    //       the prompt's narration gate is satisfied and the player gets their
    //       input back; the narration KEEPS PLAYING.
    //
    // THE ANCHOR IS ch0 TEXT RENDER -- the instant the typewriter starts for
    // this packet. Stated explicitly because P1's anchor is genuinely
    // ambiguous ("45s into a turn heartbeat" vs. from poll start) and is still
    // the project owner's call; P2 must not inherit that ambiguity. Not turn
    // start, not frame arrival, not audio start: the moment the first character
    // hits the screen.
    //
    // WARNING: DO NOT CUT, FADE, OR CROP THE AUDIO HERE. Overflow past the
    // ceiling is explicitly acceptable, verbatim: "the prompt is unlocked at
    // 45sec and the narration continues to 55seconds. no issue." Same
    // invariant the voice DROP already honors -- a clip that is already
    // PLAYING is never killed, only queued ones are discarded.
    //
    // WHY 55s IS THE RIGHT OVERFLOW BOUND AND NOT A COINCIDENCE: the ratified
    // narration cap ([2026-07-25-001-PROMPTS]) is 120 words, which at the
    // measured 13.3 chars/sec is ~55.0s voiced. 45s unlock + 10s overflow =
    // 55s. The cap needs no amendment -- but note that its authored
    // justification is "under the Higgs ~80s limit", a much looser bound. It
    // now has a tighter reason, and if the cap is ever raised THIS ceiling is
    // what starts firing.
    //
    // A PROPERTY, not a module const, for the same reason VOICE_QUEUE_STALL_MS
    // is one: the harness pin has to be able to shrink it, and the shipped
    // value has to be readable off the object rather than restated in a test.
    CH0_PLAYBACK_CEILING_MS: 45000,

    // Emit one structured [VoiceDrop] console line. payload MUST carry `event`.
    _emitVoiceEvent(level, payload) {
        const sid = (this.state && this.state.sessionId) || 'unknown';
        let line;
        try {
            line = `[VoiceDrop] ${JSON.stringify(Object.assign({ session: sid }, payload))}`;
        } catch (_e) {
            line = `[VoiceDrop] {"session":"${sid}","event":"${(payload && payload.event) || 'unknown'}"}`;
        }
        if (level === 'error') console.error(line);
        else if (level === 'warn') console.warn(line);
        else console.log(line);
    },

    // =========================================================================
    // Voice void CLASS  [2026-07-27-003-QASTI BUG 3, client half]
    // =========================================================================
    // MIRRORS the server's single-owner derivation in
    // services/plug_core/frame.py::void_class_for
    // ([2026-07-19-006-ENGINE] render-honesty 3a). Same vocabulary, same closed
    // set, same default — deliberately NOT a parallel one.
    //
    // Before this, `voice_absent` / `no_url_on_ch0` fired IDENTICALLY whether
    // voice was never requested (ch1 is unvoiced by design; the player's sound
    // pref is OFF) or was requested and dropped. That conflation is what made a
    // silent ch0 a plausible zero in prod and cost two tickets to unpick.
    //
    // ABSENCE = "no voice was ever coming, and that is correct":
    //   sound_off              — the player muted; playback suppressed client-side.
    //   sound_pref_off         — pref is OFF, so the server fired include_voice=false.
    //   ch1_unvoiced_by_design — ch1 commentary is hard-coded include_voice=False
    //                            to halve voice-GPU load. NEVER a failure.
    //   voice_not_requested    — the frame itself says voice was not requested.
    //
    // FAILURE = "voice was expected here and did not arrive / did not play":
    //   no_url_on_ch0, autoplay_blocked, load_error, queue_stall,
    //   unlock_queue_overflow — and ANY unknown reason (LOUD-FAILURE LAW: the
    //   safe wrong direction is the visible one).
    _VOICE_ABSENCE_REASONS: [
        'sound_off',
        'sound_pref_off',
        'ch1_unvoiced_by_design',
        'voice_not_requested',
    ],

    _voiceClassForReason(reason) {
        return this._VOICE_ABSENCE_REASONS.indexOf(reason) !== -1 ? 'absence' : 'failure';
    },

    // Record ONE audio_outcome breadcrumb: outcome in played|failed|suppressed|absent.
    // Pushes to the window.__VOICE_OUTCOMES ring buffer (VP-assertable) AND emits a
    // structured [VoiceDrop] event. meta: {cycleId, channel, audioUrl, code, errorName,
    // httpStatus, attempt, reason}. Purely observational — no pump interaction.
    _recordVoiceOutcome(outcome, meta) {
        meta = meta || {};
        const rec = {
            ts: Date.now(),
            outcome,
            cycle_id: meta.cycleId != null ? meta.cycleId : null,
            channel: meta.channel != null ? meta.channel : null,
            audio_url: meta.audioUrl || null,
            code: meta.code != null ? meta.code : null,
            error_name: meta.errorName || null,
            http_status: meta.httpStatus != null ? meta.httpStatus : null,
            attempt: meta.attempt != null ? meta.attempt : null,
            reason: meta.reason || null,
            // [BUG 3] 'absence' | 'failure' | null. Never derived for `played`
            // (a clip that played is neither) — deriving it there would map a
            // null reason onto 'failure' and manufacture a fault out of success.
            void_class: (outcome === 'played')
                ? null
                : (meta.voidClass || this._voiceClassForReason(meta.reason || null)),
        };
        try {
            if (typeof window !== 'undefined') {
                if (!window.__VOICE_OUTCOMES) window.__VOICE_OUTCOMES = [];
                window.__VOICE_OUTCOMES.push(rec);
                while (window.__VOICE_OUTCOMES.length > this._VOICE_OUTCOMES_MAX) {
                    window.__VOICE_OUTCOMES.shift();
                }
            }
        } catch (_e) { /* ring buffer is best-effort */ }
        this._emitVoiceEvent(outcome === 'failed' ? 'warn' : 'log', {
            event: 'audio_outcome',
            outcome,
            cycle_id: rec.cycle_id,
            channel: rec.channel,
            audio_url: rec.audio_url,
            code: rec.code,
            error_name: rec.error_name,
            http_status: rec.http_status,
            reason: rec.reason,
            void_class: rec.void_class,
        });
    },

    // Diagnostic-only HEAD probe on the FAILURE path (never on the happy path —
    // keeps the zero-overhead happy path). Records 404 (upload race / missing
    // object) vs 403 (signed-URL expiry) vs network error (connectivity) so the
    // structured failure data directs the GCS-side dig. NEVER retries playback
    // from the result — it is purely a status read.
    _probeAudioHead(audioUrl, meta) {
        meta = meta || {};
        if (!audioUrl || typeof fetch !== 'function') return;
        try {
            fetch(audioUrl, { method: 'HEAD' })
                .then((resp) => {
                    this._emitVoiceEvent(resp.ok ? 'warn' : 'error', {
                        event: 'audio_head_probe',
                        cycle_id: meta.cycleId != null ? meta.cycleId : null,
                        channel: meta.channel != null ? meta.channel : null,
                        audio_url: audioUrl,
                        http_status: resp.status,
                        network_error: false,
                    });
                })
                .catch((err) => {
                    this._emitVoiceEvent('error', {
                        event: 'audio_head_probe',
                        cycle_id: meta.cycleId != null ? meta.cycleId : null,
                        channel: meta.channel != null ? meta.channel : null,
                        audio_url: audioUrl,
                        http_status: null,
                        network_error: true,
                        error_name: (err && err.name) || 'FetchError',
                    });
                });
        } catch (_e) { /* HEAD probe is best-effort diagnostics */ }
    },

    // --- Audio-queue stall watchdog (distinct from TurnPump's 90s cycle watchdog).
    // If _isPlayingAudio sticks true with a non-empty narrationQueue and the
    // current clip makes no progress for VOICE_QUEUE_STALL_MS, everything queued
    // behind it never plays. This watchdog logs voice_queue_stall AND self-heals
    // (clear the flag, drop the wedged element, advance the queue). CONSERVATIVE:
    // a clip whose currentTime is still advancing (a long legit narration) rolls
    // the window instead of being killed.
    _armVoiceQueueWatchdog() {
        this._clearVoiceQueueWatchdog();
        this._voiceQueueWatchdogTimer = setTimeout(() => {
            this._voiceQueueWatchdogTimer = null;
            this._voiceQueueWatchdogFired();
        }, this.VOICE_QUEUE_STALL_MS);
    },
    _clearVoiceQueueWatchdog() {
        if (this._voiceQueueWatchdogTimer) {
            clearTimeout(this._voiceQueueWatchdogTimer);
            this._voiceQueueWatchdogTimer = null;
        }
    },
    _voiceQueueWatchdogFired() {
        const queueDepth = (typeof window !== 'undefined' && window.narrationQueue)
            ? window.narrationQueue.length : 0;
        // Not actually stuck: nothing playing OR queue drained -> stand down.
        if (!this._isPlayingAudio || queueDepth === 0) {
            return;
        }
        const cur = (typeof window !== 'undefined') ? window.currentNarration : null;
        // Progress guard: a long legit clip whose playhead advanced this window
        // must NOT be killed — roll the window and keep watching.
        if (cur && !cur.ended && !cur.paused
            && typeof cur.currentTime === 'number'
            && cur.currentTime > (this._voiceWatchLastTime || 0)) {
            this._voiceWatchLastTime = cur.currentTime;
            this._armVoiceQueueWatchdog();
            return;
        }
        // Genuine stall: the playing element made no progress across the window.
        const meta = {
            cycleId: this._currentNarrationCycleId || null,
            channel: this._currentNarrationChannel != null ? this._currentNarrationChannel : null,
            audioUrl: (cur && cur.src) || null,
        };
        this._emitVoiceEvent('error', {
            event: 'voice_queue_stall',
            cycle_id: meta.cycleId,
            channel: meta.channel,
            audio_url: meta.audioUrl,
            queue_depth: queueDepth,
            playhead: (cur && typeof cur.currentTime === 'number') ? cur.currentTime : null,
            stall_ms: this.VOICE_QUEUE_STALL_MS,
            self_healed: true,
        });
        this._recordVoiceOutcome('failed', Object.assign({ reason: 'queue_stall' }, meta));
        // Self-heal: drop the wedged element, clear the flag, advance the queue.
        try {
            if (cur) { cur.pause(); }
        } catch (_e) { /* element may already be dead */ }
        if (typeof window !== 'undefined') window.currentNarration = null;
        this._isPlayingAudio = false;
        this._voiceWatchLastTime = 0;
        this._playNextQueued();
    },

    // =========================================================================
    // iOS / autoplay-policy audio unlock  [2026-07-27-003-QASTI BUG 2]
    // =========================================================================
    // THE BUG THIS REPLACES: `_playAudioDirect` caught NotAllowedError (the
    // autoplay-policy rejection), emitted telemetry, and then blind-retried the
    // SAME play() after 500ms. On iOS Safari that can NEVER succeed — Safari
    // grants media permission only inside a USER-GESTURE call stack, and a
    // setTimeout callback is by definition outside one. Prod-confirmed: the
    // iPhone session was DELIVERED 8 ch0 frames with audio and played zero.
    // Desktop Chrome relaxes autoplay after domain interaction, which is exactly
    // why desktop "recovers after a few turns" and the phone never does.
    //
    // THE FIX, three parts:
    //   1. ARM a one-shot, document-level, capture-phase gesture listener at
    //      boot (GameServerConnection.init() runs on DOMContentLoaded, i.e.
    //      BEFORE login) so the FIRST gesture the player makes anywhere — the
    //      login tap, a keystroke, a tap on the terminal — primes audio. The
    //      player must NOT have to find the SOUND toggle or the
    //      "TAP TO ENABLE SOUND" affordance; those remain as EXPLICIT opt-in
    //      surfaces, this is the passive safety net behind them.
    //   2. On a NotAllowedError, QUEUE the clip (bounded) instead of looping a
    //      retry that is guaranteed to fail, and FLUSH the queue synchronously
    //      inside the next gesture handler — the only stack where play() is
    //      permitted. Retry pressure is therefore bounded by user actions, not
    //      by a timer.
    //   3. PUBLISH the unlock state on window.__VOICE_UNLOCK_STATE (+ breadcrumbs
    //      in __VOICE_OUTCOMES) so a VP or a human can SEE "audio is locked"
    //      instead of inferring it from silence.
    //
    // Ordering with voice-affordance.js: that module owns the VISIBLE
    // "TAP TO ENABLE SOUND" control and exposes unlock(). We DELEGATE the actual
    // priming to it when present so there is one primer implementation and one
    // `_unlocked` latch for the explicit path; the inline primer below is the
    // fallback for load-order/test contexts where it is absent.

    // A minimal valid silent WAV. Playing it inside a gesture satisfies iOS's
    // "the user played a media element" requirement. Byte-identical to the one
    // in voice-affordance.js (fallback path only — see delegation note above).
    _AUDIO_UNLOCK_SILENT_WAV: 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEA'
        + 'RKwAAIhYAQACABAAZGF0YQAAAAA=',

    // Gesture types that count as a media-activation gesture. A single tap
    // fires several of these; the idempotence guard collapses them.
    _AUDIO_UNLOCK_GESTURES: ['touchend', 'pointerup', 'mouseup', 'click', 'keydown'],

    // Bounded hold for clips rejected by the autoplay policy. Deliberately SMALL:
    // narration goes stale fast, and replaying a five-minute-old room description
    // on the tap that finally unlocks audio is worse than dropping it. Overflow
    // drops the OLDEST and is recorded LOUDLY as a failed outcome — never a
    // silent discard.
    BLOCKED_AUDIO_MAX: 3,

    _audioUnlocked: false,        // confirmed: something actually played
    _audioUnlockArmed: false,     // gesture listeners installed
    _audioUnlockGestures: 0,      // gestures observed since arming
    _audioUnlockPrimed: false,    // the silent primer has been played at least once
    _audioBlockedCount: 0,        // clips rejected by autoplay policy
    _audioFlushedCount: 0,        // clips re-attempted from a gesture flush
    _blockedAudioQueue: null,     // [{url, meta}] — lazily created
    _audioUnlockPrimer: null,     // reusable hidden <audio> for the fallback primer

    // The observable unlock snapshot. Written to window.__VOICE_UNLOCK_STATE on
    // every transition so a VP scenario / a human in devtools can read the state
    // directly rather than inferring it from the absence of sound.
    _publishUnlockState(reason) {
        const snap = {
            unlocked: this._audioUnlocked === true,
            armed: this._audioUnlockArmed === true,
            gestures_seen: this._audioUnlockGestures || 0,
            blocked_count: this._audioBlockedCount || 0,
            flushed_count: this._audioFlushedCount || 0,
            blocked_queue_depth: this._blockedAudioQueue ? this._blockedAudioQueue.length : 0,
            last_reason: reason || null,
            ts: Date.now(),
        };
        try {
            if (typeof window !== 'undefined') window.__VOICE_UNLOCK_STATE = snap;
        } catch (_e) { /* observability is best-effort */ }
        return snap;
    },

    // Install the one-shot gesture listeners. Idempotent — safe to call from
    // init(), from a reconnect, or from module load.
    _armAudioUnlockGesture() {
        if (this._audioUnlockArmed) return false;
        if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
            // No DOM to listen on. Say so LOUDLY rather than silently pretending
            // audio is armed — a silent no-op here is how the phone went quiet.
            this._emitVoiceEvent('warn', {
                event: 'audio_unlock_arm_skipped',
                reason: 'no_document',
            });
            return false;
        }
        const handler = (ev) => { this._onUnlockGesture(ev); };
        this._audioUnlockHandler = handler;
        for (const type of this._AUDIO_UNLOCK_GESTURES) {
            try {
                // Capture phase: the gesture is observed even if a nested handler
                // stops propagation. passive: a primer never calls preventDefault.
                document.addEventListener(type, handler, { capture: true, passive: true });
            } catch (_e) {
                // Older engines reject the options object -> boolean capture.
                try { document.addEventListener(type, handler, true); } catch (_e2) { /* give up on this type */ }
            }
        }
        this._audioUnlockArmed = true;
        this._emitVoiceEvent('log', {
            event: 'audio_unlock_armed',
            gestures: this._AUDIO_UNLOCK_GESTURES.join(','),
        });
        this._publishUnlockState('armed');
        return true;
    },

    _disarmAudioUnlockGesture() {
        if (!this._audioUnlockArmed) return;
        const handler = this._audioUnlockHandler;
        if (handler && typeof document !== 'undefined'
            && typeof document.removeEventListener === 'function') {
            for (const type of this._AUDIO_UNLOCK_GESTURES) {
                try { document.removeEventListener(type, handler, { capture: true }); } catch (_e) { /* noop */ }
                try { document.removeEventListener(type, handler, true); } catch (_e) { /* noop */ }
            }
        }
        this._audioUnlockArmed = false;
        this._audioUnlockHandler = null;
        this._publishUnlockState('disarmed');
    },

    // A user gesture landed. THIS is the only stack in which iOS permits play(),
    // so both the primer AND the blocked-clip flush must happen synchronously
    // from here — deferring either to a microtask/timer forfeits the permission.
    _onUnlockGesture(ev) {
        this._audioUnlockGestures = (this._audioUnlockGestures || 0) + 1;
        const hasParked = Boolean(this._blockedAudioQueue && this._blockedAudioQueue.length);
        // Once audio is proven unlocked and nothing is parked there is nothing
        // to do. The listeners STAY armed (they cost nothing) so a later block —
        // a fresh page-load-scoped lock, a re-suspended AudioContext — still has
        // a recovery path, but a player typing a command does not re-prime on
        // every keystroke.
        if (this._audioUnlocked && !hasParked) return;
        this._primeAudioUnlock(ev);
        this._flushBlockedAudio('gesture');
        this._publishUnlockState('gesture');
    },

    // Public, idempotent unlock entry point. Call from any real user gesture
    // (SoundToggle ON, the voice affordance, the passive listener above).
    unlockAudio(gestureEvent) {
        this._audioUnlockGestures = (this._audioUnlockGestures || 0) + 1;
        this._primeAudioUnlock(gestureEvent);
        this._flushBlockedAudio('explicit');
        this._publishUnlockState('explicit_unlock');
    },

    // Prime media playback. Delegates to VoiceAffordance (single primer owner)
    // when present; otherwise plays a silent WAV inline. NEVER sets
    // `_audioUnlocked` on its own: the primer resolving is weak evidence, a real
    // narration clip playing is proof. `_audioUnlocked` flips in the play()
    // success path so the published state cannot claim an unlock we never had.
    _primeAudioUnlock(gestureEvent) {
        // Prime ONCE per page load, and again whenever a clip is actually parked
        // (that is real evidence the earlier prime did not take). Priming on
        // every gesture would play a silent buffer on every keystroke.
        const hasParked = Boolean(this._blockedAudioQueue && this._blockedAudioQueue.length);
        if (this._audioUnlockPrimed && !hasParked) return;
        this._audioUnlockPrimed = true;
        try {
            if (typeof window !== 'undefined' && window.VoiceAffordance
                && typeof window.VoiceAffordance.unlock === 'function') {
                window.VoiceAffordance.unlock(gestureEvent);
                return;
            }
        } catch (err) {
            this._emitVoiceEvent('warn', {
                event: 'audio_unlock_prime_failed',
                source: 'voice_affordance',
                error_name: (err && err.name) || 'Error',
            });
        }
        try {
            if (typeof Audio !== 'function') return;
            if (!this._audioUnlockPrimer) {
                this._audioUnlockPrimer = new Audio(this._AUDIO_UNLOCK_SILENT_WAV);
                if (typeof this._audioUnlockPrimer.setAttribute === 'function') {
                    this._audioUnlockPrimer.setAttribute('playsinline', '');
                }
                this._audioUnlockPrimer.volume = 0;
            }
            const p = this._audioUnlockPrimer.play();
            if (p && typeof p.then === 'function') {
                p.catch((err) => {
                    this._emitVoiceEvent('warn', {
                        event: 'audio_unlock_prime_failed',
                        source: 'silent_wav',
                        error_name: (err && err.name) || 'Error',
                    });
                });
            }
        } catch (err) {
            this._emitVoiceEvent('warn', {
                event: 'audio_unlock_prime_failed',
                source: 'silent_wav',
                error_name: (err && err.name) || 'Error',
            });
        }
    },

    // Park a policy-blocked clip until the next gesture. Bounded; overflow drops
    // the OLDEST with a LOUD failed outcome (never a silent discard).
    _queueBlockedAudio(audioUrl, meta) {
        // A parked clip waits for a gesture that may never come: its text does not.
        this._settleVoiceStart(meta, 'autoplay_blocked');
        if (!this._blockedAudioQueue) this._blockedAudioQueue = [];
        this._blockedAudioQueue.push({ url: audioUrl, meta: meta || null });
        while (this._blockedAudioQueue.length > this.BLOCKED_AUDIO_MAX) {
            const dropped = this._blockedAudioQueue.shift();
            const dMeta = (dropped && dropped.meta) || {};
            this._emitVoiceEvent('error', {
                event: 'audio_unlock_queue_overflow',
                cycle_id: dMeta.cycleId != null ? dMeta.cycleId : null,
                channel: dMeta.channel != null ? dMeta.channel : null,
                audio_url: (dropped && dropped.url) || null,
                max_depth: this.BLOCKED_AUDIO_MAX,
            });
            this._recordVoiceOutcome('failed', {
                cycleId: dMeta.cycleId != null ? dMeta.cycleId : null,
                channel: dMeta.channel != null ? dMeta.channel : null,
                audioUrl: (dropped && dropped.url) || null,
                reason: 'unlock_queue_overflow',
            });
        }
        this._publishUnlockState('blocked');
    },

    // Re-attempt every parked clip. MUST be called synchronously from a gesture
    // handler. Returns the number of clips released.
    _flushBlockedAudio(source) {
        const queued = this._blockedAudioQueue;
        if (!queued || queued.length === 0) return 0;
        this._blockedAudioQueue = [];
        const released = queued.length;
        this._audioFlushedCount = (this._audioFlushedCount || 0) + released;
        this._emitVoiceEvent('log', {
            event: 'audio_unlock_flush',
            source: source || 'gesture',
            released,
        });
        // First clip plays now (inside the gesture stack); the rest ride the
        // normal narrationQueue so ordering and the stall watchdog still apply.
        const head = queued.shift();
        if (queued.length > 0) {
            if (typeof window !== 'undefined') {
                if (!window.narrationQueue) window.narrationQueue = [];
                // Parked clips are older than anything already queued -> unshift.
                window.narrationQueue.unshift(...queued);
            }
        }
        this._isPlayingAudio = false;
        this._playAudioDirect(head.url, 0, head.meta);
        return released;
    },

    // Tell the clip's owner (the ch0 typewriter) that its voice has started, or
    // that it never will. At most once per clip: the owner latches.
    _settleVoiceStart(meta, how) {
        const cb = meta && meta.onVoiceStart;
        if (typeof cb !== 'function') return;
        meta.onVoiceStart = null;
        try { cb(how); } catch (_e) { /* the typewriter must not break playback */ }
    },

    // How long a ch0 typewriter waits for its voice before failing open: the
    // audible remainder of the clip ahead of it in the FIFO, plus the fetch cap.
    _voiceStartCapMs() {
        let aheadMs = 0;
        const cur = (typeof window !== 'undefined') ? window.currentNarration : null;
        if (this._isPlayingAudio && cur
                && isFinite(cur.duration) && cur.duration > 0
                && typeof cur.currentTime === 'number') {
            aheadMs = Math.max(0, (cur.duration - cur.currentTime) * 1000);
        }
        return Math.round(aheadMs) + VOICE_READY_MAX_WAIT_MS;
    },

    playNarrationAudio(audioUrl, retryCount = 0, meta = null) {
        const sessionId = this.state.sessionId || 'unknown';

        // Global sound OFF (2026-06-25-004-ENGINE Mitigation 2): suppress all
        // audio playback client-side. This is the single chokepoint every
        // narration audio path funnels through, so it covers ch0 (typewriter)
        // AND the legacy direct-audio path. The capacity win (no voice JOB
        // fired) is enforced server-side via include_voice=false; this is the
        // belt-and-suspenders client mute so a player who toggles OFF mid-cycle
        // (after the job already fired) hears nothing either.
        if (typeof SoundToggle !== 'undefined' && !SoundToggle.isSoundOn()) {
            console.log(`[${sessionId}] Audio suppressed (sound OFF): ${audioUrl}`);
            // [voice-drop-observability] suppressed breadcrumb — a deliberate
            // mute, NOT a drop, but still an audio_outcome so /survey and VP can
            // tell "no voice heard" apart from a real failure.
            this._recordVoiceOutcome('suppressed', {
                cycleId: (meta && meta.cycleId) || this._currentNarrationCycleId || null,
                channel: (meta && meta.channel != null) ? meta.channel
                    : (this._currentNarrationChannel != null ? this._currentNarrationChannel : null),
                audioUrl,
                reason: 'sound_off',
            });
            this._settleVoiceStart(meta, 'suppressed');
            return;
        }

        // Resolve the audio URL for the current environment. GANDALF now returns an
        // ABSOLUTE V4-signed GCS URL (https://storage.googleapis.com/...) that the
        // browser fetches directly (gandalf-api v52, private bucket, no proxy) — use it
        // as-is. A relative path (local dev / legacy) still routes through the vite
        // `/gandalf/*` proxy. This is the single chokepoint, so every caller (ch0
        // typewriter, creation-flow, reawaken) gets the same resolution.
        audioUrl = this.resolveAudioUrl(audioUrl);

        console.log(`[${sessionId}] Audio received: ${audioUrl}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);

        // Queue if any chunk is currently playing or loading
        // Uses a flag instead of Audio element state to avoid race with async play()
        if (this._isPlayingAudio) {
            if (!window.narrationQueue) {
                window.narrationQueue = [];
            }
            // [voice-drop-observability] Thread cycle/channel with the URL so a
            // queued chunk (played later from onended) still carries its join
            // key when it plays or fails.
            window.narrationQueue.push({ url: audioUrl, meta: meta || null });
            console.log(`[${sessionId}] Audio queued (${window.narrationQueue.length} in queue)`);
            this._emitVoiceEvent('log', {
                event: 'audio_enqueued',
                cycle_id: (meta && meta.cycleId) || this._currentNarrationCycleId || null,
                channel: (meta && meta.channel != null) ? meta.channel
                    : (this._currentNarrationChannel != null ? this._currentNarrationChannel : null),
                audio_url: audioUrl,
                queue_depth: window.narrationQueue.length,
            });
            // Arm the audio-queue stall watchdog: if the playing clip wedges,
            // this queued chunk (and everything behind it) would never play.
            this._armVoiceQueueWatchdog();
            return;
        }

        this._playAudioDirect(audioUrl, 0, meta);
    },

    // Internal: play audio and advance queue when done
    _playAudioDirect(audioUrl, retryCount, meta = null) {
        const sessionId = this.state.sessionId || 'unknown';
        const maxRetries = 2;
        const retryDelayMs = 1500;

        // [voice-drop-observability] Resolve the join key for this chunk. Prefer
        // the meta threaded with the URL (survives queueing); fall back to the
        // currently-playing narration fields for the first (un-queued) chunk.
        const cycleId = (meta && meta.cycleId) || this._currentNarrationCycleId || null;
        const channel = (meta && meta.channel != null) ? meta.channel
            : (this._currentNarrationChannel != null ? this._currentNarrationChannel : null);
        const obsMeta = { cycleId, channel, audioUrl };

        // Mark as playing before any async work
        this._isPlayingAudio = true;
        // Reset the audio-queue watchdog progress marker for the new clip.
        this._voiceWatchLastTime = 0;

        // Stop any finished narration
        if (window.currentNarration) {
            window.currentNarration.pause();
            window.currentNarration = null;
        }

        window.currentNarration = new Audio(audioUrl);

        // ── ROUTE NARRATION ONTO THE VOICE BUS ───────────────────────────
        // Until [2026-09-01-001-ENGINE] this element never entered the Web
        // Audio graph, so `levels.json`'s voice gain -- the value the sfx bus
        // is explicitly priced against as "the reference" -- moved a GainNode
        // with NO INPUT. The fader looked correct because the element's default
        // 1.0 and the bus's 1.0 agreed by coincidence.
        //
        // `narrator` rides `meta` so the per-narrator trim can be applied here;
        // it is the lever for the mother-time vs other-narrators discrepancy.
        //
        // ⚠️ DELIBERATELY BEST-EFFORT. routeMediaElement() refuses to route
        // into a suspended context (that would make narration SILENT, since
        // createMediaElementSource permanently re-routes the element) and warns
        // loudly instead. An unrouted clip still plays -- it is merely
        // unfadeable -- so a routing failure can never cost the player the
        // narration itself. The first clip of a session is normally unrouted,
        // because playing it is what unlocks audio in the first place.
        try {
            const bank = (typeof window !== 'undefined') ? window.audioBank : null;
            if (bank && typeof bank.routeMediaElement === 'function') {
                bank.routeMediaElement(
                    window.currentNarration,
                    'voice',
                    // Same fallback shape the cycleId join key uses above: the
                    // meta wins (it survives queueing), the live field covers
                    // the first un-queued clip.
                    (meta && meta.narrator) || this._currentNarrationNarrator || null
                );
            }
        } catch (_err) {
            // Never let mixing break playback.
        }

        window.currentNarration.play()
            .then(() => {
                console.log(`[${sessionId}] Narration chunk playing`);
                this._settleVoiceStart(meta, 'playing');
                // [BUG 2] PROOF of unlock — a real clip started. The primer
                // resolving is weak evidence; this is the only thing that flips
                // the latch, so the published state can never claim an unlock
                // the platform did not actually grant.
                if (!this._audioUnlocked) {
                    this._audioUnlocked = true;
                    this._emitVoiceEvent('log', {
                        event: 'audio_unlocked',
                        cycle_id: cycleId,
                        channel,
                        gestures_seen: this._audioUnlockGestures || 0,
                        after_blocked: this._audioBlockedCount || 0,
                    });
                    this._publishUnlockState('play_succeeded');
                }
            })
            .catch(err => {
                const errName = (err && err.name) || 'Error';
                const autoplayBlocked = errName === 'NotAllowedError';
                console.warn(`[${sessionId}] Audio play() rejected: ${err.message}`);
                // [voice-drop-observability] typed play-rejection event. Autoplay
                // policy block (NotAllowedError) is distinct from a decode/network
                // reject — the former is a client gesture-gating issue, not a GCS
                // drop, so no HEAD probe is warranted for it.
                this._emitVoiceEvent('warn', {
                    event: 'audio_play_rejected',
                    cycle_id: cycleId,
                    channel,
                    audio_url: audioUrl,
                    attempt: retryCount + 1,
                    error_name: errName,
                    autoplay_blocked: autoplayBlocked,
                });
                if (!autoplayBlocked) {
                    // decode/network reject -> confirm object reachability (404/403/net)
                    this._probeAudioHead(audioUrl, obsMeta);
                    // Unchanged legacy nudge for the NON-policy case: a transient
                    // decode/network hiccup CAN clear on its own, and the element
                    // is still live. Bounded to a single re-attempt.
                    setTimeout(() => {
                        if (window.currentNarration) {
                            window.currentNarration.play().catch(() => {});
                        }
                    }, 500);
                    return;
                }

                // [2026-07-27-003-QASTI BUG 2] AUTOPLAY-POLICY BLOCK.
                // The old code blind-retried this same play() after 500ms. On
                // iOS that is guaranteed to fail (permission is granted only
                // inside a user-gesture call stack), so it was a retry that
                // could only ever burn a timer and hide the real state. Park the
                // clip and release it on the next gesture instead.
                this._audioUnlocked = false;
                this._audioBlockedCount = (this._audioBlockedCount || 0) + 1;
                this._recordVoiceOutcome('failed', Object.assign(
                    { reason: 'autoplay_blocked' }, obsMeta,
                ));

                // Unwedge the pipeline: this element will never start, and
                // everything sitting in narrationQueue behind it is blocked by
                // the same policy. Move the whole run into the bounded unlock
                // hold so the queue is not left permanently stuck (which would
                // also trip the stall watchdog with a misleading queue_stall).
                try {
                    if (window.currentNarration) { window.currentNarration.pause(); }
                } catch (_e) { /* element may already be dead */ }
                window.currentNarration = null;
                this._isPlayingAudio = false;
                this._clearVoiceQueueWatchdog();
                this._queueBlockedAudio(audioUrl, meta || obsMeta);
                if (window.narrationQueue && window.narrationQueue.length > 0) {
                    const pending = window.narrationQueue.splice(0, window.narrationQueue.length);
                    for (const item of pending) {
                        const pUrl = (item && typeof item === 'object') ? item.url : item;
                        const pMeta = (item && typeof item === 'object') ? item.meta : null;
                        this._queueBlockedAudio(pUrl, pMeta);
                    }
                }
                // Re-arm in case the listener was never installed (init() not run)
                // — a locked client with no gesture hook can never recover.
                this._armAudioUnlockGesture();
                this._emitVoiceEvent('warn', {
                    event: 'audio_unlock_required',
                    cycle_id: cycleId,
                    channel,
                    audio_url: audioUrl,
                    blocked_count: this._audioBlockedCount,
                    blocked_queue_depth: this._blockedAudioQueue ? this._blockedAudioQueue.length : 0,
                    armed: this._audioUnlockArmed === true,
                });
            });

        // When this chunk ends, play the next queued chunk
        window.currentNarration.onended = () => {
            console.log(`[${sessionId}] Narration chunk finished`);
            window.currentNarration = null;
            this._isPlayingAudio = false;
            // [voice-drop-observability] played breadcrumb.
            this._recordVoiceOutcome('played', obsMeta);
            this._playNextQueued();
        };

        window.currentNarration.onerror = () => {
            // Capture the MediaError code BEFORE nulling the element: 1 ABORTED,
            // 2 NETWORK, 3 DECODE, 4 SRC_NOT_SUPPORTED. This is the datum that
            // distinguishes a network drop from a decode failure.
            const mediaErr = window.currentNarration && window.currentNarration.error;
            const code = mediaErr ? mediaErr.code : null;
            console.warn(`[${sessionId}] Audio load failed (attempt ${retryCount + 1}/${maxRetries + 1})`);
            window.currentNarration = null;

            if (retryCount < maxRetries) {
                // [voice-drop-observability] non-terminal load error (will retry).
                this._emitVoiceEvent('warn', {
                    event: 'audio_load_error',
                    cycle_id: cycleId,
                    channel,
                    audio_url: audioUrl,
                    attempt: retryCount + 1,
                    code,
                    terminal: false,
                });
                setTimeout(() => {
                    this._playAudioDirect(audioUrl, retryCount + 1, meta);
                }, retryDelayMs);
            } else {
                console.error(`[${sessionId}] Audio chunk failed, skipping to next`);
                // [voice-drop-observability] terminal load error: typed event +
                // failed outcome + HEAD probe to record 404 vs 403 vs network.
                this._emitVoiceEvent('error', {
                    event: 'audio_load_error',
                    cycle_id: cycleId,
                    channel,
                    audio_url: audioUrl,
                    attempt: retryCount + 1,
                    code,
                    terminal: true,
                });
                this._recordVoiceOutcome('failed', Object.assign({ code, reason: 'load_error' }, obsMeta));
                this._settleVoiceStart(meta, 'load_error');
                this._probeAudioHead(audioUrl, obsMeta);
                this._isPlayingAudio = false;
                this._playNextQueued();
            }
        };

        // If this clip has followers queued behind it, (re-)arm the audio-queue
        // watchdog so a wedge on THIS clip — including one reached via a prior
        // self-heal, where the enqueue-time timer was already consumed — is still
        // caught. A clip with no followers needs no watchdog (nothing to unblock).
        if (window.narrationQueue && window.narrationQueue.length > 0) {
            this._armVoiceQueueWatchdog();
        }
    },

    // =========================================================================
    // ⭐ THE VOICE DROP  [2026-08-21-001-ENGINE] — user ruling, 2026-08-21
    // =========================================================================
    //
    // THE DEFECT THIS EXISTS TO KILL. The server holds ch0 text and its voice
    // as ONE FRAME (ratified 2026-07-27, recorded at symbol_dispatch.py:1278 —
    // decoupling was PROPOSED AND REJECTED). This client took the frame back
    // APART at the play site: `playNarrationAudio` pushed every clip onto an
    // UNBOUNDED FIFO with no staleness check, drained only by `onended` and
    // cleared only on disconnect. Text renders on arrival; voice queues behind
    // clips of 32.4s median / 59s max, so the lag ACCUMULATES MONOTONICALLY.
    // That is the player's report — "voice 1-3 turns later, worse as you go" —
    // and it is why the server-side evidence looked perfect (all 62 ch0 frames
    // carried audio:true, every cycle_id stamped, no audio against the wrong
    // beat). A contract honored at one layer, undone at the next.
    //
    // ⚠️ THE SERVER'S 45s CEILING ALONE FIXES NONE OF THIS. The backlog lives
    // DOWNSTREAM of the hold: a client already three clips deep keeps draining
    // in order no matter what the server does next.
    //
    // THE POLICY, and its bounds:
    //
    //   * It is a **DROP**, never a reorder and never a defer. Survivors keep
    //     their relative order; a discarded clip is gone for good. There is no
    //     scheduler here and there must not be one (the ruling says so).
    //   * It never kills a clip that is already PLAYING. Cutting a narrator
    //     off mid-sentence is a worse render than finishing her. A clip only
    //     ever *starts* at the frontier, which is the invariant that matters:
    //     **a clip never begins playing against a beat that is already stale.**
    //   * An UNKNOWN beat is NEVER dropped. Creation-flow narration ships
    //     `cycleId: null` by design, and an unparseable / cross-room key
    //     compares `null`. Same direction as `_isPriorBeatFrame`: the discard
    //     class must never be reachable by "we could not tell". Silence is the
    //     expensive wrong answer.
    //   * Every drop is LOUD — a typed [VoiceDrop] event AND an audio_outcome
    //     breadcrumb in `window.__VOICE_OUTCOMES`. A silent drop would just be
    //     this bug wearing a different hat.

    /**
     * The newest beat this client knows about — the FRONTIER.
     *
     * Two facts, and neither alone is it: `currentCycleId` is the beat the last
     * clock announced, `beatCursor` is the last beat that FINISHED. They
     * normally advance together but not in lockstep (a cycle can finish before
     * the next clock lands, and a clock can land before the previous cycle
     * closes). Take whichever is later; a stale read of either would under-
     * report the frontier and let a superseded clip through.
     */
    _voiceFrontierBeat() {
        if (typeof TurnPump === 'undefined' || !TurnPump._state) return null;
        const live = TurnPump._state.currentCycleId || null;
        const cursor = TurnPump._state.beatCursor || null;
        if (!live) return cursor;
        if (!cursor) return live;
        const vs = TurnPump._beatCompare(live, cursor);
        // `null` (another room / another key shape) => trust the LIVE beat: the
        // player is standing in the room the clock is ticking for.
        return (vs === null || vs >= 0) ? live : cursor;
    },

    /** The beat a queued entry belongs to; null for the legacy bare-URL shape. */
    _queuedClipBeat(item) {
        if (!item || typeof item !== 'object') return null;
        const meta = item.meta;
        return (meta && meta.cycleId) ? String(meta.cycleId) : null;
    },

    /**
     * Discard every queued clip whose frame has passed. Returns the count.
     *
     * The frontier is the newest of {the beat on screen, every beat already
     * sitting in the queue} — the queue half matters because a single 32s clip
     * can outlive two beats, so by the time we pop, a NEWER clip may already be
     * waiting behind an older one and nothing else would have noticed.
     *
     * ⚠️ Compared through `TurnPump._beatCompare`, which splits on the LAST
     * colon (the production lobby space is `time:void`, so its keys read
     * `time:void:n42` — splitting on the first colon made EVERY void frame
     * incomparable) and compares the axes NUMERICALLY (the per-space ordinal is
     * an unbounded Redis INCR with no durable padding, so `"n1000" > "n999"` is
     * false as text). One comparison rule for the whole client, not a second
     * vocabulary invented here.
     */
    _dropSupersededQueuedAudio(trigger) {
        if (typeof window === 'undefined') return 0;
        const q = window.narrationQueue;
        if (!q || q.length === 0) return 0;
        if (typeof TurnPump === 'undefined'
            || typeof TurnPump._beatCompare !== 'function') return 0;

        let frontier = this._voiceFrontierBeat();
        for (let i = 0; i < q.length; i++) {
            const b = this._queuedClipBeat(q[i]);
            if (!b) continue;
            if (!frontier) { frontier = b; continue; }
            const vs = TurnPump._beatCompare(b, frontier);
            if (vs !== null && vs > 0) frontier = b;
        }
        if (!frontier) return 0;

        const survivors = [];
        const dropped = [];
        for (let i = 0; i < q.length; i++) {
            const item = q[i];
            const beat = this._queuedClipBeat(item);
            const vs = beat ? TurnPump._beatCompare(beat, frontier) : null;
            if (vs !== null && vs < 0) dropped.push({ item, beat });
            else survivors.push(item);
        }
        if (dropped.length === 0) return 0;

        // Mutate IN PLACE: `sound-toggle.js` holds a live reference and clears
        // it with `.length = 0`. Reassigning `window.narrationQueue` would
        // leave that reference pointing at an orphan array.
        q.length = 0;
        for (let i = 0; i < survivors.length; i++) q.push(survivors[i]);

        this._voiceDroppedSuperseded =
            (this._voiceDroppedSuperseded || 0) + dropped.length;

        for (let i = 0; i < dropped.length; i++) {
            const d = dropped[i];
            const url = (d.item && typeof d.item === 'object') ? d.item.url : d.item;
            const meta = (d.item && typeof d.item === 'object') ? d.item.meta : null;
            const channel = (meta && meta.channel != null) ? meta.channel : null;
            this._emitVoiceEvent('warn', {
                event: 'audio_dropped_superseded',
                cycle_id: d.beat,
                channel,
                audio_url: url,
                frontier_cycle_id: frontier,
                trigger: trigger || 'play_site',
                queue_depth_before: survivors.length + dropped.length,
                queue_depth_after: survivors.length,
                dropped_total: this._voiceDroppedSuperseded,
            });
            // FAILURE, not absence: voice was expected for that beat and the
            // player will never hear it. `beat_superseded` is deliberately NOT
            // in _VOICE_ABSENCE_REASONS.
            this._recordVoiceOutcome('failed', {
                cycleId: d.beat,
                channel,
                audioUrl: url,
                reason: 'beat_superseded',
            });
            this._settleVoiceStart(meta, 'superseded');
        }
        return dropped.length;
    },

    // Play next audio chunk from the queue
    _playNextQueued() {
        // ⭐ THE PLAY SITE. Enforce the drop HERE, immediately before a clip is
        // chosen, because this is the only moment at which "has this clip's
        // frame passed?" has a current answer. Deliberately NOT in
        // `_playAudioDirect`: the un-queued first play of a LATE-ARRIVING frame
        // is the text and the voice landing together — exactly what the
        // one-frame ruling protects — and it must not be dropped just because
        // the clock moved on while the server was holding the frame.
        this._dropSupersededQueuedAudio('play_next');

        if (window.narrationQueue && window.narrationQueue.length > 0) {
            const next = window.narrationQueue.shift();
            // Support both the legacy bare-URL shape and the threaded {url, meta}
            // shape so a queue populated before this change still drains.
            const nextUrl = (next && typeof next === 'object') ? next.url : next;
            const nextMeta = (next && typeof next === 'object') ? next.meta : null;
            const sessionId = this.state.sessionId || 'unknown';
            console.log(`[${sessionId}] Playing next queued chunk (${window.narrationQueue.length} remaining)`);
            this._playAudioDirect(nextUrl, 0, nextMeta);
        } else {
            // Queue drained and nothing left playing -> stand the watchdog down.
            this._clearVoiceQueueWatchdog();
        }
    },

    // Audio-synced typewriter: play audio and typewriter text simultaneously.
    // voiceData: {audio_url, duration, voice_time_ms} or null (text-only fallback)
    // Extract narration text from potential JSON wrapper.
    // GANDALF LLM sometimes outputs ```json { "narration": "..." } ```
    // instead of plain text. This extracts just the narration string.
    _extractNarrationText(raw) {
        if (!raw) return raw;
        let cleaned = raw.trim();

        // Strip markdown code fences
        if (cleaned.startsWith('```')) {
            cleaned = cleaned.replace(/^```\w*\s*/, '');
            cleaned = cleaned.replace(/\s*```$/, '');
            cleaned = cleaned.trim();
        }

        // Try to parse as JSON and extract narration field
        try {
            const parsed = JSON.parse(cleaned);
            if (parsed && typeof parsed === 'object' && parsed.narration) {
                return parsed.narration;
            }
        } catch (e) {
            // Not JSON - return original
        }

        return raw;
    },

    _startAudioSyncedTypewriter(voiceData) {
        if (this._syncedTypewriterStarted) return;
        this._syncedTypewriterStarted = true;

        const sessionId = this.state.sessionId || 'unknown';
        const text = this._extractNarrationText(this._fullNarrationText || '');

        // Clear fallback timer
        if (this._textFallbackTimer) {
            clearTimeout(this._textFallbackTimer);
            this._textFallbackTimer = null;
        }

        // Create the streaming element for typewriter output
        const history = document.getElementById('history');
        if (!history || !text) {
            this._releaseNarrationGate();
            return;
        }

        let cssClass = 'message narration-stream';
        if (this._currentNarrationContext === 'commentary') {
            cssClass += ' narration-commentary';
        }
        if (this._currentTextStyle === 'italic') {
            cssClass += ' narration-italic';
        }
        // ch1 is a multi-line readout; its line breaks are content.
        if (this._currentNarrationChannel === 1) {
            cssClass += ' narration-readout';
        }

        // Actor model: placement depends on prompt lock state.
        // - Prompt locked (no .active-input-line): ACTIVE path, append to history.
        // - Prompt live (.active-input-line exists) and passive mode:
        //   PASSIVE path, insert above clock/input for non-disruptive display.
        // [2026-08-03-014-ENGINE stage 2] state via CommandLine, position via
        // the anchor -- see the split at WaitingIndicator.start().
        const _cl = (typeof window !== 'undefined') ? window.CommandLine : null;
        const activeInput = _cl ? _cl.anchor(history) : history.querySelector('.active-input-line');
        const promptOpen = _cl ? _cl.isOpen() : !!activeInput;
        const passiveMode = (typeof TurnPump !== 'undefined') ? TurnPump.getNarrationMode() === 'passive' : (promptOpen && !this._commandPending);

        // PASSIVE path anchors ABOVE the live clock (or above the input line
        // when the clock is frozen or absent), so narration for an idle beat or
        // another player never lands below the cursor the player is typing at.
        // ACTIVE path appends: the prompt is locked, and since the command block
        // left #history there is nothing in the scroll box to stay above.
        let _before = null;
        if (passiveMode && promptOpen && activeInput) {
            const clockEl = (typeof ClockLine !== 'undefined') ? ClockLine.getElement() : null;
            _before = (clockEl && clockEl.parentNode === history) ? clockEl : activeInput;
        }

        /* [2026-08-08-007-ENGINE] THE ORIGIN OF A NARRATION FRAME.
         *
         * ch0/ch1 are LEDGER CONTENT - the game speaking - and a miss is a DROP
         * with a verdict. They render declaring their channel and their beat.
         *
         * WARNING: THE BEATLESS CASE IS REAL AND IS NOT A CONTRACT VIOLATION.
         * `handleNarration` reads `cycle_id || audit_key || null`, and the
         * CREATION narrations (welcome, reawaken) legitimately carry neither:
         * they are spoken before a world, a phase or a clock exists. That is not
         * an unaccounted content frame - it is client_funnel.md section 1's
         * `game-state-transition` in its exact words, "there is no beat yet, or
         * the beat is over": character creation, death and rebirth.
         *
         * So the axis is decided by WHETHER A BEAT EXISTS, not by the wire type.
         * The cycle is resolved through the same authority the ledger uses (the
         * frame first, then the pump's active cycle) before that call is made.
         *
         * WHAT THIS MUST NEVER DO IS REFUSE TO RENDER. Narration IS the render
         * engine; a dropped scene is a full-screen render failure, and a funnel
         * that blanked ch0 over a missing coordinate would be far worse than the
         * anonymity it was built to fix.
         */
        const _pumpCycle = (typeof TurnPump !== 'undefined' && TurnPump._state)
            ? TurnPump._state.currentCycleId : null;
        const _cycleId = this._currentNarrationCycleId || _pumpCycle || null;
        const _chNum = (this._currentNarrationChannel === 1) ? 1 : 0;
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!F) {
            this._releaseNarrationGate();
            return;
        }
        let el;
        if (_cycleId) {
            el = F.write('', {
                origin: F.ORIGIN.LEDGER_CONTENT,
                channel: _chNum,
                cycleId: _cycleId,
                className: cssClass,
                parent: history,
                before: _before
            });
        } else {
            console.log(
                `[FUNNEL] narration rendered OUTSIDE a beat (ch${_chNum}, no ` +
                `cycle on the frame and none active on the pump) - classed ` +
                `game-state-transition. Expected for the welcome/reawaken ` +
                `narrations; during GAMEPLAY it means a producer dropped the stamp.`
            );
            el = F.write('', {
                origin: F.ORIGIN.GAME_STATE_TRANSITION,
                className: cssClass,
                parent: history,
                before: _before
            });
        }
        if (!el) {
            this._releaseNarrationGate();
            return;
        }
        // [vp-hardening-2026-05-09 D3] VP annotation, unchanged. The funnel's
        // `data-channel` / `data-cycle-id` stamps are ADDITIVE - the VP probe
        // and the Terminal Truth Log both still read exactly what they read
        // before, which is what lets them migrate on their own schedule.
        if (typeof window !== 'undefined' && window.__vpAnnotate) {
            window.__vpAnnotate(el, _chNum === 1 ? 'ch1' : 'ch0', _cycleId);
        }
        this._streamingElement = el;
        // THE SNAP SOURCE. `text` is the EXTRACTED string this typewriter is
        // about to walk -- which is NOT always `_fullNarrationText` (a JSON
        // envelope is unwrapped by `_extractNarrationText`). Anything that ends
        // this render early must write back exactly what was being written, so
        // it is stamped here, beside the walk it feeds, rather than re-derived
        // at the far end from a field that may say something else.
        // [2026-08-22-005-QASTI]
        this._typewriterText = text;

        // [TEXT] logging for narration placement verification
        const placement = (passiveMode && activeInput) ? 'above' : 'below';
        const promptState = activeInput ? 'live' : 'locked';
        const ch = this._currentNarrationChannel === 1 ? 'ch1' : 'ch0';
        console.log(`[TEXT] actor=${passiveMode ? 'PASSIVE_NARRATION' : 'ACTIVE_NARRATION'} placement=${placement} prompt=${promptState} channel=${ch} len=${text.length}`);

        // Calculate typewriter speed.
        //   ch1 (divine commentary, channel:1): text-only, NO audio await — spool
        //     out fast at the tunable CH1 cadence (~8s screen-time). See
        //     CH1_TYPEWRITER_MS_PER_CHAR + ticket 2026-06-24-002-ENGINE.
        //   ch0 with audio: pace the text to finish ~90% through the voice.
        //   ch0 without audio: legacy 30ms/char fallback.
        let msPerChar = 30;
        if (this._currentNarrationChannel === 1) {
            // ch1 is text-only by design (engine sends include_voice:false). Do
            // NOT await/expect audio here — render the fast italic typewriter.
            msPerChar = CH1_TYPEWRITER_MS_PER_CHAR;
            console.log(`[${sessionId}] ch1 italic typewriter (text-only): ${text.length} chars at ${msPerChar}ms/char (~${(text.length * msPerChar / 1000).toFixed(1)}s screen-time)`);
        } else if (voiceData && voiceData.duration && voiceData.duration > 0 && text.length > 0) {
            // A RECEIVED duration (`frame.md` P9) -- a real measurement from
            // the producer, so it gets the MEASURED treatment: pace to finish
            // at 90% of the audio, with NO `min(60)` ceiling.
            //
            // ⚠️ THE CEILING IS GONE ON PURPOSE, and it is the same removal the
            // project owner made for the probe path on 2026-08-26. `min(60,
            // ...)` existed to protect against the `len/12` INVENTION, and it
            // overrode the 90% intent for any speech slower than 66.7ms/char --
            // which live was the COMMON path (3 of 5 measured beats clamped,
            // the worst leaving the player on a finished screen for 9.7s while
            // the narrator kept talking). With a real number there is nothing
            // to protect against. The `max(5, ...)` FLOOR stays: it guards a
            // near-zero duration from dumping the whole narration at once.
            msPerChar = Math.max(5, (voiceData.duration * 900) / text.length);
            console.log(
                `[${sessionId}] Synced typewriter: ${text.length} chars over ` +
                `${voiceData.duration.toFixed(2)}s RECEIVED audio ` +
                `(${msPerChar.toFixed(1)}ms/char)`
            );
        } else {
            console.log(`[${sessionId}] Typewriter fallback (no audio): ${text.length} chars at ${msPerChar}ms/char`);
        }

        // Typewriter the text character by character. `pace` is passed in
        // rather than closed over, because the voice-ready probe below may
        // REPLACE the assumed pace with one derived from the real audio.
        let charIndex = 0;
        const startTyping = (pace) => {
            // The render may have been torn down while we waited for metadata
            // -- a hidden-snap, the playback ceiling, or a disconnect. Starting
            // a tick against a dead element would re-truncate text that was
            // already snapped whole. [2026-08-24-013-ENGINE]
            if (this._streamingElement !== el) return;
            const typewriterTick = () => {
                if (charIndex >= text.length) {
                    // Done - set final text, clean up all typewriter state
                    el.textContent = text;
                    this._streamingElement = null;
                    this._tokenDrainTimer = null;
                    this._syncedTypewriterStarted = false;
                    this._fullNarrationText = null;
                    this._typewriterText = null;
                    this._releaseNarrationGate();
                    return;
                }

                // Render next character
                charIndex++;
                el.textContent = text.substring(0, charIndex);
                history.scrollTop = history.scrollHeight;
                // A ch4 line held for the end of this sentence prints now.
                if (this._currentNarrationChannel === 0
                        && GameServerConnection._isSentenceEnd(text, charIndex)) {
                    this._ch4SentenceEnded();
                }

                this._tokenDrainTimer = setTimeout(typewriterTick, pace);
            };
            this._tokenDrainTimer = setTimeout(typewriterTick, pace);
        };

        // ── TEXT STARTS WHEN ITS VOICE STARTS ─────────────────────────────
        //
        // The typewriter's first character is written on this clip's
        // `onVoiceStart` -- the moment `play()` resolves -- never on frame
        // arrival. Two things sit between "play requested" and "voice audible",
        // and both put text ahead of voice when the typewriter does not wait:
        //   1. the fetch of the signed GCS object (~0.3s live, every beat);
        //   2. the FIFO: the previous beat's text finishes at 90% of its audio,
        //      so the next ch0 routinely arrives while ~10% of the previous
        //      voice is still playing, and this clip QUEUES behind it.
        //
        // FAILS OPEN. `onVoiceStart` also fires with a non-`playing` reason
        // on every path where this clip will not sound (sound off, autoplay
        // block, load failure, superseded drop, queue overflow), and a cap --
        // the audible remainder of the clip ahead plus VOICE_READY_MAX_WAIT_MS
        // -- covers any path that reports nothing. A silent text channel is a
        // worse defect than a late voice.
        const startWithVoice = (pace) => {
            let begun = false;
            let capTimer = null;
            const capMs = this._voiceStartCapMs();
            const go = (how) => {
                if (begun) return;
                begun = true;
                if (capTimer) { clearTimeout(capTimer); capTimer = null; }
                if (how === 'playing') {
                    console.log(`[${sessionId}] voice-start: playing -- text starts with its voice`);
                } else {
                    console.warn(`[${sessionId}] voice-start ${how}: text starts WITHOUT its voice`);
                }
                // `45s from ch0 text render` is what the playback ceiling
                // measures; re-anchor it on the first character actually written.
                if (this._narrationSafetyTimer && this._streamingElement === el) {
                    this._armCh0PlaybackCeiling();
                }
                startTyping(pace);
            };
            capTimer = setTimeout(() => go(`not started within ${capMs}ms`), capMs);
            this.playNarrationAudio(voiceData.audio_url, 0, {
                cycleId: this._currentNarrationCycleId || null,
                channel: this._currentNarrationChannel != null ? this._currentNarrationChannel : 0,
                narrator: this._currentNarrationNarrator || null,
                onVoiceStart: go,
            });
        };

        if (!(voiceData && voiceData.audio_url)) {
            startTyping(msPerChar);
            return;
        }

        // ── VOICE/TEXT ALIGNMENT ([2026-08-24-045-ENGINE]) ──────────────────
        //
        // TWO reasons the voice ran late, and ONE gate fixed both.
        //
        // 1. THE START. `new Audio(url).play()` must FETCH the file before a
        //    sample sounds, while the typewriter's first tick fired at
        //    `msPerChar` (~60ms). Text therefore started ahead of voice by the
        //    whole network fetch, every beat. Nothing awaited readiness.
        //
        // 2. THE RATE. `voiceData.duration` was INVENTED by the client --
        //    `Math.max(3, text.length / 12)` -- because the server sent no
        //    duration at all. [2026-08-24-014-ENGINE]
        //
        // `loadedmetadata` answered both: it is the readiness signal AND it
        // carries the TRUE duration. The probe also warms the HTTP cache, so
        // the play that follows starts promptly rather than re-fetching.
        //
        // ── AND NOW THE SERVER SENDS IT (`frame.md` P9) ─────────────────────
        //
        // Reason 2 is gone at the source: the duration RIDES THE FRAME. When it
        // is present we skip the probe entirely and start text immediately on a
        // real number -- which also retires reason 1's cost on the happy path,
        // because the probe's own round trip (capped at
        // VOICE_READY_MAX_WAIT_MS, and otherwise however long metadata takes
        // over cross-origin GCS) was DEAD AIR BEFORE THE FIRST CHARACTER on
        // every voiced beat.
        //
        // ⚠️ THE PROBE STAYS, as the fallback for a frame that carries no
        // duration -- an older server, or a producer that returned none. It is
        // not dead code: it is the honest path when the fact is missing, and it
        // measured 179 successes against 0 fallbacks in the captured corpus, so
        // it works and is worth keeping for exactly that case.
        if (voiceData.duration && voiceData.duration > 0) {
            console.log(
                `[${sessionId}] voice-aligned: ${text.length} chars over ` +
                `${voiceData.duration.toFixed(2)}s RECEIVED audio ` +
                `(${msPerChar.toFixed(1)}ms/char) -- no metadata probe needed`
            );
            startWithVoice(msPerChar);
            return;
        }
        //
        // ⚠️ FAILS OPEN, and that is load-bearing. Audio is best-effort here
        // (`_playAudioDirect` already treats a drop as a breadcrumb, never a
        // gate). If metadata never arrives -- 404 upload-race, 403 signed-URL
        // expiry, no network -- the text MUST still render, on the assumed
        // pace, exactly as before. A silent text channel would be a far worse
        // defect than a late voice.
        let started = false;
        const begin = (pace, why, trueDuration) => {
            if (started) return;
            started = true;
            if (probeTimer) { clearTimeout(probeTimer); probeTimer = null; }
            if (trueDuration != null) {
                // ⚠️ `voiceData.duration` IS NULL ON THIS PATH NOW, and this
                // line used to call `.toFixed(2)` on it unconditionally -- it
                // read `assumed was ${voiceData.duration.toFixed(2)}s (len/12)`
                // because there was always an invention to compare against.
                // There is not any more: reaching the probe AT ALL means the
                // server sent no duration, so there is nothing to contrast and
                // a bare `.toFixed` here would throw inside the metadata
                // handler and take the text render down with it.
                console.log(
                    `[${sessionId}] voice-aligned: ${text.length} chars over ` +
                    `${trueDuration.toFixed(2)}s MEASURED audio (${pace.toFixed(1)}ms/char) ` +
                    `-- the frame carried NO duration, so this was probed`
                );
            } else {
                console.warn(
                    `[${sessionId}] voice-alignment ${why}: starting text on the ` +
                    `ASSUMED pace ${pace.toFixed(1)}ms/char. Voice may trail; text is not held.`
                );
            }
            startWithVoice(pace);
        };

        let probeTimer = null;
        try {
            const probe = new Audio();
            probe.preload = 'metadata';
            probe.addEventListener('loadedmetadata', () => {
                const d = probe.duration;
                if (!(d > 0) || !isFinite(d) || text.length === 0) {
                    begin(msPerChar, 'metadata carried no usable duration', null);
                    return;
                }
                // NO CEILING on a MEASURED duration. [2026-08-24-014-ENGINE]
                //
                // `* 900` means "finish at 90% of the audio, landing just
                // before the voice stops". The old `min(60, ...)` overrode
                // that intent for any speech slower than 66.7ms/char, which
                // live was the COMMON path -- 3 of 5 measured beats clamped,
                // the worst leaving the player on a finished screen for 9.7s
                // while the narrator kept talking. Removed (project owner,
                // 2026-08-26) now that `d` is a real measurement rather than
                // the `len/12` invention the cap was protecting us from.
                //
                // The `max(5, ...)` FLOOR stays: it is the guard against a
                // near-zero duration dumping the whole narration at once.
                // The backstop at the other end is the 45s ch0 playback
                // ceiling, which snaps the text complete -- see
                // `_armCh0PlaybackCeiling`.
                begin(Math.max(5, (d * 900) / text.length), 'measured', d);
            });
            probe.addEventListener('error', () => begin(msPerChar, 'audio failed to load', null));
            probe.src = this.resolveAudioUrl(voiceData.audio_url);
            // The cap. A slow probe must not become a slow BEAT: past this the
            // text starts regardless and the voice lands where it lands.
            probeTimer = setTimeout(
                () => begin(msPerChar, `metadata not ready within ${VOICE_READY_MAX_WAIT_MS}ms`, null),
                VOICE_READY_MAX_WAIT_MS
            );
        } catch (e) {
            // No Audio constructor (test host, exotic browser). Never let the
            // ALIGNMENT feature take the TEXT down with it.
            begin(msPerChar, `audio probe unavailable (${e && e.message})`, null);
        }
    },

    // =========================================================================
    // P2 -- the ch0 playback ceiling: arm / clear / fire
    // [2026-08-21-001-ENGINE]
    // =========================================================================
    //
    // WHAT THIS REPLACES, and why it is a repair rather than a new mechanism.
    // A 45s "narration safety timer" already existed here. It fired
    // `TurnPump.event('timeout')`, which cleared `_tokenDrainTimer` and fired
    // `playback_complete` -- and that is ALL it did. Three consequences, all
    // live on this branch's parent:
    //
    //   1. THE TEXT WAS LEFT PERMANENTLY TRUNCATED mid-sentence. Stopping the
    //      tick leaves `el.textContent = text.substring(0, charIndex)` and
    //      nothing ever finishes it. The ruling says do not crop the
    //      narration; cropping the TEXT is the worse half of that.
    //   2. `_syncedTypewriterStarted` WAS NEVER RESET. It is cleared in exactly
    //      two places -- the typewriter's own completion tick and the
    //      disconnect teardown -- so after one timeout every later narration
    //      returns instantly from `_startAudioSyncedTypewriter`, types nothing,
    //      and never calls `_releaseNarrationGate`. No ch0 terminal, ever
    //      again: every subsequent beat escapes on the 90s stall watchdog.
    //      `_streamingElement` leaked the same way.
    //   3. It was not classified. One unstamped `console.warn` -- a
    //      ceiling-driven unlock read exactly like a clean completion.
    //
    // WHEN IT FIRES, stated in the units that actually decide it. The 60ms/char
    // ceiling that used to make this a question about TEXT LENGTH is gone
    // ([2026-08-24-014-ENGINE]); on the measured path the pace is exactly
    // `(duration * 900) / len`, so the text always lands at 90% of the audio
    // REGARDLESS of length. This ceiling therefore fires iff
    //
    //     0.9 * audioDuration > 45s   ->   audio longer than 50 SECONDS
    //
    // and character count no longer enters into it. The longest clip measured
    // live is 37.96s, so it stays dormant by ~12s of speech rather than the
    // ~3s of the old length-based margin -- but the thing to watch is the
    // ratified 120-word ch0 cap moving, not the character count.
    //
    // ⚠️ It is also now the UPPER backstop for the pace itself. Removing the
    // ceiling means a pathological `loadedmetadata` duration would otherwise
    // type forever; this snaps the text complete and hands the gate over.
    //
    // SCOPE -- read this before changing anything here. This bounds ONLY the
    // narration-playback half of the two-signal unlock. It satisfies the same
    // `ch0.rendered` latch a natural typewriter completion satisfies, through
    // the same single writer, and it touches NOTHING else. It does not set
    // `serverDone`, does not call `_finishCycle`, does not create an input
    // line, and does not consult `acting`. If the server has not finished
    // resolving the turn, `_cycleNativeComplete()` is still false and the
    // prompt stays locked -- which is correct: unlocking into a live turn would
    // be a worse defect than the one this fixes.
    _armCh0PlaybackCeiling() {
        this._clearCh0PlaybackCeiling();
        this._ch0CeilingArmedAt = Date.now();
        this._ch0CeilingCycleId = this._currentNarrationCycleId || null;
        this._ch0CeilingChannel = (this._currentNarrationChannel != null)
            ? this._currentNarrationChannel : 0;
        this._narrationSafetyTimer = setTimeout(() => {
            this._narrationSafetyTimer = null;
            this._ch0PlaybackCeilingFired();
        }, this.CH0_PLAYBACK_CEILING_MS);
    },

    _clearCh0PlaybackCeiling() {
        if (this._narrationSafetyTimer) {
            clearTimeout(this._narrationSafetyTimer);
            this._narrationSafetyTimer = null;
        }
    },

    /**
     * The ceiling fired: the narration is still typing at the ceiling.
     *
     * SNAP THE TEXT COMPLETE, hand the gate over, and LEAVE THE AUDIO ALONE.
     *
     * Snapping is not cropping -- every character the narrator was given ends
     * up on screen, instantly, rather than the tail being lost forever. The
     * alternative (let the typewriter keep running past the unlock) would put a
     * mutating element and a live `_streamingElement` underneath a prompt the
     * player is already typing at, which the actor model forbids and which the
     * next beat's narration would collide with.
     */
    // =========================================================================
    // THE SNAP -- the ONE writer for "stop typing, put every character up"
    // [2026-08-22-005-QASTI]
    // =========================================================================
    //
    // TWO things end a typewriter early and they must do it IDENTICALLY: the
    // 45s playback ceiling ([2026-08-21-001-ENGINE]) and the document going
    // HIDDEN. Each leaves the same three obligations, and the ceiling's own
    // history records what skipping them costs -- a stopped tick leaves
    // `substring(0, charIndex)` on screen forever, and a skipped teardown
    // leaves `_syncedTypewriterStarted` set, after which every later narration
    // returns instantly from `_startAudioSyncedTypewriter`, types nothing, and
    // never releases the gate.
    //
    // So the teardown is spelled ONCE, here. It was already hand-copied twice
    // (the completion tick and the ceiling); a third copy is how one of them
    // silently loses a line.
    //
    // This does NOT release the gate. The caller owns the REASON, and one
    // caller -- a hide with no narration running -- must stay entirely silent
    // rather than complete a beat the pump never started.
    //
    // Returns `{was_running, chars_total, chars_typed, text_snapped}` for the
    // caller's telemetry; nulls when there was nothing in flight.
    _snapNarrationComplete() {
        const wasRunning = !!this._tokenDrainTimer || !!this._streamingElement;

        // Stop the tick FIRST -- everything below assumes it is not racing us,
        // and a tick that survives re-writes `substring(0, charIndex)` over the
        // text this call just made whole.
        if (this._tokenDrainTimer) {
            clearTimeout(this._tokenDrainTimer);
            this._tokenDrainTimer = null;
        }

        // Complete the text. `_typewriterText` is the EXTRACTED string the
        // typewriter was walking (stamped beside the walk in
        // `_startAudioSyncedTypewriter`); the element is what it wrote into.
        // ⚠️ Not `_fullNarrationText` -- that is the RAW frame text, and the
        // two differ whenever a narration arrives as a JSON envelope, in which
        // case snapping to the raw one puts the envelope on screen.
        const el = this._streamingElement;
        const full = (typeof this._typewriterText === 'string')
            ? this._typewriterText : null;
        let chars = null;
        let typed = null;
        try {
            if (el && full !== null) {
                typed = (el.textContent || '').length;
                chars = full.length;
                el.textContent = full;
            }
        } catch (_e) { /* a snap failure must not strand the gate */ }

        // The SAME teardown the natural completion tick performs. Omitting it
        // is what turned the old timeout into a session-ending wedge.
        this._streamingElement = null;
        this._syncedTypewriterStarted = false;
        this._fullNarrationText = null;
        this._typewriterText = null;

        return {
            was_running: wasRunning,
            chars_total: chars,
            chars_typed: typed,
            text_snapped: chars != null && typed != null && typed < chars,
        };
    },

    /**
     * The document went HIDDEN while a narration was still typing.
     *
     * THE DEFECT THIS CLOSES (live prod `efd477ec`, 2026-08-22): the typewriter
     * is a CHAINED `setTimeout` at 60ms/char, and every browser throttles
     * chained timers in a hidden document to >=1000ms. Audio is NOT throttled.
     * So a hide mid-scene froze the text -- the player came back to
     * `You step int`, cut inside the word `into`, with the voice already past
     * it and the prompt still locked behind a ch0 that could not finish.
     * ([2026-08-22-005-QASTI]; it was filed as a length clamp, which is not
     * what it is -- every character had already been delivered.)
     *
     * A hidden document HAS NO READER, so there is nothing left to pace: snap
     * the text complete and hand the gate over. This is the ruling the ceiling
     * already applies -- "snapping is not cropping" -- on a second trigger, and
     * it reaches the freeze far sooner than the 45s ceiling would.
     *
     * SILENT when nothing is in flight, which is most of a session. Firing
     * playback_complete on every visibilitychange would complete beats the pump
     * never started.
     */
    _narrationHiddenSnap() {
        if (!this._tokenDrainTimer && !this._streamingElement) return;

        const sessionId = this.state.sessionId || 'unknown';
        const cycleId = this._currentNarrationCycleId || null;
        const channel = (this._currentNarrationChannel != null)
            ? this._currentNarrationChannel : 0;

        const snap = this._snapNarrationComplete();

        // Stand the ceiling down: it is measuring a render that just ended, and
        // left armed it would fire during the NEXT beat.
        this._clearCh0PlaybackCeiling();

        // LOUD, and its OWN outcome. A hide-driven unlock, a ceiling-driven one
        // and a clean playback are three different facts about the beat and
        // must not be greppable as the same thing.
        this._ch0HiddenSnaps = (this._ch0HiddenSnaps || 0) + 1;
        this._emitPlaybackCeilingEvent({
            event: 'ch0_hidden_snap',
            cycle_id: cycleId,
            channel,
            chars_total: snap.chars_total,
            chars_typed: snap.chars_typed,
            text_snapped: snap.text_snapped,
            audio_still_playing: !!this._isPlayingAudio,
            hidden_snaps_total: this._ch0HiddenSnaps,
        });
        console.warn(
            `[${sessionId}] narration SNAPPED on document-hidden (cycle=` +
            `${cycleId || 'none'} ch${channel}, ${snap.chars_typed}/` +
            `${snap.chars_total} chars typed): a hidden tab throttles the ` +
            `typewriter to ~1 char/s while the audio plays on, so the text is ` +
            `completed now rather than left frozen mid-word. This is a ` +
            `HIDDEN-driven unlock, not a playback-driven one.`
        );

        this._releaseNarrationGate('hidden');
    },

    _ch0PlaybackCeilingFired() {
        const sessionId = this.state.sessionId || 'unknown';
        const armedAt = this._ch0CeilingArmedAt || Date.now();
        const heldMs = Date.now() - armedAt;
        const cycleId = this._ch0CeilingCycleId || this._currentNarrationCycleId || null;
        const channel = (this._ch0CeilingChannel != null) ? this._ch0CeilingChannel
            : (this._currentNarrationChannel != null ? this._currentNarrationChannel : 0);

        // Stop the tick, complete the text, tear the typewriter down -- the one
        // shared writer, so this path and the hidden-snap path cannot drift.
        const snap = this._snapNarrationComplete();

        // LOUD, and classified as its OWN outcome. A ceiling-driven unlock and
        // a playback-driven one are different facts about the beat and must not
        // be greppable as the same thing.
        this._ch0CeilingUnlocks = (this._ch0CeilingUnlocks || 0) + 1;
        this._emitPlaybackCeilingEvent({
            event: 'ch0_playback_ceiling',
            cycle_id: cycleId,
            channel,
            ceiling_ms: this.CH0_PLAYBACK_CEILING_MS,
            held_ms: heldMs,
            chars_total: snap.chars_total,
            chars_typed: snap.chars_typed,
            text_snapped: snap.text_snapped,
            audio_still_playing: !!this._isPlayingAudio,
            ceiling_unlocks_total: this._ch0CeilingUnlocks,
        });
        console.warn(
            `[${sessionId}] ch0 PLAYBACK CEILING at ` +
            `${this.CH0_PLAYBACK_CEILING_MS / 1000}s (cycle=${cycleId || 'none'} ` +
            `ch${channel}): text snapped to complete and the narration gate ` +
            `released. The audio is NOT touched and keeps playing to its own ` +
            `end -- overflow past the ceiling is ruled acceptable. This is a ` +
            `CEILING-driven unlock, not a playback-driven one.`
        );

        // Hand over through the ONE typewriter-complete callback, so mode
        // handling (WELCOME / SPAWNING / GAMEPLAY) is identical to a natural
        // completion. Only the REASON differs, and it rides the payload.
        this._releaseNarrationGate('ceiling');
    },

    /** One structured, greppable [PlaybackCeiling] line on the client-log rail. */
    _emitPlaybackCeilingEvent(payload) {
        const sid = (this.state && this.state.sessionId) || 'unknown';
        let line;
        try {
            line = `[PlaybackCeiling] ${JSON.stringify(Object.assign({ session: sid }, payload))}`;
        } catch (_e) {
            line = `[PlaybackCeiling] {"session":"${sid}","event":"${(payload && payload.event) || 'unknown'}"}`;
        }
        console.warn(line);
    },

    // THE welcome-narration safety timeout: 90s after the player enters
    // WELCOME_NARRATION, a gate still closed is released so the menu can never
    // wedge on a scene that never came (GANDALF refused, the void scene lost).
    // ONE arm, from two triggers -- the `connected_needs_creation` entry and the
    // `welcome` step. Idempotent: a timer already armed is KEPT, so a later
    // `welcome` frame does not extend the budget the entry started.
    _armWelcomeNarrationTimeout(sessionId, trigger) {
        if (this._welcomeNarrationTimeout) {
            return;
        }
        this._welcomeNarrationTimeout = setTimeout(() => {
            this._welcomeNarrationTimeout = null;
            if (TerminalFSM.mode === 'WELCOME_NARRATION') {
                console.warn(`[${sessionId}] welcome.timeout fired trigger=${trigger} -- releasing the gate without narration`);
                this._creationNarrationPending = false;
                this._releaseNarrationGate();
            }
        }, 90000);
    },

    // Release narration gate after synced typewriter completes.
    //
    // [Phase 4 refactor] Two distinct roles:
    //   1. GAMEPLAY / WELCOME_NARRATION / SPAWNING typewriter-complete callback:
    //      All three modes now route narration through TurnPump, so this path
    //      simply fires playback_complete on the pump. The pump's drain-exit
    //      re-invokes this function (via _finishCycleTail) for the FSM hand-off
    //      half in WELCOME_NARRATION — see below.
    //   2. FSM transition for WELCOME_NARRATION, invoked from _finishCycleTail
    //      when the pump finishes the welcome cycle AND from the 90s welcome-
    //      narration safety timeout. This is the only surviving non-pump
    //      caller; it owns the WELCOME -> ROSTER_DISPLAY transition and the
    //      re-dispatch of any buffered character_choice.
    // `renderReason` is 'playback' (the typewriter finished on its own) or
    // 'ceiling' (P2 fired and the gate was released early). It rides the
    // playback_complete payload onto the cycle record; nothing branches on it.
    // A ceiling-driven unlock is a DISTINCT outcome and must not be readable as
    // a clean completion. [2026-08-21-001-ENGINE]
    _releaseNarrationGate(renderReason) {
        const sessionId = this.state.sessionId || 'unknown';
        const _reason = renderReason || 'playback';

        // Welcome narration: drive the WELCOME -> ROSTER_DISPLAY transition.
        // Guard against double-entry: only fire the transition once the pump
        // has actually exited its cycle (status === 'idle'). Pre-pump calls
        // from the typewriter-complete callback will see status === 'bridge'
        // or 'playing' and should instead hand control to the pump via
        // playback_complete; the pump's _finishCycleTail re-enters this
        // function when the cycle exits.
        if (TerminalFSM.mode === 'WELCOME_NARRATION') {
            if (typeof TurnPump !== 'undefined' && !TurnPump.isIdle()) {
                // Pump is still running the welcome cycle — hand off via
                // playback_complete and let _finishCycleTail re-enter us.
                this._clearCh0PlaybackCeiling();
                TurnPump.event('playback_complete', {
                    channel: this._currentNarrationChannel || 0,
                    // [combat-validation-observability 3a/3b] Carry the active
                    // narration's cycle_id so the playback_complete console line
                    // + the VP narration_end probe frame join to the cycle.
                    cycle_id: this._currentNarrationCycleId || null,
                    command_id: this._currentNarrationCommandId || null,
                    render_reason: _reason,
                });
                return;
            }
            // Pump is idle (drain exited) OR TurnPump unavailable — run the
            // FSM transition now. Also the 90s welcome-safety-timeout path
            // arrives here with pump still idle (no narration arrived at all).
            if (this._welcomeNarrationTimeout) {
                clearTimeout(this._welcomeNarrationTimeout);
                this._welcomeNarrationTimeout = null;
            }
            this._creationNarrationPending = false;
            if (this._pendingCharacterChoice) {
                const pending = this._pendingCharacterChoice;
                this._pendingCharacterChoice = null;
                // 800ms delay is intentional UX pacing
                setTimeout(() => {
                    // ROSTER_DISPLAY: input locked while roster renders
                    TerminalFSM.transition('narration_complete');
                    // The board or the hand renders, then fires roster_displayed
                    this._dispatchSelection(pending);
                }, 800);
            } else {
                TerminalFSM.transition('narration_complete');
            }
            return;
        }
        // Post-spawn (SPAWNING) / normal gameplay (GAMEPLAY): delegate to the
        // pump. The pump drains any buffered narration, exits the cycle via
        // _finishCycleTail, and in SPAWNING the tail calls _checkSpawnGate to
        // perform SPAWNING -> GAMEPLAY.
        if (TerminalFSM.mode === 'SPAWNING' || TerminalFSM.mode === 'GAMEPLAY') {
            // Stand the P2 ceiling down. Harmless on the 'ceiling' path (it
            // already cleared itself), load-bearing on the normal one: a timer
            // left armed here would fire during the NEXT beat and snap a
            // narration that had barely started.
            this._clearCh0PlaybackCeiling();
            if (typeof TurnPump !== 'undefined') {
                TurnPump.event('playback_complete', {
                    channel: this._currentNarrationChannel || 0,
                    // [combat-validation-observability 3a/3b] cycle_id join key.
                    cycle_id: this._currentNarrationCycleId || null,
                    command_id: this._currentNarrationCommandId || null,
                    render_reason: _reason,
                });
            }
        }
    },

    // Handle spawn message (character created and placed in world)
    handleSpawn(message) {
        const sessionId = this.state.sessionId || 'unknown';
        const character = message.character || {};
        const location = message.location || {};

        console.log(`[${sessionId}] Spawn - character: ${character.name}, location: ${location.name}`);

        // Store character name for combat log perspective coloring (ch2)
        this.state.characterName = character.name || null;

        // Display spawn information
        this.logMessage(`\n--- Character Created ---`);
        this.logMessage(`Name: ${character.name}`);
        if (character.alignment) {
            this.logMessage(`Alignment: ${character.alignment.toUpperCase()}`);
        }

        if (location.name) {
            this.logMessage(`\nArriving at ${location.name}...`);
        }

        // [Phase 4 refactor] The spawn-queue bypass (_spawnNarrationQueue +
        // _spawnNarrationPlaying) is deleted — spawn narrations now enter
        // the pump via handleNarration -> server_action + narration_received,
        // and drain through the standard ch0 pipeline. _checkSpawnGate still
        // awaits _spawnServerDone + pump idle before the SPAWNING -> GAMEPLAY
        // transition; the pump reports "idle" by the drain reaching its exit.
        this._spawnServerDone = false;
        this._currentNarrationContext = null;
        this._pendingTurnReady = false;

        // Transition FSM: character chosen, entering SPAWNING state.
        // SPAWNING gates the prompt until ALL narrations have played.
        TerminalFSM.transition('character_chosen');
        console.log(`[${sessionId}] Spawn: entering SPAWNING (gating prompt behind narration queue)`);
    },

    // Single decision point for spawn completion.
    // Called from BOTH spawn_complete handler and _finishCycleTail (Phase 4).
    // Only transitions to GAMEPLAY when ALL conditions are met.
    _checkSpawnGate() {
        if (TerminalFSM.mode !== 'SPAWNING') return;
        if (!this._spawnServerDone) {
            console.log(`[${this.state.sessionId || 'unknown'}] Spawn gate: waiting for spawn_complete`);
            return;
        }
        // [Phase 4 refactor] Pump status IS the "narration still playing"
        // signal. The old _spawnNarrationPlaying / _spawnNarrationQueue flags
        // have been retired; their role is now played by TurnPump._state.status
        // reaching 'idle' at drain exit. Since _checkSpawnGate is invoked
        // from _finishCycleTail AFTER the pump has quiesced, the typical
        // caller sees status === 'idle' here. Calls from the spawn_complete
        // handler may arrive while the pump is still draining, in which case
        // we defer — _finishCycleTail will re-call once the pump reaches idle.
        if (typeof TurnPump !== 'undefined' && !TurnPump.isIdle()) {
            console.log(`[${this.state.sessionId || 'unknown'}] Spawn gate: pump still draining (status=${TurnPump.getStatus()})`);
            return;
        }
        // All conditions met -- transition to GAMEPLAY
        console.log(`[${this.state.sessionId || 'unknown'}] Spawn gate: all done, entering GAMEPLAY`);
        TerminalFSM.transition('spawn_complete');

        if (this._bufferedRoomState) {
            this.handleRoomState(this._bufferedRoomState);
            this._bufferedRoomState = null;
        }

        // [2026-07-06-001-ENGINE / D1] Arrival is SERVER-AUTHORITATIVE. The
        // client no longer dispatches a phantom `look` on spawn arrival. The
        // membership enter funnel (server side) produces + delivers the arrival
        // scene (ch0) + its phase_clock; the client RECEIVES it through the
        // normal narration-receive path (handleNarration -> narration_received)
        // and births the prompt + clock together in _finishCycle() when the
        // server-pushed cycle completes (ch1_missing/ch1 -> ch0 -> turn_complete
        // -> phase_clock). Removing the client look dissolves W0.7 (look-loop)
        // and W0.9 (ch1-below-cursor) by construction.
    },

    // [2026-07-06-001-ENGINE / D1] The deferred movement-arrival look dispatch
    // (_dispatchRoomEntryLook) has been REMOVED. Arrival is server-authoritative:
    // walk-arrival into a new room now fires the enter funnel server-side, which
    // produces + delivers the new room's arrival scene (ch0) + phase_clock. The
    // client no longer records a `_pendingRoomEntryLook` intent nor re-dispatches
    // a follow-up `look` — it only RECEIVES the server-pushed scene through the
    // normal narration-receive path. This dissolves the W0.7 look-loop.

    // Send message to GameServer
    async sendGameMessage(message) {
        if (TerminalFSM.mode === 'DISCONNECTED') {
            this.logMessage('Not connected to any world');
            return false;
        }

        // Prevent sending a second action while narration or command is pending
        // -- unless the server asked this player per slot and slots are still
        // free: that act joins the cycle in flight (time-slot-mechanics.md 5).
        const _perSlot = (typeof TurnPump !== 'undefined' && typeof TurnPump.askedPerSlot === 'function')
            ? TurnPump.askedPerSlot() : false;
        if (!TerminalFSM.isInputOpen() || (typeof TurnPump !== 'undefined' && TurnPump.isPending() && !_perSlot)) {
            console.warn('Input locked - action ignored (mode: ' + TerminalFSM.mode + ', pending: ' + TurnPump.isPending() + ')');
            return false;
        }

        try {
            const gameMessage = {
                type: 'action',
                action: message,
                timestamp: new Date().toISOString()
            };

            // Use direct WebSocket or proxied connection based on connection type
            if (this.state.connectionType === 'direct') {
                return this.sendDirectMessage(gameMessage);
            } else {
                return await this.sendProxiedMessage(gameMessage);
            }

        } catch (error) {
            console.error('Error sending game message:', error);
            this.logMessage('Failed to send message to GameServer');
            return false;
        }
    },

    // Send message directly via WebSocket
    sendDirectMessage(message) {
        const sessionId = this.state.sessionId || 'unknown';

        if (!this.state.websocket || this.state.websocket.readyState !== WebSocket.OPEN) {
            console.error(`❌ [${sessionId}] WebSocket not open (readyState: ${this.state.websocket?.readyState})`);
            return false;
        }

        try {
            this.state.websocket.send(JSON.stringify(message));
            // [2026-08-19-009-ENGINE / Thread 0] WHICH command left the socket.
            //
            // This line logged `message.type` alone, and EVERY player command
            // wires as type `action` (sendGameMessage wraps input as
            // {type:'action', action, timestamp}). So the log proved that *a*
            // frame was sent, never *which* -- and correlating a specific
            // command to a specific send fell back to timestamp-matching
            // against the terminal echo. That ambiguity is precisely the branch
            // [2026-08-19-009-ENGINE] could not resolve: "the client ate the
            // keystroke" and "the frame hit the socket" produce the same line.
            //
            // WHY THIS CANNOT WEDGE INPUT (this is the submit funnel):
            //  - It runs AFTER send(); the frame is already on the wire.
            //  - Control flow, ordering and return values are UNCHANGED. The
            //    `return true` below is the same statement it always was.
            //  - The detail computation is wrapped in its own try/catch and
            //    touches only String()/slice/JSON.stringify of a scalar, so it
            //    cannot throw out of this block. If it somehow did, the OUTER
            //    catch would return false for a send that SUCCEEDED -- caller
            //    reports failure on a delivered frame, which is the exact wedge
            //    class we are hunting. Hence the belt and the braces.
            //  - Only primitives are handed to console.log.
            //  - Unconditional (not gated on the capture flag) because the cost
            //    is two string ops per PLAYER COMMAND -- roughly one per beat,
            //    not per frame -- and because the manual /dump-logs path needs
            //    the same discriminator.
            let detail = '';
            try {
                if (message && message.type === 'action') {
                    const act = String(message.action == null ? '' : message.action);
                    detail = ' action=' + JSON.stringify(act.slice(0, 200));
                    if (message.target_name) {
                        detail += ' target=' + String(message.target_name).slice(0, 80);
                    }
                }
            } catch (_detailErr) {
                detail = ' <action-detail-unavailable>';
            }
            console.log(`📤 [${sessionId}] Sent direct message:`, message.type, detail);
            return true;
        } catch (error) {
            console.error(`❌ [${sessionId}] Failed to send direct message:`, error);
            return false;
        }
    },

    // Send message through the relay — over the SAME single WebSocket.
    // (Both the production relay path and the local direct path now send over
    // this.state.websocket; sendDirectMessage holds the readyState guard.)
    async sendProxiedMessage(message) {
        return this.sendDirectMessage(message);
    },

    // ---------------------------------------------------------------------
    // [2026-07-02-007] client_logs over the WS
    // Browser /dump-logs cannot POST to the gameserver HTTP port on prod (not
    // public), so GameDiagnostics.dumpTestLogs routes the dump over this live
    // WS instead. sendClientLogsOverWs() sends a {type:'client_logs', dump_id}
    // frame and returns a promise that resolves with the server's
    // client_logs_ack (matched by dump_id), or rejects on timeout. The ack is
    // intercepted at the top of handleGameServerMessage and handed here.
    // ---------------------------------------------------------------------
    _resolveClientLogsAck(message) {
        const waiters = this._clientLogsAckWaiters;
        if (!waiters) return;
        const dumpId = message.dump_id;
        // Prefer a dump_id-keyed one-shot.
        let waiter = (dumpId != null) ? waiters.get(dumpId) : undefined;
        if (waiter) {
            waiters.delete(dumpId);
        } else {
            // [2026-08-19-009-ENGINE / Thread 0] The un-keyed fallback used to
            // resolve the OLDEST pending waiter unconditionally. That was safe
            // while this transport served exactly one manual /dump-logs click
            // at a time. It is NOT safe now that the account-scoped capture
            // rail sends segments for the whole session: with two waiters
            // outstanding, resolving "the oldest" can mark segment N delivered
            // on the ack for segment N+1 -- a LOST segment reported as ACKED,
            // which is precisely the silent drop this rail exists to prevent.
            //
            // So: fall back only when the mapping is UNAMBIGUOUS (exactly one
            // waiter). Otherwise refuse, loudly, and let the waiters time out
            // -- a timeout surfaces as a reported gap in the artifact, whereas
            // a mis-resolution is invisible forever.
            if (waiters.size === 0) return;
            if (waiters.size > 1) {
                console.error(
                    `[capture] client_logs_ack with no dump_id and ${waiters.size} `
                    + 'waiters pending -- AMBIGUOUS, refusing to resolve. The '
                    + 'affected segments will time out and be reported as gaps.'
                );
                return;
            }
            const firstKey = waiters.keys().next().value;
            waiter = waiters.get(firstKey);
            waiters.delete(firstKey);
        }
        if (waiter.timer) clearTimeout(waiter.timer);
        waiter.resolve(message);
    },

    sendClientLogsOverWs(payload, timeoutMs = 10000) {
        return new Promise((resolve, reject) => {
            if (!this.state.websocket || this.state.websocket.readyState !== WebSocket.OPEN) {
                reject(new Error('WebSocket not open'));
                return;
            }
            if (!this._clientLogsAckWaiters) {
                this._clientLogsAckWaiters = new Map();
            }
            const dumpId = `dump_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const timer = setTimeout(() => {
                this._clientLogsAckWaiters.delete(dumpId);
                reject(new Error('client_logs_ack timeout'));
            }, timeoutMs);
            this._clientLogsAckWaiters.set(dumpId, { resolve, timer });

            const sent = this.sendDirectMessage({
                type: 'client_logs',
                dump_id: dumpId,
                ...payload,
            });
            if (!sent) {
                clearTimeout(timer);
                this._clientLogsAckWaiters.delete(dumpId);
                reject(new Error('WebSocket send failed'));
            }
        });
    },

    // =====================================================================
    // [2026-08-19-009-ENGINE / Thread 0] ACCOUNT-SCOPED UNCONDITIONAL CAPTURE
    // =====================================================================
    //
    // WHY THIS EXISTS. [2026-08-19-009-ENGINE] stalled on a branch it could not
    // resolve: when the prompt wedges, did the CLIENT swallow the keystroke, or
    // did the frame reach the socket and the server go quiet? The three
    // submit-path outcomes each already leave a distinct console line --
    //   1. `Input locked - action ignored (mode: X, pending: Y)`  (sendGameMessage)
    //   2. `WebSocket not open (readyState: N)`                   (sendDirectMessage)
    //   3. `Sent direct message: action action="..."`             (sendDirectMessage)
    // -- and the parse-time console interceptor in game-diagnostics.js already
    // buffers all three. The missing half was never the SIGNAL. It was getting
    // the signal OFF THE BROWSER: the only existing route was a human clicking
    // /dump-logs, and nobody clicks it during the wedge they did not know was
    // happening.
    //
    // The earlier idea -- auto-dump when a wedge is DETECTED -- was circular: it
    // required detecting the thing we cannot detect. This does not wait for a
    // symptom. A flagged account captures the ENTIRE session, continuously,
    // from `connected` to disconnect, whether or not anything goes wrong.
    //
    // ARMING IS SERVER-AUTHORITATIVE AND OFF BY DEFAULT. The GameServer has
    // already JWT-verified the Firebase UID (it is `player_id`), so the server
    // -- not the client -- decides who is captured. An unflagged session never
    // sets a timer, never drains, never sends: `_armClientCapture` returns
    // immediately when `armed !== true`, so the hot path is untouched.
    //
    // WHY SEGMENTS AND NOT ONE DUMP AT THE END. `exportTestLogs()` serializes
    // the WHOLE buffer, and that buffer is a 5000-entry FIFO. A session-long
    // single-shot capture would (a) re-send everything on every attempt and
    // (b) still lose whatever the FIFO had already evicted. Draining
    // incrementally means the buffer only ever has to survive ONE flush
    // interval instead of the whole session -- which is the real answer to
    // buffer adequacy, not a bigger cap.
    //
    // LOUD-FAILURE LAW: a capture rail that silently drops is worse than none,
    // because it manufactures confident, incomplete evidence. Three holes are
    // therefore VISIBLE IN THE ARTIFACT ITSELF, never invisible truncation:
    //   * `missed_before` -- entries the FIFO evicted before this drain reached
    //     them (computed from the seq gap; see drainLogsSince).
    //   * `prior_lost`    -- seq ranges from EARLIER segments whose ack failed
    //     or timed out, carried FORWARD so a lost segment announces itself in
    //     the next segment that lands.
    //   * `capture_seq` / `from_seq` -- monotonic, so the SERVER can detect a
    //     segment that never arrived at all. That is the one case the client
    //     cannot report, because a client that died cannot file a report.
    // ---------------------------------------------------------------------
    _clientCapture: {
        armed: false,
        label: 'capture',
        intervalMs: 15000,
        cursor: 0,          // last console seq handed to the transport
        captureSeq: 0,      // monotonic segment ordinal
        timer: null,
        inFlight: false,
        sent: 0,
        acked: 0,
        failed: 0,
        priorLost: [],      // [{capture_seq, from_seq, to_seq}] awaiting report
        lastError: null,
    },

    // Arm from the `connected` frame. Default OFF -- absent/false does nothing.
    _armClientCapture(message) {
        const cfg = (message && message.client_capture) || null;
        const cap = this._clientCapture;
        if (!cfg || cfg.armed !== true) {
            // Not flagged. Do NO work: no timer, no drain, no listener.
            return false;
        }
        if (cap.armed) return true;   // idempotent across a re-`connected`

        cap.armed = true;
        cap.label = String(cfg.label || 'capture').slice(0, 48);
        const iv = Number(cfg.interval_ms);
        // Floor 2s so a bad config cannot busy-loop the player's device;
        // ceiling 120s so a wedge window is never lost to a flush that had not
        // come round yet.
        cap.intervalMs = Math.min(
            120000,
            Math.max(2000, (isFinite(iv) && iv > 0) ? iv : 15000),
        );

        // Baseline the cursor at 0 so the stream includes everything buffered
        // BEFORE `connected` (module-load logs, auth, the WS open). That is
        // session provenance and costs exactly one segment.
        cap.cursor = 0;

        console.log(
            `[capture] ARMED label=${cap.label} interval=${cap.intervalMs}ms`
            + ' -- account-scoped session capture [2026-08-19-009-ENGINE]'
        );

        cap.timer = setInterval(() => {
            this._captureFlush(false);   // never rejects
        }, cap.intervalMs);

        // Best-effort final segment. `pagehide` fires on mobile Safari where
        // `beforeunload` does not; a double-fire is harmless because the cursor
        // has already advanced and the second drain returns empty.
        try {
            const finalFlush = () => { this._captureFlush(true); };
            window.addEventListener('beforeunload', finalFlush);
            window.addEventListener('pagehide', finalFlush);
        } catch (_e) { /* non-browser host (test harness) */ }

        // Ship the pre-connect backlog now rather than waiting a full interval.
        this._captureFlush(false);
        return true;
    },

    _disarmClientCapture() {
        const cap = this._clientCapture;
        if (cap.timer) { clearInterval(cap.timer); cap.timer = null; }
        cap.armed = false;
        cap.inFlight = false;
    },

    // Drain one segment and ship it. Never throws, never rejects.
    async _captureFlush(isFinal) {
        const cap = this._clientCapture;
        if (!cap.armed) return false;

        // Serialize the PERIODIC flushes. Overlapping segments would make the
        // un-keyed ack fallback in _resolveClientLogsAck ambiguous, and an
        // ambiguous ack resolves the WRONG segment -- marking a lost segment as
        // delivered. One in flight at a time; the next tick picks up whatever
        // accrued meanwhile.
        //
        // The FINAL flush deliberately bypasses this. On disconnect/pagehide
        // there IS no next tick, so honouring the guard would silently discard
        // the tail -- and the tail is the most interesting part of a wedge. Two
        // outstanding segments are safe here because both carry a `dump_id`
        // that the server echoes, so each ack maps to exactly one waiter; the
        // ambiguous un-keyed case is refused loudly rather than mis-resolved.
        if (cap.inFlight && !isFinal) return false;

        const diag = (typeof window !== 'undefined') ? window.GameDiagnostics : null;
        if (!diag || typeof diag.drainLogsSince !== 'function') {
            // The buffer IS the subject. If it is not there, say so once and
            // stop pretending to capture.
            if (!cap.lastError) {
                cap.lastError = 'GameDiagnostics.drainLogsSince unavailable';
                console.error('[capture] ABORT -- ' + cap.lastError);
            }
            return false;
        }

        let drained;
        try {
            drained = diag.drainLogsSince(cap.cursor);
        } catch (drainErr) {
            cap.failed += 1;
            cap.lastError = String((drainErr && drainErr.message) || drainErr);
            console.error('[capture] drain failed:', cap.lastError);
            return false;
        }

        if (!drained.entries.length && !isFinal && !cap.priorLost.length) {
            return false;   // nothing new, and not even a hole to report
        }

        cap.inFlight = true;
        cap.captureSeq += 1;
        const segSeq = cap.captureSeq;
        const fromSeq = drained.fromSeq;
        const toSeq = drained.toSeq;

        // Report-and-clear: what we carry now is either delivered with this
        // segment or re-queued below. It is never dropped on the floor.
        const priorLost = cap.priorLost.slice();
        cap.priorLost = [];

        const lines = drained.entries.map((e) => {
            const lvl = String(e.level || 'log').toUpperCase();
            return `${e.timestamp || ''} #${e.seq || 0} [${lvl}] ${e.message || ''}`;
        });

        if (drained.missed > 0) {
            // Put the hole IN the text as well as in the metadata, so a human
            // reading the file cannot miss it.
            lines.unshift(
                `!!! CAPTURE GAP: ${drained.missed} console `
                + `entr${drained.missed === 1 ? 'y' : 'ies'} EVICTED from the `
                + `5000-entry FIFO after seq ${fromSeq} -- this window is INCOMPLETE`
            );
        }
        for (const lost of priorLost) {
            lines.unshift(
                `!!! CAPTURE GAP: segment #${lost.capture_seq} (console seq `
                + `${lost.from_seq}..${lost.to_seq}) was NOT ACKNOWLEDGED `
                + `(${lost.reason || 'unknown'}) -- those lines never reached the server`
            );
        }

        // Advance the cursor at SEND time, not at ack time. Holding it back
        // would make a slow ack re-send the same window forever; the catch
        // below records the range in priorLost instead, so a failure is
        // REPORTED rather than retried into a loop.
        cap.cursor = toSeq;

        const payload = {
            mode: 'segment',
            session_id: this.state.sessionId || 'unknown',
            player_id: this.state.playerId || null,
            label: cap.label,
            capture_seq: segSeq,
            from_seq: fromSeq,
            to_seq: toSeq,
            missed_before: drained.missed,
            total_dropped: drained.totalDropped,
            prior_lost: priorLost,
            final: Boolean(isFinal),
            console_logs: lines.join('\n'),
            entry_count: drained.entries.length,
            user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
            timestamp: new Date().toISOString(),
        };

        cap.sent += 1;
        try {
            const ack = await this.sendClientLogsOverWs(payload, 20000);
            if (ack && ack.success) {
                cap.acked += 1;
            } else {
                throw new Error((ack && ack.error) || 'server reported failure');
            }
        } catch (err) {
            cap.failed += 1;
            cap.lastError = String((err && err.message) || err);
            // The segment is gone (the cursor already advanced). Record the
            // hole so the NEXT segment announces it. This is the difference
            // between an instrument that drops silently and one that admits it.
            //
            // Re-carry the ranges this segment was CARRYING as well as its own.
            // They rode on a frame that did not land, so they have not been
            // reported yet -- dropping them here would mean that two
            // consecutive failures silently erase the first one's record, which
            // is the precise silent-drop this whole rail exists to refuse.
            //
            // Bounded so a permanently-failing socket cannot grow this without
            // limit; the elision is itself reported rather than being a quiet
            // truncation of the truncation record.
            const carried = priorLost.concat([{
                capture_seq: segSeq,
                from_seq: fromSeq,
                to_seq: toSeq,
                reason: cap.lastError,
            }]);
            const LOST_CAP = 50;
            if (carried.length > LOST_CAP) {
                const elided = carried.length - LOST_CAP;
                cap.priorLost = carried.slice(-LOST_CAP);
                cap.priorLost.unshift({
                    capture_seq: -1,
                    from_seq: -1,
                    to_seq: -1,
                    reason: `${elided} earlier lost-segment record(s) elided -- `
                        + 'the capture rail has been failing continuously',
                });
            } else {
                cap.priorLost = carried;
            }
            console.error(
                `[capture] segment #${segSeq} (seq ${fromSeq}..${toSeq}) `
                + `FAILED: ${cap.lastError}`
            );
        } finally {
            cap.inFlight = false;
        }
        return true;
    },

    // ---------------------------------------------------------------------
    // [2026-07-27-002-QASTI] vp_capture over the WS
    // The probe's relative POST to /api/vp-capture NEVER reached the
    // GameServer on prod: firebase.json rewrites /api/** to the gameAPI Cloud
    // Function, whose route table has no vp-capture branch. Route captures over
    // this already-authenticated WS instead (same reasoning as client_logs, and
    // it also sidesteps the Cloud Functions request-size limit that a full-page
    // base64 PNG can exceed).
    //
    // Fire-and-forget by design: at capture volume a per-frame ack waiter (the
    // client_logs Map/timeout pattern) would be a leak. Delivery is instead
    // COUNTED -- sends on this side, acks on the way back -- and the counters
    // live on window.__VP_PROBE_STATE where a VP scenario can assert them.
    // ---------------------------------------------------------------------
    sendVpCaptureOverWs(payload) {
        if (!this.state.websocket || this.state.websocket.readyState !== WebSocket.OPEN) {
            return false;
        }
        return this.sendDirectMessage({
            type: 'vp_capture',
            ...payload,
        });
    },

    _recordVpCaptureAck(message) {
        // The probe owns the counter object; if the probe is disabled the ack
        // is simply dropped. Never throw from the intake path.
        try {
            const st = (typeof window !== 'undefined') ? window.__VP_PROBE_STATE : null;
            if (!st) return;
            if (message && message.ok) {
                st.acked = (st.acked || 0) + 1;
                st.last_path = message.path || st.last_path;
                if (message.screens_md) st.last_screens_md = message.screens_md;
                // A server-side GCS failure is LOUD on the server; surface it on
                // the page too, so "captured" is never mistaken for "persisted".
                if (message.gcs_error) {
                    st.gcs_failed = (st.gcs_failed || 0) + 1;
                    st.last_gcs_error = message.gcs_error;
                }
            } else {
                st.failed = (st.failed || 0) + 1;
                st.last_error = (message && message.error) || 'vp_capture_ack: not ok';
            }
        } catch (_) { /* counters are best-effort */ }
    },

    // Handle connection loss
    handleConnectionLoss(connectionType) {
        const sessionId = this.state.sessionId || 'unknown';

        if (TerminalFSM.mode === 'DISCONNECTED') {
            console.log(`[${sessionId}] Connection already closed, ignoring duplicate loss event`);
            return; // Already handling disconnection
        }

        console.log(`[${sessionId}] Connection lost (type: ${connectionType})`);

        // [2026-07-06-001-ENGINE / D1] The deferred room-entry look intent
        // (_pendingRoomEntryLook) was removed with server-authoritative arrival,
        // so there is nothing to clear here anymore.

        // Transition FSM to DISCONNECTED
        TerminalFSM.transition('disconnect');

        this.logMessage('Connection to GameServer lost');

        // Attempt reconnection if not too many attempts
        if (this.state.reconnectAttempts < this.state.maxReconnectAttempts) {
            console.log(`[${sessionId}] Scheduling reconnection (attempt ${this.state.reconnectAttempts + 1}/${this.state.maxReconnectAttempts})`);
            this.scheduleReconnect();
        } else {
            console.error(`[${sessionId}] Maximum reconnection attempts reached`);
            this.logMessage('Maximum reconnection attempts reached');
            this.logMessage('Use /world reconnect to try connecting again');
        }
    },

    // Schedule reconnection attempt
    scheduleReconnect() {
        const sessionId = this.state.sessionId || 'unknown';

        // Clear any existing reconnect timer
        if (this.state.reconnectTimer) {
            console.log(`🧹 [${sessionId}] Clearing existing reconnect timer`);
            clearTimeout(this.state.reconnectTimer);
            this.state.reconnectTimer = null;
        }

        this.state.reconnectAttempts++;
        this.state.isReconnecting = true;

        // Exponential backoff: base_delay * 2^(attempts - 1)
        const exponentialDelay = this.config.reconnectBaseDelay * Math.pow(2, this.state.reconnectAttempts - 1);
        const delay = Math.min(exponentialDelay, this.config.reconnectMaxDelay);

        console.log(`⏱️ [${sessionId}] Reconnect delay: ${delay}ms (exponential: ${exponentialDelay}ms, capped at ${this.config.reconnectMaxDelay}ms)`);
        this.logMessage(`Attempting to reconnect in ${delay / 1000} seconds... (${this.state.reconnectAttempts}/${this.state.maxReconnectAttempts})`);

        this.state.reconnectTimer = setTimeout(() => {
            if (this.state.connectionDetails) {
                console.log(`🔄 [${sessionId}] Executing reconnection attempt ${this.state.reconnectAttempts}`);
                this.logMessage('Attempting to reconnect...');
                this.connect(this.state.connectionDetails);
            } else {
                console.error(`❌ [${sessionId}] No connection details available for reconnection`);
            }
        }, delay);
    },

    // Check if message should be routed to GameServer
    isGameCommand(input) {
        const trimmed = input.trim().toLowerCase();

        // World management commands stay local
        if (trimmed.startsWith('/world') || trimmed.startsWith('world')) {
            return false;
        }

        // Admin commands (if any) stay local
        if (trimmed.startsWith('/admin') || trimmed.startsWith('admin')) {
            return false;
        }

        // Everything else goes to GameServer if in game or choosing a character
        return TerminalFSM.isInGame() || TerminalFSM.mode === 'CHARACTER_CHOICE';
    },

    // Process user input (called from main terminal)
    async processInput(input) {
        // [2026-07-29-012-QASTI] ACKNOWLEDGEMENT. A submit while the vessel
        // offer is held means "I have read it" — the player is never forced to
        // sit through their own death scene. The keystroke is SPENT on the
        // acknowledgement rather than on a turn: the character is dead, the
        // room is gone, and sending it as an action is exactly how nine reclaim
        // phrasings were eaten in [2026-07-29-002-QASTI].
        if (this._rosterHold) {
            this._maybeReleaseRosterHold('acknowledged');
            return true;
        }
        if (this.isGameCommand(input)) {
            // During character choice, just send to GameServer without FSM state changes.
            // The creation flow handlers (handleCreationFlow/handleSpawn) manage transitions.
            if (TerminalFSM.mode === 'CHARACTER_CHOICE') {
                await this.sendGameMessage(input);
                return true;
            }

            /* [2026-08-19-009-ENGINE] A SWALLOWED SUBMIT MUST NOT BE SILENT.
             *
             * ⚠️ BY THE TIME WE ARE CALLED, THE PROMPT IS ALREADY SHUT.
             * `main.js::processInput` closes the command line, clears the field
             * and sets `disabled = true` BEFORE dispatch -- deliberately, per
             * [2026-08-03-014-ENGINE] stage 2 ("the command line is closed by
             * the act of SUBMITTING"). So returning `true` here means: handled,
             * nothing sent, and `main.js` will NOT re-open, because a `true`
             * tells it the pump now owns the prompt.
             *
             * Both arms below used to be mute. The pending arm's comment claimed
             * "pump will re-enable input when ready" -- true only if the pump
             * ever finishes, and the whole of this ticket is the case where it
             * does not. The send-failure arm called `logMessage`, which writes
             * to the DIAGNOSTICS PANE, not `#history`: loud to us, invisible to
             * the player. Two different reasons a command evaporated, and the
             * player saw the same nothing for both.
             */
            const _joins = (typeof TurnPump !== 'undefined' && typeof TurnPump.askedPerSlot === 'function')
                ? TurnPump.isPending() && TurnPump.askedPerSlot() : false;
            if (typeof TurnPump !== 'undefined' && TurnPump.isPending() && !_joins) {
                // A cycle IS in flight, so the pump legitimately owns the prompt
                // and we must NOT re-open one here -- a command line during a
                // resolving beat is the dead affordance `_renderInputSuppressed`
                // exists to prevent. But the player just lost a keystroke, so
                // say so. The backstop bounds how long this can last.
                this._renderSystemNotice(
                    'Not sent — the previous beat is still resolving. Try again when the prompt returns.',
                    'degradation',
                    { cycleId: TurnPump._beatId ? TurnPump._beatId() : null });
                // The block LOCKED a moment ago (main.js, before dispatch), so
                // re-ask the overdue label: on a passive cycle the prompt was
                // still open when the notice timer fired, and a one-shot render
                // would leave an already-stopped beat wearing the ordinary
                // `resolving gameplay` label. No-op unless the cycle is overdue.
                if (typeof TurnPump._renderStallNotice === 'function') {
                    TurnPump._renderStallNotice();
                }
                return true;
            }

            // Send to GameServer FIRST, then transition FSM.
            // sendGameMessage guards against DISCONNECTED and non-input-open modes,
            // so we must send while still in GAMEPLAY with input open.
            const sent = await this.sendGameMessage(input);
            if (!sent) {
                // NOTHING IS IN FLIGHT -- the send failed, so no cycle was armed
                // and the pump is idle. That makes this the one swallow arm that
                // both CAN and MUST re-open: there is no cycle to own the prompt,
                // so leaving it shut hands the player a dead terminal over a
                // transient socket error. `_applyPromptGate` is the one opener,
                // and it still honours a not-asked statement.
                this.logMessage('Failed to send command to GameServer');
                this._renderSystemNotice(
                    'Not sent — the command did not reach the server. Your prompt is back; try again.',
                    'degradation');
                if (typeof TurnPump !== 'undefined'
                        && typeof TurnPump._applyPromptGate === 'function') {
                    TurnPump._applyPromptGate();
                }
                return true; // Command was handled (even though it failed)
            }

            // TurnPump owns all turn state: timers, flags, thinking animation.
            // ClockLine.freeze() and WaitingIndicator.start() are called inside user_action.
            if (_joins) {
                // A second act into the beat in flight: the cycle is the
                // beat's and is not restarted. The server states this player
                // again (the slots still free, or spent), and the line follows.
                console.log(`[${this.state.sessionId || '?'}] prompt.slot_act`
                    + ` cycle=${TurnPump._beatId ? TurnPump._beatId() : '-'} text=${JSON.stringify(input)}`);
                return true;
            }
            TurnPump.event('user_action', { text: input });

            return true;
        }

        return false; // Let other systems handle it
    },

    // Disconnect from GameServer
    disconnect() {
        const sessionId = this.state.sessionId || 'unknown';
        console.log(`[${sessionId}] Initiating disconnect from GameServer`);

        // [2026-08-19-009-ENGINE / Thread 0] Ship the capture tail FIRST, while
        // the socket is still open. `_captureFlush` runs synchronously up to
        // its `await` -- the drain, the segment build and websocket.send() all
        // complete before this returns -- so the final frame is on the wire
        // before the teardown below closes the socket. Only the ACK is awaited
        // asynchronously, and losing that ack merely costs the acked counter,
        // not the data.
        //
        // No-op for unflagged sessions (`armed` is false).
        try {
            this._captureFlush(true);
            this._disarmClientCapture();
        } catch (_capErr) {
            console.error('[capture] final flush failed:', String(_capErr));
        }

        // 1. Transition FSM to DISCONNECTED first -- triggers _cleanup() which
        //    clears WaitingIndicator, ClockLine, audio, and
        //    resets play queue state via terminal:cleanup event.
        if (TerminalFSM.mode !== 'DISCONNECTED') {
            TerminalFSM.transition('disconnect');
        }

        // 2. Stop heartbeat mechanism (connection-specific)
        this.stopHeartbeat();

        // 3. Reset TurnPump (clears all turn timers and state)
        TurnPump.event('reset');
        this._ledgerReset();

        // 3b. Clear connection-specific internal timers
        if (this._welcomeNarrationTimeout) {
            clearTimeout(this._welcomeNarrationTimeout);
            this._welcomeNarrationTimeout = null;
        }
        if (this._textFallbackTimer) {
            clearTimeout(this._textFallbackTimer);
            this._textFallbackTimer = null;
        }
        if (this._tokenDrainTimer) {
            clearTimeout(this._tokenDrainTimer);
            this._tokenDrainTimer = null;
        }
        this._clearCh0PlaybackCeiling();
        // [2026-07-29-012-QASTI] A held vessel offer belongs to a session that
        // no longer exists. Drop it (and its ceiling) rather than performing it
        // into a torn-down terminal.
        this._clearRosterHold();

        // 4. Reset connection-specific internal state
        // [fnf queue/warming UX] Clear the warm-up hold so a stale flag can't
        // suppress the heartbeat watchdog on the next connection, and tear down
        // any leftover queue surface (e.g. user left the queue).
        this.state.queueHold = false;
        this._removeQueueSurface();
        this._isPlayingAudio = false;
        // `_lastRenderedRoomName` is gone with the room_state prose render it
        // gated (ch9 content-leak fix — ch9 carries no content, channels.md
        // §4). `_lastArrivalPoiSlug` still gates the arrival splash, which is
        // structured and stays.
        this._lastArrivalPoiSlug = null;
        // [voice-drop-observability] tear down the audio-queue stall watchdog so
        // a stale timer can't fire against the next connection's audio.
        this._clearVoiceQueueWatchdog();
        this._voiceWatchLastTime = 0;
        // [2026-08-21-001-ENGINE] The superseded-drop tally is PER SESSION —
        // carried across a reconnect it would read as this connection's health.
        this._voiceDroppedSuperseded = 0;
        this._streamingElement = null;
        this._tokenBuffer = [];
        this._creationNarrationPending = false;
        this._pendingCharacterChoice = null;
        this._postCommentaryGate = false;
        this._narrationWaitingElement = null;
        this._fullNarrationText = null;
        this._typewriterText = null;
        this._textCompleteReceived = false;
        this._voiceReadyData = null;
        this._syncedTypewriterStarted = false;
        window.narrationQueue = [];

        // 5. Close WebSocket (single socket for both the relay and direct paths)
        if (this.state.websocket) {
            console.log(`[${sessionId}] Closing game socket`);
            try { this.state.websocket.close(); } catch (e) { /* already closing */ }
            this.state.websocket = null;
        }
        // Legacy SSE socket (retired) — close defensively if anything set it.
        if (this.state.proxySocket) {
            try { this.state.proxySocket.close(); } catch (e) { /* noop */ }
            this.state.proxySocket = null;
        }

        // 6. Clear reconnect timer
        if (this.state.reconnectTimer) {
            clearTimeout(this.state.reconnectTimer);
            this.state.reconnectTimer = null;
        }

        // 7. Reset connection state (NOT connectionDetails -- needed for reconnection)
        this.state.connectionType = null;
        this.state.currentWorld = null;
        this.state.reconnectAttempts = 0;
        this.state.isReconnecting = false;

        console.log(`[${sessionId}] Disconnect complete`);
        this.logMessage('Disconnected from GameServer');
    },

    // Start heartbeat mechanism (both sending and monitoring)
    startHeartbeat() {
        const sessionId = this.state.sessionId || 'unknown';
        console.log(`💓 [${sessionId}] Starting heartbeat mechanism (interval: ${this.config.heartbeatInterval}ms, timeout: ${this.config.heartbeatTimeout}ms)`);

        // Clear any existing heartbeat timers
        this.stopHeartbeat();

        // Send ping to server every heartbeatInterval
        this.state.heartbeatTimer = setInterval(() => {
            if (TerminalFSM.mode !== 'DISCONNECTED') {
                this.sendPing();
            }
        }, this.config.heartbeatInterval);

        // Check for heartbeat timeout
        this.state.heartbeatCheckTimer = setInterval(() => {
            // [fnf queue/warming UX] While held in the warm-up queue, do NOT let
            // the watchdog declare the link dead. A cold GANDALF cold-start can
            // run ~2min and the server may not push a frame for longer than
            // heartbeatTimeout (60s) between position changes. Every received
            // queue_position already refreshes lastHeartbeat (handleGameServerMessage),
            // but suppressing the check entirely is the robust guarantee that the
            // held socket survives until `connected` (promotion) or the user leaves.
            if (this.state.queueHold) {
                return;
            }
            if (TerminalFSM.mode !== 'DISCONNECTED' && this.state.lastHeartbeat) {
                const timeSinceHeartbeat = Date.now() - this.state.lastHeartbeat;

                if (timeSinceHeartbeat > this.config.heartbeatTimeout) {
                    console.error(`❌ [${sessionId}] Heartbeat timeout (${timeSinceHeartbeat}ms since last heartbeat)`);
                    this.handleConnectionLoss(this.state.connectionType);
                }
            }
        }, this.config.heartbeatInterval);

        console.log(`✅ [${sessionId}] Heartbeat mechanism started`);
    },

    // Stop heartbeat mechanism
    stopHeartbeat() {
        const sessionId = this.state.sessionId || 'unknown';

        if (this.state.heartbeatTimer) {
            console.log(`🛑 [${sessionId}] Stopping heartbeat timer`);
            clearInterval(this.state.heartbeatTimer);
            this.state.heartbeatTimer = null;
        }

        if (this.state.heartbeatCheckTimer) {
            console.log(`🛑 [${sessionId}] Stopping heartbeat check timer`);
            clearInterval(this.state.heartbeatCheckTimer);
            this.state.heartbeatCheckTimer = null;
        }
    },

    // Send ping to server
    async sendPing() {
        const sessionId = this.state.sessionId || 'unknown';

        if (TerminalFSM.mode === 'DISCONNECTED') {
            console.warn(`[${sessionId}] Cannot send ping: not connected`);
            return;
        }

        try {
            console.log(`💓 [${sessionId}] Sending ping to GameServer`);

            const pingMessage = {
                type: 'ping',
                timestamp: new Date().toISOString()
            };

            const sent = await this.sendProxiedMessage(pingMessage);
            if (!sent) {
                console.error(`❌ [${sessionId}] Failed to send ping`);
            }
        } catch (error) {
            console.error(`❌ [${sessionId}] Error sending ping:`, error);
        }
    },

    // Setup heartbeat monitoring (deprecated - replaced by startHeartbeat)
    setupHeartbeat() {
        // This method is kept for backward compatibility
        // The actual heartbeat is now started in startHeartbeat()
        console.log('📡 Heartbeat monitoring setup (legacy method)');
    },

    // Setup reconnection logic
    setupReconnectionLogic() {
        // Handle browser visibility changes
        document.addEventListener('visibilitychange', () => {
            // HIDDEN: finish any narration still typing, before the platform
            // throttles its chained setTimeout to ~1 char/second and freezes it
            // mid-word while the (un-throttled) audio plays on.
            // [2026-08-22-005-QASTI]
            if (document.hidden) {
                this._narrationHiddenSnap();
            }
            if (!document.hidden && this.state.currentWorld && TerminalFSM.mode === 'DISCONNECTED') {
                // Tab became visible and we have a world but no connection
                this.logMessage('Tab became active, checking connection...');
                if (this.state.reconnectAttempts < this.config.maxReconnectAttempts) {
                    this.connect(this.state.currentWorld);
                }
            }
        });

        // Handle network status changes
        window.addEventListener('online', () => {
            if (this.state.currentWorld && TerminalFSM.mode === 'DISCONNECTED') {
                this.logMessage('Network connection restored, attempting to reconnect...');
                this.connect(this.state.currentWorld);
            }
        });

        window.addEventListener('offline', () => {
            this.logMessage('Network connection lost');
        });
    },

    // Handle stateless GameServer connection
    // =========================================================================
    // Queue / warming surface (fnf live-deploy)
    // =========================================================================
    // When a player connects while GANDALF's GPU pods are cold (scale-to-zero),
    // the GameServer admission controller HOLDS the player on this same websocket
    // and emits `queue_position` frames while the pods warm. The held socket must
    // survive a ~2min cold start -- see queueHold + the heartbeat watchdog
    // suppression in startHeartbeat().
    //
    // [2026-08-06-006-QASTI] There ARE two distinct end-of-hold frames, and they
    // are NOT the same moment. This comment used to claim "there is no dedicated
    // promotion frame" — the server grew one (networking.py `queue_admitted`) and
    // the claim went stale, which is exactly why the arm was never written:
    //
    //   `queue_admitted`  the LINE is over. The last `queue_position` has been
    //                     sent; no more will come. The server now runs the connect
    //                     sequence on this same socket (player_connect, the
    //                     player-authority bind, the ux_strings read, and on a
    //                     CONTINUE the whole resume-through-authority path).
    //                     -> handleQueueAdmitted: repaint, keep holding.
    //
    //   `connected`       the WORLD is open. The hold is genuinely over and the
    //                     normal connect flow renders.
    //                     -> handleQueuePromotion: drop queueHold, tear down.
    //
    // Collapsing the two is what the player saw: between them the surface froze
    // on a position that was already false. The teardown still belongs to
    // `connected` alone -- see handleQueueAdmitted for why queueHold must NOT be
    // released early.

    /**
     * First `queue_position` enters the QUEUED state + paints the surface;
     * each subsequent frame refreshes position / ETA / message in place.
     */
    handleQueuePosition(message) {
        // Arm the watchdog suppression FIRST so a slow warm-up can never trip the
        // heartbeat-timeout disconnect from this point until promotion / leave.
        this.state.queueHold = true;

        // Enter QUEUED on the first frame only (idempotent on repeats). Guarded
        // so a stray frame outside the connect handshake can't yank an in-game
        // player out of GAMEPLAY.
        if (TerminalFSM.mode === 'CONNECTING') {
            TerminalFSM.transition('queued');
        } else if (TerminalFSM.mode !== 'QUEUED') {
            // Defensive: a queue frame in an unexpected mode -- still suppress the
            // watchdog (queueHold set above) but don't force a surface render that
            // would clobber another state.
            console.warn(
                `[queue] queue_position in mode ${TerminalFSM.mode}; suppressing ` +
                `watchdog but not rendering surface`
            );
            return;
        }

        this._renderQueueSurface(message);
    },

    /**
     * [2026-08-06-006-QASTI] `queue_admitted`: the LINE is over, the WORLD is not
     * open yet. This is the last frame the admission controller sends -- from here
     * the server is running the connect sequence on this same socket, and NO
     * further `queue_position` will arrive.
     *
     * Without an arm this frame fell through to `default:`, which meant the queue
     * surface simply stopped updating: it kept showing the final position + ETA
     * ("Position 3 of 7 in line", "Estimated wait: a minute or two") for the whole
     * connect/resume window, claiming the player was still queued after they had
     * been admitted. Repaint to an honest, position-free "you're through" state.
     *
     * ⚠️ Deliberately does NOT clear `state.queueHold`. The heartbeat watchdog
     * must stay suppressed until `connected`: the server pushes nothing while it
     * binds the session and runs resume-through-authority, and that silence can
     * outrun heartbeatTimeout (60s). Releasing the hold here would arm the
     * watchdog against the server's own connect work and kill the socket the
     * player is waiting on. Teardown stays with handleQueuePromotion.
     */
    handleQueueAdmitted(message) {
        // Belt-and-braces: the server only emits this for a held socket, so a
        // frame outside the queue means a contract we don't understand. Say so
        // rather than painting a warming surface over a live game.
        if (!this.state.queueHold && TerminalFSM.mode !== 'QUEUED') {
            console.warn(
                `[queue] queue_admitted with no active hold (mode=${TerminalFSM.mode}); ` +
                `ignoring — the server only sends this to a held socket`
            );
            return;
        }
        this._renderQueueSurface({
            ...(message || {}),
            admitted: true,
            // Drop any stale position/ETA the last queue_position left behind:
            // they describe a line this player is no longer standing in.
            position: null,
            total_waiting: null,
            eta_seconds: null,
        });
    },

    /**
     * Promotion: the `connected` frame arrived, the hold is over. Clear the hold
     * flag + remove the queue surface so the normal connect flow renders cleanly.
     * Safe to call when no queue surface exists (warm path never queued).
     */
    handleQueuePromotion() {
        if (!this.state.queueHold) {
            return; // warm path: never held, nothing to tear down
        }
        const sessionId = this.state.sessionId || 'unknown';
        console.log(`[${sessionId}] Queue promotion -- pods warm, resuming connect flow`);
        this.state.queueHold = false;
        this._removeQueueSurface();
        // FSM transition out of QUEUED is performed by handleStatelessConnection
        // (connected_needs_creation / connected_no_creation), the existing path.
    },

    /**
     * Render (or refresh in place) the queue surface in the terminal `#history`.
     * Apple-II idiom: a small framed block presenting this as intentional ("in
     * line, servers warming"), never as an error.
     */
    _renderQueueSurface(message) {
        const history = document.getElementById('history');
        if (!history) {
            // Headless / unit harness -- log and bail.
            console.log('[queue]', message.message, message.position, message.total_waiting);
            return;
        }

        let surface = document.getElementById('queue-surface');
        if (!surface) {
            surface = document.createElement('div');
            surface.id = 'queue-surface';
            surface.className = 'message queue-surface';
            // [2026-08-08-007-ENGINE] ORIGIN: server-authority. QUEUE POSITION
            // is named in client_funnel.md section 1 as a control-plane fact,
            // and it is the only thing a held player has: going silent here is
            // exactly the wedge this axis exists to prevent.
            const _F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
            if (!_F) return;
            surface = _F.insert(surface, {
                origin: _F.ORIGIN.SERVER_AUTHORITY,
                parent: history
            });
            if (!surface) return;
        }

        // [2026-08-06-006-QASTI] `admitted` = the queue_admitted frame. Same
        // surface, different claim: the line is behind us, so every line about
        // standing in one is suppressed.
        const admitted = !!message.admitted;
        const serverMsg = admitted
            ? "You're through the queue."
            : (message.message || 'Servers are warming up.').trim();
        const position = (!admitted && typeof message.position === 'number') ? message.position : null;
        const total = (!admitted && typeof message.total_waiting === 'number') ? message.total_waiting : null;
        const wait = admitted ? null : this._formatQueueWait(message.eta_seconds);

        const lines = [];
        lines.push('+-----------------------------------------+');
        lines.push(admitted ? '  ENTERING' : '  WARMING UP');
        lines.push('');
        lines.push(`  ${serverMsg}`);
        if (position !== null && total !== null) {
            lines.push(`  Position ${position} of ${total} in line.`);
        } else if (position !== null) {
            lines.push(`  Position ${position} in line.`);
        }
        if (wait) {
            lines.push(`  Estimated wait: ${wait}`);
        }
        lines.push('');
        lines.push(admitted
            ? '  Opening the world -- one moment.'
            : '  Hold tight -- the world is spinning up for you.');
        lines.push('+-----------------------------------------+');

        // Render as plain text lines (preserves the monospace terminal idiom).
        surface.textContent = lines.join('\n');
        history.scrollTop = history.scrollHeight;
    },

    _removeQueueSurface() {
        const surface = document.getElementById('queue-surface');
        if (surface && surface.parentNode) {
            surface.parentNode.removeChild(surface);
        }
    },

    /**
     * Soft, non-alarming wait phrasing from the server's soft `eta_seconds`.
     * Small numbers -> "~Ns"; large -> "a few minutes". Missing -> null (omit).
     */
    _formatQueueWait(etaSeconds) {
        if (typeof etaSeconds !== 'number' || etaSeconds <= 0) return null;
        if (etaSeconds <= 90) return `~${Math.round(etaSeconds)}s`;
        if (etaSeconds <= 180) return 'a minute or two';
        return 'a few minutes';
    },

    handleStatelessConnection(message) {
        // Server-authoritative session_id: replace client-generated sessionId
        // with the one returned by the server (derived from JWT-verified identity).
        if (message.session_id) {
            const oldSessionId = this.state.sessionId;
            this.state.sessionId = message.session_id;
            console.log(`[${oldSessionId}] Session ID updated to server-authoritative: ${message.session_id}`);
        }

        const sessionId = this.state.sessionId || 'unknown';

        // [2026-08-03-026-ENGINE] Adopt the universe's AUTHORED UX copy.
        //
        // ⚠️ CALLED UNCONDITIONALLY, INCLUDING WHEN THE BLOCK IS ABSENT. That
        // is what makes the fallback DETECTABLE: adoptPhrases() warns and sets
        // `__VP_WAITING_SOURCE = 'fallback'` when it gets nothing, so "the
        // server had no authored phrases" and "the server sent them" are
        // distinguishable from outside the client. Guarding this call on
        // `message.ux_strings` would restore the silent-twin state that made
        // the dormant surface invisible in the first place.
        WaitingIndicator.adoptPhrases(
            (message.ux_strings || {}).waiting_phrases
        );

        // [channel-delivery-ledger Step 4 — Fold X-1] Capture the durable
        // session correlation the server now emits on `connected` so VP's
        // recorder can persist it into manifest.json as first-class fields
        // (server_session_id / player_id / world_id), retiring the brittle
        // log-filename bridge. Exposed for the recorder + diagnostics.
        this.state.serverSessionId = message.server_session_id || message.session_id;
        this.state.playerId = message.player_id || this.state.playerId;
        this.state.worldId = message.world_id || this.state.worldId;

        // [prompt-survey fast-follow 2026-05-22] Expose the durable session
        // correlation on an always-on introspection global so the Python VP
        // harness can read it (page.evaluate) after `connected` arrives and
        // call recorder.set_session_correlation(...). Tiny + additive, no
        // gating needed (follows the __VP_PUMP_STATE / __LEDGER_SHADOW
        // observe-only convention). Retires the brittle Logs/browser filename
        // bridge the prompt-survey resolver depends on.
        try {
            window.__VP_SESSION_CORRELATION = {
                server_session_id: this.state.serverSessionId || null,
                player_id: this.state.playerId || null,
                world_id: this.state.worldId || null,
            };
        } catch (_err) { /* introspection is best-effort */ }

        // [2026-08-19-009-ENGINE / Thread 0] Arm account-scoped session capture
        // if THIS Firebase UID is flagged server-side. Server-authoritative and
        // OFF by default: with no `client_capture` block (the normal case for
        // every unflagged player) this returns immediately having set no timer,
        // registered no listener and touched no hot path.
        //
        // Placed after the session/player identity is adopted above, because
        // the first segment stamps session_id + player_id.
        try {
            this._armClientCapture(message);
        } catch (_capErr) {
            // Diagnostics must never be able to break the connect path.
            console.error('[capture] arm failed:', String(_capErr));
        }

        // [channel-delivery-ledger Step 4d] Apply the reconnect watermark
        // handshake (fresh-page adopt-baseline OR WS-blip ledger_resync). Must
        // run BEFORE any pump-driving frame is admitted so the baseline is
        // authoritative. The `connected` frame is the first frame of the
        // connection generation, so this ordering holds.
        this._ledgerApplyHandshake(message.ledger);

        // Extract stateless GameServer metadata
        this.state.gameserverName = message.gameserver_name;
        this.state.gameserverGeneration = message.gameserver_generation;
        this.state.isStateless = message.stateless === true;

        console.log(`[${sessionId}] Stateless GameServer connection established:`, {
            gameserver_name: this.state.gameserverName,
            generation: this.state.gameserverGeneration,
            connected_players: message.connected_players,
            isStateless: this.state.isStateless,
            needs_creation: message.needs_creation
        });

        // Transition FSM based on whether creation is needed
        // (isInGame, inCreationFlow, awaitingFlowCompletion are all FSM-derived)
        if (message.needs_creation) {
            TerminalFSM.transition('connected_needs_creation', {
                worldName: this.state.currentWorld?.world_name,
                gameserverName: this.state.gameserverName,
                isStateless: this.state.isStateless
            });
            console.log(`[${sessionId}] Player needs creation - entering WELCOME_NARRATION`);
            // Armed on ENTRY, not on the `welcome` step: a lost void scene (no
            // `welcome` frame at all) must still release the menu.
            this._armWelcomeNarrationTimeout(sessionId, 'connected_needs_creation');
        }

        // Buffer connection messages instead of adding immediately
        // This prevents them from appearing after the input prompt
        const connectionMessages = [];

        if (this.state.isReconnecting) {
            connectionMessages.push('Reconnected successfully!');
            if (this.state.gameserverGeneration > 1) {
                connectionMessages.push(`GameServer restarted (generation ${this.state.gameserverGeneration})`);
                connectionMessages.push('Your session has been restored from the database.');
            }
            this.state.isReconnecting = false;
        } else {
            connectionMessages.push('Connected to GameServer');
            if (this.state.isStateless) {
                connectionMessages.push('Stateless session enabled (your progress is saved)');
            }
        }

        connectionMessages.push(`${message.connected_players} player(s) connected`);

        // Display connection messages immediately
        connectionMessages.forEach(msg => this.logMessage(msg));

        // Narration delivery is via WebSocket "narration" messages (v25 consumer-pull).
        // No SSE audio channels needed.

        // Store character name for returning players (combat log perspective)
        if (message.character && message.character.name) {
            this.state.characterName = message.character.name;
        }

        // If not needing creation, transition to WORLD_MENU
        if (!message.needs_creation) {
            TerminalFSM.transition('connected_no_creation', {
                worldName: this.state.currentWorld?.world_name,
                gameserverName: this.state.gameserverName,
                isStateless: this.state.isStateless
            });

            // [2026-07-02-012-ENGINE] Returning-player reconnect resume.
            // [2026-07-06-001-ENGINE / D1] Now SERVER-AUTHORITATIVE.
            //
            // ROOT CAUSE of the reconnect prompt-lock (still valid): a returning
            // player (character + location already exist) landed in WORLD_MENU
            // and NEVER entered GAMEPLAY, so the REG-2 two-signal unlock never
            // ran and the prompt was locked forever.
            //
            // FIX (D1 shape): the CLIENT enters GAMEPLAY and tears down the stale
            // WORLD_MENU prompt, but it NO LONGER dispatches a `look`. The
            // membership enter funnel (server side) fires resume as an `enter`
            // and delivers the arrival scene (ch0 room scene) + the room's next
            // phase_clock down the normal narration-receive path. Those drain
            // through the pump and birth the prompt + clock together in
            // _finishCycle() -- the same REG-2 "clock + prompt born together"
            // contract, just triggered server-side instead of by a client look.
            // Removing the client look dissolves W0.7/W0.9 on the resume path too.
            //
            // Gate: only when the connected frame carries a resumable
            // character + location (returning player), not a fresh no-creation
            // menu return (e.g. world switch with no character yet).
            const resumeChar = message.character && message.character.name;
            const resumeLoc = message.spawn_location && message.spawn_location.name;
            if (resumeChar && resumeLoc) {
                console.log(
                    `[${sessionId}] Returning player resume: ${resumeChar} @ ${resumeLoc} ` +
                    `-- entering GAMEPLAY, awaiting server-pushed arrival scene (REG-2 unlock)`
                );
                TerminalFSM.transition('resume_gameplay', {
                    characterName: resumeChar,
                    locationName: resumeLoc,
                });
                // Tear down any stale WORLD_MENU prompt born from the
                // connected_no_creation transition above. The returning
                // player's real GAMEPLAY prompt is born (with its clock) by
                // _finishCycle() when the server-pushed arrival cycle completes
                // -- the REG-2 "clock + prompt born together" contract. A
                // leftover WORLD_MENU .active-input-line would make
                // _shouldShowPrompt() skip the real prompt (duplicate-prompt
                // guard); the insert-above display guard keeps any early
                // server-pushed narration above it in the meantime.
                // [2026-08-03-014-ENGINE stage 3] ⚠️ NO REMOVAL. The command
                // line is permanent markup now; removing it here would delete
                // the returning player's only way to type. Closing is what the
                // reap was for -- the real GAMEPLAY prompt is opened (with its
                // clock) by _finishCycle() when the server-pushed arrival cycle
                // completes, the REG-2 "clock + prompt born together" contract,
                // and a CLOSED block is exactly what lets that happen.
                if (typeof window !== 'undefined' && window.CommandLine) window.CommandLine.close();
                if (typeof window !== 'undefined'
                        && typeof window.renderCommandBlockLock === 'function') {
                    window.renderCommandBlockLock(true);
                }
                // NOTE: no client `look` / server_action here. The server's
                // enter funnel delivers ch1_missing (no prior turn) -> ch0
                // (room scene) -> turn_complete -> phase_clock; the existing
                // pump handlers receive that sequence and complete the cycle.
            }
        }
    },

    // =========================================================================
    // WebSocket Narration Handler (Rolling Gate Play Queue)
    // =========================================================================
    // Receives {"type": "narration", "channel": 0|1, "text": "...", "audio_url": "..."}
    // from GameServer. Buffers in play queue. Ch0 (channel:0) is the anchor --
    // when ch0 finishes playing, the turn is over and input re-enables.

    handleNarration(message) {
        const sessionId = this.state.sessionId || 'unknown';
        const text = this._extractNarrationText(message.text || '');
        // [voice-drop-observability #2] Accept the legacy `voice_url` field as a
        // fallback for `audio_url`. networking.py:211 emits `voice_url` on the
        // legacy broadcast path — reading only `audio_url` silently played
        // nothing for a legacy-shaped frame. This is a real drop fix, not just
        // logging.
        const audioUrl = message.audio_url || message.voice_url || null;
        // THE RECEIVED DURATION (`frame.md` P9, ruled 2026-09-15).
        //
        // `null` when the server did not send one, and that is a REAL state
        // rather than a hole -- a frame from a server that predates this key,
        // or a voice whose producer returned no length. It is NEVER defaulted
        // and NEVER estimated here: the client inventing a duration is exactly
        // what P9 retires. A null means the typewriter falls back to its
        // assumed pace, loudly, and the pace is then honestly labelled assumed.
        const audioDurationMs = (
            typeof message.audio_duration_ms === 'number'
                ? message.audio_duration_ms
                : null
        );
        const narrator = message.narrator || 'mother_time';
        const requestId = message.request_id || null;
        // Support both new channel field and legacy context field
        const channel = (message.channel !== undefined) ? message.channel : (message.context === 'commentary' ? 1 : 0);
        const context = message.context || (channel === 1 ? 'commentary' : 'gameplay');
        // [combat-validation-observability GAP 1+2] Source the {room}:NNN.PP join
        // key off the wire. ch0/ch1 narration frames carry `cycle_id`; the
        // mechanics/control frames (ch2 resolution_event, ch3 interval, ch9 phase_clock)
        // carry the same value under `audit_key`. Accept either so the cycle id
        // rides the narration_received event -> the entry's cycleId ->
        // _currentNarrationCycleId -> playback_complete, and the VP probe's
        // narration_start/mid/end frames join to the cycle. Name-space, NOT a UUID.
        const cycleId = message.cycle_id || message.audit_key || null;

        // Clear welcome narration timeout as soon as narration arrives
        if (this._welcomeNarrationTimeout) {
            clearTimeout(this._welcomeNarrationTimeout);
            this._welcomeNarrationTimeout = null;
        }

        console.log(
            `[${sessionId}] Narration received: channel=${channel}, narrator=${narrator}, ` +
            `req=${requestId || 'none'}, msg_session=${message.session_id || 'none'}, ` +
            `text=${(text || '').substring(0, 80)}..., ` +
            `audio=${audioUrl ? 'yes' : 'no'}, text_style=${message.text_style || 'missing'}, fsm=${TerminalFSM.mode}`
        );

        // Session ownership check: discard narrations from stale/ghost sessions.
        // If the message carries a session_id and it doesn't match ours, drop it.
        if (message.session_id && message.session_id !== sessionId) {
            console.warn(
                `[${sessionId}] Narration DISCARDED (wrong session): ` +
                `msg_session=${message.session_id}, req=${requestId}`
            );
            return;
        }

        if (!text) {
            console.warn(`[${sessionId}] Empty narration text, skipping`);
            // Empty text: still signal the pump so any in-flight
            // server-initiated cycle completes cleanly. In GAMEPLAY this
            // delegates to playback_complete via _releaseNarrationGate. In
            // WELCOME_NARRATION / SPAWNING we fire the ch0 void so the drain
            // proceeds to exit and the FSM listener advances the mode.
            if (TerminalFSM.mode === 'WELCOME_NARRATION' || TerminalFSM.mode === 'SPAWNING') {
                if (typeof TurnPump !== 'undefined') {
                    // CLIENT-INTERNAL void synthesis. Synthesized locally from
                    // empty narration text, it has NO server-assigned seq, so it
                    // is deliberately NOT routed through the wire dispatch /
                    // _ledgerShadowRecord — recording it would invent a phantom
                    // seq the server never assigned, and seq reconciliation is
                    // server-frame-only. It carries no cycle_id either, so the
                    // classifier attributes it temporally (the unstamped class).
                    //
                    // It sets ch0.VOIDED, not rendered. The non-GAMEPLAY arm
                    // reads `rendered` alone, so this synthetic void does NOT
                    // complete the welcome/spawn cycle — an empty text is a
                    // failure to speak, not a scene the player watched.
                    TurnPump.event('ch0_missing', { reason: 'empty_narration_text' });
                }
            } else {
                this._releaseNarrationGate();
            }
            return;
        }

        // [voice-drop-observability #1] voice_absent — a ch0 frame with real text
        // but no audio URL is a DROP (server-side voice failure or a legacy frame
        // whose fallback also missed), not a legitimate text-only frame. ch0 is
        // the voiced anchor; ch1 (commentary) is text-only BY DESIGN, so never
        // warn on it. This is the observational counterpart to the legacy
        // voice_url fallback above: if both fields were empty on a ch0 frame, it
        // surfaces here as a cycle-stamped, greppable event + an `absent` outcome.
        if (channel === 0 && !audioUrl) {
            // [2026-07-27-003-QASTI BUG 3] Classify BEFORE reporting. A ch0 with
            // no audio is only a FAILURE when voice was actually requested. Two
            // discriminators, in order of authority:
            //   1. the frame itself — if the server stamps voice_requested /
            //      include_voice=false, that is the ground truth, no inference.
            //      ⚠️ DORMANT AS OF 2026-07-27: the server logs include_voice at
            //      the gateway <narrate> dispatch but does NOT yet put it on the
            //      narration wire frame. This branch is forward-compatible and
            //      untriggered today; discriminator 2 is what actually fires.
            //      When the server starts stamping it, this takes precedence and
            //      the inference below stops being load-bearing.
            //   2. the client's own sound preference — OFF means the server
            //      fired include_voice=false for us, so no voice was ever coming.
            // Everything else is a real drop and must stay LOUD.
            const frameSaysUnvoiced = (message.voice_requested === false)
                || (message.include_voice === false);
            let soundPrefOn = true;
            try {
                if (typeof SoundToggle !== 'undefined'
                    && typeof SoundToggle.isSoundOn === 'function') {
                    soundPrefOn = SoundToggle.isSoundOn() !== false;
                }
            } catch (_e) { /* unreadable pref -> treat as ON (report loudly) */ }
            const absentReason = frameSaysUnvoiced
                ? 'voice_not_requested'
                : (soundPrefOn ? 'no_url_on_ch0' : 'sound_pref_off');
            const voidClass = this._voiceClassForReason(absentReason);
            this._emitVoiceEvent(voidClass === 'failure' ? 'warn' : 'log', {
                event: 'voice_absent',
                cycle_id: cycleId,
                channel: 0,
                audio_url: null,
                narrator,
                request_id: requestId,
                text_len: (text || '').length,
                fsm: TerminalFSM.mode,
                reason: absentReason,
                void_class: voidClass,
                sound_pref_on: soundPrefOn,
            });
            this._recordVoiceOutcome('absent', {
                cycleId, channel: 0, audioUrl: null, reason: absentReason,
            });
        } else if (channel === 1 && !audioUrl) {
            // ch1 commentary is hard-coded include_voice=False server-side
            // (prompt_builder.py / narration_client.py) to halve voice-GPU load.
            // Its silence is BY DESIGN. It was previously reported as NOTHING at
            // all, which left the ledger ambiguous by omission — a reader could
            // not tell "classified as expected" from "never looked at". Record it
            // EXPLICITLY as an absence so every unvoiced frame in the session
            // carries a class, and no one re-derives ch1 as a symptom.
            this._recordVoiceOutcome('absent', {
                cycleId, channel: 1, audioUrl: null, reason: 'ch1_unvoiced_by_design',
            });
        }

        // [Phase 4] Unified pump entry — welcome and spawn narrations no longer
        // bypass the pump. They enter via a server-initiated cycle so the drain
        // runs and exits properly. FSM mode transitions (WELCOME_NARRATION ->
        // ROSTER_DISPLAY, SPAWNING -> GAMEPLAY) happen on pump_finish_cycle via
        // the FSM listener wired in init().
        //
        // If the pump is idle when narration arrives during a non-GAMEPLAY
        // server-initiated mode, start the cycle. If it's already running
        // (another narration in the queue), just enqueue — the drain handles
        // ordering. GAMEPLAY narrations fall through to the existing routing.
        if (TerminalFSM.mode === 'WELCOME_NARRATION' || TerminalFSM.mode === 'SPAWNING') {
            if (TurnPump._state.status === 'idle') {
                console.log(
                    `[Pump Audit] server_initiated_cycle fsm=${TerminalFSM.mode} channel=${channel} ` +
                    `narrator=${narrator} req=${requestId || 'none'} reason=${TerminalFSM.mode.toLowerCase()}`
                );
                TurnPump.event('server_action', { origin: TerminalFSM.mode });
            }
        }

        // --- TurnPump Play Queue ---
        // All narrations (GAMEPLAY + the Phase-4 unified welcome/spawn flow)
        // are routed through TurnPump's reducer.
        TurnPump.event('narration_received', {
            text, audioUrl, audioDurationMs, narrator, context,
            channel, requestId,
            textStyle: message.text_style || 'normal',
            // ch1's address: the command it answers.
            command_id: message.command_id || null,
            // [combat-validation-observability GAP 1+2] Carry the cycle_id so the
            // pump entry stamps it (entry.cycleId) and the VP probe populates the
            // screens.md Audit column for narration_start/mid/end.
            cycle_id: cycleId,
        });
    },

    /**
     * Ch4 — a spoken line arrives, and prints (decision-log 2026-10-14).
     *
     * The line was generated upstream in the character's own words; ch4 does
     * not re-narrate it and asks the LLM nothing. It is pushed on arrival,
     * outside any beat's result, so receiving it neither locks nor unlocks the
     * prompt and never touches the pump. This handler validates the frame,
     * settles the line's owed debt, and prints it -- after the current ch0
     * sentence if a scene is typing.
     *
     * Ch4 is UNVOICED by contract (Q4). Every refusal here is LOUD: a hollow
     * frame is dropped with an error naming the speaker and the cycle, never
     * rendered as an empty line and never silently ignored.
     */
    handleDialogue(message) {
        const sessionId = this.state.sessionId || 'unknown';
        const text = String((message && message.text) || '').trim();
        const speaker = String((message && message.speaker) || '').trim();
        const listener = (message && message.listener) || null;
        const ordinal = message ? message.channel_ordinal : undefined;
        const cycleId = (message && (message.cycle_id || message.audit_key)) || null;

        console.log(
            `[${sessionId}] ch4 dialogue received: speaker=${speaker || 'MISSING'} ` +
            `listener=${listener || 'room'} ordinal=${ordinal !== undefined ? ordinal : 'MISSING'} ` +
            `cycle=${cycleId || 'none'} seq=${message && message.seq !== undefined ? message.seq : 'unstamped'} ` +
            `text=${text.substring(0, 60)}...`
        );
        console.log(`[Delivery] cycle=${cycleId || 'none'} ch=4 phase=received`);

        // Stale/ghost session discard — the same ownership rule narration uses.
        if (message && message.session_id && message.session_id !== sessionId) {
            console.warn(
                `[${sessionId}] ch4 dialogue DISCARDED (wrong session): ` +
                `msg_session=${message.session_id} speaker=${speaker}`
            );
            return;
        }

        // A frame with no words or no speaker is the silent-zero this channel
        // exists to kill — the old pipe's hop 5 returned None on an empty reply
        // and called it "skipped, never raised". Refuse it, loudly, with enough
        // context to find the hop that produced it.
        if (!text || !speaker) {
            console.error(
                `[${sessionId}] ch4 dialogue DROPPED — hollow frame: ` +
                `speaker=${speaker || 'EMPTY'} text_len=${text.length} ` +
                `ordinal=${ordinal !== undefined ? ordinal : 'MISSING'} ` +
                `cycle=${cycleId || 'none'}. The server should have refused to ` +
                `build this frame (build_ch4_frame raises on both).`
            );
            return;
        }

        // D6 — the ordinal MUST ADVANCE. A repeat or a regression is the precise
        // defect that stalled every second lobby command before M3a, so it is
        // called out by name rather than left to look like a rendering glitch.
        // The line still renders: a visible line with a suspect ordinal beats a
        // swallowed one, and a legitimate reset (reconnect / new stream epoch)
        // looks identical from here.
        if (typeof ordinal === 'number') {
            const prev = this._ch4LastOrdinal;
            if (typeof prev === 'number' && ordinal <= prev) {
                console.warn(
                    `[${sessionId}] ch4 ORDINAL DID NOT ADVANCE: ${ordinal} <= ` +
                    `${prev} (cycle=${cycleId || 'none'}). Either the mint is ` +
                    `not INCRing or this is a stream-epoch reset. Rendering ` +
                    `anyway — but a repeat on consecutive lines is a D6 bug.`
                );
            }
            this._ch4LastOrdinal = ordinal;
        } else {
            console.warn(
                `[${sessionId}] ch4 frame carries NO channel_ordinal ` +
                `(cycle=${cycleId || 'none'}) — it cannot be joined to the ` +
                `survey corpus or de-duplicated across listeners.`
            );
        }

        const entry = { text, speaker, listener, ordinal, cycleId };
        entry.addressed = this._ch4IsAddressed(entry);

        // THE LINE PAYS ITS DEBT FIRST, above every exit: a held line and a
        // rendered line discharge the same obligation. It is also recorded as
        // HEARD, because a pushed line normally arrives BEFORE the owed
        // statement for it (the statement rides the beat's result).
        this._ch4NoteHeard(speaker, cycleId);
        this._dischargeCh4Owed(speaker, cycleId);

        // ch4 PRINTS ON ARRIVAL (decision-log 2026-10-14, rulings 1 and 4).
        // Nothing below reads or writes the prompt or the pump. The only waits
        // are for the reader: a line never splits a sentence of narration, and
        // (an owner's option) an overheard line may wait for the player to stop
        // typing.
        if (!entry.addressed
                && this.CH4_LOOK.overheardWhileTyping === 'hold'
                && this._ch4PlayerIsTyping()) {
            this._ch4Overheard.push(entry);
            this._ch4WatchTyping();
            console.log(
                `[${sessionId}] ch4 overheard line HELD while the player types ` +
                `(speaker=${speaker}, ${this._ch4Overheard.length} held)`
            );
            return;
        }
        // A line never overtakes one already held: the beat reads in the
        // order it happened ([2026-08-25-029-ENGINE]).
        if (this._ch4Held.length || this._ch4Ch0Typing()) {
            this._ch4HoldForSentence(entry);
            return;
        }
        this._renderCh4Line(entry);
    },

    /**
     * ch4's look and feel (decision-log 2026-10-14, proposal 5). The style
     * guide (`frontend/web/preview/style-guide/`, section "ch4 on arrival")
     * shows each choice live.
     *
     * ⚠️ OWNER'S OPEN CHOICES, NOT DECIDED HERE. The values below are the
     * proposal's defaults so the client runs; the owner marks the choice in
     * the style guide and this object takes it:
     *   arrivalCue          'fade' | 'name' | 'none'   -- how a line arrives
     *   overheardWhileTyping 'show' | 'hold'           -- an overheard line
     *                                                     while the player types
     *   sentenceWaitMaxMs   number                     -- the longest a line
     *                                                     waits for the end of
     *                                                     a ch0 sentence
     */
    CH4_LOOK: {
        arrivalCue: 'fade',
        overheardWhileTyping: 'show',
        sentenceWaitMaxMs: 6000,
    },

    _ch4Held: [],
    _ch4HeldTimer: null,
    _ch4Overheard: [],
    _ch4Owed: [],
    _ch4Heard: [],
    // How many arrivals are remembered for an owed statement that comes after
    // its line. A beat's statement follows its lines within seconds.
    CH4_HEARD_MEMORY: 64,

    /**
     * True while a ch0 scene is TYPING. A queued scene that has not started
     * holds nothing: the line prints now and the scene follows it.
     *
     * Conservative: an unreadable pump reports FALSE (print now), because a
     * line printed beside a sentence is cosmetic and a line held forever is a
     * lost line.
     */
    _ch4Ch0Typing() {
        try {
            if (typeof TurnPump === 'undefined' || !TurnPump._state) return false;
            return TurnPump._state.status === 'playing'
                && GameServerConnection._currentNarrationChannel === 0;
        } catch (_e) {
            return false;
        }
    },

    /** A line spoken to this player, or by them, carries full weight. */
    _ch4IsAddressed(entry) {
        const me = this._ch4Norm(this.state && this.state.characterName);
        if (!me) return false;
        return this._ch4Norm(entry.listener) === me || this._ch4Norm(entry.speaker) === me;
    },

    _ch4Norm(name) {
        return String(name || '').trim().toLowerCase().replace(/[-_]+/g, ' ');
    },

    /** The player has words in the command line. */
    _ch4PlayerIsTyping() {
        try {
            const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
            const field = CL ? CL.inputField(document) : null;
            return !!(field && String(field.value || '').length);
        } catch (_e) {
            return false;
        }
    },

    /**
     * Hold a line until the scene's current sentence ends (`_ch4SentenceEnded`),
     * the scene finishes (`_flushCh4Held`), or `sentenceWaitMaxMs` passes --
     * whichever is first.
     */
    _ch4HoldForSentence(entry) {
        this._ch4Held.push(entry);
        if (!this._ch4HeldTimer) {
            this._ch4HeldTimer = setTimeout(
                () => this._flushCh4Held('sentence wait elapsed'),
                this.CH4_LOOK.sentenceWaitMaxMs,
            );
        }
        console.log(
            `[${this.state.sessionId || 'unknown'}] ch4 line HELD for the end of ` +
            `a ch0 sentence (speaker=${entry.speaker}, ${this._ch4Held.length} held)`
        );
    },

    /**
     * True when the first ``end`` characters of ``text`` end a sentence: the
     * last is a full stop, `!` or `?` (or a closing quote or bracket after
     * one), and the next is whitespace or the end of the text.
     */
    _isSentenceEnd(text, end) {
        let i = end - 1;
        while (i >= 0 && /["'”’)\]]/.test(text[i])) i--;
        if (i < 0 || !/[.!?]/.test(text[i])) return false;
        return end >= text.length || /\s/.test(text[end]);
    },

    /** The typewriter wrote the last character of a sentence. */
    _ch4SentenceEnded() {
        if (this._ch4Held.length) this._flushCh4Held('ch0 sentence ended');
    },

    /** Print every held line, in arrival order. Safe when nothing is held. */
    _flushCh4Held(why) {
        if (this._ch4HeldTimer) {
            clearTimeout(this._ch4HeldTimer);
            this._ch4HeldTimer = null;
        }
        const held = this._ch4Held;
        if (!held.length) return;
        this._ch4Held = [];
        console.log(
            `[${this.state.sessionId || 'unknown'}] ch4 flushing ${held.length} ` +
            `held line(s) (${why})`
        );
        held.forEach(entry => this._renderCh4Line(entry));
    },

    /** Release held overheard lines once the command line is empty again
     *  (cleared by hand, or by a submit). Attached once. */
    _ch4WatchTyping() {
        if (this._ch4TypingWatched || typeof document === 'undefined') return;
        this._ch4TypingWatched = true;
        const release = () => {
            if (this._ch4Overheard.length && !this._ch4PlayerIsTyping()) {
                this._flushCh4Overheard('the player stopped typing');
            }
        };
        document.addEventListener('input', release, true);
        document.addEventListener('keyup', release, true);
    },

    /** Print the overheard lines held while the player typed. */
    _flushCh4Overheard(why) {
        const held = this._ch4Overheard;
        if (!held.length) return;
        this._ch4Overheard = [];
        console.log(
            `[${this.state.sessionId || 'unknown'}] ch4 flushing ${held.length} ` +
            `overheard line(s) (${why})`
        );
        held.forEach(entry => this._renderCh4Line(entry));
    },

    // -- THE OWED LINE, judged in SECONDS (channels.md section 5) -----------
    //
    // A `ch4_owed` frame says a player's utterance was ACCEPTED, so a ch4 line
    // is owed to them. It is judged on a wall clock measured from its
    // enrolment: escalated (logged louder; the ledger's NACK/resend is the
    // recovery, ch4 being enrolled) at the first number and judged DROPPED,
    // with the player told, at the second. Both numbers are the registry's
    // (`frame.py` CH4_OWED_SECONDS), never the turn clock's.
    //
    // The join is (speaker, beat): the line carries the beat it was spoken in
    // and the statement names the beat that accepted it, which is the same
    // beat -- a player's line is cast on the beat that dispatches it.

    _ch4Key(speaker, cycleId) {
        return `${this._ch4Norm(speaker)}|${cycleId || 'none'}`;
    },

    _ch4NoteHeard(speaker, cycleId) {
        this._ch4Heard.push(this._ch4Key(speaker, cycleId));
        if (this._ch4Heard.length > this.CH4_HEARD_MEMORY) this._ch4Heard.shift();
    },

    _ch4OwedSeconds() {
        const row = globalThis.FrameRegistry.REGISTRY['ch4-owed'];
        return { escalate: row.owed_escalate_seconds, fail: row.owed_fail_seconds };
    },

    _enrolCh4Owed(message) {
        const sid = this.state.sessionId || 'unknown';
        const speaker = (message && message.speaker) || '';
        const cycleId = (message && message.cycle_id) || null;
        const key = this._ch4Key(speaker, cycleId);
        const heardAt = this._ch4Heard.indexOf(key);
        if (heardAt !== -1) {
            // One line pays one debt.
            this._ch4Heard.splice(heardAt, 1);
            console.log(
                `[${sid}] ch4 owed SETTLED on arrival of the statement -- ` +
                `speaker=${speaker || '?'} cycle=${cycleId || 'none'}: the line ` +
                `was already here`
            );
            return;
        }
        const budget = this._ch4OwedSeconds();
        const entry = { speaker, cycleId, key, enrolledAt: Date.now(), timers: [] };
        entry.timers.push(setTimeout(() => this._ch4OwedEscalate(entry), budget.escalate * 1000));
        entry.timers.push(setTimeout(() => this._ch4OwedFail(entry), budget.fail * 1000));
        this._ch4Owed.push(entry);
        console.log(
            `[${sid}] ch4 owed ENROLLED -- speaker=${speaker || '?'} ` +
            `cycle=${cycleId || 'none'}; judged at ${budget.fail}s ` +
            `(escalated at ${budget.escalate}s)`
        );
    },

    _dischargeCh4Owed(speaker, cycleId) {
        const key = this._ch4Key(speaker, cycleId);
        const at = this._ch4Owed.findIndex(e => e.key === key);
        if (at === -1) return false;
        const entry = this._ch4Owed.splice(at, 1)[0];
        entry.timers.forEach(t => clearTimeout(t));
        // The line paid the debt; it does not also settle a later statement.
        const heardAt = this._ch4Heard.lastIndexOf(key);
        if (heardAt !== -1) this._ch4Heard.splice(heardAt, 1);
        console.log(
            `[${this.state.sessionId || 'unknown'}] ch4 owed DISCHARGED -- ` +
            `speaker=${speaker} cycle=${cycleId || 'none'} after ` +
            `${((Date.now() - entry.enrolledAt) / 1000).toFixed(1)}s`
        );
        return true;
    },

    _ch4OwedEscalate(entry) {
        if (this._ch4Owed.indexOf(entry) === -1) return;
        console.warn(
            `[${this.state.sessionId || 'unknown'}] ch4 owed and STILL UNARRIVED ` +
            `after ${this._ch4OwedSeconds().escalate}s -- speaker=` +
            `${entry.speaker || '?'} owed_at=${entry.cycleId || 'none'}. The ` +
            `ledger's resend is the only recovery.`
        );
    },

    _ch4OwedFail(entry) {
        const at = this._ch4Owed.indexOf(entry);
        if (at === -1) return;
        this._ch4Owed.splice(at, 1);
        const seconds = this._ch4OwedSeconds().fail;
        console.error(
            `[${this.state.sessionId || 'unknown'}] ch4 UNDELIVERED past its ` +
            `budget -- speaker=${entry.speaker || '?'} owed_at=` +
            `${entry.cycleId || 'none'} waited=${seconds}s. Telling the player.`
        );
        try {
            if (typeof TurnPump !== 'undefined' && typeof TurnPump._recordDrop === 'function') {
                TurnPump._recordDrop(4, entry.cycleId || 'unstamped',
                    `an ACCEPTED utterance never arrived after ${seconds}s`);
            }
        } catch (_e) { /* the verdict stands even if the record cannot */ }
        try {
            this._renderSystemNotice('Your words did not reach anyone.', 'degradation',
                { cycleId: entry.cycleId });
        } catch (_e) { /* the verdict stands even if the notice cannot render */ }
    },

    /**
     * Put one spoken line on the screen, verbatim.
     *
     * D2 — a proper name actually SPOKEN stays as the character said it; the
     * line is NOT re-rendered per listener. The node is admitted through the
     * terminal funnel as LEDGER CONTENT on channel 4, so the Terminal Truth Log
     * and `exportGameplayLog` see it ([2026-08-08-007-ENGINE]).
     *
     * The reader keeps their place: a line scrolls only a history already at
     * its bottom; otherwise a quiet "new line below" cue shows. The command
     * line is outside `#history`, so its focus, text and caret are never
     * touched.
     */
    _renderCh4Line(entry) {
        const sid = this.state.sessionId || 'unknown';
        try {
            const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
            const history = (typeof document !== 'undefined')
                ? document.getElementById('history') : null;
            const addressedTo = entry.listener ? ` to ${entry.listener}` : '';
            if (!F || !history) {
                this.logMessage(`${entry.speaker}${addressedTo}: "${entry.text}"`);
            } else {
                const atBottom = this._ch4AtBottom(history);
                const node = document.createElement('div');
                const cue = this.CH4_LOOK.arrivalCue;
                node.className = 'message ai-response ch4-dialogue '
                    + (entry.addressed ? 'ch4-addressed' : 'ch4-overheard')
                    + (cue === 'fade' ? ' ch4-arrive-fade' : '')
                    + (cue === 'name' ? ' ch4-arrive-name' : '');
                if (cue === 'name') {
                    // The accent needs the speaker as its own element.
                    const who = document.createElement('span');
                    who.className = 'ch4-speaker';
                    who.textContent = `${entry.speaker}${addressedTo}`;
                    node.appendChild(who);
                    node.appendChild(document.createTextNode(`: "${entry.text}"`));
                } else {
                    node.textContent = `${entry.speaker}${addressedTo}: "${entry.text}"`;
                }
                F.insert(node, {
                    origin: F.ORIGIN.LEDGER_CONTENT,
                    channel: 4,
                    cycleId: entry.cycleId,
                    parent: history,
                    follow: atBottom,
                });
                if (!atBottom) this._ch4ShowNewBelow(history);
            }
            console.log(
                `[Delivery] cycle=${entry.cycleId || 'none'} ch=4 phase=rendered ` +
                `ordinal=${entry.ordinal !== undefined ? entry.ordinal : 'none'}`
            );
        } catch (e) {
            // A render failure is a DROPPED LINE and must say so -- this is the
            // last hop, so silence here is indistinguishable from never having
            // been spoken.
            console.error(
                `[${sid}] ch4 line FAILED TO RENDER: speaker=${entry.speaker} ` +
                `ordinal=${entry.ordinal} cycle=${entry.cycleId || 'none'}: ${e}`
            );
        }
    },

    /** The reader is at the bottom of the history (within a few pixels). */
    _ch4AtBottom(history) {
        if (!history || history.scrollHeight === undefined) return true;
        return history.scrollHeight - history.scrollTop - history.clientHeight <= 4;
    },

    /** Show the "new line below" cue until the reader scrolls back down. */
    _ch4ShowNewBelow(history) {
        const host = history.parentNode;
        if (!host) return;
        let cue = host.querySelector('.ch4-new-below');
        if (!cue) {
            cue = document.createElement('button');
            cue.type = 'button';
            cue.className = 'ch4-new-below';
            cue.textContent = 'new line below';
            cue.addEventListener('click', () => {
                history.scrollTop = history.scrollHeight;
            });
            history.addEventListener('scroll', () => {
                if (this._ch4AtBottom(history)) cue.classList.remove('ch4-new-below-shown');
            });
            host.appendChild(cue);
        }
        cue.classList.add('ch4-new-below-shown');
    },

    // NOTE: _pump() and _playNextFromQueue() have been replaced by TurnPump._advance().

    // Play a single narration entry (typewriter + audio).
    // [W1 P2 combat_log retirement, 2026-07-09] The legacy instant-render
    // `combatLog` packet branch (CombatLog.render) is removed — nothing
    // enqueued a combatLog packet since the L9 wire cutover, and the
    // `combat_log` wire vector itself is retired. ch2 dice render via
    // Ch2Renderer.renderResolutionEvent on the resolution_event stream.
    _playNarration(packet) {
        const { text, audioUrl, audioDurationMs, narrator, context, channel, requestId, turnId, textStyle, cycleId, commandId } = packet;
        // [vp-hardening-2026-05-09 D3] Cache cycle_id for the typewriter
        // path. Set BEFORE either branch so the typewriter element gets
        // annotated regardless of audio / text-only path.
        this._currentNarrationCycleId = cycleId || null;

        this._currentNarrationContext = context;
        this._currentNarrationChannel = channel !== undefined ? channel : 0;
        // [2026-09-01-001-ENGINE] `narrator` was destructured here and then
        // dropped. It is the key the per-narrator voice trim is looked up by,
        // so it now rides the same `_currentNarration*` path the channel does
        // and reaches `playAudio`'s meta -> routeMediaElement().
        this._currentNarrationNarrator = narrator || null;
        this._currentNarrationTurnId = turnId;
        this._currentTextStyle = textStyle || 'normal';
        // ch1's command id rides its playback_complete, which attributes by it.
        this._currentNarrationCommandId = commandId || null;
        let voiceData = null;
        if (audioUrl) {
            // THE DURATION IS RECEIVED, NEVER INVENTED (`frame.md` P9).
            //
            // ⚠️ THIS READ `duration: Math.max(3, text.length / 12)` -- a made-up
            // number that the pace formula and the alignment log then reported
            // back in the units of a MEASUREMENT ("over 46.58s audio" WAS
            // len/12). Measured against 83 real narrations in the captured
            // corpus it UNDER-estimated 75% of the time: mean -6.9%, worst
            // -29.1%, and up to 14.1 SECONDS of audio the client did not know
            // about on a single long passage. Real speech ran 8.5-15.1
            // chars/sec against the formula's assumed flat 12.0.
            //
            // `null` when the server sent none. NOT a fallback estimate: the
            // consumer below branches on null and says out loud that its pace
            // is assumed, which is the honest state. Re-deriving a number here
            // is the prompt-lock class one layer down -- the client computing a
            // fact the server already holds.
            voiceData = {
                audio_url: audioUrl,
                duration: (
                    typeof audioDurationMs === 'number' && audioDurationMs > 0
                        ? audioDurationMs / 1000
                        : null
                ),
            };
        }

        this._fullNarrationText = text;
        // [2026-07-29-012-QASTI] Keep the last SCENE (ch0) so a screen that
        // covers the terminal can re-state the beat it covered. DoD 2: the
        // overlay may not destroy history the player has not read.
        if (this._currentNarrationChannel === 0 && text) {
            this._lastSceneText = String(text);
        }
        this._textCompleteReceived = true;
        // P2 ANCHOR: armed on the line before the typewriter starts, so "45s
        // from ch0 text render" is literally what the timer measures. Arming it
        // any earlier (on frame arrival, on the pump event) would silently
        // charge the ceiling for time the player spent looking at nothing.
        this._armCh0PlaybackCeiling();
        this._startAudioSyncedTypewriter(voiceData);
    },


    // Get connection status
    getStatus() {
        return {
            isConnected: this.state.isConnected,
            connectionType: this.state.connectionType,
            currentWorld: this.state.currentWorld?.world_name || null,
            isInGame: this.state.isInGame,
            reconnectAttempts: this.state.reconnectAttempts,
            gameserverName: this.state.gameserverName,
            gameserverGeneration: this.state.gameserverGeneration,
            isStateless: this.state.isStateless
        };
    },

    // Log message to terminal
    // [render-honesty 3a] Mechanical, client-side system copy. NO LLM, no
    // prompt entities, no fiction voice — the spark thread (Lane 3) owns
    // fictional rendering later. Keyed on the gate's verbatim refusal_reason;
    // an unknown reason falls back to the generic line (never a blank).
    // [render-honesty 3a §3] Mechanical copy per refusal_reason.
    //
    // The `||` default is load-bearing: a server that adds a reason before the
    // client knows it still renders a VISIBLE line. Never replace it with a
    // lookup that can return empty -- that would reintroduce the silence this
    // whole path exists to kill.
    //
    // Copy is about WHAT HAPPENED MECHANICALLY, never about the character's
    // capability ([2026-08-03-012-ENGINE]). "You don't know how to do that"
    // would be a lie whenever the miss is a parse failure over a skill the
    // character genuinely has -- and it would teach the player their build is
    // wrong when the parser is.
    _refusalCopy(reason) {
        const map = {
            'no-skill-start': 'You cannot act.',
            'no-skill-matched': 'That command was not understood.',
            'arbitration-unresolved': 'Your action could not be resolved.',
            // [2026-08-06-007-ENGINE] MOVEMENT. Until this landed, a move that
            // did not happen produced no refusal frame at all -- it stayed on
            // the narration surface and ch0 wrote a confident arrival into the
            // room the player had just failed to leave. Eight of ten movement
            // commands on live prod ([2026-08-06-004-QASTI]), and the same
            // invisible-render half filed 28 days earlier off an entirely
            // different root ([2026-07-09-024-ENGINE]).
            'movement-no-direction': 'You do not move -- which way?',
            // TWO OF A KIND. The target named several the rule could not tell
            // apart: nothing was sent, and the player is asked, never answered
            // with a pick. The names ride `refusal_detail`; the command per
            // choice rides `refusal_choices`, each a chip that lays it.
            'target-which-one': 'Which one?',
            'movement-no-such-exit': 'You do not move -- there is no way through.',
            'movement-refused': 'You do not move.',
            // [2026-08-08-005-ENGINE] DIALOGUE. `talk to <name>` names a
            // person and says nothing. The parser used to MINT the word
            // "hello" and ship it as the player's own line on ch4 -- speech
            // they never typed, in their voice. That invention is gone, so the
            // wordless opener now reaches the server's existing
            // `dialogue-empty-payload` refusal, and this is the line that
            // makes it visible instead of dead air.
            //
            // Copy asks for the words rather than reporting a failure: the
            // player did nothing wrong, they just have not said anything yet.
            'dialogue-empty-payload': 'You say nothing -- what do you want to say?',
            // POSSESSION (take / drop / give / loot): an item that did not move.
            // A refused move is partitioned out of the narration, so this line
            // is the player's only account of it.
            'possession-no-such-item': 'There is nothing like that here to take.',
            'possession-not-carried': 'You are not carrying that.',
            'possession-no-recipient': 'No one here can take it from you.',
            'possession-no-source': 'There is no one like that here.',
            'possession-not-fallen': 'They are still alive -- you can only loot the fallen.',
            'possession-not-on-body': 'They carry nothing like that.',
            // A kit item stays with its bearer (decision-log 2026-10-08).
            'possession-kit-item': "You'd feel naked without it.",
            'possession-kit-of-fallen': 'That stays with the fallen.',
            'possession-refused': 'Nothing changes hands.',
        };
        return map[reason] || 'Your command is refused.';
    },

    // [2026-08-08-014-ENGINE] Mechanical copy per notice_reason.
    //
    // A NOTICE IS NOT A REFUSAL. Every line here must be true of an action that
    // HAPPENED — it reports what did not land, never "your command is refused",
    // which would contradict the ch4 echo of the player's own words arriving in
    // the same beat.
    //
    // The `||` default is load-bearing for the same reason it is on
    // `_refusalCopy`: a server that adds a reason before the client knows it
    // still renders a VISIBLE line. Never replace it with a lookup that can
    // return empty -- that rebuilds the silence this path exists to kill.
    _noticeCopy(reason) {
        const map = {
            // The player named somebody who is not here. They still spoke, and
            // the room still heard them -- so the copy says what was missed,
            // not that nothing happened. The absent name rides `notice_detail`.
            'dialogue-addressee-absent': 'No one here answers to that name.',
            // The player spoke to a body. The words were spoken; the one they
            // were meant for is dead and was sent nothing. No `notice_detail`
            // rides this one.
            'dialogue-addressee-down': 'The one you spoke to is dead.',
        };
        return map[reason] || 'Part of your command did not take effect.';
    },

    // [render-honesty 3a §4] Mechanical degradation copy for a FAILURE-class
    // void, distinct per channel. ch0 = the scene, ch1 = the lead-in (N-1
    // dead-air commentary). Absence-class voids never reach here.
    _voidDegradationCopy(channel) {
        if (channel === 1) return 'The lead-in fails to render.';
        return 'The scene fails to render.';
    },

    // [render-honesty 3a] Insert a visible, system-styled notice line into
    // #history, respecting the actor model (above a live prompt, or appended
    // when the prompt is locked) via the shared insert guard. `extraClass`
    // distinguishes a refusal from a render degradation for styling. Text is
    // mechanical — never narration — so it carries the `system-notice` class,
    // NOT `narration-stream`.
    //
    // [FB-HUD-02] `opts.cycleId` — THE BEAT THIS NOTICE IS ABOUT, when it is
    // about one. Two things follow from supplying it, and they are the same
    // fact stated twice:
    //
    //   1. the node is STAMPED with it. client_funnel.md §2 says a
    //      `server-authority` write carries a `cycle_id` "when the frame
    //      carries one" — and the owed-channel verdicts are COMPUTED from a
    //      beat id, then rendered without it. That is a coordinate the writer
    //      held and dropped.
    //   2. the line is anchored ABOVE that beat's ch0, so ch0 stays the last
    //      thing on screen when the prompt unlocks.
    //
    // ⚠️ THE DEFAULT IS STILL APPEND, DELIBERATELY. A notice that is NOT about
    // a beat must not be hoisted into one — the roster-hold line ("Press ENTER
    // when you are ready to take up a vessel") is a call to action, and burying
    // it above a scene would be a worse defect than the one this fixes. Absence
    // of a coordinate is the honest signal there, not an oversight.
    _renderSystemNotice(text, extraClass, opts) {
        const history = (typeof document !== 'undefined')
            ? document.getElementById('history')
            : null;
        if (!history) {
            // Headless / pre-DOM: fall back to the log channel so the fact is
            // never lost (also the unit-harness path).
            this.logMessage(`[System] ${text}`);
            return;
        }
        // [2026-08-08-007-ENGINE] ORIGIN: server-authority. THIS IS THE SINK
        // THE REFUSAL LINE USES, and channels.md section 4 REQUIRES a refusal to
        // render: "a refused command renders a refusal, never a scene." The
        // client's own copy map records that silence shipping TWICE - once on
        // movement, once on dialogue - which is exactly why the funnel is an
        // ORIGIN CONTRACT and not a channel whitelist. A whitelist would have
        // deleted this line and rebuilt the bug.
        const _F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!_F) {
            this.logMessage(`[System] ${text}`);
            return null;
        }
        const _beat = (opts && opts.cycleId) ? String(opts.cycleId) : null;
        const _anchor = (_beat && typeof TurnPump !== 'undefined')
            ? TurnPump._blockAnchor('trailing', _beat) : null;
        const _node = _F.write(text, {
            origin: _F.ORIGIN.SERVER_AUTHORITY,
            className: `message system-notice ${extraClass || ''}`.trim(),
            parent: history,
            cycleId: _beat,
            before: _anchor
        });
        // [2026-08-23-006-ENGINE] THE AUDIT RECORD THIS SINK NEVER HAD.
        //
        // `[TEXT]` is the render oracle -- the one line that separates "the
        // frame never arrived" from "it arrived, the handler ran, and the
        // render died in the sink". It was emitted by exactly six call sites
        // (CLOCK, IDLE_INJECT, THINKING, ROOM_STATE_PANEL, and the two
        // narration paths) and by NONE of the system-notice paths.
        //
        // ⚠️ SO THE ABSENCE OF A `[TEXT]` AFTER A REFUSAL PROVED NOTHING, and
        // 006 was originally diagnosed as "the write died inside
        // TerminalFunnel" on exactly that absence. It had not: the funnel
        // renders this line correctly (server-authority allows a null
        // cycle_id -- CONTRACT says `cycleId: 'optional'`), it logged no
        // violation, and the fallback `[System]` path was not taken either.
        // The instrument simply did not cover the path, so every future
        // investigation of a "swallowed" command would have re-run the same
        // dead end.
        //
        // Emitted AFTER the write and keyed on its RETURN VALUE, so it reports
        // what actually happened rather than what was attempted -- a counter
        // that counts attempts is a lie.
        try {
            if (_node) {
                console.log(
                    `[TEXT] actor=SYSTEM_NOTICE placement=history prompt=live `
                    + `channel=system class=${extraClass || 'none'} len=${String(text).length}`
                );
            } else {
                console.error(
                    `[TEXT] actor=SYSTEM_NOTICE DROPPED class=${extraClass || 'none'} `
                    + `len=${String(text).length} -- the funnel returned no node; `
                    + `the player was told nothing.`
                );
            }
        } catch (_e) { /* never break the render path for an audit line */ }
        return _node;
    },

    // [render-honesty 3a §3] Render the refusal line. Render-only (see the
    // dispatch case); does not touch TurnPump.
    _renderActionRefused(message) {
        const base = this._refusalCopy(message && message.refusal_reason);
        // [2026-08-06-007-ENGINE] Append the server's MECHANICAL detail when it
        // sent one -- for a movement refusal that is the room's own authored
        // exit list ("exits: east, south"), which is the single most useful
        // thing a stuck booth player can be told. It is derived from authored
        // data, never LLM prose, so it belongs on this mechanical line and not
        // in the scene. Absent detail => the reason-keyed copy alone, exactly
        // as before. The reason line always renders first, so a malformed or
        // empty detail can never turn this frame back into silence.
        const detail = (message && typeof message.refusal_detail === 'string')
            ? message.refusal_detail.trim()
            : '';
        const text = detail ? `${base} (${detail})` : base;
        const node = this._renderSystemNotice(text, 'refusal');
        const choices = (message && Array.isArray(message.refusal_choices))
            ? message.refusal_choices.filter((c) => typeof c === 'string' && c.trim())
            : [];
        if (choices.length) this._appendChoiceChips(node, choices);
    },

    /** An ask's answers under its line: one chip per choice, each laying its
     *  whole command on the line (a press never sends; a locked prompt takes
     *  none). A line that did not render carries none, said. */
    _appendChoiceChips(node, choices) {
        if (!node || typeof document === 'undefined') {
            console.error(`[chip] choices path=no-line count=${choices.length} -- the ask's line did not render`);
            return;
        }
        const row = document.createElement('span');
        row.className = 'which-one-choices';
        choices.forEach((fill) => {
            const chip = this._buildChipButton('line', fill);
            chip.className = 'room-state-chip which-one-chip';
            chip.textContent = fill;
            row.appendChild(chip);
        });
        // A chip must not take focus: the field's blur handler SENDS its line.
        row.addEventListener('mousedown', (e) => e.preventDefault());
        row.addEventListener('click', (e) => {
            const chip = e.target.closest ? e.target.closest('.which-one-chip[data-fill]') : null;
            if (!chip) return;
            const CL = (typeof window !== 'undefined') ? window.CommandLine : null;
            if (!CL || CL.isLocked()) {
                console.log('[chip] fill path=locked -- the prompt is closed; nothing laid');
                return;
            }
            this._layChipDraft(this._composeChip('line', chip.getAttribute('data-fill')));
        });
        node.appendChild(row);
        console.log(`[chip] choices path=laid count=${choices.length}`);
    },

    // [2026-08-08-004-QASTI] An NPC's failed turn has no other surface: no
    // ch2 row, no ch0 acknowledgement, no refusal frame (those address the
    // acting PLAYER). From the player's chair a dead NPC turn and an NPC
    // legitimately doing nothing were byte-identical — which is how five
    // dispatch failures per fight ran silent on live prod
    // (`red - Unknown skill category for 'idle'`, funnel-5e13250a).
    //
    // Render it as server-authority degradation copy, same sink as the
    // player's own action-error line. Render-only; does not touch TurnPump.
    //
    // Boundaries, each deliberate:
    //  * controller gate — stamped server-side by _get_controller_type
    //    ("human" | "script" | "goap"). A human row already renders its own
    //    error via the top-level `result.error` path; re-rendering it here
    //    would double it. ABSENT controller means an old server: stay
    //    silent rather than mis-render a player's row.
    //  * rejected rows are excluded — a refusal renders via its own
    //    `action_refused` frame; surfacing it twice contradicts the
    //    refusal partition.
    //  * ABSENCE rows are excluded — see `_isEngineTimeoutSubstitution`.
    //  * the error detail is truncated mechanical text, never LLM prose.
    _surfaceNpcActionFailure(action, execResult) {
        if (!action || !execResult) return;
        if (!action.controller || action.controller === 'human') return;
        if (execResult.rejected) return;
        // [Thread 0 / T0-B] THE CLIENT HALF OF THE all_timeout SPLIT
        // ([2026-08-13-015-ENGINE]). `execution_result.success === false` is
        // NOT the discriminator: a beat nobody submitted into carries
        // `{success:false, fallback:true, error:"timeout"}` for every
        // non-acting actor, so this line drew one "fails to resolve
        // (timeout)" per NPC per phase — 93 error frames in 30 commands on
        // the 2026-08-19 round-2 capture, printed ABOVE the scene it buried.
        // Nothing failed. Nobody acted.
        if (this._isEngineTimeoutSubstitution(action)) return;
        const npcName = action.display_name
            || action.character_name || 'An inhabitant';
        const errDetail = String(execResult.error || '').slice(0, 120);
        this._renderSystemNotice(
            `${npcName}'s action fails to resolve.`
                + (errDetail ? ` (${errDetail})` : ''),
            'action-error'
        );
    },

    // [Thread 0 / T0-B] Was this row the ENGINE'S OWN clock substitution —
    // i.e. did NOBODY submit anything for this actor this beat?
    //
    // ONE AUTHORITY FOR THE FACT: both the render gate above and the
    // batched-error log in `handleActionResult` ask this, so the predicate
    // lives here once. It reads the THREADED marker and nothing else.
    //
    // ⚠️ THE MARKER IS THE ONLY DISCRIMINATOR THAT SURVIVES TO THE WIRE, and
    // it is threaded, never re-derived. The room clock sets `timeout_fallback`
    // on the row it substitutes (action_queue.py:322-325 is the authority on
    // that marker); `_collect_beat_actions` stamps it as `is_timeout_fallback`
    // on the phase-resolve action (turn_engine.py:4246);
    // `_resolve_phase` re-threads it onto the resolved row
    // (turn_engine.py:4502-4511); the whole `phase_result` is POSTed to
    // `/api/phase-result` (turn_engine.py:8207) and `actions` is forwarded
    // VERBATIM onto the ch9 `action_result` frame
    // (game_server/networking.py:3350 -> :3532). Confirmed present on the live
    // wire in the 2026-08-16 capture (`auto-vp-103018-1786905206/ws.json`:
    // 10 rows `is_timeout_fallback:true` alongside 10 `success:false` rows with
    // it `false`). Matching `action_text` against "idle" instead is the
    // re-derivation [2026-07-17-002-ENGINE] forbids — a player may legitimately
    // type "idle", and that is a real submission.
    //
    // ⚠️ ABSENT => LOUD (RENDER). Ruled deliberately, and NOT by analogy to the
    // `controller` gate above, which stays silent on absence:
    //
    //  * the two gates answer different questions. An absent `controller` means
    //    the server never had the fact, so rendering would GUESS that a
    //    player's own row belongs to an NPC and double their error line — the
    //    failure mode there is mis-attribution.
    //  * an absent `is_timeout_fallback` is a POSITIVE SIGNAL from a current
    //    server. `_resolve_phase`'s `except` arm — the fourth timeout producer,
    //    a DEAD AI SERVICE — rebuilds every row in the phase from scratch and
    //    the re-thread block is skipped (`if character_actions and not
    //    _resolve_raised`), so those rows carry NO key at all. Failing silent
    //    on absence would mute exactly the hard fault the loud-failure law
    //    protects, and mute it precisely when the whole engine is down.
    //  * so absence here is AMBIGUOUS-AT-WORST (old server) and
    //    HARD-FAULT-AT-LIKELIEST, and the rule for a conflation is already
    //    written: "an ambiguous reason takes the LOUD direction, because the
    //    safe wrong direction is the visible one"
    //    (services/plug_core/frame.py, `VOID_ABSENCE_REASONS`).
    //    A player shown one stale error line can still play; a player shown
    //    silence while the service is dead cannot tell that from an idle room.
    //
    // Strict `=== true`, not truthiness: the marker is a JSON boolean minted by
    // one funnel. Anything else is not that funnel's fact and takes the loud
    // direction with every other unknown.
    _isEngineTimeoutSubstitution(action) {
        return !!action && action.is_timeout_fallback === true;
    },

    // [2026-08-08-005-QASTI] The empty character_choice frame, rendered
    // honestly. Two rules, both learned live:
    //  * THE ADVICE MUST MATCH THE CAUSE. "Please reconnect to try again"
    //    was rendered for a POOL-EXHAUSTED world (`dockertest-1783099471`,
    //    pool 12/0) where reconnecting provably re-reads the same full
    //    world. The frame's own markers (roster_exhausted, 2026-07-30-004)
    //    carry the distinction — read them.
    //  * THE WAY BACK MUST BE NAMED. `/world` commands stay local in
    //    CHARACTER_CHOICE (isGameCommand routes them to WorldCommands), so
    //    the route back to the lobby has existed all along — but no copy
    //    ever told the player, and the funnel probe sat wedged in
    //    CHARACTER_CHOICE until its timeout. An affordance the screen
    //    never names does not exist from the player's chair.
    _renderEmptyRosterFrame(message, content) {
        this.logMessage(content || 'No characters are available right now.');
        const exhausted = !!(message && message.roster_exhausted === true);
        if (exhausted) {
            this.logMessage(
                'This world is full — every character is spoken for. '
                + 'Type /world list to choose another world.');
        } else {
            this.logMessage(
                'The world could not offer you a vessel. Type /world list '
                + 'to choose another world, or reconnect to try again.');
        }
    },

    // [2026-08-08-014-ENGINE] Render the notice line. Render-only (see the
    // dispatch case); does not touch TurnPump.
    //
    // Same sink and same shape as the refusal line -- `_renderSystemNotice`
    // writes through TerminalFunnel as SERVER_AUTHORITY -- with its own
    // `notice` class so a notice can be styled apart from a refusal. The reason
    // line always renders first, so a malformed or empty detail can never turn
    // this frame back into the silence it exists to end.
    _renderActionNotice(message) {
        const base = this._noticeCopy(message && message.notice_reason);
        const detail = (message && typeof message.notice_detail === 'string')
            ? message.notice_detail.trim()
            : '';
        const text = detail ? `${base} (${detail})` : base;
        this._renderSystemNotice(text, 'notice');
    },

    // [render-honesty 3a §4] Render a FAILURE-class void degradation line.
    //
    // Dedup ONLY when cycle_id is genuinely known: the double-void the memo
    // targets (a phase-result narrate_failed AND a phase_settled empty_phase for
    // the same ch0 position) is ch0-only and BOTH carry cycle_id. ch1 voids
    // carry cycle_id=None BY CONTRACT (`_send_ch1_void`: a ch1 answers a command and
    // carries no beat) and have no double-emit path — so
    // a null-cycle void must render EVERY time. Keying a null cycle would dedup
    // every ch1 failure after the first into silence: the exact suppression 3a
    // exists to kill. Latch/drain flip is the caller's and is unaffected.
    _renderVoidDegradation(channel, message) {
        const cycleId = message ? message.cycle_id : null;
        if (cycleId != null) {
            const dedupKey = `${cycleId}:${channel}`;
            if (!this._degradationRendered) this._degradationRendered = {};
            if (this._degradationRendered[dedupKey]) {
                console.log(`[render-honesty] degradation line already rendered for ${dedupKey} — skipping duplicate`);
                return;
            }
            // Light cap: keyed by cycle_id, the memo grows one entry per cycle
            // over a session. Prune oldest keys past a generous ceiling so it
            // stays bounded without affecting within-cycle dedup.
            const keys = Object.keys(this._degradationRendered);
            if (keys.length >= 256) {
                delete this._degradationRendered[keys[0]];
            }
            this._degradationRendered[dedupKey] = true;
        }
        const text = this._voidDegradationCopy(channel);
        // [FB-HUD-02] A ch1 void on a beat whose ch0 DID render belongs above
        // that ch0. A ch0 void leaves no ch0 node to anchor to, so it appends —
        // and it IS the beat's last line, which is correct: it is standing in
        // for the scene.
        this._renderSystemNotice(text, 'degradation', { cycleId: cycleId });
    },

    /**
     * [2026-08-08-007-ENGINE / client_funnel.md section 5 leak 2]
     * A DORMANT PROSE ARM FIRED. That is news, not routine.
     *
     * `game_message`, `world_event`, `player_joined` and `player_left` are wire
     * types this client has an arm for and NOTHING IN THE REPOSITORY EMITS -
     * no producer in services/, none in game_server/. Each rendered free prose
     * with no channel, no cycle and no ledger enrolment: unaccounted by
     * construction, exactly like the ch9 leak, and only harmless because
     * nothing sends them.
     *
     * They are refused rather than deleted BECAUSE a producer appearing is
     * information. Deleting the arms would send the frame to `default:`, and a
     * frame that vanishes into a default arm is how ch4's `queue_admitted`
     * stayed mute through the one moment it existed to communicate. This says
     * so instead.
     */
    _refuseDormantProse(wireType, payload) {
        const sessionId = this.state.sessionId || 'unknown';
        console.warn(
            `[FUNNEL] DORMANT ARM FIRED: '${wireType}' has no producer in ` +
            `services/ or game_server/, and this arm rendered unaccounted prose ` +
            `(len=${payload ? String(payload).length : 0}) with no channel and no ` +
            `cycle. NOT RENDERED. [${sessionId}] If this type is now real, give it ` +
            `a registry row in combat.md section 10, a channel, and a client arm ` +
            `that declares an origin — all three, in one PR.`
        );
    },

    /**
     * Route a session notice to the terminal.
     *
     * ⚠️ THIS IS NOT A CONTENT SINK. It forwards to `WorldCommands.addMessage`,
     * whose declared axis is `game-state-transition` — read the contract in the
     * header of that function before adding a caller. ch4 dialogue used to come
     * through here and no longer does; that was leak 5.
     *
     * @param {string} message
     * @param {{origin?: string, cycleId?: string}} [opts] — for a caller on a
     *        different axis than the sink's default.
     */
    logMessage(message, opts) {
        if (typeof WorldCommands !== 'undefined') {
            WorldCommands.addMessage(message, opts);
        } else {
            console.log(message);
        }
    },

    // NOTE: _startPendingClock() has been replaced by TurnPump._revealClock().

    // Flush buffered connection messages
    flushBufferedMessages() {
        if (this.state.bufferedMessages && this.state.bufferedMessages.length > 0) {
            this.state.bufferedMessages.forEach(msg => this.logMessage(msg));
            this.state.bufferedMessages = [];
        }
    }
};

// Make available globally
if (typeof window !== 'undefined') {
    window.GameServerConnection = GameServerConnection;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = GameServerConnection;
}

// [VP-1 / dispatch-substrate-write-card] Named ESM exports so this file can be
// imported as a Vite module from main.js. Window globals above are preserved
// for non-module consumers (clock-line.js, ch2-renderer.js, game-diagnostics.js,
// script-loader.js, terminal-fsm.js) and for VP cross-document introspection
// (window.__VP_PUMP_STATE, window.__VP_PROMPT_STATE, etc. are set in
// vpInstrumentation IIFE at top of file and stay window-bound by design).
export {
    GameServerConnection,
    TurnPump,
    PhaseBanner,
    WaitingIndicator,
    // [2026-08-08-007-ENGINE] `insertContentInHistory` is gone from this export
    // list because it is gone from the file. The door is window.TerminalFunnel.
    logSync,
};
// [2026-07-27-003-QASTI BUG 2] Belt-and-braces arm at module load.
// init() is the PRIMARY hook (DOMContentLoaded, before login) — this exists only
// so a boot path that never reaches init() (script-loader bail, a shell that
// constructs the connection directly) still has the gesture hook installed. It
// is idempotent with init()'s call: the first one wins, the second is a no-op.
try {
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
        GameServerConnection._armAudioUnlockGesture();
    }
} catch (_e) { /* arming must never break module load */ }

export default GameServerConnection;